// src/generateApiKey.ts
var CHARSET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
var generateApiKey = (length = 128) => {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.getRandomValues) {
    throw new Error(
      "generateApiKey requires the Web Crypto API (globalThis.crypto.getRandomValues), which is unavailable in this environment."
    );
  }
  const bytes = new Uint32Array(length);
  cryptoObj.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += CHARSET[bytes[i] % CHARSET.length];
  }
  return out;
};
var maskApiKey = (key) => {
  if (!key) return "";
  if (key.length <= 12) return key;
  return `${key.slice(0, 8)}\u2026${key.slice(-4)}`;
};

// src/perf/marks.ts
var EQUIPMENT_PHASES = {
  /** Mode entered → both panel and scene complete. The number Rob is actually reporting. */
  total: "equipment:total",
  /** Envelope over both fetches: earliest start → latest end. */
  fetch: "equipment:fetch",
  /**
   * The two Equipment fetches, separately.
   *
   * NOT IN THE SPEC'S TABLE, and added because the spec did not know there were two: Equipment mode
   * issues `getEquipmentClassifications` (from `TwinPage`) and the dateless baseline read (from
   * `DataContext`) independently, from two uncoordinated effects. A single `equipment:fetch` would
   * hide which of them is slow — the first question anyone will ask.
   */
  fetchClassifications: "equipment:fetch:classifications",
  fetchBaseline: "equipment:fetch:baseline",
  /** Response received → data in store. Answers "are we deserialising something huge?". */
  parse: "equipment:parse",
  /**
   * Mode entered → the panel's first painted frame with content.
   *
   * NB timed from MODE ENTRY rather than from "data in store": the panel renders its rows from the
   * DGraph twin model while the classifications and baseline reads land in two other stores at two
   * other times, so there is no single "data in store" instant to anchor to. This is also the number
   * the user experiences. Subtract `fetch`/`parse` to isolate render time.
   */
  panelRender: "equipment:panel-render",
  /** Mode entered → the 3D scene's first painted frame. Separately attributable from the panel, per R2. */
  sceneRender: "equipment:scene-render"
};
var handlers = [];
var available = () => typeof performance !== "undefined" && typeof performance.mark === "function" && typeof performance.measure === "function";
var isDev = () => typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
var onMeasure = (handler) => {
  handlers.push(handler);
  return () => {
    handlers = handlers.filter((h) => h !== handler);
  };
};
var markStart = (phase) => {
  if (!available()) return;
  try {
    performance.mark(`${phase}:start`);
  } catch {
  }
};
var markEnd = (phase) => {
  if (!available()) return void 0;
  const startMark = `${phase}:start`;
  try {
    if (performance.getEntriesByName(startMark, "mark").length === 0) return void 0;
    const entry = performance.measure(phase, startMark);
    const durationMs = entry?.duration ?? performance.getEntriesByName(phase, "measure").slice(-1)[0]?.duration;
    if (durationMs === void 0) return void 0;
    performance.clearMarks?.(startMark);
    const measure = { name: phase, durationMs };
    emit(measure);
    return measure;
  } catch {
    return void 0;
  }
};
var recordMeasure = (phase, durationMs) => {
  if (!Number.isFinite(durationMs) || durationMs < 0) return;
  const measure = { name: phase, durationMs };
  if (available()) {
    try {
      performance.measure(phase, { start: performance.now() - durationMs, duration: durationMs });
    } catch {
    }
  }
  emit(measure);
};
var clearPhase = (phase) => {
  if (!available()) return;
  try {
    performance.clearMarks?.(`${phase}:start`);
    performance.clearMeasures?.(phase);
  } catch {
  }
};
var emit = (measure) => {
  if (isDev()) {
    console.debug(`[perf] ${measure.name} ${Math.round(measure.durationMs)}ms`);
  }
  for (const handler of handlers) {
    try {
      handler(measure);
    } catch {
    }
  }
};

// src/perf/equipmentLoad.ts
var freshState = () => ({
  panelDone: false,
  sceneDone: false,
  dataArrived: {},
  renderPhasesStarted: false,
  fetchSpans: {},
  envelopeEmitted: false
});
var state = freshState();
var installed = false;
var loadToken = 0;
var currentEquipmentLoadToken = () => loadToken;
var noteEquipmentDataArrived = (source) => {
  if (state.dataArrived[source]) return;
  state.dataArrived[source] = true;
  if (state.renderPhasesStarted) return;
  if (!state.dataArrived.classifications || !state.dataArrived.baseline) return;
  state.renderPhasesStarted = true;
  markStart(EQUIPMENT_PHASES.panelRender);
  markStart(EQUIPMENT_PHASES.sceneRender);
};
var beginEquipmentLoad = () => {
  state = freshState();
  loadToken += 1;
  Object.values(EQUIPMENT_PHASES).forEach(clearPhase);
};
var installEquipmentLoadEnvelopes = (now = () => Date.now()) => {
  if (installed) return () => void 0;
  installed = true;
  const off = onMeasure((measure) => {
    switch (measure.name) {
      case EQUIPMENT_PHASES.fetchClassifications:
      case EQUIPMENT_PHASES.fetchBaseline: {
        const end = now();
        const key = measure.name === EQUIPMENT_PHASES.fetchClassifications ? "classifications" : "baseline";
        state.fetchSpans[key] = { start: end - measure.durationMs, end };
        const { classifications, baseline } = state.fetchSpans;
        if (!state.envelopeEmitted && classifications && baseline) {
          state.envelopeEmitted = true;
          recordMeasure(
            EQUIPMENT_PHASES.fetch,
            Math.max(classifications.end, baseline.end) - Math.min(classifications.start, baseline.start)
          );
        }
        break;
      }
      case EQUIPMENT_PHASES.panelRender:
        state.panelDone = true;
        closeTotalIfDone();
        break;
      case EQUIPMENT_PHASES.sceneRender:
        state.sceneDone = true;
        closeTotalIfDone();
        break;
      default:
        break;
    }
  });
  return () => {
    off();
    installed = false;
  };
};
var closeTotalIfDone = () => {
  if (!state.panelDone || !state.sceneDone) return;
  markEnd(EQUIPMENT_PHASES.total);
};

// src/mode/modeSelectors.ts
import {
  AppMode,
  DataMode
} from "@repo/frontend-types";
var dataModeFor = (appMode) => (
  // OPERATIONS is live-like: its headline occupancy reads the real-time dGraph subscription, so it
  // shares LIVE's DataMode (replace-embeddable ops-dashboard R1).
  appMode === AppMode.LIVE || appMode === AppMode.OPERATIONS ? DataMode.LIVE : DataMode.TIME_SERIES
);
var entityKeyFor = (appMode) => (
  // OPERATIONS reads the live subscription like LIVE, so its entities are keyed by `id` too
  // (replace-embeddable ops-dashboard R1 — the fly-to/hover wiring resolves via entityKeyFor).
  appMode === AppMode.LIVE || appMode === AppMode.OPERATIONS ? "id" : "bID"
);
var modeConfigFor = (heroMetric, appMode) => {
  if (!heroMetric) return void 0;
  switch (appMode) {
    case AppMode.LIVE:
      return heroMetric.modes[AppMode.LIVE];
    case AppMode.HISTORICAL:
      return heroMetric.modes[AppMode.HISTORICAL];
    case AppMode.INSIGHTS:
      return heroMetric.modes[AppMode.INSIGHTS];
    case AppMode.EQUIPMENT:
      return heroMetric.modes[AppMode.EQUIPMENT];
    case AppMode.OPERATIONS:
      return heroMetric.modes[AppMode.LIVE];
    case AppMode.ORG_SELECTION:
    case AppMode.TWIN_SELECTION:
    case AppMode.EDIT:
    case AppMode.DASHBOARD_UTILISATION:
      return void 0;
    default: {
      const _exhaustive = appMode;
      return _exhaustive;
    }
  }
};
var activeSceneBehaviourFor = (heroMetric, appMode) => (
  // Explicit guard (not `?? 'box'`): any value other than 'heatmap' — undefined,
  // or an unexpected string that bypassed hydration's normalisation — resolves to
  // 'box', so the return type is always honest.
  modeConfigFor(heroMetric, appMode)?.behaviour === "heatmap" ? "heatmap" : "box"
);
var heroMetricModeFor = (appMode) => {
  switch (appMode) {
    case AppMode.LIVE:
      return "LIVE";
    case AppMode.HISTORICAL:
      return "ANALYSIS";
    case AppMode.INSIGHTS:
      return "INSIGHTS";
    case AppMode.EQUIPMENT:
      return "EQUIPMENT";
    case AppMode.OPERATIONS:
      return "LIVE";
    case AppMode.ORG_SELECTION:
    case AppMode.TWIN_SELECTION:
    case AppMode.EDIT:
    case AppMode.DASHBOARD_UTILISATION:
      return null;
    default: {
      const _exhaustive = appMode;
      return _exhaustive;
    }
  }
};
var labelModeFor = (appMode) => heroMetricModeFor(appMode) ?? "ANALYSIS";
var indicatorScopeFor = (modes, appMode) => {
  const mode = heroMetricModeFor(appMode);
  if (mode === null) {
    return { mode: null, behaviour: null };
  }
  const row = modes?.find((entry) => entry.mode === mode);
  if (!row) {
    return { mode, behaviour: null };
  }
  return { mode, behaviour: row.behaviour ? row.behaviour.toUpperCase() : "BOX" };
};
var isHeroMetricEnabledForMode = (modes, appMode) => {
  const modeName = heroMetricModeFor(appMode);
  return modeName !== null && (modes?.some((entry) => entry.mode === modeName) ?? false);
};
var isDatelessCalendarMode = (appMode) => appMode === AppMode.INSIGHTS || appMode === AppMode.EQUIPMENT;
var isArealessSceneMode = (appMode) => appMode === AppMode.EQUIPMENT;

// src/indicatorScope/resolveIndicatorConfigScope.ts
var resolveIndicatorConfigScope = (entries, scope) => {
  if (!entries || entries.length === 0) return void 0;
  const M = scope?.mode ?? null;
  const B = scope?.behaviour ?? null;
  if (M !== null && B !== null) {
    const exact = entries.find((e) => e.mode === M && e.behaviour === B);
    if (exact) return exact;
  }
  if (M !== null) {
    const byMode = entries.find((e) => e.mode === M && e.behaviour == null);
    if (byMode) return byMode;
  }
  if (B !== null) {
    const byBehaviour = entries.find((e) => e.mode == null && e.behaviour === B);
    if (byBehaviour) return byBehaviour;
  }
  return entries.find((e) => e.mode == null && e.behaviour == null);
};

// src/findIndicatorConfigForMetric.ts
var findIndicatorConfigForMetric = (indicatorConfigs, indicatorMetricName, scope) => {
  if (!indicatorConfigs || indicatorConfigs.length === 0) {
    return void 0;
  }
  const candidates = indicatorConfigs.filter((ic) => ic.metric_name === indicatorMetricName);
  return resolveIndicatorConfigScope(candidates, scope)?.configurations;
};

// src/onboardingMode.ts
import { TWIN_LIFECYCLE_RANK, TwinLifecycleStageValues } from "@repo/frontend-types";
var UPDATE_FROM_RANK = TWIN_LIFECYCLE_RANK[TwinLifecycleStageValues.GraphSaved];
var resolveOnboardingMode = (stage) => {
  const rank = TWIN_LIFECYCLE_RANK[stage];
  return rank != null && rank >= UPDATE_FROM_RANK ? "UPDATE" : "CREATE";
};

// src/onboardingRoute.ts
var resolveOnboardingRoute = (args) => {
  if (resolveOnboardingMode(args.lifecycleStage) === "CREATE") return "create";
  switch (args.launchRecord) {
    case "present":
      return "manage";
    case "absent":
      return "migrate";
    default:
      return "live-unread";
  }
};
var isExistingTwinRoute = (route) => route !== "create";

// src/getIndicatorColor.ts
import { IndicatorColor } from "@repo/frontend-types";
var warnedMissingConfigMetrics = /* @__PURE__ */ new Set();
var warnMissingIndicatorConfig = (metricName) => {
  if (!metricName || warnedMissingConfigMetrics.has(metricName)) return;
  warnedMissingConfigMetrics.add(metricName);
  console.warn(
    `[getIndicatorColor] No IndicatorConfig for metric "${metricName}"; falling back to hardcoded colour ranges.`
  );
};
var evaluateCondition = (value, operator, compareValue) => {
  if (operator === null || compareValue === null) return true;
  switch (operator) {
    case "EQUAL_TO":
      return value === compareValue;
    case "GREATER_THAN":
      return value > compareValue;
    case "LESS_THAN":
      return value < compareValue;
    case "GREATER_THAN_OR_EQUAL_TO":
      return value >= compareValue;
    case "LESS_THAN_OR_EQUAL_TO":
      return value <= compareValue;
    default:
      console.warn("Unknown operator:", operator);
      return true;
  }
};
var getIndicatorColor = (value, config, selected, metricName) => {
  let textColor = IndicatorColor.BLACK;
  let bgColor = IndicatorColor.NA;
  if (value !== null && !config) {
    warnMissingIndicatorConfig(metricName);
  }
  if (value !== null && config) {
    const rangeMap = selected ? config.selected : config.non_selected;
    for (let i = 0; i < rangeMap.length; i++) {
      const rule = rangeMap[i];
      const minCondition = evaluateCondition(value, rule.min_value_op, rule.min_value);
      const maxCondition = evaluateCondition(value, rule.max_value_op, rule.max_value);
      if (minCondition && maxCondition) {
        textColor = rule.text_color || IndicatorColor.BLACK;
        bgColor = rule.bg_color || IndicatorColor.NA;
        break;
      }
    }
  } else {
    if (value) {
      if (selected) {
        if (value === 0) {
          bgColor = IndicatorColor.ZERO;
        } else if (value >= 1 && value <= 10) {
          bgColor = IndicatorColor.VERY_LOW;
        } else if (value >= 11 && value <= 20) {
          bgColor = IndicatorColor.LOW;
        } else if (value >= 21 && value <= 30) {
          bgColor = IndicatorColor.MEDIUM_LOW;
        } else if (value >= 31 && value <= 40) {
          bgColor = IndicatorColor.MEDIUM;
        } else if (value >= 41 && value <= 50) {
          bgColor = IndicatorColor.MEDIUM_HIGH;
        } else if (value >= 51 && value <= 75) {
          bgColor = IndicatorColor.HIGH;
        } else if (value >= 76 && value <= 100) {
          bgColor = IndicatorColor.VERY_HIGH;
        } else if (value > 100) {
          bgColor = IndicatorColor.EXTREMELY_HIGH;
        }
      } else {
        if (value === 0) {
          bgColor = IndicatorColor.ZERO_NOT_SELECTED;
        } else if (value >= 1 && value <= 10) {
          bgColor = IndicatorColor.VERY_LOW_NOT_SELECTED;
        } else if (value >= 11 && value <= 20) {
          bgColor = IndicatorColor.LOW_NOT_SELECTED;
        } else if (value >= 21 && value <= 30) {
          bgColor = IndicatorColor.MEDIUM_LOW_NOT_SELECTED;
        } else if (value >= 31 && value <= 40) {
          bgColor = IndicatorColor.MEDIUM_NOT_SELECTED;
        } else if (value >= 41 && value <= 50) {
          bgColor = IndicatorColor.MEDIUM_HIGH_NOT_SELECTED;
        } else if (value >= 51 && value <= 75) {
          bgColor = IndicatorColor.HIGH_NOT_SELECTED;
        } else if (value >= 76 && value <= 100) {
          bgColor = IndicatorColor.VERY_HIGH_NOT_SELECTED;
        } else if (value > 100) {
          bgColor = IndicatorColor.EXTREMELY_HIGH_NOT_SELECTED;
        }
      }
    }
  }
  return { textColor, bgColor };
};

// src/indicatorRamp/rasteriseIndicatorRamp.ts
var RAMP_WIDTH = 256;
var SAMPLE_STEPS = 512;
var FALLBACK_DOMAIN = [0, 150];
var parseHex = (hex) => {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
};
var indicatorRampDomain = (config) => {
  if (!config) return FALLBACK_DOMAIN;
  const bounds = [];
  for (const rule of config.selected) {
    if (rule.min_value != null) bounds.push(rule.min_value);
    if (rule.max_value != null) bounds.push(rule.max_value);
  }
  if (bounds.length === 0) return FALLBACK_DOMAIN;
  const min = Math.min(...bounds, 0);
  const max = Math.max(...bounds);
  if (!Number.isFinite(max) || max <= min) return FALLBACK_DOMAIN;
  return [min, max * 1.5];
};
var toHex = ({ r, g, b }) => `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
var rasteriseIndicatorRamp = (config, selected, metricName) => {
  const effectiveConfig = config && config.selected.length > 0 ? config : void 0;
  const domain = indicatorRampDomain(effectiveConfig);
  const [min, max] = domain;
  const span = max - min || 1;
  const bands = [];
  for (let i = 0; i < SAMPLE_STEPS; i++) {
    const t = i / (SAMPLE_STEPS - 1);
    const value = min + t * span;
    const hex = getIndicatorColor(value, effectiveConfig, selected, metricName).bgColor;
    const color = parseHex(hex) ?? { r: 255, g: 255, b: 255 };
    const last = bands[bands.length - 1];
    if (last && last.color.r === color.r && last.color.g === color.g && last.color.b === color.b) {
      last.endT = t;
    } else {
      bands.push({ startT: t, endT: t, color });
    }
  }
  const stops = bands.map((b) => ({ t: (b.startT + b.endT) / 2, color: b.color }));
  const data = new Uint8Array(RAMP_WIDTH * 4);
  const colorAt = (t) => {
    if (stops.length === 0) return { r: 255, g: 255, b: 255 };
    if (t <= stops[0].t) return stops[0].color;
    if (t >= stops[stops.length - 1].t) return stops[stops.length - 1].color;
    for (let s = 0; s < stops.length - 1; s++) {
      const a = stops[s];
      const b = stops[s + 1];
      if (t >= a.t && t <= b.t) {
        const f = (t - a.t) / (b.t - a.t || 1);
        return {
          r: Math.round(a.color.r + (b.color.r - a.color.r) * f),
          g: Math.round(a.color.g + (b.color.g - a.color.g) * f),
          b: Math.round(a.color.b + (b.color.b - a.color.b) * f)
        };
      }
    }
    return stops[stops.length - 1].color;
  };
  for (let i = 0; i < RAMP_WIDTH; i++) {
    const { r, g, b } = colorAt(i / (RAMP_WIDTH - 1));
    data[i * 4] = r;
    data[i * 4 + 1] = g;
    data[i * 4 + 2] = b;
    data[i * 4 + 3] = 255;
  }
  return { domain, stops: stops.map((stop) => ({ t: stop.t, color: toHex(stop.color) })), data };
};

// src/buildHeroMetric.ts
import {
  AppMode as AppMode2
} from "@repo/frontend-types";
var APP_MODE_FOR = {
  LIVE: AppMode2.LIVE,
  ANALYSIS: AppMode2.HISTORICAL,
  INSIGHTS: AppMode2.INSIGHTS,
  EQUIPMENT: AppMode2.EQUIPMENT
};
var buildHeroMetric = (storeMetric, indicatorConfig) => {
  const modes = {};
  for (const entry of storeMetric.modes ?? []) {
    const appMode = APP_MODE_FOR[entry.mode];
    if (appMode === void 0) continue;
    const config = {
      showDecimalPlace: entry.show_decimal_place ?? false,
      showUnit: entry.show_unit ?? false,
      unit: entry.unit ?? void 0,
      // R7 behaviour: universal (every mode, unlike aggregation). Normalised to
      // the known set here (the hydration boundary) so downstream readers get an
      // honest 'box' | 'heatmap' — anything else (absent/null, deploy-skew from an
      // older middleware, a future/typo'd wire value) degrades to 'box'.
      behaviour: entry.behaviour?.toLowerCase() === "heatmap" ? "heatmap" : "box"
    };
    if (entry.mode !== "LIVE") {
      config.aggregation = entry.aggregation?.toLowerCase();
    }
    modes[appMode] = config;
  }
  return {
    id: storeMetric.id,
    name: storeMetric.name,
    valueMetricName: storeMetric.value_metric.name,
    indicatorMetricName: storeMetric.indicator_metric.name,
    icon: storeMetric.icon || void 0,
    modes,
    indicatorConfig
  };
};

// src/buildTimeSeriesMetricList.ts
var buildTimeSeriesMetricList = (heroMetrics) => {
  if (!heroMetrics) return [];
  return Array.from(
    new Set(heroMetrics.flatMap((m) => [m.value_metric?.name, m.indicator_metric?.name]))
  ).filter((name) => !!name);
};

// src/buildBaselineMetricList.ts
var buildBaselineMetricList = (heroMetrics, appMode) => buildTimeSeriesMetricList(
  heroMetrics?.filter((hm) => isHeroMetricEnabledForMode(hm.modes, appMode))
);

// src/buildNameKeyedMetricRegistry.ts
var buildNameKeyedMetricRegistry = (metrics, heroMetrics) => {
  const heroMetricNames = buildTimeSeriesMetricList(heroMetrics);
  if (heroMetricNames.length === 0) return metrics;
  const existingNames = new Set(metrics.map((m) => m.name));
  const synthetic = heroMetricNames.filter((name) => !existingNames.has(name)).map(
    (name) => ({
      id: `twin-config:${name}`,
      name,
      unit: ""
    })
  );
  return synthetic.length === 0 ? metrics : [...metrics, ...synthetic];
};

// src/func-metrics/getDataValue.ts
import { DataMode as DataMode2 } from "@repo/frontend-types";

// src/findObjectByPropertyValue.ts
var findObjectByPropertyValue = (array, property, value) => {
  if (!array || !Array.isArray(array)) {
    return null;
  }
  return array.find((item) => item && item[property] === value) || null;
};

// src/func-metrics/getDataValue.ts
var getDataValue = (dataMode, data, id) => {
  return findObjectByPropertyValue(data, dataMode === DataMode2.LIVE ? "entityId" : "bID", id);
};

// src/func-metrics/getWindOrientation.ts
var getWindOrientation = (dataMode, data, id) => {
  if (data && data.length > 0) {
    const windOrientation = getDataValue(dataMode, data, id)?.value;
    return isNaN(windOrientation) ? `N/A` : `${windOrientation}`;
  } else {
    return "N/A";
  }
};

// src/func-metrics/getWindSpeed.ts
var getWindSpeed = (dataMode, data, id) => {
  if (data && data.length > 0) {
    const windSpeed = getDataValue(dataMode, data, id)?.value;
    return isNaN(windSpeed) ? `N/A` : `${windSpeed}m/s`;
  } else {
    return "N/A";
  }
};

// src/clearOrganizationData.ts
var clearOrganizationData = () => {
  try {
    localStorage.removeItem("twinlabs_selected_twin_bid");
    localStorage.removeItem("twinlabs_selected_org_bid");
    localStorage.removeItem("twinlabs_selected_org_name");
    localStorage.removeItem("twinlabs_selected_org_id");
    localStorage.removeItem("twinlabs_legal_acceptance");
  } catch (error) {
    console.error("Error clearing organization data from localStorage:", error);
  }
};

// src/extractClaims.ts
var extractClaims = (user, auth0Namespace) => {
  const userMetadata = user[`${auth0Namespace}/jwt/claims/user_metadata`];
  const jwtClaims = user[`${auth0Namespace}/jwt/claims`];
  const organisations = userMetadata?.organisations;
  const roles = user[`${auth0Namespace}/jwt/claims/roles`];
  const mapboxToken = jwtClaims?.mapboxToken;
  const modelUrlKey = jwtClaims?.modelUrlKey;
  const legal = userMetadata?.legal;
  const personalisation = userMetadata?.personalisation;
  return { organisations, roles, mapboxToken, modelUrlKey, legal, personalisation };
};

// src/floorplannerSchema.ts
var EMPTY_DESIGN = {
  name: "Current",
  areas: [],
  surfaces: [],
  items: [],
  walls: [],
  dimensions: [],
  labels: [],
  lines: []
};
function findLatestDesign(designs) {
  const markedCurrent = designs.find(
    (d) => typeof d?.name === "string" && d.name.toLowerCase().includes("current")
  );
  return markedCurrent ?? designs[0] ?? EMPTY_DESIGN;
}
var asArray = (value) => Array.isArray(value) ? value : [];
var isLive = (entry) => !!entry && !entry._deleted;
function layoutCurrentDesigns(layout) {
  return asArray(layout?.floors).map((f) => findLatestDesign(asArray(f?.designs)));
}
function floorCameraCounts(layout) {
  return layoutCurrentDesigns(layout).map((d) => asArray(d?.cameras).filter(isLive).length);
}
function layoutHasGeometry(layout) {
  const designs = layoutCurrentDesigns(layout);
  const items = designs.flatMap((d) => asArray(d?.items)).filter(isLive);
  const surfaces = designs.flatMap((d) => asArray(d?.surfaces)).filter(isLive);
  const walls = designs.flatMap((d) => asArray(d?.walls)).filter(Boolean);
  return items.length + surfaces.length + walls.length > 0;
}

// src/parseCustomName.ts
var ENTITY_TYPES = {
  ZONE: "Z",
  SECTION: "S",
  AREA: "A",
  EQUIPMENT: "E"
};
var VALID_ENTITY_TYPES = Object.values(ENTITY_TYPES).join("");
var _customNameRegexCache = /* @__PURE__ */ new Map();
var makeCustomNameRegex = (hierarchyString) => {
  const cacheKey = hierarchyString.toLowerCase();
  if (!_customNameRegexCache.has(cacheKey)) {
    _customNameRegexCache.set(
      cacheKey,
      new RegExp(
        `^([${hierarchyString}][0-9]{3})[ ]*(?:,[ ]*([^,]*)[ ]*)?(?:,[ ]*([0-9]*)[ ]*)?(?:,[ ]*([01])[ ]*)?(?:,[ ]*([${hierarchyString}][0-9]{3})[ ]*)?$`,
        "i"
      )
    );
  }
  return _customNameRegexCache.get(cacheKey);
};
var expandHierarchyString = (hierarchyString) => hierarchyString.split("").map((x) => x.toUpperCase()).join("/");
var parseCustomName = (customName, hierarchyString) => {
  const errors = [];
  const customNameRegex = makeCustomNameRegex(hierarchyString);
  const match = customNameRegex.exec(customName);
  if (!match) {
    return {
      errors: [
        {
          message: `Invalid custom name format: '${customName}'.

  Custom name should be: [code],[name?],[capacity?],[track?],[parentID?] where:
  - code is a letter (${expandHierarchyString(hierarchyString)}) followed by 3 digits (required)
  - name is text without commas or hyphens (optional, defaults to entity code)
  - capacity is a number (optional)
  - track is 0 or 1 (optional)
  - parentID matches another entity's code (optional)

  EntityCode is mandatory while other components are optional as the can be edited in the Google sheet.`,
          value: customName
        }
      ]
    };
  }
  let [, rawEntityCode, rawName, rawCapacityStr, rawTrackStr, rawParentId] = match;
  const entityCode = rawEntityCode?.trim() || "";
  const name = rawName?.trim() || entityCode;
  const capacityStr = rawCapacityStr?.trim() || null;
  const trackStr = rawTrackStr?.trim() || null;
  const parentId = rawParentId?.trim() || null;
  if (!entityCode) {
    return {
      errors: [
        {
          message: "Entity code is required",
          value: customName
        }
      ]
    };
  }
  const capacity = capacityStr ? parseInt(capacityStr, 10) : 1;
  const track = trackStr === "0" ? false : true;
  const entityType = entityCode.charAt(0).toUpperCase();
  const parentType = parentId ? parentId.charAt(0).toUpperCase() : null;
  const entityIndex = VALID_ENTITY_TYPES.indexOf(entityType);
  const expectedParentCheck = entityType !== "E";
  if (expectedParentCheck) {
    const expectedParentType = entityType === "E" ? void 0 : entityIndex > 0 ? VALID_ENTITY_TYPES.charAt(entityIndex - 1) : void 0;
    if (expectedParentType === void 0 && parentId) {
      errors.push({
        message: `${entityType}-type entities should not have a parent ID`,
        value: customName
      });
    } else if (expectedParentType !== void 0 && !parentId) {
      errors.push({
        message: `${entityType}-type entities must have a parent ID starting with '${expectedParentType}'`,
        value: customName
      });
    } else if (parentId && expectedParentType !== parentType) {
      errors.push({
        message: `Invalid parent type for ${entityType}-type entity. Expected parent type: ${expectedParentType}`,
        value: customName
      });
    }
  }
  if (errors.length > 0) {
    return { errors };
  }
  return {
    entityCode,
    name,
    capacity,
    track,
    parentID: parentId
  };
};

// src/extractFloorplannerBIDs.ts
var extractFloorplannerBIDs = (floorplannerFile, twinBID, hierarchyString = "ZSAE") => {
  const bIDs = /* @__PURE__ */ new Set();
  bIDs.add(twinBID);
  for (const floor of floorplannerFile.floors) {
    if (floorplannerFile.floors.length > 1) {
      bIDs.add(`${twinBID}-${floor.name}`);
    }
    const latestDesign = findLatestDesign(floor.designs);
    for (const surface of latestDesign.surfaces) {
      if (surface.customName) {
        const parsedData = parseCustomName(surface.customName, hierarchyString);
        if (!("errors" in parsedData)) {
          bIDs.add(`${twinBID}-${parsedData.entityCode}`);
        }
      }
    }
    for (const item of latestDesign.items) {
      if (item.name) {
        const parsedData = parseCustomName(item.name, hierarchyString);
        if (!("errors" in parsedData)) {
          bIDs.add(`${twinBID}-${parsedData.entityCode}`);
        }
      }
    }
  }
  return Array.from(bIDs).sort();
};

// src/extractFloorplannerNames.ts
var extractFloorplannerNamesByBid = (floorplannerFile, twinBID, hierarchyString = "ZSAE") => {
  const namesByBid = {};
  const recordName = (entityCode, parsedName) => {
    if (!parsedName) return;
    const trimmed3 = parsedName.trim();
    if (!trimmed3 || trimmed3 === entityCode) return;
    namesByBid[`${twinBID}-${entityCode}`] = trimmed3;
  };
  for (const floor of floorplannerFile.floors) {
    const latestDesign = findLatestDesign(floor.designs);
    for (const surface of latestDesign.surfaces) {
      if (!surface.customName) continue;
      const parsed = parseCustomName(surface.customName, hierarchyString);
      if (!("errors" in parsed)) recordName(parsed.entityCode, parsed.name);
    }
    for (const item of latestDesign.items) {
      if (!item.name) continue;
      const parsed = parseCustomName(item.name, hierarchyString);
      if (!("errors" in parsed)) recordName(parsed.entityCode, parsed.name);
    }
  }
  return namesByBid;
};

// src/formatCopilotMessage.ts
var formatCopilotMessage = (response) => {
  response = response.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  return response;
};

// src/getMetricData.ts
var getMetricData = (data, heroMetric) => {
  if (data?.processed && heroMetric && typeof data.processed === "object" && data.processed !== null) {
    const processedData = data.processed;
    const metricData = processedData[heroMetric.metric] || [];
    const indicatorData = processedData[heroMetric.metric] || [];
    return { metricData, indicatorData };
  }
  return { metricData: [], indicatorData: [] };
};

// src/updateProgressFactory.ts
var updateProgressFactory = (progressCallback) => {
  const progressInfo = {
    completed: 0,
    total: 0,
    messages: []
  };
  const updateProgress = (updater) => {
    updater(progressInfo);
    progressCallback?.(progressInfo);
  };
  return updateProgress;
};

// src/dgraphErrors.ts
var isDgraphNamespaceError = (error) => {
  if (error?.response?.data?.errors) {
    const errors = error.response.data.errors;
    return errors.some(
      (err) => err.message?.includes("has no DGraph namespace configured") || err.message?.includes("no Dgraph namespace")
    );
  }
  return false;
};
var extractOrgBidFromDgraphError = (error) => {
  if (error?.response?.data?.errors) {
    const errors = error.response.data.errors;
    for (const err of errors) {
      if (err.message?.includes("has no DGraph namespace configured")) {
        const match = err.message.match(/Organisation\s+(\w+)\s+has no DGraph namespace/);
        return match ? match[1] : null;
      }
    }
  }
  return null;
};

// src/resolveEndpoints.ts
var resolveEndpoints = (graphqlUrl, config, middlewareApi) => {
  if (config.useLocalDgraph) {
    return {
      httpUrl: "http://localhost:8080/graphql",
      wsUrl: "ws://localhost:8080/graphql"
    };
  }
  const httpUrl = `${middlewareApi}api/dgraph`;
  const cleanUrl = graphqlUrl.trim().replace(/^(https?|wss?):\/\//, "").replace(/^\/+/, "").replace(/\/graphql$/, "");
  if (!cleanUrl) {
    throw new Error("Invalid graphqlUrl: URL cannot be empty");
  }
  const wsUrl = `wss://${cleanUrl}/graphql`;
  try {
    new URL(wsUrl);
  } catch (error) {
    throw new Error(`Invalid graphqlUrl: "${graphqlUrl}" produces malformed WebSocket URL`);
  }
  return { httpUrl, wsUrl };
};

// src/extractBIDs.ts
var extractBIDs = (node) => {
  const bIDs = [];
  const traverse = (currentNode) => {
    if (currentNode.bID) {
      bIDs.push(currentNode.bID);
    }
    if (Array.isArray(currentNode.children)) {
      for (const child of currentNode.children) {
        traverse(child);
      }
    }
  };
  traverse(node);
  return bIDs;
};

// src/extractOrgBidFromTwinBid.ts
var extractOrgBidFromTwinBid = (bid) => {
  if (!bid) return null;
  const parts2 = bid.split("-");
  return parts2.length > 0 ? parts2[0] : null;
};

// src/parseScenarioPayload.ts
var parseScenarioPayload = (raw) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const candidate = raw;
  if (candidate.scenarioVersion !== 1) {
    return null;
  }
  if (typeof candidate.dayAssessed !== "string" || candidate.dayAssessed.trim() === "") {
    return null;
  }
  if (typeof candidate.assessedTotal !== "number") {
    return null;
  }
  if (!Array.isArray(candidate.findings) || candidate.findings.length === 0) {
    return null;
  }
  const findings = [];
  for (const entry of candidate.findings) {
    if (!entry || typeof entry !== "object") {
      return null;
    }
    const f = entry;
    if (typeof f.entityBid !== "string" || typeof f.entityLabel !== "string" || typeof f.yesterdayUtilPct !== "number" || typeof f.baselineUtilPct !== "number" || typeof f.variationPct !== "number") {
      return null;
    }
    findings.push({
      entityBid: f.entityBid,
      entityLabel: f.entityLabel,
      // Nullable, LEFT-joined fields: accept string or coerce absent/other to null.
      zoneLabel: typeof f.zoneLabel === "string" ? f.zoneLabel : null,
      equipmentType: typeof f.equipmentType === "string" ? f.equipmentType : null,
      yesterdayUtilPct: f.yesterdayUtilPct,
      baselineUtilPct: f.baselineUtilPct,
      variationPct: f.variationPct
    });
  }
  return {
    scenarioVersion: 1,
    dayAssessed: candidate.dayAssessed,
    assessedTotal: candidate.assessedTotal,
    findings
  };
};

// src/twinBelongsToOrg.ts
var twinBelongsToOrg = (bid, orgBID) => {
  if (!bid || !orgBID) return false;
  const twinOrgBid = extractOrgBidFromTwinBid(bid);
  if (bid === "NPL-STN" && orgBID === "NPLV2") {
    return true;
  }
  return twinOrgBid === orgBID;
};

// src/createHourObjects.ts
var createHourObjects = (startTime, finishTime) => {
  const defaultStartTime = "00:00";
  const defaultFinishTime = "24:00";
  const actualStartTime = startTime || defaultStartTime;
  const actualFinishTime = finishTime || defaultFinishTime;
  const startHour = parseInt(actualStartTime.split(":")[0]);
  const finishHour = parseInt(actualFinishTime.split(":")[0]);
  const hourObjects = [];
  for (let hour = startHour; hour < finishHour; hour++) {
    const hourText = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour < 12 ? "am" : "pm";
    const hourValue = (hour < 10 ? "0" : "") + hour + ":00";
    hourObjects.push({
      text: `${hourText}${ampm}`,
      value: hourValue,
      hour
    });
  }
  return hourObjects;
};

// src/createFilterPath.ts
import { FilterType } from "@repo/frontend-types";

// src/findAncestors.ts
var findAncestors = (id, data) => {
  const searchNode = (currentNode, id2) => {
    if (currentNode.id === id2) {
      return [currentNode];
    }
    if (currentNode.children && currentNode.children.length > 0) {
      for (let child of currentNode.children) {
        const result = searchNode(child, id2);
        if (result) {
          return [currentNode, ...result];
        }
      }
    }
    return null;
  };
  return searchNode(data, id) || [];
};

// src/loneFloor.ts
import { TwinEntityType as TwinEntityType2 } from "@repo/frontend-types";

// src/stringToTwinEntityType.ts
import { TwinEntityType } from "@repo/frontend-types";
var stringToTwinEntityType = (value) => {
  let type = TwinEntityType.UNKNOWN;
  switch (value) {
    case "area":
    case "Area":
    case "AREA":
      type = TwinEntityType.AREA;
      break;
    case "asset":
    case "Asset":
    case "ASSET":
      type = TwinEntityType.ASSET;
      break;
    case "category":
    case "Category":
    case "CATEGORY":
      type = TwinEntityType.CATEGORY;
      break;
    case "floor":
    case "Floor":
    case "FLOOR":
      type = TwinEntityType.FLOOR;
      break;
    case "nfarea":
    case "Nfarea":
    case "NFAREA":
      type = TwinEntityType.NFAREA;
      break;
    case "site":
    case "Site":
    case "SITE":
      type = TwinEntityType.SITE;
      break;
    case "structure":
    case "Structure":
    case "STRUCTURE":
      type = TwinEntityType.STRUCTURE;
      break;
    case "wall":
    case "WALL":
    case "Wall":
      type = TwinEntityType.WALL;
      break;
    case "zone":
    case "Zone":
    case "ZONE":
      type = TwinEntityType.ZONE;
      break;
    case "event":
    case "Event":
    case "EVENT":
      type = TwinEntityType.EVENT;
      break;
    case "station":
    case "Station":
    case "STATION":
      type = TwinEntityType.STATION;
      break;
    case "operator":
    case "Operator":
    case "OPERATOR":
      type = TwinEntityType.OPERATOR;
      break;
  }
  return type;
};

// src/loneFloor.ts
var isFloor = (n) => !!n && stringToTwinEntityType(n.type?.name ?? "") === TwinEntityType2.FLOOR;
function isLoneFloor(entity, site) {
  if (!isFloor(entity) || !site) return false;
  const floors = (site.children ?? []).filter(isFloor);
  return floors.length === 1 && floors[0] === entity;
}
function liftLoneFloor(site, children) {
  const lone = children.find((child) => isLoneFloor(child, site));
  if (!lone) return children;
  return children.flatMap((child) => child === lone ? child.children ?? [] : [child]);
}
function twinFloorShape(site) {
  const children = site?.children ?? [];
  const hasFloorLevel = children.length > 0 && isFloor(children[0]);
  return { hasFloorLevel, multifloor: hasFloorLevel && children.filter(isFloor).length >= 2 };
}

// src/createFilterPath.ts
var createFilterPath = (entityId, model) => {
  const filterPaths = [];
  const ancestors = findAncestors(entityId, model);
  ancestors.forEach((anc) => {
    filterPaths.push({
      id: anc.id,
      bId: anc.bID,
      type: FilterType.ENTITY,
      label: anc.name,
      // A lone floor stays in the path, so visibility reads exactly as on a multi-floor twin, but is
      // never shown as a pill (`floor-placement`: a one-floor twin looks as it always has).
      display: !isLoneFloor(anc, ancestors[0])
    });
  });
  return filterPaths;
};
var createFilterPathFromLineage = (lineage) => {
  const filterPaths = [];
  lineage.forEach((anc) => {
    filterPaths.push({
      id: anc.id,
      bId: anc.bID,
      type: FilterType.ENTITY,
      label: anc.name,
      // As `createFilterPath`: a lone floor is in the path and never a pill.
      display: !isLoneFloor(anc, lineage[0])
    });
  });
  return filterPaths;
};

// src/prepareModelVersion.ts
var removeVersionSuffix = (value) => {
  const verIndex = value.indexOf("-ver-");
  return verIndex !== -1 ? value.substring(0, verIndex) : value;
};
var prepareModelVersion = (model, versionSuffix) => {
  if (!model) return model;
  const updatedModel = structuredClone(model);
  delete updatedModel.id;
  delete updatedModel.depth;
  delete updatedModel.sensors;
  if (updatedModel.bID) {
    if (versionSuffix && versionSuffix.length > 0) {
      updatedModel.bID = `${updatedModel.bID}-ver-${versionSuffix}`;
    } else {
      updatedModel.bID = removeVersionSuffix(updatedModel.bID);
    }
  }
  if (Array.isArray(updatedModel.children)) {
    updatedModel.children = updatedModel.children.map(
      (child) => prepareModelVersion(child, versionSuffix)
    );
  }
  return updatedModel;
};

// src/getIndicator.ts
var getIndicator = (metricValue, indicatorValue, config, selected, showDecimals, showUnit, presentationUnit, metricName) => {
  const value = showDecimals === void 0 || showDecimals ? metricValue.value : Math.round(metricValue.value);
  const unit = showUnit === void 0 || showUnit ? presentationUnit || "" : "";
  const colors = getIndicatorColor(
    indicatorValue.value,
    config,
    selected,
    metricName
  );
  return { value, unit, colors };
};

// src/behaviourColor/resolveEntityBehaviourColor.ts
import { IndicatorColor as IndicatorColor2 } from "@repo/frontend-types";
var resolveEntityBehaviourColor = (args) => {
  const { entity, heroMetric, appMode, processed, selected } = args;
  if (!heroMetric || !processed) return IndicatorColor2.NA;
  const id = entity[entityKeyFor(appMode)];
  const metricData = processed[heroMetric.valueMetricName] ?? [];
  const indicatorData = processed[heroMetric.indicatorMetricName] ?? [];
  const metricValue = getDataValue(dataModeFor(appMode), metricData, id);
  const indicatorValue = getDataValue(dataModeFor(appMode), indicatorData, id);
  if (!metricValue || !indicatorValue) return IndicatorColor2.NA;
  return getIndicatorColor(
    indicatorValue.value,
    heroMetric.indicatorConfig,
    selected,
    heroMetric.name
  ).bgColor;
};

// src/behaviourColor/normaliseHeadcountValue.ts
var normaliseHeadcountValue = (heroMetricName, capacity, rawValue) => {
  const metricType = (heroMetricName ?? "").toLowerCase();
  if ((metricType.includes("headcount") || metricType.includes("count")) && capacity && capacity > 0) {
    return rawValue / capacity * 100;
  }
  return rawValue;
};

// src/getMetricColor.ts
var getMetricColor = (value) => {
  if (value < 50) {
    return {
      backgroundColor: "#d4edda",
      textColor: "#155724"
    };
  } else if (value < 80) {
    return {
      backgroundColor: "#fff3cd",
      textColor: "#856404"
    };
  } else {
    return {
      backgroundColor: "#f8d7da",
      textColor: "#721c24"
    };
  }
};

// src/getNotificationTypeColors.ts
import { NotificationType } from "@repo/frontend-types";
var getNotificationTypeColors = (type) => {
  switch (type) {
    case NotificationType.INSIGHT:
      return {
        background: "#ccf2f5",
        border: "none",
        color: "#006d77"
      };
    case NotificationType.RELEASE:
      return {
        background: "#d4edda",
        border: "none",
        color: "#155724"
      };
    case NotificationType.SYSTEM:
      return {
        background: "#fff3cd",
        border: "none",
        color: "#856404"
      };
    case NotificationType.ADMIN:
      return {
        background: "#d6d8f7",
        border: "none",
        color: "#3b3f8c"
      };
    case NotificationType.ALERT:
      return {
        background: "#f8d7da",
        border: "none",
        color: "#721c24"
      };
    default:
      return {
        background: "#e9ecef",
        border: "none",
        color: "#495057"
      };
  }
};

// src/calcPercent.ts
var calcPercent = (num2, denom, digits = 1) => {
  let percent;
  if (denom === 0) {
    percent = "0";
  } else {
    percent = (num2 / denom * 100).toFixed(digits);
  }
  return `${percent}%`;
};

// src/capitaliseFirstLetter.ts
var capitaliseFirstLetter = (value) => {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

// src/serverMessage.ts
var MAX_SERVER_MESSAGE_LENGTH = 300;
var normaliseServerMessage = (raw, fallback) => {
  const joined = Array.isArray(raw) ? raw.filter((part) => typeof part === "string").join(" ") : typeof raw === "string" ? raw : "";
  const collapsed = joined.replace(/\s+/g, " ").trim();
  if (!collapsed) return fallback;
  const points2 = Array.from(collapsed);
  return points2.length > MAX_SERVER_MESSAGE_LENGTH ? `${points2.slice(0, MAX_SERVER_MESSAGE_LENGTH).join("")}\u2026` : collapsed;
};

// src/planSaveFailureMessage.ts
var NAMES_NO_CAUSE = [
  // axios's own, for a non-2xx whose body carried no `message`, and its transport failures.
  /^Request failed with status code \d+$/i,
  /^Network Error$/i,
  /^timeout of \d+ms exceeded$/i,
  /^canceled$/i,
  /*
   * The middleware's own generic bodies, which are just as useless to an operator and cost the
   * correct advice. All three are reachable on these two routes: `twin-plan.service.ts` throws a
   * plain `Error` on an empty write result, which Nest's default filter renders as
   * `Internal server error`; the global `ValidationPipe`'s exception factory returns
   * `Validation Failed`; and `Unhandled exception occurred.` is the filter's own fallback. For all
   * three "please try again" is better advice than the string itself.
   */
  /^Internal server error$/i,
  /^Validation Failed$/i,
  /^Unhandled exception occurred\.?$/i
];
var planSaveFailureReason = (error) => {
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const message = normaliseServerMessage(raw, "");
  if (message === "") return null;
  if (NAMES_NO_CAUSE.some((pattern) => pattern.test(message))) return null;
  return message;
};
var describeFailedSave = (lead, reason, fallbackAdvice) => `${lead} ${reason ?? fallbackAdvice}`;

// src/planSaveConflict.ts
var text = (value) => typeof value === "string" ? value : null;
var readPlanSaveConflict = (error) => {
  if (!error || error.status !== 409) return null;
  const body = error.body;
  if (!body || typeof body !== "object") return null;
  if (body.code === "plan_changed") {
    return {
      code: "plan_changed",
      modifiedAt: text(body.modified_at),
      modifiedBy: text(body.modified_by)
    };
  }
  if (body.code === "launch_in_progress") {
    return {
      code: "launch_in_progress",
      launchedBy: text(body.launched_by),
      launchedAt: text(body.launched_at)
    };
  }
  return null;
};

// src/caseInsensitiveArrayIncludes.ts
var caseInsensitiveArrayIncludes = (array, value) => {
  return array.some((item) => item.toLowerCase() === value.toLowerCase());
};

// src/convertDateTimeToUTC.ts
import { DateTime } from "luxon";
var convertDateTimeISOToUTC = (dateTimeISO) => {
  let utcDateTime;
  if (dateTimeISO !== null) {
    const dateTimeWithZone = DateTime.fromISO(dateTimeISO);
    utcDateTime = dateTimeWithZone.toUTC().toISO();
  } else {
    utcDateTime = "N/A";
  }
  return utcDateTime;
};

// src/convertIntervalToNumber.ts
var convertIntervalToNumber = (interval) => {
  let value = 15;
  switch (interval) {
    case "5m":
      value = 5;
      break;
    case "10m":
      value = 10;
      break;
    case "15m":
      value = 15;
      break;
    case "30m":
      value = 30;
      break;
    case "60m":
      value = 60;
      break;
    case "1h":
      value = 60;
      break;
    default:
      value = 15;
      break;
  }
  return value;
};

// src/convertUnixTimestampToDate.ts
var convertUnixTimestampToDate = (uts) => {
  const milliseconds = uts * 1e3;
  return new Date(milliseconds);
};

// src/floatEquals.ts
var floatEquals = (num1, num2, epsilon = Number.EPSILON) => {
  return Math.abs(num1 - num2) < epsilon;
};

// src/formatDateRange.ts
import { DateTime as DateTime2 } from "luxon";

// src/getOrdinalSuffix.ts
var getOrdinalSuffix = (number) => {
  if (number >= 11 && number <= 13) {
    return "th";
  } else {
    const lastDigit = number % 10;
    switch (lastDigit) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }
};

// src/formatDateRange.ts
var formatDateRange = (startDateISO, endDateISO) => {
  const drParts = extractDateRangeParts(startDateISO, endDateISO);
  let startDay = drParts.startDay;
  let finishDay = drParts.finishDay;
  let startMonth = drParts.startMonth;
  let finishMonth = drParts.finishMonth;
  let startYear = drParts.startYear;
  let finishYear = drParts.finishYear;
  const startDayString = `${startDay}${getOrdinalSuffix(startDay)}`;
  const finishDayString = `${finishDay}${getOrdinalSuffix(finishDay)}`;
  let output;
  if (startYear !== finishYear) {
    output = `${startDayString} ${startMonth} ${startYear}-${finishDayString} ${finishMonth} ${finishYear}`;
  } else if (startYear === finishYear && startDay === finishDay && startMonth === finishMonth) {
    output = `${startDayString} ${startMonth} ${startYear}`;
  } else if (startYear === finishYear && startMonth !== finishMonth) {
    output = `${startDayString} ${startMonth}-${finishDayString} ${finishMonth} ${startYear}`;
  } else if (startYear === finishYear && startDay !== finishDay && startMonth === finishMonth) {
    output = `${startDayString}-${finishDayString} ${startMonth} ${startYear}`;
  }
  return `${output}`;
};
var extractDateRangeParts = (startDateISO, endDateISO) => {
  const startDate = DateTime2.fromISO(startDateISO);
  const endDate = DateTime2.fromISO(endDateISO);
  const startDay = startDate.day;
  const finishDay = endDate.day;
  const startMonth = startDate.toFormat("MMM");
  const finishMonth = endDate.toFormat("MMM");
  const startYear = startDate.year;
  const finishYear = endDate.year;
  return { startDay, finishDay, startMonth, finishMonth, startYear, finishYear };
};

// src/formatWeekdayRange.ts
import { WEEKDAYS } from "@repo/frontend-types";
var formatWeekdayRange = (weekdays) => {
  const indices = weekdays.map((weekday) => WEEKDAYS.indexOf(weekday)).filter((index) => index >= 0);
  if (indices.length === 0) {
    return "";
  }
  const minIndex = Math.min(...indices);
  const maxIndex = Math.max(...indices);
  const short = (index) => WEEKDAYS[index].slice(0, 3);
  return minIndex === maxIndex ? short(minIndex) : `${short(minIndex)}-${short(maxIndex)}`;
};

// src/prependZero.ts
var prependZero = (value, threshold = 10) => {
  if (value < threshold) {
    return `0${value}`;
  }
  return value.toString();
};

// src/formatHourRange.ts
var formatHourRange = (startHour, endHour) => `${prependZero(startHour)}:00-${prependZero(endHour)}:00`;

// src/formatDateTime.ts
var formatDateTime = (value) => {
  const day = value.getDate();
  const month = value.toLocaleDateString(void 0, { month: "long" });
  const year = value.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

// src/formatDateToDateTimeString.ts
var formatDateToDateTimeString = (date, separator = "/", incTime = true, includeSeconds = false) => {
  const dateString = `${prependZero(date.getDate())}${separator}${prependZero(date.getMonth() + 1)}${separator}${date.getFullYear()}`;
  if (!incTime) return dateString;
  const timeString = includeSeconds ? `${prependZero(date.getHours())}:${prependZero(date.getMinutes())}:${prependZero(date.getSeconds())}` : `${prependZero(date.getHours())}:${prependZero(date.getMinutes())}`;
  return `${dateString} ${timeString}`;
};

// src/formatDateToDayMonthYear.ts
var formatDateToDayMonthYear = (date, separator = "/") => {
  return `${prependZero(date.getDate())}${separator}${prependZero(date.getMonth() + 1)}${separator}${date.getFullYear()}`;
};

// src/formatDateToISO8601.ts
var formatDateToISO8601 = (date, incTime = false) => {
  if (!(date instanceof Date)) {
    return "Invalid date";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  let isoDate = `${year}-${month}-${day}`;
  if (incTime) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    isoDate += `T${hours}:${minutes}:${seconds}`;
  }
  return isoDate;
};

// src/formatTimeRange.ts
import { DateTime as DateTime3 } from "luxon";
var formatTimeRange = (startDate, endDate) => {
  const startDateTime = DateTime3.fromISO(startDate);
  const startTime = startDateTime.toFormat("HH:mm:ss");
  const finishDateTime = DateTime3.fromISO(endDate);
  const finishTime = finishDateTime.toFormat("HH:mm:ss");
  return `${startTime}-${finishTime}`;
};

// src/formatToOneDecimal.ts
var formatToOneDecimal = (value) => {
  return value.toFixed(1);
};

// src/getISOWeekNumber.ts
var getISOWeekNumber = (date) => {
  const tempDate = new Date(date);
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));
  const startOfYear = new Date(tempDate.getFullYear(), 0, 1);
  const weekNo = Math.ceil(((tempDate.getTime() - startOfYear.getTime()) / 864e5 + 1) / 7);
  return weekNo;
};

// src/generateCalendarMonth.ts
var generateCalendarMonth = (date) => {
  const daysOfWeek2 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const result = [];
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay();
  const offsetToMonday = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  firstDay.setDate(firstDay.getDate() - offsetToMonday);
  const lastDay = new Date(year, month + 1, 0);
  const numDays = lastDay.getDate();
  let currentWeekNo = getISOWeekNumber(firstDay);
  let weekDays = [];
  for (let i = -offsetToMonday; i < numDays + (7 - lastDay.getDay()); i++) {
    const currentDate = new Date(year, month, i + 1);
    const currentWeekDay = currentDate.getDay();
    const formattedDate = currentDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
    weekDays.push({
      date: currentDate,
      dateString: formatDateToISO8601(currentDate),
      tableString: `${daysOfWeek2[currentWeekDay].toLowerCase()}-${formattedDate.replace(/\//g, "-")}`,
      count: 0
    });
    if (currentDate.getMonth() === month && (currentDate.getDay() === 0 || i === numDays - 1) && weekDays.length === 7) {
      result.push({ weekNo: currentWeekNo, days: weekDays });
      weekDays = [];
      currentWeekNo++;
    }
    if (i === numDays - 1 && weekDays.length > 0) {
      result.push({ weekNo: currentWeekNo, days: weekDays });
    }
  }
  return result;
};

// src/generateTimeSlots.ts
import { DateTime as DateTime4 } from "luxon";
var generateTimeSlots = (startDateTimeISO, data, interval = 15, hourRange) => {
  const startDateTime = new Date(startDateTimeISO);
  const endTime = new Date(startDateTime);
  let startHour = 0;
  let endHour = 23;
  if (hourRange) {
    startHour = parseInt(hourRange.startTime.split(":")[0]);
    endHour = parseInt(hourRange.finishTime.split(":")[0]);
  }
  startDateTime.setHours(startHour, 0, 0, 0);
  endTime.setHours(endHour, 59, 59, 999);
  const timeSlots = [];
  let startTimeRange = new Date(startDateTime);
  while (startTimeRange <= endTime) {
    let displayIndicator = false;
    let count = 0;
    let foundCounts = [];
    const startRangeHours = startTimeRange.getHours();
    const startRangeMinutes = startTimeRange.getMinutes();
    const finishTimeRange = new Date(startTimeRange.getTime() + interval * 6e4);
    const finishRangeHours = finishTimeRange.getHours();
    const finishRangeMinutes = finishTimeRange.getMinutes();
    const period = startRangeHours < 12 ? "am" : "pm";
    const text4 = (startRangeHours % 12 || 12) + period;
    const startValue = `${startRangeHours.toString().padStart(2, "0")}:${startRangeMinutes.toString().padStart(2, "0")}`;
    const finishValue = `${finishRangeHours.toString().padStart(2, "0")}:${finishRangeMinutes.toString().padStart(2, "0")}`;
    const isOnHour = startRangeMinutes === 0;
    const currentISO = startDateTimeISO.replace("T00:00:00", `T${startValue}:00`);
    displayIndicator = showIndicator(currentISO);
    if (data && data.length > 0) {
      foundCounts = filterDatesInRange(data, startTimeRange, finishTimeRange);
      foundCounts && foundCounts.forEach((fc) => {
        count = count + fc.value;
      });
    }
    timeSlots.push({
      text: text4,
      value: `${startValue}-${finishValue}`,
      count,
      isOnHour,
      showIndicator: displayIndicator
    });
    startTimeRange.setMinutes(startTimeRange.getMinutes() + interval);
  }
  return timeSlots;
};
var filterDatesInRange = (data, startTimeRange, finishTimeRange) => data.filter((record) => {
  try {
    const timestamp = record.dateTime.getTime();
    return timestamp >= startTimeRange.getTime() && timestamp <= finishTimeRange.getTime();
  } catch (error) {
  }
  return null;
});
var showIndicator = (isoDateString) => {
  const currentDateTime = DateTime4.now();
  const inputDate = DateTime4.fromISO(isoDateString);
  const nearestPastInterval = getNearestPassedInterval(currentDateTime, 15);
  return inputDate.hasSame(nearestPastInterval, "minute");
};
var getNearestPassedInterval = (currentDateTime, interval = 15) => {
  const currentMinutes = currentDateTime.minute;
  return currentDateTime.set({
    minute: Math.floor(currentMinutes / interval) * interval,
    second: 0,
    millisecond: 0
  });
};

// src/calendar/weekGridGeometry.ts
var CALENDAR_LABEL_COLUMN_WIDTH = 43;
var computeCellDimensions = (gridWidth, gridHeight, theadHeight, numCols, numRows, labelColumnWidth = CALENDAR_LABEL_COLUMN_WIDTH) => ({
  adjustedCellWidth: (gridWidth - labelColumnWidth) / numCols,
  adjustedCellHeight: (gridHeight - theadHeight) / numRows
});
var computeSelectionBoxFromPointer = (initialMouseX, initialMouseY, relativeX, relativeY, adjustedCellWidth, adjustedCellHeight) => {
  const startCol = Math.floor(initialMouseX / adjustedCellWidth);
  const startRow = Math.floor(initialMouseY / adjustedCellHeight);
  const endCol = Math.ceil(relativeX / adjustedCellWidth);
  const endRow = Math.ceil(relativeY / adjustedCellHeight);
  return {
    startCol,
    startRow,
    endCol,
    endRow,
    snapStartX: startCol * adjustedCellWidth,
    snapStartY: startRow * adjustedCellHeight,
    snapEndX: endCol * adjustedCellWidth,
    snapEndY: endRow * adjustedCellHeight
  };
};
var computeAutoSelectBox = (minRow, maxRow, minCol, maxCol, theadHeight, adjustedCellWidth, adjustedCellHeight) => {
  const startX = (minCol + 1) * adjustedCellWidth;
  const startY = theadHeight + minRow * adjustedCellHeight;
  const endX = (maxCol + 2) * adjustedCellWidth;
  const endY = theadHeight + (maxRow + 1) * adjustedCellHeight;
  return {
    startX,
    startY,
    width: endX - startX,
    height: endY - startY
  };
};
var computeContiguousSelectionOutlines = (cells, theadHeight, adjustedCellWidth, adjustedCellHeight) => {
  if (cells.length === 0) {
    return [];
  }
  const cellKey = (r, c) => `${r},${c}`;
  const selected = new Set(cells.map((c) => cellKey(c.rowIndex, c.colIndex)));
  const visited = /* @__PURE__ */ new Set();
  const components2 = [];
  for (const cell of cells) {
    const startKey = cellKey(cell.rowIndex, cell.colIndex);
    if (visited.has(startKey)) {
      continue;
    }
    const component = [];
    const stack = [cell];
    visited.add(startKey);
    while (stack.length > 0) {
      const cur = stack.pop();
      component.push(cur);
      const neighbours = [
        { rowIndex: cur.rowIndex - 1, colIndex: cur.colIndex },
        { rowIndex: cur.rowIndex + 1, colIndex: cur.colIndex },
        { rowIndex: cur.rowIndex, colIndex: cur.colIndex - 1 },
        { rowIndex: cur.rowIndex, colIndex: cur.colIndex + 1 }
      ];
      for (const n of neighbours) {
        const nk = cellKey(n.rowIndex, n.colIndex);
        if (selected.has(nk) && !visited.has(nk)) {
          visited.add(nk);
          stack.push(n);
        }
      }
    }
    components2.push(component);
  }
  const pixelX = (cornerX) => (cornerX + 1) * adjustedCellWidth;
  const pixelY = (cornerY) => theadHeight + cornerY * adjustedCellHeight;
  const cornerKey = (x, y) => `${x},${y}`;
  const parseCorner = (s) => {
    const [x, y] = s.split(",").map(Number);
    return [x, y];
  };
  const outlines = components2.map((component) => {
    const inComponent = new Set(component.map((c) => cellKey(c.rowIndex, c.colIndex)));
    const has = (r, c) => inComponent.has(cellKey(r, c));
    const outgoing = /* @__PURE__ */ new Map();
    const addEdge = (fromX, fromY, toX, toY) => {
      const key = cornerKey(fromX, fromY);
      const list = outgoing.get(key) ?? [];
      list.push({
        to: [toX, toY],
        dir: [Math.sign(toX - fromX), Math.sign(toY - fromY)],
        used: false
      });
      outgoing.set(key, list);
    };
    for (const { rowIndex: r, colIndex: c } of component) {
      if (!has(r - 1, c)) addEdge(c, r, c + 1, r);
      if (!has(r, c + 1)) addEdge(c + 1, r, c + 1, r + 1);
      if (!has(r + 1, c)) addEdge(c + 1, r + 1, c, r + 1);
      if (!has(r, c - 1)) addEdge(c, r + 1, c, r);
    }
    const allEdges = [];
    outgoing.forEach((list, key) => {
      const from = parseCorner(key);
      list.forEach((edge) => allEdges.push({ from, edge }));
    });
    allEdges.sort((a, b) => a.from[1] - b.from[1] || a.from[0] - b.from[0]);
    const rotateCW = ([x, y]) => [-y, x];
    const rotateCCW = ([x, y]) => [y, -x];
    const subPaths = [];
    for (const startRef of allEdges) {
      if (startRef.edge.used) {
        continue;
      }
      const startCorner = startRef.from;
      const loop = [startCorner];
      let edge = startRef.edge;
      edge.used = true;
      let corner = edge.to;
      let dir = edge.dir;
      let guard = allEdges.length + 1;
      while (!(corner[0] === startCorner[0] && corner[1] === startCorner[1]) && guard-- > 0) {
        loop.push(corner);
        const candidates = outgoing.get(cornerKey(corner[0], corner[1])) ?? [];
        const preferred = [
          rotateCW(dir),
          dir,
          rotateCCW(dir),
          [-dir[0], -dir[1]]
        ];
        let next;
        for (const pd of preferred) {
          next = candidates.find((e) => !e.used && e.dir[0] === pd[0] && e.dir[1] === pd[1]);
          if (next) break;
        }
        if (!next) break;
        next.used = true;
        dir = next.dir;
        corner = next.to;
      }
      const turns = [];
      for (let i = 0; i < loop.length; i++) {
        const prev = turns[turns.length - 1];
        const point = loop[i];
        const next = loop[(i + 1) % loop.length];
        if (!prev) {
          turns.push(point);
          continue;
        }
        const collinear = prev[0] === point[0] && point[0] === next[0] || prev[1] === point[1] && point[1] === next[1];
        if (!collinear) {
          turns.push(point);
        }
      }
      const d = turns.map((pt, i) => `${i === 0 ? "M" : "L"} ${pixelX(pt[0])} ${pixelY(pt[1])}`).join(" ");
      subPaths.push(`${d} Z`);
    }
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;
    for (const { rowIndex: r, colIndex: c } of component) {
      minRow = Math.min(minRow, r);
      maxRow = Math.max(maxRow, r);
      minCol = Math.min(minCol, c);
      maxCol = Math.max(maxCol, c);
    }
    const box = computeAutoSelectBox(
      minRow,
      maxRow,
      minCol,
      maxCol,
      theadHeight,
      adjustedCellWidth,
      adjustedCellHeight
    );
    return { path: subPaths.join(" "), box };
  });
  return outlines.sort((a, b) => a.box.startY - b.box.startY || a.box.startX - b.box.startX);
};

// src/calendar/weekCellAppearance.ts
var resolveWeekCellAppearance = ({
  indicatorConfig,
  colorValue,
  selected,
  isFuture,
  open,
  value,
  metricName
}) => {
  let backgroundColor = "none";
  let colors = null;
  if (indicatorConfig) {
    colors = getIndicatorColor(colorValue, indicatorConfig, selected);
  } else {
    backgroundColor = "white";
    warnMissingIndicatorConfig(metricName);
  }
  if (isFuture) {
    backgroundColor = "white";
  } else if (open === false) {
    backgroundColor = "transparent";
  } else if (open === true) {
    backgroundColor = colors?.bgColor || "white";
  }
  let displayValue;
  if (isFuture) {
    displayValue = "-";
  } else if (!open) {
    displayValue = "";
  } else {
    displayValue = typeof value === "number" ? `${formatToOneDecimal(value)}` : "-";
  }
  return {
    backgroundColor,
    textColor: colors?.textColor,
    displayValue
  };
};

// src/calendar/weekGridModel.ts
import { DateTime as DateTime5 } from "luxon";
var daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var generateCalendarWeek = (weekViewDateTimeISO) => {
  const week = [];
  for (let i = 0; i < 7; i++) {
    if (weekViewDateTimeISO) {
      const weekDay = DateTime5.fromISO(weekViewDateTimeISO).plus({ days: i }).toJSDate();
      const cellDate = `${daysOfWeek[weekDay.getDay()].toLowerCase()}-${(weekDay.getDate() < 10 ? "0" : "") + weekDay.getDate()}-${(weekDay.getMonth() + 1 < 10 ? "0" : "") + (weekDay.getMonth() + 1)}-${weekDay.getFullYear()}`;
      const hours = [];
      week.push({
        dateObj: weekDay,
        weekDay: daysOfWeek[weekDay.getDay()].toLowerCase(),
        dateISO: formatDateToISO8601(weekDay),
        date: weekDay.getDate(),
        cellDate,
        hours,
        activeHours: 0
      });
    }
  }
  return week;
};
var buildClearedWeek = (calendarWeek, weeklyCalStartTime, weeklyCalFinishTime) => {
  const defaultHours = [];
  for (let hour = 0; hour < 24; hour++) {
    const hourString = hour.toString().padStart(2, "0") + ":00";
    const startHour = parseInt((weeklyCalStartTime || "00:00").split(":")[0]);
    const endHour = parseInt((weeklyCalFinishTime || "24:00").split(":")[0]);
    const isOpen = hour >= startHour && hour < endHour;
    defaultHours.push({
      hour,
      hourString,
      value: 0,
      open: isOpen,
      count: null,
      hourRecordCount: 0
    });
  }
  return calendarWeek.map((day) => ({
    ...day,
    hours: defaultHours,
    activeHours: 0
  }));
};
var mergeWeekHoursData = (theWeek, tsData) => {
  const convertHoursData = (hourData) => {
    const hoursArray = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourString = hour.toString().padStart(2, "0");
      const data = hourData[hourString];
      if (data) {
        const [value, open] = data;
        hoursArray.push({
          hour,
          hourString: hourString + ":00",
          value,
          open: open === 1 ? true : false
        });
      } else {
        hoursArray.push({
          hour,
          hourString: hourString + ":00",
          value: 0,
          open: false
        });
      }
    }
    return hoursArray;
  };
  return theWeek.map((targetDay) => {
    const dayData = tsData[targetDay.dateISO];
    if (dayData) {
      const hoursArray = convertHoursData(dayData);
      const activeHours = hoursArray.filter((h) => h.open === true).length;
      return {
        ...targetDay,
        hours: hoursArray,
        activeHours
      };
    }
    return targetDay;
  });
};

// src/calendar/selectionProfile.ts
import { AppMode as AppMode4, FilterType as FilterType2 } from "@repo/frontend-types";

// src/isFullDay.ts
import { DateTime as DateTime6 } from "luxon";
var isFullDay = (startDateTime, finishDateTime) => {
  const sdt = DateTime6.fromISO(startDateTime);
  const fdt = DateTime6.fromISO(finishDateTime);
  const isStartOfDay = sdt.hour === 0 && sdt.minute === 0 && sdt.second === 0;
  const isEndOfDay = fdt.hour === 23 && fdt.minute === 59 && fdt.second >= 59 || fdt.hour === 23 && fdt.minute === 59 && fdt.second === 0 || fdt.endOf("day").equals(fdt);
  return isStartOfDay && isEndOfDay;
};

// src/calendar/selectionProfile.ts
var buildHistoricalDefaultWindows = (input) => {
  const { earliestOpeningTime, latestClosingTime, referenceDate } = input;
  const startTime = referenceDate.startOf("week");
  const finishTime = referenceDate.endOf("week");
  const startDateTimeISO = startTime.toISO({ suppressMilliseconds: true, includeOffset: false });
  const finishDateTimeISO = finishTime.toISO({ suppressMilliseconds: true, includeOffset: false });
  if (startDateTimeISO === null || finishDateTimeISO === null) {
    throw new Error(
      "Unable to set startDateTime and finishDateTime in selectionProfile > buildHistoricalDefaultWindows"
    );
  }
  const startDateTime = startDateTimeISO;
  const finishDateTime = finishDateTimeISO.split(".")[0];
  const startHour = earliestOpeningTime ? parseInt(earliestOpeningTime.split(":")[0]) : 0;
  const endHour = latestClosingTime ? parseInt(latestClosingTime.split(":")[0]) : 24;
  const windows = [];
  for (let day = 0; day < 7; day++) {
    const dayStart = referenceDate.startOf("week").plus({ days: day });
    for (let hour = startHour; hour < endHour; hour++) {
      const start = dayStart.set({ hour, minute: 0, second: 0, millisecond: 0 });
      const end = start.plus({ hours: 1 });
      const startISO = start.toISO({ suppressMilliseconds: true, includeOffset: false });
      const endISO = end.toISO({ suppressMilliseconds: true, includeOffset: false });
      if (startISO && endISO) {
        windows.push([startISO, endISO]);
      }
    }
  }
  return { startDateTime, finishDateTime, windows };
};
var deriveHistoricalPills = (startDateTime, finishDateTime) => ({
  date: {
    label: formatDateRange(startDateTime, finishDateTime),
    type: FilterType2.DATE,
    display: true
  },
  time: {
    label: formatTimeRange(startDateTime, finishDateTime),
    type: FilterType2.TIME,
    display: !isFullDay(startDateTime, finishDateTime)
  }
});
var HISTORICAL_PROFILE = {
  buildDefaultWindows: buildHistoricalDefaultWindows,
  derivePills: deriveHistoricalPills
};
var LIVE_PROFILE = {
  buildDefaultWindows: () => null,
  derivePills: () => null
};
var deriveInsightsWeekdayPills = (weekdayWindows) => {
  if (weekdayWindows.length === 0) {
    return null;
  }
  const weekdays = weekdayWindows.map((window) => window.weekday);
  const minStartHour = Math.min(...weekdayWindows.map((window) => window.start_hour));
  const maxEndHour = Math.max(...weekdayWindows.map((window) => window.end_hour));
  return {
    date: {
      label: formatWeekdayRange(weekdays),
      type: FilterType2.DATE,
      display: true
    },
    time: {
      label: formatHourRange(minStartHour, maxEndHour),
      type: FilterType2.TIME,
      // Hidden when the selection spans the whole day (0→24), mirroring the HISTORICAL time pill.
      display: !(minStartHour === 0 && maxEndHour === 24)
    }
  };
};
var INSIGHTS_PROFILE = {
  buildDefaultWindows: () => null,
  derivePills: () => null,
  deriveWeekdayPills: deriveInsightsWeekdayPills
};
var getSelectionProfile = (mode) => {
  switch (mode) {
    case AppMode4.HISTORICAL:
      return HISTORICAL_PROFILE;
    case AppMode4.LIVE:
      return LIVE_PROFILE;
    case AppMode4.INSIGHTS:
    case AppMode4.EQUIPMENT:
      return INSIGHTS_PROFILE;
    default:
      return null;
  }
};

// src/calendar/buildBaselineWeekdayWindows.ts
import { WEEKDAYS as WEEKDAYS2 } from "@repo/frontend-types";
var buildBaselineWeekdayWindows = () => WEEKDAYS2.map((weekday) => ({ weekday, start_hour: 0, end_hour: 24 }));

// src/calendar/insightsGridModel.ts
import { WEEKDAYS as WEEKDAYS3 } from "@repo/frontend-types";
var mergeWeekdayHoursData = (valueTsData, indicatorTsData) => {
  const buildHours = (weekdayData) => {
    const hoursArray = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourKey = hour.toString().padStart(2, "0");
      const data = weekdayData ? weekdayData[hourKey] : void 0;
      if (data) {
        const [value, open] = data;
        hoursArray.push({
          hour,
          hourString: `${hourKey}:00`,
          value,
          open: open === 1,
          count: null,
          hourRecordCount: 0
        });
      } else {
        hoursArray.push({
          hour,
          hourString: `${hourKey}:00`,
          value: 0,
          open: false,
          count: null,
          hourRecordCount: 0
        });
      }
    }
    return hoursArray;
  };
  const result = {};
  for (const weekday of WEEKDAYS3) {
    const hours = buildHours(valueTsData ? valueTsData[weekday] : void 0);
    const weekdayIndicator = indicatorTsData ? indicatorTsData[weekday] : void 0;
    if (weekdayIndicator) {
      hours.forEach((hour) => {
        const hourKey = hour.hour.toString().padStart(2, "0");
        const indicatorHourData = weekdayIndicator[hourKey];
        if (indicatorHourData) {
          hour.indicatorValue = indicatorHourData[0];
        }
      });
    }
    result[weekday] = hours;
  }
  return result;
};

// src/calendar/weekdayWindowCells.ts
var cellIdsToWeekdayWindows = (cellIds) => cellIds.map((cellId) => {
  const separatorIndex = cellId.lastIndexOf("T");
  const weekday = cellId.slice(0, separatorIndex);
  const time = cellId.slice(separatorIndex + 1);
  const startHour = parseInt(time.split(":")[0], 10);
  return { weekday, start_hour: startHour, end_hour: startHour + 1 };
});
var weekdayWindowsToCellIds = (windows) => {
  const cellIds = [];
  windows.forEach(({ weekday, start_hour, end_hour }) => {
    for (let hour = start_hour; hour < end_hour; hour++) {
      cellIds.push(`${weekday}T${prependZero(hour)}:00`);
    }
  });
  return cellIds;
};

// src/calendar/deriveSingleEntityBid.ts
import { FilterType as FilterType3 } from "@repo/frontend-types";
var deriveSingleEntityBid = (twinRootBid, filter) => {
  let entity = twinRootBid;
  const entityPills = filter.filter((item) => item.type === FilterType3.ENTITY);
  if (entityPills.length > 0) {
    entity = entityPills[entityPills.length - 1].bId;
  }
  return entity;
};

// src/getCookie.ts
var getCookie = (name) => {
  const cookieName = `${name}=`;
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return null;
};

// src/getCurrentTime.ts
var getCurrentTime = () => {
  const timestamp = /* @__PURE__ */ new Date();
  const hours = timestamp.getHours().toString().padStart(2, "0");
  const minutes = timestamp.getMinutes().toString().padStart(2, "0");
  const seconds = timestamp.getSeconds().toString().padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

// src/getDiffBetweenTimes.ts
var getDiffBetweenTimes = (startTime, finishTime) => {
  const startHour = parseInt(startTime.split(":")[0]);
  const finishHour = parseInt(finishTime.split(":")[0]);
  return finishHour - startHour;
};

// src/getDifferenceBetweenDates.ts
import { DateTime as DateTime7 } from "luxon";
var getDifferenceBetweenDates = (startDateTime, finishDateTime, unit = "days") => {
  let difference = 0;
  const startDate = DateTime7.fromISO(startDateTime);
  const endDate = DateTime7.fromISO(finishDateTime);
  switch (unit) {
    case "seconds":
      difference = endDate.diff(startDate, "seconds").seconds;
      break;
    case "days":
      difference = endDate.diff(startDate, "days").days + 1;
      break;
    default:
      break;
  }
  return Math.floor(difference);
};

// src/getInitials.ts
var getInitials = (value) => {
  const words2 = value.split(" ");
  let initials = "";
  words2.forEach((word) => {
    const firstLetter = word.charAt(0).toUpperCase();
    initials += firstLetter;
  });
  return initials;
};

// src/getTodaysDateRange.ts
import { DateTime as DateTime8 } from "luxon";
var getTodaysDateRange = (timeZone) => {
  const currentDate = DateTime8.now().setZone(timeZone);
  const startTime = currentDate.startOf("day");
  const finishTime = currentDate.endOf("day");
  const startDateTimeISO = startTime.toISO({ includeOffset: true });
  const finishDateTimeISO = finishTime.toISO({ includeOffset: true });
  if (startDateTimeISO !== null && finishDateTimeISO !== null) {
    return {
      startDateTime: startDateTimeISO,
      finishDateTime: finishDateTimeISO
    };
  } else {
    throw new Error("Unable to set startDateTime and finishDateTime in getTodaysDateRange util");
  }
};

// src/getVersionSuffix.ts
var getVersionSuffix = () => {
  const now = /* @__PURE__ */ new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
};

// src/getWeekRange.ts
import { DateTime as DateTime9 } from "luxon";
var getWeekRange = (dateTimeISO) => {
  const startOfWeek = DateTime9.fromISO(dateTimeISO).startOf("week");
  const endOfWeek = DateTime9.fromISO(dateTimeISO).endOf("week");
  if (startOfWeek.isValid && endOfWeek.isValid) {
    const startOfWeekMidnight = startOfWeek.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const endOfWeekElevenFiftyNine = endOfWeek.set({
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 0
    });
    return {
      startDate: startOfWeekMidnight.toISO({ includeOffset: true }),
      endDate: endOfWeekElevenFiftyNine.toISO({ includeOffset: true })
    };
  } else {
    throw new Error("getWeekRange has encountered an invalid date");
  }
};

// src/isNullOrUndefined.ts
var isNullOrUndefined = (...values) => {
  if (Array.isArray(values)) {
    return values.some((val) => val === null || val === void 0);
  }
};
var oneIsNullOrUndefined = isNullOrUndefined;

// src/isSameDate.ts
var isSameDate = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

// src/parseCalendarDateStringToDate.ts
var parseCalendarDateStringToDate = (dateString) => {
  const parts2 = dateString.split(/-| |:/);
  const dayOfMonth = parseInt(parts2[1], 10);
  const month = parseInt(parts2[2], 10) - 1;
  const year = parseInt(parts2[3], 10);
  const hour = parseInt(parts2[4], 10);
  const minute = parseInt(parts2[5], 10);
  return new Date(year, month, dayOfMonth, hour, minute);
};

// src/passwordValidation.ts
import * as yup from "yup";
var passwordValidation = yup.string().required("Please enter your new password").test(
  "password-strength",
  "Password must be at least 8 characters long and contain at least 3 of the following: lowercase, uppercase, numbers, special characters (@#$%^&*).",
  (value) => {
    if (!value) return false;
    const minLength = value.length >= 8;
    const lowercase = /[a-z]/.test(value);
    const uppercase = /[A-Z]/.test(value);
    const numbers = /[0-9]/.test(value);
    const specialChars = /[@#$%^&*]/.test(value);
    const passedConditions = [lowercase, uppercase, numbers, specialChars].filter(Boolean).length;
    return minLength && passedConditions >= 3;
  }
);

// src/perceptualColorDifferences.ts
import Color from "color";
var adjustColorByDeltaE = (color, deltaE) => {
  try {
    const colorObj = color instanceof Color ? color : Color(color);
    const [L, A, B] = colorObj.lab().array();
    const newL = Math.max(0, L + deltaE);
    const adjustedColor = Color.lab([newL, A, B]);
    return adjustedColor.hex().toUpperCase();
  } catch (e) {
    throw new Error(`Failed to adjust color: ${String(e)}`);
  }
};
var getPerceptualDifference = (color1, color2) => {
  try {
    const c1 = color1 instanceof Color ? color1 : Color(color1);
    const c2 = color2 instanceof Color ? color2 : Color(color2);
    const [L1, A1, B1] = c1.lab().array();
    const [L2, A2, B2] = c2.lab().array();
    const deltaL = L2 - L1;
    const deltaA = A2 - A1;
    const deltaB = B2 - B1;
    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  } catch (e) {
    throw new Error(`Failed to calculate perceptual difference: ${String(e)}`);
  }
};

// src/removeNonNumeric.ts
var removeNonNumeric = (value) => {
  return value.replace(/\D/g, "");
};

// src/removeProtocol.ts
var removeProtocol = (url) => {
  return url.replace(/^https?:\/\//, "");
};

// src/roundToOneDecimalPlace.ts
var roundToOneDecimalPlace = (num2) => {
  return Math.round(num2 * 10) / 10;
};

// src/setCookie.ts
var setCookie = (name, value, days = 365) => {
  const date = /* @__PURE__ */ new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1e3);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

// src/compareEntitiesForDisplay.ts
import { TwinEntityType as TwinEntityType3 } from "@repo/frontend-types";

// src/naturalSort.ts
function naturalSort(a, b) {
  return a.localeCompare(b, void 0, { numeric: true, sensitivity: "base" });
}

// src/compareEntitiesForDisplay.ts
function compareEntitiesForDisplay(a, b) {
  const aIsFloor = stringToTwinEntityType(a.type?.name ?? "") === TwinEntityType3.FLOOR;
  const bIsFloor = stringToTwinEntityType(b.type?.name ?? "") === TwinEntityType3.FLOOR;
  if (aIsFloor && bIsFloor) {
    const altitudeDelta = (a.altitude ?? 0) - (b.altitude ?? 0);
    if (altitudeDelta !== 0) return altitudeDelta;
    return naturalSort(a.name, b.name);
  }
  return naturalSort(a.name, b.name);
}

// src/shuffleArray.ts
var shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// src/stringToTimeUnit.ts
import { TimeUnit } from "@repo/frontend-types";
var stringToTimeUnit = (value) => {
  switch (value.toUpperCase()) {
    case "0":
      return TimeUnit.WEEK;
    case "1":
      return TimeUnit.MONTH;
    default:
      return TimeUnit.WEEK;
  }
};

// src/timeToMinutes.ts
var timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

// src/totalHours.ts
var calculateTotalHours = (startTime, endTime) => {
  let [startHour, startMinute] = startTime.split(":").map(Number);
  let [endHour, endMinute] = endTime.split(":").map(Number);
  const startDate = new Date(0, 0, 0, startHour, startMinute);
  const endDate = new Date(0, 0, 0, endHour, endMinute);
  let diff = endDate.getTime() - startDate.getTime();
  let diffHours = diff / (1e3 * 60 * 60);
  if (diffHours < 0) {
    diffHours += 24;
  }
  return diffHours;
};

// src/transformTimeSeriesData.ts
var transformTimeSeriesData = (timeSeriesData, metrics) => {
  const metricsLookup = /* @__PURE__ */ new Map();
  metrics.forEach((metric) => {
    metricsLookup.set(metric.name, metric);
  });
  const transformedData = {};
  Object.keys(timeSeriesData).forEach((key) => {
    const metric = metricsLookup.get(key);
    if (metric) {
      const dataValues = [];
      Object.keys(timeSeriesData[key]).forEach((bID) => {
        const item = timeSeriesData[key][bID];
        const dataValue = {
          bID,
          metric,
          value: item.aggregated_value,
          unit: metric.unit,
          weekValues: item.weekday_aggregation
        };
        dataValues.push(dataValue);
      });
      transformedData[key] = dataValues;
    } else {
      console.warn(`No metric found for key: ${key}`);
      transformedData[key] = [];
    }
  });
  return transformedData;
};

// src/trimStringFromChar.ts
var trimStringFromChar = (str, char) => {
  let charIndex = str.indexOf(char);
  return charIndex !== -1 ? str.substring(0, charIndex) : str;
};

// src/truncateString.ts
var truncateString = (str, len, appendStr = "...") => {
  return str.length > len ? `${str.slice(0, len)}${appendStr}` : str;
};

// src/zeroPad.ts
var zeroPad = (num2, totalWidth) => {
  return num2.toString().padStart(totalWidth, "0");
};

// src/findEntityByBusinessId.ts
var findEntityByBusinessId = (array, businessId) => {
  let foundEntity = null;
  if (array) {
    array.some((obj) => {
      if (obj.bID === businessId) {
        foundEntity = obj;
        return true;
      }
      if (obj.children && Array.isArray(obj.children)) {
        foundEntity = findEntityByBusinessId(obj.children, businessId);
        return foundEntity !== null;
      }
      return null;
    });
  }
  return foundEntity;
};

// src/findEntityById.ts
import { DataMode as DataMode3 } from "@repo/frontend-types";
var findEntityById = (dataMode, array, id) => {
  let foundEntity = null;
  if (array) {
    array.some((obj) => {
      if (dataMode === DataMode3.LIVE && obj.id === id || dataMode === DataMode3.TIME_SERIES && obj.bID === id) {
        foundEntity = obj;
        return true;
      }
      if (obj.children && Array.isArray(obj.children)) {
        foundEntity = findEntityById(dataMode, obj.children, id);
        return foundEntity !== null;
      }
      return null;
    });
  }
  return foundEntity;
};
function findEntityWithBID(twinEntity, bID) {
  let found;
  if (twinEntity.bID === bID) {
    found = twinEntity;
  } else {
    if (twinEntity.children) {
      for (const child of twinEntity.children) {
        const result = findEntityWithBID(child, bID);
        if (result) {
          found = result;
        }
      }
    }
  }
  return found;
}

// src/findMinAndMaxDates.ts
import { DateTime as DateTime10 } from "luxon";
var findMinMaxDates = (dates) => {
  let dateObjects = dates.map((date) => DateTime10.fromISO(date, { zone: "utc" }));
  const minDate = DateTime10.min(...dateObjects);
  const maxDate = DateTime10.max(...dateObjects);
  return {
    minDate: minDate ? minDate.toISO({ includeOffset: false }) : null,
    maxDate: maxDate ? maxDate.toISO({ includeOffset: false }) : null
  };
};

// src/findObjectById.ts
var findObjectById = (array, id) => {
  let foundObject = null;
  if (array) {
    array.some((obj) => {
      if (obj.id === id) {
        foundObject = obj;
        return true;
      }
      if (obj.children && Array.isArray(obj.children)) {
        foundObject = findObjectById(obj.children, id);
        return foundObject !== null;
      }
      return null;
    });
  }
  return foundObject;
};

// src/addDataInterval.ts
var addDataInterval = (date, dataInterval) => {
  const newDate = new Date(date);
  switch (dataInterval) {
    case "5s":
      newDate.setSeconds(newDate.getSeconds() + 5);
      break;
    case "10s":
      newDate.setSeconds(newDate.getSeconds() + 10);
      break;
    case "15s":
      newDate.setSeconds(newDate.getSeconds() + 15);
      break;
    case "30s":
      newDate.setSeconds(newDate.getSeconds() + 30);
      break;
    case "1m":
      newDate.setMinutes(newDate.getMinutes() + 1);
      break;
    case "2m":
      newDate.setMinutes(newDate.getMinutes() + 2);
      break;
    case "5m":
      newDate.setMinutes(newDate.getMinutes() + 5);
      break;
    case "10m":
      newDate.setMinutes(newDate.getMinutes() + 10);
      break;
    case "15m":
      newDate.setMinutes(newDate.getMinutes() + 15);
      break;
    case "1h":
      newDate.setHours(newDate.getHours() + 1);
      break;
    case "2h":
      newDate.setHours(newDate.getHours() + 2);
      break;
    default:
      throw new Error("Invalid data interval");
  }
  return newDate;
};

// src/escapeHtml.ts
var escapeHtml = (text4) => {
  const div = document.createElement("div");
  div.textContent = text4;
  return div.innerHTML;
};

// src/sanitizeLegalHtml.ts
var ALLOWED_TAGS = /* @__PURE__ */ new Set([
  "H1",
  "H2",
  "H3",
  "P",
  "UL",
  "OL",
  "LI",
  "STRONG",
  "EM",
  "BR",
  "A",
  "TABLE",
  "THEAD",
  "TBODY",
  "TR",
  "TH",
  "TD"
]);
var SAFE_ABSOLUTE_SCHEMES = ["http:", "https:", "mailto:"];
var stripUrlNoise = (href) => (
  // Also strip U+FFFD: the HTML parser replaces a NUL in an attribute value with the
  // replacement char, which would otherwise mask a following `javascript:` scheme.
  href.replace(/[\u0000-\u0020\uFFFD]+/g, "")
);
var isExternalHref = (href) => /^https?:\/\//i.test(stripUrlNoise(href));
var isSafeHref = (href) => {
  const s = stripUrlNoise(href);
  if (s === "") return false;
  if (/^(?![a-z][a-z0-9+.-]*:)(?!\/\/)/i.test(s)) return true;
  const lower = s.toLowerCase();
  return SAFE_ABSOLUTE_SCHEMES.some((scheme) => lower.startsWith(scheme));
};
function sanitizeLegalHtml(html) {
  if (!html) return "";
  if (typeof document === "undefined") return "";
  const wrap = document.createElement("div");
  wrap.innerHTML = html;
  const walk = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node;
      if (!ALLOWED_TAGS.has(el.tagName)) {
        const textNode = document.createTextNode(el.textContent || "");
        el.parentNode?.replaceChild(textNode, el);
        return;
      }
      const isAnchor = el.tagName === "A";
      const rawHref = isAnchor ? el.getAttribute("href") : null;
      Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));
      if (isAnchor && rawHref && isSafeHref(rawHref)) {
        el.setAttribute("href", rawHref);
        if (isExternalHref(rawHref)) {
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener noreferrer");
        }
      }
      Array.from(el.childNodes).forEach(walk);
    }
  };
  Array.from(wrap.childNodes).forEach(walk);
  return wrap.innerHTML;
}

// src/legalDocumentLinks.ts
var DOCUMENT_SLUGS = {
  acceptableUse: "acceptable-use",
  dataProcessing: "data-processing",
  saasStandardTerms: "saas-standard-terms"
};
var legalDocumentLinks = (twinAppBaseUrl) => {
  const base = twinAppBaseUrl?.replace(/\/$/, "");
  if (!base) {
    return null;
  }
  return {
    acceptableUse: `${base}/legal/${DOCUMENT_SLUGS.acceptableUse}`,
    dataProcessing: `${base}/legal/${DOCUMENT_SLUGS.dataProcessing}`,
    saasStandardTerms: `${base}/legal/${DOCUMENT_SLUGS.saasStandardTerms}`
  };
};
var SLUG_TO_DOCUMENT = Object.fromEntries(
  Object.entries(DOCUMENT_SLUGS).map(([key, slug]) => [
    slug,
    key
  ])
);
var slugToDocument = (slug) => Object.prototype.hasOwnProperty.call(SLUG_TO_DOCUMENT, slug) ? SLUG_TO_DOCUMENT[slug] : null;
var parseLegalDocumentVersion = (raw) => /^[1-9]\d*$/.test(raw) ? Number(raw) : null;

// src/fmlPageLink.ts
var fmlPageLink = (twinAppBaseUrl, bid) => {
  const base = twinAppBaseUrl?.trim().replace(/\/$/, "");
  const twinBid = bid?.trim();
  if (!base || !twinBid) {
    return null;
  }
  const encoded = encodeURIComponent(twinBid);
  return `${base}/twin?spotlight=${encoded}&edit=${encoded}`;
};

// src/formatPublishedDate.ts
var formatPublishedDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC"
  });
};

// src/formatRoles.ts
import { Roles } from "@repo/frontend-types";
var formatRoles = (roles) => {
  return roles.map((role) => formatRole(role));
};
var formatRole = (role) => {
  switch (role) {
    case Roles.CUSTOMER_ADMIN:
      return "Admin";
    case Roles.CUSTOMER_USER:
      return "User";
    case Roles.PARTNER_USER:
      return "Partner";
    case Roles.TL_ADMIN:
      return "TL Admin";
    default:
      return "";
  }
};

// src/hasCalendarBeenUsed.ts
var hasCalendarBeenUsed = (startDateTime, finishDateTime) => {
  const startDate = startDateTime instanceof Date ? startDateTime : new Date(startDateTime);
  const finishDate = finishDateTime instanceof Date ? finishDateTime : new Date(finishDateTime);
  const now = /* @__PURE__ */ new Date();
  const currentStartDateTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  );
  const startDateOnly = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const finishDateOnly = new Date(
    finishDate.getFullYear(),
    finishDate.getMonth(),
    finishDate.getDate()
  );
  const currentDateOnly = new Date(
    currentStartDateTime.getFullYear(),
    currentStartDateTime.getMonth(),
    currentStartDateTime.getDate()
  );
  const datesAreDifferent = startDateOnly.getTime() !== currentDateOnly.getTime() || finishDateOnly.getTime() !== currentDateOnly.getTime();
  if (datesAreDifferent) return true;
  const startTimeIsDifferent = startDate.getHours() !== 0 || startDate.getMinutes() !== 0 || startDate.getSeconds() !== 0;
  const finishTimeIsDifferent = finishDate.getHours() !== 23 || finishDate.getMinutes() !== 59 || finishDate.getSeconds() !== 59;
  return startTimeIsDifferent || finishTimeIsDifferent;
};

// src/hexToRgb.ts
var hexToRgb = (hex) => {
  hex = hex.replace(/^#/, "");
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    throw new Error("Invalid HEX color.");
  }
  return `${r}, ${g}, ${b}`;
};

// src/json.ts
var json = (obj) => JSON.stringify(obj, null, 2);

// src/geocodeAddress.ts
var CACHE_KEY = "org-geocode-cache-v1";
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}
function writeCache(cache4) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache4));
  } catch {
  }
}
async function geocodeAddress(address, token, countryIso2, timeoutMs = 1e3, proximity) {
  if (!address || !token) return null;
  const cache4 = readCache();
  const cacheKey = countryIso2 ? `${address}__${countryIso2}` : address;
  if (cache4[cacheKey]) return cache4[cacheKey];
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const base = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`;
  const params = new URLSearchParams();
  params.set("limit", "1");
  if (countryIso2) params.set("country", countryIso2);
  if (proximity) params.set("proximity", `${proximity.lng},${proximity.lat}`);
  params.set("access_token", token);
  try {
    const res = await fetch(`${base}?${params.toString()}`, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const json3 = await res.json();
    const feature = json3?.features?.[0];
    if (!feature?.center) return null;
    const coords = { lng: feature.center[0], lat: feature.center[1] };
    cache4[cacheKey] = coords;
    writeCache(cache4);
    return coords;
  } catch {
    clearTimeout(timer);
    return null;
  }
}
function buildAddressLine(parts2) {
  return parts2.map((x) => (x || "").trim()).filter(Boolean).join(", ");
}

// src/mapStyles.ts
var mapStyleOverrides = {};
function getMapStyleUrl(mode, baseStyleUrl) {
  return mapStyleOverrides[mode] ?? baseStyleUrl;
}

// src/labels/resolveLabelRow.ts
var MISSING_VALUE = "N/A";
var formatNumber = (value, showDecimalPlace) => {
  if (showDecimalPlace === true) {
    const fixed = value.toFixed(1);
    return fixed === "-0.0" ? "0.0" : fixed;
  }
  if (showDecimalPlace === false) {
    return Math.round(value).toString();
  }
  return String(value);
};
var withUnit = (formatted, row) => {
  if (row.show_unit !== true || !row.unit || typeof row.unit !== "string") {
    return formatted;
  }
  return /^\p{L}/u.test(row.unit) ? `${formatted} ${row.unit}` : `${formatted}${row.unit}`;
};
var resolveMetricValue = (row, entity, data, appMode) => {
  const series = data?.processed?.[row.value_key];
  if (!Array.isArray(series) || series.length === 0) {
    return MISSING_VALUE;
  }
  const entityKey = entity[entityKeyFor(appMode)] ?? "";
  const value = getDataValue(dataModeFor(appMode), series, entityKey)?.value;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return MISSING_VALUE;
  }
  return withUnit(formatNumber(value, row.show_decimal_place), row);
};
var resolvePropertyValue = (row, entity) => {
  const key = row.value_key;
  if (!Object.prototype.hasOwnProperty.call(entity, key)) {
    return null;
  }
  const value = entity[key];
  if (!value) {
    return null;
  }
  if (typeof value !== "string" && typeof value !== "number") {
    return MISSING_VALUE;
  }
  return String(value);
};
var resolveLabelRow = (row, entity, data, appMode) => {
  if (row === null || typeof row !== "object") {
    return null;
  }
  const name = row.display_name;
  if (typeof name !== "string" || name === "") {
    return null;
  }
  if (row.value_type !== "HERO_METRIC" && row.value_type !== "ENTITY_PROPERTY") {
    return null;
  }
  if (!row.value_key) {
    return { name, value: MISSING_VALUE };
  }
  const value = row.value_type === "HERO_METRIC" ? resolveMetricValue(row, entity, data, appMode) : resolvePropertyValue(row, entity);
  if (value === null || value === "") {
    return null;
  }
  if (row.hide_missing_values && (value === "0" || value === MISSING_VALUE)) {
    return null;
  }
  return { name, value };
};
var resolveLabelRows = (wireLabels, context) => {
  if (!Array.isArray(wireLabels)) {
    return [];
  }
  const mode = labelModeFor(context.appMode);
  const resolved = [];
  wireLabels.forEach((row) => {
    if (row === null || typeof row !== "object") {
      return;
    }
    if (row.entity_type !== context.entityType || row.mode !== mode) {
      return;
    }
    const labelRow = resolveLabelRow(row, context.entity, context.data, context.appMode);
    if (labelRow !== null) {
      resolved.push(labelRow);
    }
  });
  return resolved;
};

// src/labels/labelTargets.ts
import { TwinEntityType as TwinEntityType4 } from "@repo/frontend-types";
var LABEL_TARGETS = [
  {
    target: TwinEntityType4.ZONE,
    label: "Zone",
    description: "Any defined space \u2014 a business Zone, Section or Area alike"
  },
  {
    target: TwinEntityType4.ASSET,
    label: "Asset",
    description: "Any defined thing"
  },
  {
    target: TwinEntityType4.FLOOR,
    label: "Floor",
    description: "A floor within a structure"
  },
  {
    target: TwinEntityType4.STATION,
    label: "Station",
    description: "Event twins only"
  },
  {
    target: TwinEntityType4.OPERATOR,
    label: "Operator",
    description: "Event twins only"
  }
];
var isLabelTarget = (entityType) => LABEL_TARGETS.some((entry) => entry.target === entityType);

// src/polygonUtils.ts
var parseCfgArrayToPolygons = (cfgs) => {
  const polygons = [];
  cfgs.forEach((cfgStr, index) => {
    try {
      if (!cfgStr || cfgStr.trim() === "") return;
      const parsed = JSON.parse(cfgStr);
      if (parsed.polygon && Array.isArray(parsed.polygon) && parsed.polygon.length > 0) {
        const validPoints = parsed.polygon.filter(
          (point) => point && typeof point.x === "number" && typeof point.y === "number" && !isNaN(point.x) && !isNaN(point.y)
        );
        if (validPoints.length >= 3) {
          polygons.push({
            id: `frame-polygon-${index}-${Date.now()}`,
            points: validPoints,
            isComplete: true
          });
        }
      }
    } catch (error) {
      console.warn(`Failed to parse CFG at index ${index}:`, error);
    }
  });
  return polygons;
};
var hasValidPolygon = (cfgStr) => {
  try {
    if (!cfgStr || cfgStr.trim() === "") return false;
    const parsed = JSON.parse(cfgStr);
    return parsed.polygon && Array.isArray(parsed.polygon) && parsed.polygon.length >= 3 && parsed.polygon.every(
      (point) => point && typeof point.x === "number" && typeof point.y === "number" && !isNaN(point.x) && !isNaN(point.y)
    );
  } catch {
    return false;
  }
};

// src/sortDataByType.ts
var sortDataValuesByType = (dataValues, metrics) => {
  const sortedObject = {};
  metrics.forEach((metric) => {
    sortedObject[metric.name] = [];
  });
  dataValues.forEach((dv) => {
    if (!sortedObject[dv.metric.name]) {
      sortedObject[dv.metric.name] = [];
    }
    sortedObject[dv.metric.name].push(dv);
  });
  return sortedObject;
};

// src/chart/calcChartWidth.ts
var chartWidthCalc = (data, multiplier) => {
  let width = 0;
  if (Array.isArray(data) && data.length > 0) {
    width = data.length * multiplier;
  }
  return `${width}px`;
};

// src/chart/calcYLabelWidth.ts
var chartYLabelWidthCalc = (data) => {
  let width = 40;
  let divider = 0;
  if (data && data.length > 0) {
    const longestString = data.reduce((a, b) => a.length > b.length ? a : b);
    if (longestString.length >= 0 && longestString.length <= 5) {
      divider = 4;
    } else if (longestString.length >= 5 && longestString.length <= 19) {
      divider = 5;
    } else if (longestString.length >= 20 && longestString.length <= 39) {
      divider = 5;
    } else if (longestString.length > 40) {
      divider = 5.75;
    }
    width = width * longestString.length / divider;
  }
  return `${width}px`;
};

// src/chart/createStackedChartLegend.ts
var createStackedChartLegend = (seriesData) => {
  let legendData = [];
  seriesData.forEach((series) => {
    legendData.push({
      name: series.name,
      itemStyle: { color: series.areaStyle.color }
    });
  });
  return legendData;
};

// src/chart/getDaysOfTheWeek.ts
var getDaysOfTheWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// src/chart/createStackedChartOptions.ts
var createStackedChartOptions = (seriesData) => {
  const legendData = createStackedChartLegend(seriesData);
  return {
    grid: { top: "45px", left: "0", right: "5%", bottom: "0", containLabel: true },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: getDaysOfTheWeek,
      nameGap: 5,
      interval: 1,
      axisLine: { show: false },
      axisLabel: { padding: [0, 0, 0, 0], fontSize: 11, show: true },
      axisTick: { show: false }
    },
    yAxis: {
      type: "value",
      axisLabel: { show: true },
      splitLine: { show: false }
    },
    legend: {
      padding: 0,
      top: 10,
      data: legendData,
      icon: "rect",
      itemGap: 5,
      itemWidth: 8,
      itemHeight: 8
    },
    series: seriesData
  };
};

// src/chart/getStackedChartColors.ts
var stackedChartColors = [
  { fill: "#51C4E4", lineColor: "#75BFFF" },
  { fill: "#66AEEC", lineColor: "#6BBBFF" },
  { fill: "#7599F3", lineColor: "#5CB4FF" },
  { fill: "#7C82FB", lineColor: "#47AAFF" },
  { fill: "#8368FE", lineColor: "#47AAFF" },
  { fill: "#7A4DFE", lineColor: "#6BBBFF" }
];

// src/chart/createStackedChartSeries.ts
var createStackedChartSeries = (stackedChartData) => {
  let seriesData = [];
  stackedChartData.forEach((record, i) => {
    seriesData.push({
      name: record.key,
      type: "line",
      stack: "Total",
      smooth: true,
      lineStyle: {
        width: 1,
        type: "solid",
        opacity: 0.25,
        color: stackedChartColors[i].lineColor
      },
      showSymbol: false,
      areaStyle: {
        opacity: 0.8,
        color: stackedChartColors[i].fill,
        borderColor: "rgb(204, 46, 72)"
      },
      emphasis: { focus: "series" },
      data: record.value
    });
  });
  return seriesData;
};

// src/mappers/mapMetric.ts
import { DataMode as DataMode4 } from "@repo/frontend-types";
var mapMetric = (dataMode, metrics, metricId) => {
  return findObjectByPropertyValue(metrics, dataMode === DataMode4.LIVE ? "id" : "name", metricId);
};

// src/mappers/mapTimeSeriesData.ts
import { DataMode as DataMode5 } from "@repo/frontend-types";
var mapTimeSeriesData = (timeSeriesmetricData, metrics) => {
  const data = [];
  const keys = Object.keys(timeSeriesmetricData);
  try {
    keys.forEach((key) => {
      const TSMetricValues = timeSeriesmetricData[key];
      Array.isArray(TSMetricValues) && TSMetricValues.forEach((mr) => {
        let bID = mr.metric.digitalTwinEntity;
        let bIDPath = mr.metric.digitalTwinEntityPath;
        let metric = mapMetric(DataMode5.TIME_SERIES, metrics, mr.metric.metric);
        if (mr.values) {
          mr.values.forEach((val) => {
            data.push({
              bID,
              bIDPath,
              metric,
              timestamp: convertUnixTimestampToDate(val[0]).toISOString(),
              value: parseFloat(val[1]),
              unit: metric.unit
            });
          });
        }
        if (mr.value) {
          data.push({
            bID,
            bIDPath,
            metric,
            timestamp: convertUnixTimestampToDate(mr.value[0]).toISOString(),
            value: parseFloat(mr.value[1]),
            unit: metric.unit
          });
        }
      });
    });
  } catch (error) {
    console.error(`Error encountered in mapTimeSeriesData function`, error);
  }
  return data;
};

// src/mappers/mapLiveSubscriptionData.ts
import { DataMode as DataMode6 } from "@repo/frontend-types";
import { DateTime as DateTime11 } from "luxon";
var mapLiveSubscriptionData = (metricValues, metrics) => {
  const data = [];
  const nowInTimeZone = DateTime11.utc();
  const millisSinceEpoch = nowInTimeZone.toMillis();
  let mostRecentDateTime = null;
  let mostRecentMillis = 0;
  let warning = false;
  metricValues.forEach((mv) => {
    if (!mostRecentDateTime) {
      mostRecentDateTime = DateTime11.fromISO(mv.timestamp, { zone: "utc" });
    } else {
      let recordDate = DateTime11.fromISO(mv.timestamp, { zone: "utc" });
      if (recordDate > mostRecentDateTime) {
        mostRecentMillis = recordDate.toMillis();
      }
    }
    const metric = mapMetric(DataMode6.LIVE, metrics, mv.metricID);
    try {
      data.push({
        entityId: mv.entityID,
        metric: mapMetric(DataMode6.LIVE, metrics, mv.metricID),
        timestamp: mv.timestamp,
        value: mv.value,
        unit: metric.unit
      });
    } catch (error) {
      console.error(`Error mapping Live Subscription Data`);
      console.error(`Metric Value`, mv);
      console.error(`Matched Metric`, metric ? metric : `No metric matched for ${mv.metricID}`);
      console.error(`Available Metrics`, metrics);
    }
  });
  const alarmInMilliseconds = 15 * 60 * 1e3;
  if (millisSinceEpoch - mostRecentMillis > alarmInMilliseconds) {
    warning = true;
  }
  return { data, warning };
};

// src/describeSubscriptionError.ts
var messageOf = (value) => {
  if (typeof value === "string") return value || null;
  if (typeof value === "object" && value !== null) {
    const { message } = value;
    if (typeof message === "string" && message) return message;
  }
  return null;
};
var propertyOf = (value, key) => typeof value === "object" && value !== null ? value[key] : void 0;
var SECRET_KEY = /token|authorization|secret|password|jwt|cookie|credential/i;
var MAX_SERIALISED_LENGTH = 500;
var describeSubscriptionError = (error) => {
  if (error === null || error === void 0) {
    return "no error detail provided";
  }
  const parts2 = [];
  const direct = messageOf(error);
  if (direct) parts2.push(direct);
  const graphQLErrors = propertyOf(error, "graphQLErrors");
  if (Array.isArray(graphQLErrors)) {
    graphQLErrors.forEach((gqlError) => {
      const message = messageOf(gqlError);
      if (!message) return;
      const code = propertyOf(propertyOf(gqlError, "extensions"), "code");
      const described = code ? `GraphQL: ${message} [code=${String(code)}]` : `GraphQL: ${message}`;
      if (!code && parts2.includes(message)) return;
      parts2.push(described);
    });
  }
  const networkMessage = messageOf(propertyOf(error, "networkError"));
  if (networkMessage) parts2.push(`network: ${networkMessage}`);
  if (parts2.length > 0) return parts2.join(" | ");
  const type = propertyOf(error, "type");
  if (typeof type === "string" && type) return `event: ${type}`;
  try {
    const serialised = JSON.stringify(
      error,
      (key, value) => SECRET_KEY.test(key) ? "[redacted]" : value
    );
    if (serialised && serialised !== "{}") return serialised.slice(0, MAX_SERIALISED_LENGTH);
  } catch {
  }
  return `unrecognised error of type ${typeof error}`;
};

// src/twin-planner/geometry.ts
function snapToGrid(value, gridSize) {
  return Math.round(value / gridSize) * gridSize;
}
function snapPlannerPoint(point, opts) {
  if (opts.free || !opts.gridSnap || !(opts.gridSizeCm > 0)) return point;
  const snap = (metres) => snapToGrid(metres * 100, opts.gridSizeCm) / 100;
  return { x: snap(point.x), y: snap(point.y) };
}
function snapPlannerDistance(distanceM, opts) {
  if (opts.free || !opts.gridSnap || !(opts.gridSizeCm > 0)) return distanceM;
  const snapped = snapToGrid(distanceM * 100, opts.gridSizeCm) / 100;
  return snapped === 0 ? 0 : snapped;
}
function degreesToRadians(degrees) {
  return degrees * Math.PI / 180;
}
function radiansToDegrees(radians) {
  return radians * 180 / Math.PI;
}
function polygonToFlatArray(points2) {
  return points2.flatMap((p) => [p.x, p.y]);
}
function polygonArea(points2) {
  if (points2.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points2.length; i++) {
    const j = (i + 1) % points2.length;
    area += points2[i].x * points2[j].y;
    area -= points2[j].x * points2[i].y;
  }
  return Math.abs(area) / 2;
}
function pointInPolygon(point, polygon) {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    if (yi > point.y !== yj > point.y && point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}
var usablePoly = (poly) => Array.isArray(poly) && poly.length >= 3 && poly.every(
  (p) => p != null && typeof p.x === "number" && isFinite(p.x) && typeof p.y === "number" && isFinite(p.y)
);
function findContainingSurface(point, surfaces) {
  let best = null;
  let bestArea = Infinity;
  for (const s of surfaces) {
    if (!s.poly || s.poly.length < 3) continue;
    if (!pointInPolygon(point, s.poly)) continue;
    const area = polygonArea(s.poly);
    if (area < bestArea) {
      bestArea = area;
      best = s;
    }
  }
  return best;
}
function polygonInteriorPoint(poly) {
  if (!usablePoly(poly)) return null;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of poly) {
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  if (!(maxY > minY)) return null;
  const NUDGE = 1e-6;
  let y = (minY + maxY) / 2;
  for (let attempt = 0; attempt < 8 && poly.some((p) => p.y === y); attempt++) {
    y += NUDGE * (attempt % 2 === 0 ? attempt + 1 : -(attempt + 1));
  }
  const xs = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > y !== b.y > y) xs.push(a.x + (y - a.y) * (b.x - a.x) / (b.y - a.y));
  }
  xs.sort((m, n) => m - n);
  let best = null;
  let bestWidth = 0;
  for (let k = 0; k + 1 < xs.length; k += 2) {
    const width = xs[k + 1] - xs[k];
    if (width > bestWidth) {
      bestWidth = width;
      best = { x: (xs[k] + xs[k + 1]) / 2, y };
    }
  }
  return best;
}
function polygonCentroid(points2) {
  if (points2.length === 0) return { x: 0, y: 0 };
  const sum = points2.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points2.length, y: sum.y / points2.length };
}
var NO_AREA = 1e-6;
function polygonAreaCentroid(points2) {
  if (points2.length < 3) return null;
  let twiceArea = 0;
  let cx = 0;
  let cy = 0;
  points2.forEach((a, k) => {
    const b = points2[(k + 1) % points2.length];
    const cross2 = a.x * b.y - b.x * a.y;
    twiceArea += cross2;
    cx += (a.x + b.x) * cross2;
    cy += (a.y + b.y) * cross2;
  });
  if (Math.abs(twiceArea / 2) < NO_AREA) return null;
  return [cx / (3 * twiceArea), cy / (3 * twiceArea)];
}
function distance(a, b) {
  return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
}
function wallPerpendicularOffset(a, b, thickness) {
  const len = distance(a, b);
  if (len === 0) return { dx: 0, dy: 0 };
  const half = thickness / 2;
  return {
    dx: -(b.y - a.y) / len * half,
    dy: (b.x - a.x) / len * half
  };
}
var PIXELS_PER_METER = 50;
function metersToPixels(meters) {
  return meters * PIXELS_PER_METER;
}
function pixelsToMeters(pixels) {
  return pixels / PIXELS_PER_METER;
}
function computeDesignBounds(design) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let hasPoints = false;
  const expand = (px, py) => {
    hasPoints = true;
    if (px < minX) minX = px;
    if (py < minY) minY = py;
    if (px > maxX) maxX = px;
    if (py > maxY) maxY = py;
  };
  for (const wall of design.walls) {
    const offset = wallPerpendicularOffset(wall.a, wall.b, wall.thickness);
    for (const pt of [wall.a, wall.b]) {
      expand(metersToPixels(pt.x) + offset.dx, metersToPixels(pt.y) + offset.dy);
      expand(metersToPixels(pt.x) - offset.dx, metersToPixels(pt.y) - offset.dy);
    }
  }
  for (const surface of design.surfaces) {
    for (const pt of surface.poly) {
      expand(metersToPixels(pt.x), metersToPixels(pt.y));
    }
  }
  for (const item of design.items) {
    expand(metersToPixels(item.x), metersToPixels(item.y));
  }
  return hasPoints ? { minX, minY, maxX, maxY } : null;
}

// src/clientId.ts
function newClientId({
  uuidPrefix,
  fallbackPrefix
}) {
  const uuid = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
  if (uuid) return `${uuidPrefix}${uuid()}`;
  const chunk = () => Math.random().toString(36).slice(2).padEnd(10, "0").slice(0, 10);
  return `${fallbackPrefix}${Date.now().toString(36).padStart(9, "0")}-${chunk()}-${chunk()}`;
}

// src/twin-planner/rotationSnap.ts
var ROTATION_DRAG_SNAP_STEP_DEG = 45;
var ROTATION_DRAG_SNAP_TOLERANCE_DEG = 3;
var normaliseRotation = (degrees) => (degrees % 360 + 360) % 360;
var rotationFromLocalOffset = (dx, dy, facing = "+y") => normaliseRotation((facing === "+x" ? Math.atan2(dy, dx) : Math.atan2(-dx, dy)) * 180 / Math.PI);
var BUILD_ROTATION_SHIFT_STEP_DEG = 15;
var snapBuildRotation = (rawDegrees, { shift }) => {
  const step = shift ? BUILD_ROTATION_SHIFT_STEP_DEG : 1;
  return normaliseRotation(Math.round(normaliseRotation(rawDegrees) / step) * step);
};
var stepRotation = (current, direction, shift) => {
  const now = normaliseRotation(Math.round(current));
  if (!shift) return normaliseRotation(now + direction);
  const step = BUILD_ROTATION_SHIFT_STEP_DEG;
  const next = direction === 1 ? Math.floor(now / step) * step + step : Math.ceil(now / step) * step - step;
  return normaliseRotation(next);
};
var snapRotationDrag = (rawDegrees) => {
  const whole = Math.round(normaliseRotation(rawDegrees));
  const nearestStep = Math.round(whole / ROTATION_DRAG_SNAP_STEP_DEG) * ROTATION_DRAG_SNAP_STEP_DEG;
  const snapped = Math.abs(whole - nearestStep) <= ROTATION_DRAG_SNAP_TOLERANCE_DEG ? nearestStep : whole;
  return normaliseRotation(snapped);
};
var snapItemRotation = (rawDegrees, { shift }) => shift ? snapBuildRotation(rawDegrees, { shift: true }) : snapRotationDrag(rawDegrees);

// src/twin-planner/floorPlacement.ts
var IDENTITY_PLACEMENT = Object.freeze({ x: 0, y: 0, rotation: 0 });
var placementOf = (floor) => floor?.placement ?? IDENTITY_PLACEMENT;
var samePlacement = (a, b) => a.x === b.x && a.y === b.y && a.rotation === b.rotation;
var isIdentityPlacement = (p) => p.x === 0 && p.y === 0 && normaliseRotation(p.rotation) === 0;
var rotate = ({ x, y }, degrees) => {
  const rad = degrees * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
};
var floorToWorld = (p, placement) => {
  const r = rotate(p, placement.rotation);
  return { x: placement.x + r.x, y: placement.y + r.y };
};
var worldToFloor = (p, placement) => rotate({ x: p.x - placement.x, y: p.y - placement.y }, -placement.rotation);
var floorPixelsToWorld = (p, placement) => {
  const r = rotate(p, placement.rotation);
  return { x: metersToPixels(placement.x) + r.x, y: metersToPixels(placement.y) + r.y };
};
var worldPixelsToFloor = (p, placement) => rotate(
  { x: p.x - metersToPixels(placement.x), y: p.y - metersToPixels(placement.y) },
  -placement.rotation
);
var rotatePlacementAbout = (placement, localPivot, rotation) => {
  const world = floorToWorld(localPivot, placement);
  const turned = rotate(localPivot, rotation);
  return { x: world.x - turned.x, y: world.y - turned.y, rotation };
};
var reexpressUnderlay = (underlay, from, to) => {
  if (samePlacement(from, to)) return underlay;
  const centre = worldToFloor(floorToWorld({ x: underlay.x, y: underlay.y }, from), to);
  return {
    ...underlay,
    x: centre.x,
    y: centre.y,
    rotation: normaliseRotation(underlay.rotation + from.rotation - to.rotation)
  };
};
var floorPlaceLabel = (placement) => {
  const p = placement ?? IDENTITY_PLACEMENT;
  const d = Math.hypot(p.x, p.y);
  const distance2 = d >= 0.05 ? `+${d.toFixed(1)} m` : "0 m";
  return `${distance2} \xB7 ${Math.round(normaliseRotation(p.rotation)) % 360}\xB0`;
};
var BUILD_NUDGE_STEP_M = 0.1;
var SCREEN_DIRECTIONS = {
  ArrowRight: { x: 1, y: 0 },
  ArrowLeft: { x: -1, y: 0 },
  ArrowDown: { x: 0, y: 1 },
  ArrowUp: { x: 0, y: -1 }
};
var screenNudge = (key, viewRotationDeg, stepM = BUILD_NUDGE_STEP_M) => {
  const dir = SCREEN_DIRECTIONS[key];
  if (!dir) return null;
  const back = rotate(dir, -viewRotationDeg);
  return { x: tidy(back.x * stepM), y: tidy(-back.y * stepM) };
};
var moveInFloor = (move, placement) => {
  const r = rotate(move, -placement.rotation);
  return { x: tidy(r.x), y: tidy(r.y) };
};
var tidy = (n) => Math.abs(n) < 1e-9 ? 0 : n;
var placedBounds = (box, placement) => {
  if (isIdentityPlacement(placement)) return box;
  const corners = [
    { x: box.minX, y: box.minY },
    { x: box.maxX, y: box.minY },
    { x: box.maxX, y: box.maxY },
    { x: box.minX, y: box.maxY }
  ].map((c) => floorPixelsToWorld(c, placement));
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys)
  };
};
var cleanRotation = (r) => typeof r === "number" && Number.isFinite(r) ? normaliseRotation(Math.round(r)) : 0;
var cleanNumber = (n) => typeof n === "number" && Number.isFinite(n) ? n : 0;
var cleanPlacement = (p) => ({
  x: cleanNumber(p.x),
  y: cleanNumber(p.y),
  rotation: cleanRotation(p.rotation)
});
function normaliseFloorPlacement(layout) {
  let changed = false;
  const floors = (layout.floors ?? []).map((floor) => {
    if (floor.placement === void 0) return floor;
    if (floor.placement === null || typeof floor.placement !== "object") {
      changed = true;
      const { placement: _none, ...rest } = floor;
      return rest;
    }
    const clean = cleanPlacement(floor.placement);
    if (isIdentityPlacement(clean)) {
      changed = true;
      const { placement: _identity, ...rest } = floor;
      return rest;
    }
    const p = floor.placement;
    if (samePlacement(clean, p)) return floor;
    changed = true;
    return { ...floor, placement: clean };
  });
  return changed ? { ...layout, floors } : layout;
}
function normaliseFloorLevels(layout) {
  let changed = false;
  const floors = (layout.floors ?? []).map((floor, index) => {
    const stored = floor;
    if (floor.level === index && !("elevation" in stored)) return floor;
    changed = true;
    const { elevation: _storedAltitude, ...rest } = stored;
    return { ...rest, level: index };
  });
  return changed ? { ...layout, floors } : layout;
}
function withFloorOrderFrom(current, snapshot2) {
  const place = new Map((snapshot2.floors ?? []).map((floor, index) => [floor.id, index]));
  const rank = (id) => place.get(id) ?? Number.MAX_SAFE_INTEGER;
  const floors = [...current.floors ?? []].sort((a, b) => rank(a.id) - rank(b.id));
  return normaliseFloorLevels({ ...current, floors });
}

// src/twin-planner/underlays.ts
var DEFAULT_UNDERLAY_OPACITY = 0.3;
var DEFAULT_UNDERLAY_NAME = "Floor plan PDF";
var MAX_UNDERLAY_NAME_LENGTH = 120;
var HIDDEN_OPACITY = 1e-3;
var newUnderlayId = () => newClientId({ uuidPrefix: "ul:", fallbackPrefix: "ul:" });
function underlayNameFromFile(fileName) {
  const base = (fileName ?? "").trim().replace(/\.(pdf|png|jpe?g)$/i, "").trim();
  return (base || DEFAULT_UNDERLAY_NAME).slice(0, MAX_UNDERLAY_NAME_LENGTH);
}
function cleanUnderlayName(name) {
  const trimmed3 = name.trim();
  return trimmed3 ? trimmed3.slice(0, MAX_UNDERLAY_NAME_LENGTH) : null;
}
function copyName(name, taken) {
  const names = new Set(taken);
  const base = name.replace(/ copy( \d+)?$/, "").trim() || DEFAULT_UNDERLAY_NAME;
  for (let n = 1; ; n++) {
    const suffix = n === 1 ? " copy" : ` copy ${n}`;
    const candidate = base.slice(0, MAX_UNDERLAY_NAME_LENGTH - suffix.length).trimEnd() + suffix;
    if (!names.has(candidate)) return candidate;
  }
}
function copyOfUnderlay(source, takenNames) {
  return {
    ...source,
    id: newUnderlayId(),
    name: copyName(source.name, takenNames),
    locked: false
  };
}
function stackPlace(indexFromTop, count) {
  if (indexFromTop <= 0) return "the top";
  if (indexFromTop >= count - 1) return "the bottom";
  return `position ${indexFromTop + 1} of ${count}`;
}
function newUnderlay({
  imageRef,
  widthPx,
  heightPx,
  name,
  source
}) {
  return {
    id: newUnderlayId(),
    name: underlayNameFromFile(name),
    ...source ? { source } : {},
    imageRef,
    widthPx,
    heightPx,
    x: 0,
    y: 0,
    rotation: 0,
    opacity: DEFAULT_UNDERLAY_OPACITY,
    visible: true
  };
}
function underlayImageScale(u) {
  return u.scale && u.scale > 0 ? PIXELS_PER_METER / u.scale : 1;
}
function underlayPixelsToWorld(u, p) {
  const s = underlayImageScale(u);
  const lx = s * (p.x - u.widthPx / 2);
  const ly = -s * (p.y - u.heightPx / 2);
  const rad = u.rotation * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: metersToPixels(u.x) + lx * cos - ly * sin,
    y: metersToPixels(u.y) + lx * sin + ly * cos
  };
}
function worldToUnderlayPixels(u, p) {
  const s = underlayImageScale(u);
  const dx = p.x - metersToPixels(u.x);
  const dy = p.y - metersToPixels(u.y);
  const rad = u.rotation * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = dx * cos + dy * sin;
  const ly = -dx * sin + dy * cos;
  return { x: lx / s + u.widthPx / 2, y: -ly / s + u.heightPx / 2 };
}
function underlayWorldCorners(u) {
  return [
    { x: 0, y: 0 },
    { x: u.widthPx, y: 0 },
    { x: u.widthPx, y: u.heightPx },
    { x: 0, y: u.heightPx }
  ].map((p) => underlayPixelsToWorld(u, p));
}
function underlayWorldBounds(u) {
  const corners = underlayWorldCorners(u);
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys)
  };
}
var UNDERLAY_CONTRAST_MIN = 0.5;
var UNDERLAY_CONTRAST_MAX = 3;
function underlayContrast(u) {
  const c = u.contrast;
  if (typeof c !== "number" || !Number.isFinite(c)) return 1;
  return Math.min(UNDERLAY_CONTRAST_MAX, Math.max(UNDERLAY_CONTRAST_MIN, c));
}
var UNDERLAY_WIDTH_MIN_M = 1;
var UNDERLAY_WIDTH_MAX_M = 5e3;
var roundToMm = (m) => Math.round(m * 1e3) / 1e3;
function underlayWidthMetres(u) {
  return u.widthPx * underlayImageScale(u) / PIXELS_PER_METER;
}
function floorDrawState(floors, index, activeFloorIndex, visibility) {
  const floor = floors[index];
  if (!floor || visibility.hidden.includes(floor.id)) return "hidden";
  if (index === activeFloorIndex) return "selected";
  if (index === activeFloorIndex - 1) return "below";
  return visibility.shown.includes(floor.id) ? "other" : "hidden";
}
var visibleFloors = (floors, activeFloorIndex, visibility) => floors.filter(
  (_, index) => floorDrawState(floors, index, activeFloorIndex, visibility) !== "hidden"
);
function alignFloorDrawOrder(floors, activeFloorIndex, visibility) {
  const shown = new Set(visibleFloors(floors, activeFloorIndex, visibility));
  return floors.map((floor, index) => ({ floor, index })).filter(({ floor }) => shown.has(floor)).sort((a, b) => Number(a.index === activeFloorIndex) - Number(b.index === activeFloorIndex));
}
function drawnUnderlaysInOrder(floors, activeFloorIndex, visibility, isReady) {
  const out = [];
  for (const { floor, index } of alignFloorDrawOrder(floors, activeFloorIndex, visibility)) {
    for (const underlay of findLatestDesign(floor.designs ?? []).underlays ?? []) {
      if (underlay.visible && isReady(underlay))
        out.push({ underlay, floorIndex: index, placement: floor.placement });
    }
  }
  return out;
}
function underlayAtPoint(candidates, world) {
  for (let i = candidates.length - 1; i >= 0; i--) {
    const u = candidates[i].underlay;
    const floorPlacement = candidates[i].placement;
    const onFloor = floorPlacement ? worldPixelsToFloor(world, floorPlacement) : world;
    const p = worldToUnderlayPixels(u, onFloor);
    if (p.x >= 0 && p.x <= u.widthPx && p.y >= 0 && p.y <= u.heightPx) return candidates[i];
  }
  return null;
}
function underlayFromLegacy(bg) {
  const stored = bg.opacity ?? DEFAULT_UNDERLAY_OPACITY;
  const visible = stored > HIDDEN_OPACITY;
  const s = underlayImageScale(bg);
  const centreXPx = bg.offsetX !== void 0 ? bg.offsetX + bg.widthPx * s / 2 : 0;
  const centreYPx = bg.offsetY !== void 0 ? bg.offsetY - bg.heightPx * s / 2 : 0;
  const source = bg.imageRef ? { imageRef: bg.imageRef, ...bg.data ? { data: bg.data } : {} } : { data: bg.data };
  return {
    id: newUnderlayId(),
    name: DEFAULT_UNDERLAY_NAME,
    ...source,
    widthPx: bg.widthPx,
    heightPx: bg.heightPx,
    ...bg.scale !== void 0 ? { scale: bg.scale } : {},
    x: pixelsToMeters(centreXPx),
    y: pixelsToMeters(centreYPx),
    rotation: 0,
    // A legacy hidden PDF had already lost its opacity (hiding set it to 0); give it the default.
    opacity: visible ? stored : DEFAULT_UNDERLAY_OPACITY,
    visible
  };
}
function normaliseUnderlay(u) {
  const raw = u;
  const needsName = typeof raw.name !== "string" || !raw.name.trim();
  const needs = needsName || typeof raw.id !== "string" || typeof raw.x !== "number" || typeof raw.y !== "number" || typeof raw.rotation !== "number" || typeof raw.opacity !== "number" || typeof raw.visible !== "boolean";
  if (!needs) return u;
  return {
    ...u,
    id: typeof raw.id === "string" ? raw.id : newUnderlayId(),
    name: needsName ? DEFAULT_UNDERLAY_NAME : raw.name,
    x: typeof raw.x === "number" ? raw.x : 0,
    y: typeof raw.y === "number" ? raw.y : 0,
    rotation: typeof raw.rotation === "number" ? raw.rotation : 0,
    opacity: typeof raw.opacity === "number" ? raw.opacity : DEFAULT_UNDERLAY_OPACITY,
    visible: typeof raw.visible === "boolean" ? raw.visible : true
  };
}
function foldDesign(design) {
  const bg = design.backgroundImage;
  const hasLegacy = !!bg && (!!bg.imageRef || !!bg.data);
  const raw = design.underlays;
  const existing = (Array.isArray(raw) ? raw : []).filter(
    (u) => typeof u === "object" && u !== null
  );
  const malformed = raw !== void 0 && (!Array.isArray(raw) || existing.length !== raw.length);
  const normalised = existing.map(normaliseUnderlay);
  const changed = malformed || normalised.some((u, i) => u !== existing[i]);
  if (!hasLegacy && !changed && !("backgroundImage" in design)) return design;
  const { backgroundImage: _legacy, ...rest } = design;
  const underlays = hasLegacy ? [underlayFromLegacy(bg), ...normalised] : normalised;
  return underlays.length > 0 || design.underlays ? { ...rest, underlays } : rest;
}
function foldLegacyBackground(layout) {
  let layoutChanged = false;
  const floors = (layout.floors ?? []).map((floor) => {
    let floorChanged = false;
    const designs = (floor.designs ?? []).map((design) => {
      const folded = foldDesign(design);
      if (folded !== design) floorChanged = true;
      return folded;
    });
    if (!floorChanged) return floor;
    layoutChanged = true;
    return { ...floor, designs };
  });
  return layoutChanged ? { ...layout, floors } : layout;
}
function withAlignStateFrom(current, snapshot2) {
  const byId = new Map((snapshot2.floors ?? []).map((floor) => [floor.id, floor]));
  return {
    ...current,
    floors: (current.floors ?? []).map((floor) => {
      const before2 = byId.get(floor.id);
      if (!before2) return floor;
      const { placement: _now, ...unplaced } = floor;
      return {
        ...unplaced,
        ...before2.placement ? { placement: before2.placement } : {},
        designs: (floor.designs ?? []).map((design, index) => {
          const was = before2.designs?.[index];
          const { underlays: _was, ...rest } = design;
          return was?.underlays ? { ...rest, underlays: was.underlays } : rest;
        })
      };
    })
  };
}

// src/twin-planner/planView.ts
var worldPxToPane = ({ x: wx, y: wy }, { zoom, rotation, pan }) => {
  const rad = rotation * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const sx = wx * zoom;
  const sy = wy * -zoom;
  return { x: pan.x + sx * cos - sy * sin, y: pan.y + sx * sin + sy * cos };
};
var paneToWorldPx = (point, { zoom, rotation, pan }) => {
  const dx = point.x - pan.x;
  const dy = point.y - pan.y;
  const rad = rotation * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const rx = dx * cos + dy * sin;
  const ry = -dx * sin + dy * cos;
  return { x: rx / zoom, y: ry / -zoom };
};
var planPointToPane = (point, view) => worldPxToPane({ x: metersToPixels(point.x), y: metersToPixels(point.y) }, view);
var panePointToPlan = (point, view) => {
  const world = paneToWorldPx(point, { ...view, zoom: view.zoom || 1 });
  return { x: pixelsToMeters(world.x), y: pixelsToMeters(world.y) };
};
var screenDirectionToPlanDelta = (screenDir, distanceM, view) => {
  const probe = { zoom: 1, pan: { x: 0, y: 0 }, rotation: view.rotation };
  const origin = panePointToPlan({ x: 0, y: 0 }, probe);
  const tip = panePointToPlan({ x: screenDir.x, y: screenDir.y }, probe);
  const len = Math.hypot(tip.x - origin.x, tip.y - origin.y) || 1;
  const clean = (v) => Math.abs(v) < 1e-9 ? 0 : v;
  return {
    x: clean((tip.x - origin.x) / len * distanceM),
    y: clean((tip.y - origin.y) / len * distanceM)
  };
};

// src/twin-planner/planScroll.ts
var PLAN_SCROLL_MARGIN = 24;
var PLAN_SCROLL_KEY_STEP = 40;
var PLAN_KEY_PAN_KEEP = 80;
var EPSILON = 0.5;
function planScrollBounds(design, {
  underlays: underlayRule,
  includeCameras = true,
  exclude,
  footprintOf
}) {
  if (!design) return null;
  const shown = (e) => !e._deleted && !(e.refid && exclude?.has(e.refid));
  const drawn = computeDesignBounds({
    walls: design.walls,
    surfaces: design.surfaces.filter((s) => !s._deleted),
    items: design.items.filter(shown)
  });
  const hasDrawn = !!drawn && drawn.maxX > drawn.minX && drawn.maxY > drawn.minY;
  let box = drawn;
  const expand = (minX, minY, maxX, maxY) => {
    box = box ? {
      minX: Math.min(box.minX, minX),
      minY: Math.min(box.minY, minY),
      maxX: Math.max(box.maxX, maxX),
      maxY: Math.max(box.maxY, maxY)
    } : { minX, minY, maxX, maxY };
  };
  for (const item of design.items) {
    if (!shown(item)) continue;
    const footprint = footprintOf(item.modelFile ?? "");
    if (!footprint) continue;
    const { width, depth } = footprint;
    const rad = (item.rotation ?? 0) * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const halfX = metersToPixels(width * cos + depth * sin) / 2;
    const halfY = metersToPixels(width * sin + depth * cos) / 2;
    const cx = metersToPixels(item.x);
    const cy = metersToPixels(item.y);
    expand(cx - halfX, cy - halfY, cx + halfX, cy + halfY);
  }
  for (const camera of includeCameras ? design.cameras ?? [] : []) {
    if (!shown(camera)) continue;
    const x = metersToPixels(camera.x);
    const y = metersToPixels(camera.y);
    expand(x, y, x, y);
  }
  if (underlayRule === "always" || underlayRule === "untilDrawn" && !hasDrawn) {
    for (const u of design.underlays ?? []) {
      if (!u.visible) continue;
      const b = underlayWorldBounds(u);
      expand(b.minX, b.minY, b.maxX, b.maxY);
    }
  }
  return box;
}
var floorOutline = (design, footprintOf) => planScrollBounds(design, { underlays: "always", includeCameras: false, footprintOf });
var floorOutlineCentre = (design, footprintOf) => {
  const box = floorOutline(design, footprintOf);
  if (!box) return { x: 0, y: 0 };
  return {
    x: pixelsToMeters((box.minX + box.maxX) / 2),
    y: pixelsToMeters((box.minY + box.maxY) / 2)
  };
};
var planDrawingOutline = (design, footprintOf) => planScrollBounds(design, { underlays: "none", includeCameras: false, footprintOf });
function planScrollBoundsForDesigns(designs, options, placements) {
  let union = null;
  for (const [i, design] of designs.entries()) {
    const local = planScrollBounds(design, options);
    if (!local) continue;
    const placement = placements?.[i];
    const b = placement ? placedBounds(local, placement) : local;
    union = union ? {
      minX: Math.min(union.minX, b.minX),
      minY: Math.min(union.minY, b.minY),
      maxX: Math.max(union.maxX, b.maxX),
      maxY: Math.max(union.maxY, b.maxY)
    } : b;
  }
  return union;
}
function planScreenBox(bounds, view) {
  if (!bounds) return null;
  const corners = [
    worldPxToPane({ x: bounds.minX, y: bounds.minY }, view),
    worldPxToPane({ x: bounds.maxX, y: bounds.minY }, view),
    worldPxToPane({ x: bounds.minX, y: bounds.maxY }, view),
    worldPxToPane({ x: bounds.maxX, y: bounds.maxY }, view)
  ];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys)
  };
}
var noScroll = (viewport) => ({
  overflows: false,
  extent: viewport,
  viewport,
  offset: 0
});
var axisFrom = (lo, hi, viewport, margin) => {
  const overflows = lo < -EPSILON || hi > viewport + EPSILON;
  if (!overflows) return noScroll(viewport);
  const start = Math.min(lo - margin, 0);
  const end = Math.max(hi + margin, viewport);
  return { overflows, extent: end - start, viewport, offset: -start };
};
function planScrollAxes({
  bounds,
  zoom,
  rotation,
  pan,
  viewport
}, margin = PLAN_SCROLL_MARGIN) {
  const box = planScreenBox(bounds, { zoom, rotation, pan });
  if (!box) return { x: noScroll(viewport.width), y: noScroll(viewport.height) };
  return {
    x: axisFrom(box.minX, box.maxX, viewport.width, margin),
    y: axisFrom(box.minY, box.maxY, viewport.height, margin)
  };
}
function panForOffset(pan, axis, from, nextOffset) {
  const max = Math.max(0, from.extent - from.viewport);
  const clamped = Math.min(Math.max(0, nextOffset), max);
  return { ...pan, [axis]: pan[axis] - (clamped - from.offset) };
}
function fitViewToBounds(bounds, {
  rotation,
  viewport,
  padding,
  zoomRange
}) {
  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  const rad = rotation * Math.PI / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const rotatedWidth = contentWidth * cos + contentHeight * sin;
  const rotatedHeight = contentWidth * sin + contentHeight * cos;
  const fitScaleX = (viewport.width - padding * 2) / rotatedWidth;
  const fitScaleY = (viewport.height - padding * 2) / rotatedHeight;
  const zoom = Math.max(zoomRange.min, Math.min(zoomRange.max, Math.min(fitScaleX, fitScaleY)));
  const centre = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
  return { zoom, pan: centreViewOn(centre, { zoom, rotation, viewport }) };
}
function centreViewOn(point, {
  zoom,
  rotation,
  viewport
}) {
  const rad = rotation * Math.PI / 180;
  const sx = point.x * zoom;
  const sy = point.y * -zoom;
  const cosR = Math.cos(rad);
  const sinR = Math.sin(rad);
  const rx = sx * cosR - sy * sinR;
  const ry = sx * sinR + sy * cosR;
  return { x: viewport.width / 2 - rx, y: viewport.height / 2 - ry };
}
function entityWorldCentre(design, refid) {
  if (!design) return null;
  const live2 = (e) => !!e && !e._deleted && e.refid === refid;
  const item = design.items.find(live2);
  if (item) return { x: metersToPixels(item.x), y: metersToPixels(item.y) };
  const camera = (design.cameras ?? []).find(live2);
  if (camera) return { x: metersToPixels(camera.x), y: metersToPixels(camera.y) };
  const surface = design.surfaces.find(live2);
  if (!surface) return null;
  const box = computeDesignBounds({ walls: [], surfaces: [surface], items: [] });
  return box ? { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 } : null;
}
function entitiesWorldBounds(design, refids) {
  if (!design || refids.length === 0) return null;
  const wanted = new Set(refids);
  const isWanted = (e) => !!e && !e._deleted && typeof e.refid === "string" && wanted.has(e.refid);
  const box = computeDesignBounds({
    walls: [],
    surfaces: design.surfaces.filter(isWanted),
    items: [...design.items, ...design.cameras ?? []].filter(isWanted)
  });
  if (!box) return null;
  const { minX, minY, maxX, maxY } = box;
  const pad = metersToPixels(1);
  const padX = maxX - minX > 0 ? 0 : pad;
  const padY = maxY - minY > 0 ? 0 : pad;
  return { minX: minX - padX, minY: minY - padY, maxX: maxX + padX, maxY: maxY + padY };
}
function zoomAboutPoint({ zoom, pan, rotation }, point, factor, { min, max }) {
  const rad = rotation * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point.x - pan.x;
  const dy = point.y - pan.y;
  const wx = (dx * cos + dy * sin) / zoom;
  const wy = -(-dx * sin + dy * cos) / zoom;
  const nextZoom = Math.max(min, Math.min(max, zoom * factor));
  const sx = wx * nextZoom;
  const sy = wy * -nextZoom;
  const rx = sx * cos - sy * sin;
  const ry = sx * sin + sy * cos;
  return { zoom: nextZoom, pan: { x: point.x - rx, y: point.y - ry } };
}
var KEY_MOVES = {
  ArrowRight: { axis: "x", sign: 1 },
  ArrowLeft: { axis: "x", sign: -1 },
  ArrowDown: { axis: "y", sign: 1 },
  ArrowUp: { axis: "y", sign: -1 }
};
function scrollPlanByKey(event, scene, step = PLAN_SCROLL_KEY_STEP, keep = PLAN_KEY_PAN_KEEP) {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  const move = KEY_MOVES[event.key];
  if (!move) return null;
  const box = planScreenBox(scene.bounds, scene);
  if (!box) return null;
  const lo = move.axis === "x" ? box.minX : box.minY;
  const hi = move.axis === "x" ? box.maxX : box.maxY;
  const pane = move.axis === "x" ? scene.viewport.width : scene.viewport.height;
  const need = Math.min(keep, hi - lo, pane);
  const allowed = move.sign === 1 ? Math.max(0, hi - need) : Math.max(0, pane - need - lo);
  const distance2 = Math.min(step, allowed);
  if (distance2 < EPSILON) return null;
  const { pan } = scene;
  return { ...pan, [move.axis]: pan[move.axis] - move.sign * distance2 };
}

// src/twin-planner/floorElevation.ts
var FLOOR_HEIGHT_M = 3;
var FLOOR_GAP_M = 0.5;
var FLOOR_SPACING_M = FLOOR_HEIGHT_M + FLOOR_GAP_M;
var floorElevationM = (position) => Number.isInteger(position) && position >= 0 ? position * FLOOR_SPACING_M : 0;

// src/twin-planner/countPlanContents.ts
var countPlanContents = (layout) => {
  const designs = layoutCurrentDesigns(layout);
  let spaces = 0;
  let machines = 0;
  let cameras = 0;
  designs.forEach((design) => {
    spaces += asArray(design?.surfaces).filter(isLive).length;
    machines += asArray(design?.items).filter(isLive).length;
    cameras += asArray(design?.cameras).filter(isLive).length;
  });
  const floors = asArray(layout?.floors).filter(
    (floor) => floor !== null && typeof floor === "object" && !Array.isArray(floor)
  ).length;
  return { floors, spaces, machines, cameras };
};

// src/twin-planner/floorPlanLandingTab.ts
var floorPlanLandingTab = ({
  start,
  existingTwin,
  register
}) => {
  if (start === "twin") return "checks";
  if (start === "resume" && existingTwin) return "checks";
  return register.state === "ready" && register.rows === 0 ? "library" : "assets";
};

// src/twin-planner/rememberedPlanView.ts
var TABS = /* @__PURE__ */ new Set([
  "checks",
  "assets",
  "library",
  "hierarchy"
]);
var planViewKey = (twinBid) => `tl:floor-plan-view:${twinBid}`;
var readPlanView = (storage, twinBid) => {
  if (!storage) return { ok: false };
  try {
    const raw = storage.getItem(planViewKey(twinBid));
    if (raw == null) return { ok: true, view: null };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ok: false };
    const { tab, floorId } = parsed;
    const view = {};
    if (typeof tab === "string" && TABS.has(tab)) view.tab = tab;
    if (typeof floorId === "number" && Number.isFinite(floorId)) view.floorId = floorId;
    return { ok: true, view };
  } catch {
    return { ok: false };
  }
};
var writePlanView = (storage, twinBid, view) => {
  if (!storage) return false;
  try {
    const current = readPlanView(storage, twinBid);
    const merged = { ...current.ok ? current.view : null, ...view };
    storage.setItem(planViewKey(twinBid), JSON.stringify(merged));
    return true;
  } catch {
    return false;
  }
};
var planOpeningFloorIndex = (floors, rememberedFloorId, fallback = 0) => {
  if (rememberedFloorId === void 0 || !floors) return fallback;
  const index = floors.findIndex((f) => f?.id === rememberedFloorId);
  return index >= 0 ? index : fallback;
};

// src/twin-planner/entityCapacity.ts
var MAX_ENTITY_CAPACITY = 2147483647;
var isEntityCapacity = (value) => typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= MAX_ENTITY_CAPACITY;

// src/twin-planner/itemRegisterState.ts
import { normaliseAssetUid } from "@repo/frontend-types";

// src/twin-planner/entityCode.ts
var entityIdentityKey = (raw) => (raw ?? "").trim().toUpperCase();
var entityIdentityFor = (twinBid, raw) => {
  const key = entityIdentityKey(raw);
  if (key === "") return "";
  const twin = entityIdentityKey(twinBid);
  if (twin === "") return key;
  let tail = key;
  for (; ; ) {
    if (tail === twin) return "";
    if (!tail.startsWith(`${twin}-`)) break;
    tail = tail.slice(twin.length + 1);
  }
  return tail === "" ? "" : `${twin}-${tail}`;
};
var stripTwinBidPrefix = (bID, twinBID, orgBID) => {
  const withOrg = `${orgBID}-${twinBID}-`;
  if (bID.startsWith(withOrg)) return bID.slice(withOrg.length);
  const withTwin = `${twinBID}-`;
  if (bID.startsWith(withTwin)) return bID.slice(withTwin.length);
  return bID;
};

// src/twin-planner/itemRegisterState.ts
var resolveItemRegisterState = (item, rows, readState) => itemRegisterStateResolver(rows, readState)(item);
var linkIndexCache = /* @__PURE__ */ new WeakMap();
var linkIndexFor = (rows) => {
  const cached = linkIndexCache.get(rows);
  if (cached) return cached;
  const index = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = normaliseAssetUid(row?.id);
    if (key !== void 0 && !index.has(key)) index.set(key, row);
  }
  linkIndexCache.set(rows, index);
  return index;
};
var itemRegisterStateResolver = (rows, readState) => {
  let index;
  return (item) => {
    const key = item ? normaliseAssetUid(item.assetUid) : void 0;
    if (key === void 0) return { kind: "legacy" };
    if (readState !== "ready") return { kind: "unknown", reason: readState };
    index ?? (index = linkIndexFor(rows));
    const row = index.get(key);
    if (row) return { kind: "backed", row };
    return { kind: "orphan", hasIdentity: entityIdentityKey(item?.entityCode) !== "" };
  };
};

// src/twin-planner/cameraCoverage.ts
var CAMERA_COVERAGE_PRESETS = [
  { key: "wide", label: "Wide", fovAngle: 100, detectionDistance: 10 },
  { key: "focused", label: "Focused", fovAngle: 53, detectionDistance: 10 },
  { key: "omni", label: "360\xB0", fovAngle: 360, detectionDistance: 7 }
];
var DEFAULT_CAMERA_COVERAGE = CAMERA_COVERAGE_PRESETS[0];
var CAMERA_FOV_MIN = 1;
var CAMERA_FOV_MAX = 360;
var CAMERA_DISTANCE_MIN = 1;
var isPositiveFinite = (n) => typeof n === "number" && Number.isFinite(n) && n > 0;
var effectiveCoverage = (camera) => ({
  fovAngle: isPositiveFinite(camera.fovAngle) ? camera.fovAngle : DEFAULT_CAMERA_COVERAGE.fovAngle,
  detectionDistance: isPositiveFinite(camera.detectionDistance) ? camera.detectionDistance : DEFAULT_CAMERA_COVERAGE.detectionDistance
});
var coveragePatch = (key) => {
  const preset = CAMERA_COVERAGE_PRESETS.find((p) => p.key === key);
  return preset ? { fovAngle: preset.fovAngle, detectionDistance: preset.detectionDistance } : null;
};

// src/twin-planner/cameraVisibility.ts
var angleDiff = (a, b) => {
  let d = (a - b) % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return Math.abs(d);
};
var orient = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
var segmentsIntersect = (p1, p2, q1, q2) => {
  const o1 = orient(p1, p2, q1);
  const o2 = orient(p1, p2, q2);
  const o3 = orient(q1, q2, p1);
  const o4 = orient(q1, q2, p2);
  return o1 * o2 < 0 && o3 * o4 < 0;
};
var finitePoint = (p) => !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
var isPointVisibleFromCamera = (camera, walls, point) => {
  const { fovAngle, detectionDistance: range } = effectiveCoverage(camera);
  const dx = point.x - camera.x;
  const dy = point.y - camera.y;
  const dist = Math.hypot(dx, dy);
  if (dist > range) return false;
  if (dist > 1e-9) {
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;
    if (angleDiff(angle, camera.rotation) > fovAngle / 2) return false;
  }
  const camPt = { x: camera.x, y: camera.y };
  return !walls.some(
    (w) => finitePoint(w?.a) && finitePoint(w?.b) && segmentsIntersect(camPt, point, w.a, w.b)
  );
};
var ENDPOINT_NUDGE_DEG = 0.01;
var rayHit = (origin, dir, a, b) => {
  const ex = b.x - a.x;
  const ey = b.y - a.y;
  const denom = dir.x * ey - dir.y * ex;
  if (Math.abs(denom) < 1e-12) return null;
  const ox = a.x - origin.x;
  const oy = a.y - origin.y;
  const t = (ox * ey - oy * ex) / denom;
  const u = (ox * dir.y - oy * dir.x) / denom;
  if (t <= 1e-12 || u <= 0 || u >= 1) return null;
  return t;
};
var cameraVisibilityPolygon = (camera, walls, options = {}) => {
  const maxStep = Math.max(0.1, options.maxStepDeg ?? 2);
  const { fovAngle, detectionDistance: range } = effectiveCoverage(camera);
  const origin = { x: camera.x, y: camera.y };
  const full = fovAngle >= 360;
  const malformed = [];
  const near = [];
  walls.forEach((w, index) => {
    if (!finitePoint(w?.a) || !finitePoint(w?.b)) {
      malformed.push(index);
      return;
    }
    const minX = Math.min(w.a.x, w.b.x);
    const maxX = Math.max(w.a.x, w.b.x);
    const minY = Math.min(w.a.y, w.b.y);
    const maxY = Math.max(w.a.y, w.b.y);
    if (maxX < origin.x - range || minX > origin.x + range || maxY < origin.y - range || minY > origin.y + range) {
      return;
    }
    near.push(w);
  });
  if (malformed.length > 0) {
    console.warn(
      `cameraVisibilityPolygon: skipped ${malformed.length} wall(s) with a missing or non-finite endpoint (indexes ${malformed.join(", ")})`
    );
  }
  const start = full ? 0 : camera.rotation - fovAngle / 2;
  const span = full ? 360 : fovAngle;
  const steps = Math.max(1, Math.ceil(span / maxStep));
  const angles = [];
  for (let i = 0; i <= steps; i++) angles.push(start + span * i / steps);
  if (full) angles.pop();
  for (const w of near) {
    for (const p of [w.a, w.b]) {
      const dx = p.x - origin.x;
      const dy = p.y - origin.y;
      if (Math.hypot(dx, dy) > range || Math.hypot(dx, dy) < 1e-9) continue;
      const bearing = Math.atan2(dy, dx) * 180 / Math.PI;
      for (const a of [bearing - ENDPOINT_NUDGE_DEG, bearing + ENDPOINT_NUDGE_DEG]) {
        if (full) {
          angles.push((a % 360 + 360) % 360);
        } else {
          const offset = ((a - start) % 360 + 360) % 360;
          if (offset > 0 && offset < span) angles.push(start + offset);
        }
      }
    }
  }
  angles.sort((x, y) => x - y);
  const rays = angles.filter((a, i) => i === 0 || a - angles[i - 1] > 1e-9);
  const ring = rays.map((deg) => {
    const rad = deg * Math.PI / 180;
    const dir = { x: Math.cos(rad), y: Math.sin(rad) };
    let reach = range;
    for (const w of near) {
      const t = rayHit(origin, dir, w.a, w.b);
      if (t !== null && t < reach) reach = t;
    }
    return { x: origin.x + dir.x * reach, y: origin.y + dir.y * reach };
  });
  return full ? ring : [origin, ...ring, origin];
};

// src/twin-planner/cameraDisplayName.ts
var cameraDisplayName = (camera, index) => camera.name?.trim() || `Camera ${index + 1}`;

// src/equipment/buildEquipmentClassificationTrees.ts
function buildEquipmentClassificationTrees(equipment) {
  const equipmentTypeRoots = [];
  const manufacturerRoots = [];
  for (const item of equipment) {
    if (item.equipmentType) {
      const t = item.equipmentType;
      insertPath(
        equipmentTypeRoots,
        [t.category, t.subcategory, t.type, t.variation],
        item.entityBid
      );
    }
    if (item.manufacturer) {
      const m = item.manufacturer;
      const labels = [m.manufacturer];
      if (m.productLine !== null) {
        labels.push(m.productLine);
      }
      labels.push(m.model);
      insertPath(manufacturerRoots, labels, item.entityBid);
    }
  }
  return {
    equipmentType: freeze(equipmentTypeRoots),
    manufacturer: freeze(manufacturerRoots)
  };
}
function insertPath(roots, labels, entityBid) {
  let siblings = roots;
  let pathKey = "";
  labels.forEach((label, depth) => {
    pathKey = depth === 0 ? label : `${pathKey} > ${label}`;
    let node = siblings.find((n) => n.bid === pathKey);
    if (!node) {
      node = {
        bid: pathKey,
        label,
        level: depth,
        children: [],
        equipmentBids: /* @__PURE__ */ new Set()
      };
      siblings.push(node);
    }
    node.equipmentBids.add(entityBid);
    siblings = node.children;
  });
}
function freeze(nodes) {
  return nodes.slice().sort((a, b) => naturalSort(a.label, b.label)).map((node) => ({
    bid: node.bid,
    label: node.label,
    level: node.level,
    // Array.from (not [...set]) so consumers compiling this source with a pre-es2015 target /
    // no downlevelIteration (e.g. @repo/ui's ts-jest) don't hit TS2802.
    equipmentBids: Array.from(node.equipmentBids).sort(naturalSort),
    children: freeze(node.children)
  }));
}

// src/equipment/collectEquipmentEntities.ts
var collectEquipmentEntities = (root) => {
  const equipment = [];
  const visit = (entity) => {
    if (!entity) return;
    if (entity.type?.name?.toLowerCase() === "asset") {
      equipment.push(entity);
    }
    entity.children?.forEach((child) => visit(child));
  };
  visit(root);
  return equipment;
};

// src/equipment/averageSelectedEquipmentValue.ts
var averageSelectedEquipmentValue = (equipment, processed, valueMetricName, selectedEntityIds) => {
  const metricData = processed?.[valueMetricName] ?? [];
  const valueByBid = /* @__PURE__ */ new Map();
  metricData.forEach((dataValue) => {
    if (dataValue.bID !== void 0 && !valueByBid.has(dataValue.bID) && Number.isFinite(dataValue.value)) {
      valueByBid.set(dataValue.bID, dataValue.value);
    }
  });
  let sum = 0;
  let count = 0;
  equipment.forEach((entity) => {
    if (!selectedEntityIds.has(entity.id)) return;
    const value = valueByBid.get(entity.bID);
    if (value !== void 0) {
      sum += value;
      count += 1;
    }
  });
  return count > 0 ? sum / count : null;
};

// src/equipment/buildFloorFilterTree.ts
import { TwinEntityType as TwinEntityType5 } from "@repo/frontend-types";
var buildFloorFilterTree = (root, multifloor) => {
  if (!multifloor || !root?.children) return [];
  const floors = [];
  for (const child of root.children) {
    const node = child;
    if (stringToTwinEntityType(node.type?.name ?? "") !== TwinEntityType5.FLOOR) continue;
    const floorBid = node.bID ?? node.id;
    if (!floorBid) continue;
    const equipmentBids = Array.from(
      new Set(
        collectEquipmentEntities(node).map((e) => e.bID).filter((bid) => Boolean(bid))
      )
    ).sort();
    floors.push({
      bid: floorBid,
      label: node.name ?? "Floor",
      level: 0,
      children: [],
      equipmentBids
    });
  }
  return floors;
};

// src/equipment/isFloorVisible.ts
var isFloorVisible = ({
  floorBid,
  entityFilters,
  siteBid,
  floorFilter
}) => {
  const identifies = (candidate) => Boolean(floorBid) && candidate === floorBid;
  const entityAllows = entityFilters.length === 0 || entityFilters.length === 1 && entityFilters[0].bId === siteBid || entityFilters.some((filterPath) => identifies(filterPath.bId));
  const floorFilterAllows = floorFilter.length === 0 || floorFilter.some((node) => identifies(node.bid));
  return entityAllows && floorFilterAllows;
};

// src/equipment/triStateSelection.ts
var isLeaf = (node) => !node.children || node.children.length === 0;
var subtreeBids = (node) => {
  const out = [node.bid];
  node.children?.forEach((child) => out.push(...subtreeBids(child)));
  return out;
};
var findPath = (roots, bid) => {
  for (const root of roots) {
    if (root.bid === bid) return [root];
    const childPath = findPath(root.children ?? [], bid);
    if (childPath) return [root, ...childPath];
  }
  return null;
};
var computeTriStates = (roots, selectedBids) => {
  const states = /* @__PURE__ */ new Map();
  const walk = (node, ancestorCovered) => {
    const covered = ancestorCovered || selectedBids.has(node.bid);
    if (isLeaf(node)) {
      states.set(node.bid, covered ? "on" : "off");
      return [covered ? 1 : 0, 1];
    }
    let leavesOn = 0;
    let leavesTotal = 0;
    for (const child of node.children) {
      const [on, total] = walk(child, covered);
      leavesOn += on;
      leavesTotal += total;
    }
    const state2 = leavesOn === 0 ? "off" : leavesOn === leavesTotal ? "on" : "mixed";
    states.set(node.bid, state2);
    return [leavesOn, leavesTotal];
  };
  roots.forEach((root) => walk(root, false));
  return states;
};
var nextSelectionOnToggle = (roots, selectedBids, clickedBid) => {
  const path = findPath(roots, clickedBid);
  if (!path) return new Set(selectedBids);
  const clicked = path[path.length - 1];
  const ancestors = path.slice(0, -1);
  const next = new Set(selectedBids);
  const descBids = subtreeBids(clicked);
  const currentState = computeTriStates(roots, selectedBids).get(clickedBid);
  if (currentState !== "on") {
    for (const bid of descBids) {
      if (bid !== clicked.bid) next.delete(bid);
    }
    next.add(clicked.bid);
    return next;
  }
  for (const bid of descBids) next.delete(bid);
  let ancestorIdx = -1;
  for (let i = ancestors.length - 1; i >= 0; i--) {
    if (selectedBids.has(ancestors[i].bid)) {
      ancestorIdx = i;
      break;
    }
  }
  if (ancestorIdx !== -1) {
    next.delete(ancestors[ancestorIdx].bid);
    for (let i = ancestorIdx; i < path.length - 1; i++) {
      const parent = path[i];
      const onPath = path[i + 1];
      for (const child of parent.children ?? []) {
        if (child.bid !== onPath.bid) next.add(child.bid);
      }
    }
  }
  return next;
};
var selectedNodesFromBids = (roots, selectedBids) => {
  const labels = /* @__PURE__ */ new Map();
  const walk = (nodes) => {
    for (const node of nodes) {
      labels.set(node.bid, node.label);
      walk(node.children ?? []);
    }
  };
  walk(roots);
  return Array.from(selectedBids).filter((bid) => labels.has(bid)).map((bid) => ({ bid, label: labels.get(bid) }));
};

// src/equipment/averageWeekValuesForBids.ts
var WEEKDAYS4 = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];
var averageWeekValuesForBids = (processed, metricName, bids) => {
  const metricData = processed?.[metricName] ?? [];
  const weekValuesByBid = /* @__PURE__ */ new Map();
  metricData.forEach((dataValue) => {
    if (dataValue.bID !== void 0 && !weekValuesByBid.has(dataValue.bID) && dataValue.weekValues && typeof dataValue.weekValues === "object") {
      weekValuesByBid.set(dataValue.bID, dataValue.weekValues);
    }
  });
  const result = {};
  for (const weekday of WEEKDAYS4) {
    let sum = 0;
    let count = 0;
    for (const bid of bids) {
      const weekValues = weekValuesByBid.get(bid);
      const value = weekValues?.[weekday];
      if (value !== void 0 && Number.isFinite(value)) {
        sum += value;
        count += 1;
      }
    }
    if (count > 0) {
      result[weekday] = sum / count;
    }
  }
  return result;
};

// src/equipment/catalogueLabels.ts
var usableQualifier = (value, productName) => {
  const trimmed3 = value?.trim();
  return trimmed3 && trimmed3 !== productName.trim() ? trimmed3 : void 0;
};
var QUALIFIER_FIELDS = ["equipmentType", "equipmentSubcategory", "sku"];
var bucketKey = (row) => `${row.manufacturer}\0${row.productLine}\0${row.product}`;
var labelWith = (row, qualifier) => qualifier && row.product.trim() ? `${row.product} (${qualifier})` : row.product;
var catalogueDisplayNames = (rows) => {
  const buckets = /* @__PURE__ */ new Map();
  const groups = [];
  for (const row of rows) {
    const key = bucketKey(row);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      const created = [row];
      buckets.set(key, created);
      groups.push(created);
    }
  }
  const labels = /* @__PURE__ */ new Map();
  const put = (row, label) => {
    if (!labels.has(row.id)) labels.set(row.id, label);
  };
  for (const bucket of groups) {
    if (bucket.length === 1) {
      put(bucket[0], bucket[0].product);
      continue;
    }
    let resolved = false;
    for (const field of QUALIFIER_FIELDS) {
      const candidate = bucket.map((row) => labelWith(row, usableQualifier(row[field], row.product)));
      if (new Set(candidate).size !== bucket.length) continue;
      bucket.forEach((row, i) => put(row, candidate[i]));
      resolved = true;
      break;
    }
    if (!resolved) for (const row of bucket) put(row, row.product);
  }
  return labels;
};

// src/equipment/genericManufacturer.ts
var GENERIC_CATALOGUE_MANUFACTURER = "Unknown Manufacturer";
var GENERIC_GROUP = "Generic";
var isGenericCatalogueManufacturer = (name) => name === GENERIC_CATALOGUE_MANUFACTURER;
var toDisplayManufacturer = (name) => isGenericCatalogueManufacturer(name) ? GENERIC_GROUP : name ?? "";

// src/equipment/registerToSidebarEntries.ts
var ASSET_KEY_PREFIX = "ASSET-";
var NO_CATALOGUE_MATCH_GROUP = "No catalogue match";
var isUncatalogued = (row) => row.match_status === "uncatalogued";
var NOT_IN_CATALOGUE_LABEL = "Not in the catalogue";
var assetSidebarKey = (id) => `${ASSET_KEY_PREFIX}${id}`;
var registerToSidebarEntries = (assets) => assets.map((a) => ({
  entity_bid: assetSidebarKey(a.id),
  asset_uid: String(a.id),
  // The card's title. Same precedence as the title-bar search's label for a register row.
  asset_name: a.asset_name?.trim() || a.name?.trim() || void 0,
  model_name: a.product || a.model_raw || a.name,
  product_line: a.product_line || a.product_line_raw || a.manufacturer_raw || "",
  /*
   * Unmatched assets group under `No catalogue match` whatever the customer's own text says;
   * that text still reaches the user as the card's product-line subtitle below.
   *
   * `toDisplayManufacturer` is applied to the MATCHED manufacturer only, never to
   * `manufacturer_raw` (feature 4b). The two columns are not the same kind of thing: the matched
   * one is the catalogue's own value, so `Unknown Manufacturer` there means "this is catalogue
   * generic equipment" and belongs under `Generic`. The raw one is customer free text, and a
   * customer who happened to type "Unknown Manufacturer" has said "I don't know who made this" —
   * filing that under `Generic` would dress a failed match up as a deliberate classification.
   * `'supplied'` needs no arm of its own (`asset-register-adopt-existing`): it is a MATCHED row
   * and always carries a `reference_id`, so the server has already written its `manufacturer`
   * and it falls through to the branch that reads it — grouping under the real manufacturer,
   * which is where an adopted asset belongs. Asserted by a test rather than left to be noticed.
   *
   * `match_status === 'unmatched'` keeps absolute precedence: it is an explicit statement the
   * SERVER made about the match, and it may not be overridden by customer free text.
   *
   * `'uncatalogued'` shares that arm (`uncatalogued-terminal-state`, ledger row 119). A person
   * decided the row has no catalogue product, so it has no `manufacturer`, and without the arm it
   * would fall through to `manufacturer_raw` — filing the customer's "Pulse Fitness" under a
   * Pulse Fitness heading that reads like a real catalogue brand, the exact failure above. It
   * stays in the no-match group, where it is still placeable; the register table, not the
   * sidebar, is where the decision is shown.
   *
   * ⚠ A second arm sat beside it — `'generic'` → `GENERIC_GROUP` — for rows created by placing
   * a `twin_generic_asset` from the Library, which write no `*_raw` column and would otherwise
   * have fallen through to **No catalogue match**. That table and that status retired
   * (`generic-source-retirement`, ledger row 85), and the arm went with them. Unbranded
   * equipment is ordinary `Unknown Manufacturer` catalogue product now, so the server writes
   * its `manufacturer` and `toDisplayManufacturer` files it under `Generic` on the fall-through
   * below — the same heading, reached the same way the Library reaches it, which is what keeps
   * the "Your Assets" tab and the Library tab agreeing about what "Generic" means.
   */
  manufacturer_name: a.match_status === "unmatched" || isUncatalogued(a) ? NO_CATALOGUE_MATCH_GROUP : a.manufacturer ? toDisplayManufacturer(a.manufacturer) : a.manufacturer_raw || NO_CATALOGUE_MATCH_GROUP,
  model_file: a.model_3d || void 0,
  /*
   * The entity code, when the row has one (`asset-register-adopt-existing`, ruling O7).
   *
   * `|| undefined`, not `?? undefined` — the same choice `model_file` above makes, for the same
   * reason. An empty string is not a code, and putting one on the plan item would add a key that
   * says nothing: `stampEntityCodes` and `PlannerConverter` both treat a blank code as absent, so
   * it would change no outcome while making the persisted plan JSON claim an identity the row
   * does not have.
   */
  entity_code: a.entity_code || void 0,
  capacity: a.capacity ?? void 0,
  /*
   * The customer's own IDs, read-only, for the Floor Plan step's Assets rows
   * (`floor-plan-page-assets-library`, R5): *Integrity 95T - Integrity · LF-CON-0117* since
   * `floor-plan-asset-card-labels` R9. A blank reads as absent.
   */
  customer_asset_identifier: a.customer_asset_identifier?.trim() || void 0,
  source_asset_id: a.source_asset_id?.trim() || void 0
}));

// src/equipment/identityState.ts
var IDENTITY_STATE_LABELS = {
  brand: "Brand",
  to_confirm: "To confirm",
  generic: "Generic",
  not_tracked: "Not tracked",
  to_identify: "To identify"
};
var assetIdentityState = (row) => {
  if (isUncatalogued(row)) return "not_tracked";
  if (row.match_status === "unmatched") return row.match_proposal ? "to_confirm" : "to_identify";
  if (row.reference_id != null && row.reference_id !== "") {
    return isGenericCatalogueManufacturer(row.manufacturer) ? "generic" : "brand";
  }
  return "to_identify";
};
var isPlaceableIdentity = (state2) => state2 === "brand" || state2 === "generic";
var isKeptOffPlan = (row) => row.keep_off_plan === true && isPlaceableIdentity(assetIdentityState(row));

// src/equipment/planPlacement.ts
import { normaliseAssetUid as normaliseAssetUid2 } from "@repo/frontend-types";
var indexCache = /* @__PURE__ */ new WeakMap();
var buildIndex = (layout) => {
  const byAssetUid = /* @__PURE__ */ new Map();
  const byRefid = /* @__PURE__ */ new Map();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const item of asArray(design?.items)) {
      if (!isLive(item)) continue;
      const uid = normaliseAssetUid2(item.assetUid);
      if (!uid || byAssetUid.has(uid)) continue;
      if (typeof item.refid !== "string" || item.refid === "") continue;
      byAssetUid.set(uid, item.refid);
      if (!byRefid.has(item.refid)) byRefid.set(item.refid, uid);
    }
  }
  return { byAssetUid, byRefid };
};
var placementIndexFor = (layout) => {
  if (!layout || typeof layout !== "object") return null;
  const cached = indexCache.get(layout);
  if (cached) return cached;
  const built = buildIndex(layout);
  indexCache.set(layout, built);
  return built;
};
var emptyPlacementIndex = () => ({
  byAssetUid: /* @__PURE__ */ new Map(),
  byRefid: /* @__PURE__ */ new Map()
});
var placementStateFor = (index, assetUid) => {
  if (!index) return "unknown";
  const uid = normaliseAssetUid2(assetUid);
  if (!uid) return "unknown";
  return index.byAssetUid.has(uid) ? "placed" : "unplaced";
};

// src/equipment/placementWhere.ts
import { normaliseAssetUid as normaliseAssetUid3 } from "@repo/frontend-types";

// src/twin-planner/planParentage.ts
var finite = (value) => typeof value === "number" && isFinite(value);
var containerCache = /* @__PURE__ */ new WeakMap();
var containers = (surfaces) => {
  const cached = containerCache.get(surfaces);
  if (cached) return cached;
  const prepared = surfaces.filter((s) => isLive(s) && typeof s.refid === "string" && s.refid !== "" && usablePoly(s.poly)).map((surface) => ({ surface, area: polygonArea(surface.poly) }));
  containerCache.set(surfaces, prepared);
  return prepared;
};
var smallestContaining = (point, candidates, minArea, excludeRefid) => {
  let best = null;
  for (const candidate of candidates) {
    if (candidate.surface.refid === excludeRefid) continue;
    if (!(candidate.area > minArea) || best && !(candidate.area < best.area)) continue;
    if (!pointInPolygon(point, candidate.surface.poly)) continue;
    best = candidate;
  }
  return best ? best.surface.refid : null;
};
var spatialParentOfSurface = (surface, surfaces) => {
  const point = polygonInteriorPoint(surface.poly);
  if (!point) return null;
  return smallestContaining(point, containers(surfaces), polygonArea(surface.poly), surface.refid);
};
var spatialParentOfItem = (item, surfaces) => finite(item.x) && finite(item.y) ? smallestContaining({ x: item.x, y: item.y }, containers(surfaces), 0) : null;
var firstLiveSurfaces = (surfaces) => {
  const seen = /* @__PURE__ */ new Set();
  return asArray(surfaces).filter((s) => {
    if (!isLive(s) || typeof s.refid !== "string" || s.refid === "" || seen.has(s.refid)) {
      return false;
    }
    seen.add(s.refid);
    return true;
  });
};
var cache = /* @__PURE__ */ new WeakMap();
var resolvePlanParentage = (design) => {
  if (design) {
    const cached = cache.get(design);
    if (cached) return cached;
  }
  const result = derive(design);
  if (design) cache.set(design, result);
  return result;
};
var derive = (design) => {
  const surfaces = asArray(design?.surfaces).filter(
    (s) => isLive(s) && typeof s.refid === "string" && s.refid !== ""
  );
  const byRefid = /* @__PURE__ */ new Map();
  for (const s of surfaces) if (!byRefid.has(s.refid)) byRefid.set(s.refid, s);
  const spatial = /* @__PURE__ */ new Map();
  const candidates = [...byRefid.values()];
  for (const [refid, s] of byRefid) spatial.set(refid, spatialParentOfSurface(s, candidates));
  const drawn = (s) => polygonInteriorPoint(s.poly) !== null;
  const recordedOf = (s) => {
    const recorded = s.parentSurfaceBid;
    const valid = recorded && recorded !== s.refid && byRefid.has(recorded) ? recorded : void 0;
    if (s.parentChosen) return valid ?? (recorded ? void 0 : null);
    return drawn(s) && !(valid && !drawn(byRefid.get(valid))) ? void 0 : valid;
  };
  const parent = /* @__PURE__ */ new Map();
  const usesRecorded = /* @__PURE__ */ new Set();
  for (const [refid, s] of byRefid) {
    const recorded = recordedOf(s);
    if (recorded !== void 0) {
      parent.set(refid, recorded);
      usesRecorded.add(refid);
    } else parent.set(refid, spatial.get(refid) ?? null);
  }
  for (; ; ) {
    const onCycle = /* @__PURE__ */ new Set();
    for (const refid of byRefid.keys()) {
      const seen = /* @__PURE__ */ new Set();
      let cursor = refid;
      while (cursor != null && !seen.has(cursor)) {
        seen.add(cursor);
        cursor = parent.get(cursor);
      }
      if (cursor === refid) onCycle.add(refid);
    }
    const breakable = [...onCycle].filter((refid) => usesRecorded.has(refid));
    if (breakable.length === 0) {
      for (const refid of onCycle) parent.set(refid, null);
      break;
    }
    for (const refid of breakable) {
      parent.set(refid, spatial.get(refid) ?? null);
      usesRecorded.delete(refid);
    }
  }
  const surfaceAnswers = /* @__PURE__ */ new Map();
  for (const refid of byRefid.keys()) {
    const spatialParent = spatial.get(refid) ?? null;
    const effectiveParent = parent.get(refid) ?? null;
    surfaceAnswers.set(refid, {
      spatialParent,
      effectiveParent,
      disagrees: usesRecorded.has(refid) && !!byRefid.get(refid)?.parentChosen && drawn(byRefid.get(refid)) && effectiveParent !== spatialParent
    });
  }
  const itemAnswers = /* @__PURE__ */ new Map();
  for (const item of asArray(design?.items)) {
    if (!isLive(item)) continue;
    const spatialParent = spatialParentOfItem(item, candidates);
    const placed = finite(item.x) && finite(item.y);
    const valid = item.parentSpaceBid && byRefid.has(item.parentSpaceBid) ? item.parentSpaceBid : void 0;
    const recorded = item.parentChosen ? valid ?? (item.parentSpaceBid ? void 0 : null) : placed && !(valid && !drawn(byRefid.get(valid))) ? void 0 : valid;
    itemAnswers.set(item, {
      spatialParent,
      effectiveParent: recorded === void 0 ? spatialParent : recorded,
      disagrees: !!item.parentChosen && placed && recorded !== void 0 && recorded !== spatialParent
    });
  }
  return {
    surface: (refid) => surfaceAnswers.get(refid),
    item: (item) => itemAnswers.get(item)
  };
};

// src/twin-planner/entityNamesByRefid.ts
var surfaceName = (surface) => surface.name?.trim() || surface.customName?.trim() || "";
var surfaceDisplayName = (surface) => surfaceName(surface) || "Zone";
var itemDisplayName = (item) => item.name || "Equipment";
var entityNamesByRefid = (layout) => {
  const names = /* @__PURE__ */ new Map();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      if (typeof surface.refid === "string" && surface.refid) {
        names.set(surface.refid, surfaceDisplayName(surface));
      }
    }
    for (const item of asArray(design?.items).filter(isLive)) {
      if (typeof item.refid === "string" && item.refid) {
        names.set(item.refid, itemDisplayName(item));
      }
    }
  }
  return names;
};

// src/equipment/placementWhere.ts
var whereOfRefid = (layout, match) => {
  for (const floor of asArray(layout.floors)) {
    if (!floor) continue;
    const designs = asArray(floor.designs);
    if (designs.length === 0) continue;
    const design = findLatestDesign(designs);
    const item = asArray(design?.items).find((candidate) => isLive(candidate) && match(candidate));
    if (!item) continue;
    const parent = resolvePlanParentage(design).item(item)?.effectiveParent ?? null;
    const surface = parent === null ? void 0 : asArray(design?.surfaces).find((s) => isLive(s) && s.refid === parent);
    return {
      floorName: floor.name ?? "",
      spaceName: surface ? surfaceDisplayName(surface) : null
    };
  }
  return null;
};
var placementWhereForRefid = (layout, refid) => layout ? whereOfRefid(layout, (candidate) => candidate.refid === refid) : null;
var placementWhereFor = (layout, assetUid) => {
  const uid = normaliseAssetUid3(assetUid);
  if (uid === void 0) return null;
  const index = placementIndexFor(layout);
  const refid = index?.byAssetUid.get(uid);
  if (!layout || refid === void 0) return null;
  return whereOfRefid(
    layout,
    (candidate) => candidate.refid === refid && normaliseAssetUid3(candidate.assetUid) === uid
  );
};

// src/equipment/identifyModel.ts
var PLAN_STATE_LABELS = {
  on_plan: "On plan",
  not_on_plan: "Not on plan",
  keep_off_plan: "Keep off plan",
  plan_not_loaded: "Plan not loaded"
};
var IDENTITY_STATES = [
  "brand",
  "to_confirm",
  "generic",
  "not_tracked",
  "to_identify"
];
var PLAN_STATES = [
  "on_plan",
  "not_on_plan",
  "keep_off_plan",
  "plan_not_loaded"
];
var TRAILING_NUMBER = /[\s\-_#]+\d+$/;
var collapseWhitespace = (value) => value.trim().replace(/\s+/g, " ");
var normaliseWritten = (value) => {
  if (typeof value !== "string") return null;
  const collapsed = collapseWhitespace(value);
  return collapsed === "" ? null : collapsed.toLowerCase();
};
var stripTrailingNumber = (name) => {
  const stripped = name.replace(TRAILING_NUMBER, "");
  return stripped === "" ? name : stripped;
};
var nameStem = (name) => stripTrailingNumber(normaliseWritten(name) ?? "");
var outcomeProduct = (row, state2) => {
  if (state2 === "to_confirm") return row.match_proposal?.reference_id ?? null;
  if (state2 === "brand" || state2 === "generic") return row.reference_id ?? null;
  return null;
};
var groupKey = (row, state2) => JSON.stringify([
  state2,
  normaliseWritten(row.manufacturer_raw),
  normaliseWritten(row.model_raw),
  nameStem(row.name),
  outcomeProduct(row, state2)
]);
var assetPlanStateFor = (row, index) => {
  const placement = placementStateFor(index, String(row.id));
  if (placement === "unknown") return "plan_not_loaded";
  if (placement === "placed") return "on_plan";
  return isKeptOffPlan(row) ? "keep_off_plan" : "not_on_plan";
};
var scorePercent = (brand, base) => {
  if (base === 0) return null;
  const rounded = Math.round(100 * brand / base);
  if (brand < base && rounded > 99) return 99;
  if (brand > 0 && rounded < 1) return 1;
  return rounded;
};
var zeroed = (keys) => {
  const record = {};
  for (const key of keys) record[key] = 0;
  return record;
};
var buildIdentifyModel = (rows, plan) => {
  const index = plan.layout == null && plan.planAbsent === true ? emptyPlacementIndex() : placementIndexFor(plan.layout);
  const stateById = /* @__PURE__ */ new Map();
  const planStateById = /* @__PURE__ */ new Map();
  const groupsByKey = /* @__PURE__ */ new Map();
  const byState = zeroed(IDENTITY_STATES);
  const byPlanState = zeroed(PLAN_STATES);
  let doesntFit = 0;
  let doesntFitKept = 0;
  let fitUnjudged = 0;
  let keptOffButPlaced = 0;
  for (const row of rows) {
    if (stateById.has(row.id)) continue;
    const state2 = assetIdentityState(row);
    const planState = assetPlanStateFor(row, index);
    stateById.set(row.id, state2);
    planStateById.set(row.id, planState);
    byState[state2] += 1;
    byPlanState[planState] += 1;
    if (row.doesnt_fit === true) {
      if (row.doesnt_fit_kept === true) doesntFitKept += 1;
      else doesntFit += 1;
    } else if ((state2 === "brand" || state2 === "generic") && row.doesnt_fit == null)
      fitUnjudged += 1;
    if (planState === "on_plan" && isKeptOffPlan(row)) keptOffButPlaced += 1;
    const key = groupKey(row, state2);
    const group = groupsByKey.get(key);
    if (group) {
      group.rowIds.push(row.id);
    } else {
      groupsByKey.set(key, {
        key,
        state: state2,
        label: stripTrailingNumber(collapseWhitespace(row.name)),
        written: {
          manufacturer: row.manufacturer_raw ?? null,
          model: row.model_raw ?? null,
          category: row.category_raw ?? null
        },
        proposal: state2 === "to_confirm" ? row.match_proposal ?? null : null,
        rowIds: [row.id]
      });
    }
  }
  const groups = [];
  groupsByKey.forEach((group) => groups.push(group));
  groups.sort(
    (a, b) => b.rowIds.length - a.rowIds.length || a.label.localeCompare(b.label, "en", { sensitivity: "base" }) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
  );
  const total = stateById.size;
  const base = total - byState.not_tracked;
  return {
    stateOf: (assetId) => stateById.get(assetId),
    planStateOf: (assetId) => planStateById.get(assetId),
    groups,
    counts: {
      total,
      byState,
      byPlanState,
      doesntFit,
      doesntFitKept,
      fitUnjudged,
      keptOffButPlaced
    },
    score: {
      brand: byState.brand,
      generic: byState.generic,
      base,
      percent: scorePercent(byState.brand, base)
    },
    planKnown: index !== null
  };
};

// src/equipment/groupHint.ts
var written = (value) => {
  const trimmed3 = value?.trim() ?? "";
  return trimmed3 === "" ? null : trimmed3;
};
var capitalise = (part) => part.charAt(0).toUpperCase() + part.slice(1);
var groupHint = (group) => {
  const manufacturer = written(group.manufacturer);
  const model = written(group.model);
  const category = written(group.category);
  const parts2 = [];
  if (manufacturer) parts2.push(`Manufacturer "${manufacturer}"`);
  if (model) parts2.push(`Model "${model}"`);
  if (manufacturer && !model) parts2.push("no model");
  if (model && !manufacturer) parts2.push("no manufacturer");
  if (!manufacturer && !model) parts2.push("No manufacturer");
  if (category) parts2.push(`Category: ${category}`);
  return parts2.map((part, index) => index === 0 ? capitalise(part) : part).join(" \xB7 ");
};

// src/equipment/notTracked.ts
var NOT_TRACKED_HINT_CATEGORIES = [
  "accessories",
  "accessory",
  "consumables",
  "consumable",
  "small equipment",
  "mats",
  "mat"
];
var words = (text4) => text4.toLowerCase().split(/[^a-z]+/).filter((word) => word !== "");
var suggestsNotTracked = (category) => {
  if (!category) return false;
  const categoryWords = words(category);
  if (categoryWords.length === 0) return false;
  const joined = ` ${categoryWords.join(" ")} `;
  return NOT_TRACKED_HINT_CATEGORIES.some((entry) => joined.includes(` ${words(entry).join(" ")} `));
};

// src/equipment/nextGroupToIdentify.ts
var IDENTIFY_CARD_STATES = ["to_identify", "to_confirm"];
var opensIdentifyCard = (state2) => state2 !== void 0 && IDENTIFY_CARD_STATES.includes(state2);
var nextGroupToIdentify = (groups, current, states = ["to_identify"]) => {
  const at = groups.findIndex((group) => group.key === current.key);
  const start = at >= 0 ? at + 1 : Math.max(0, current.index);
  for (let step = 0; step < groups.length; step += 1) {
    const group = groups[(start + step) % groups.length];
    if (states.includes(group.state) && group.key !== current.key) return group;
  }
  return null;
};

// src/equipment/projectedScore.ts
var projectedScore = (score, written2) => ({
  before: score.percent,
  after: scorePercent(Math.min(score.brand + Math.max(0, written2), score.base), score.base)
});

// src/equipment/assetCsvColumnDocs.ts
var ASSET_CSV_IDENTIFIER_HEADER = "customer_asset_identifier";
var ASSET_CSV_COLUMNS = [
  {
    header: "name",
    label: "Name",
    type: "text",
    required: true,
    notes: 'The equipment name as you know it, e.g. "Leg Press".',
    fileNote: "The only required column."
  },
  {
    header: ASSET_CSV_IDENTIFIER_HEADER,
    label: "Asset Identifier",
    type: "text",
    required: false,
    notes: "Your own code for the item \u2014 asset tag, QR, serial or internal id. Strongly recommended: it is what lets a later upload update this row instead of adding it again. Shown as Asset Identifier in the register."
  },
  {
    header: "source_asset_id",
    label: "Source asset ID",
    type: "text",
    required: false,
    notes: "The record id in your own asset system, so a later export can be re-synced."
  },
  {
    header: "manufacturer_raw",
    label: "Manufacturer",
    type: "text",
    required: false,
    notes: "Manufacturer / brand. Improves the catalogue match.",
    fileNote: '"manufacturer" also works.'
  },
  {
    header: "product_line_raw",
    label: "Product line",
    type: "text",
    required: false,
    notes: "Product line or range, as written in your register."
  },
  {
    header: "model_raw",
    label: "Model",
    type: "text",
    required: false,
    notes: "Model, as written in your register. Also improves the catalogue match."
  },
  {
    header: "manufacturer_sku_raw",
    label: "Manufacturer SKU",
    type: "text",
    required: false,
    notes: "Manufacturer SKU or part number, if your register carries one."
  },
  {
    header: "category_raw",
    label: "Category",
    type: "text",
    required: false,
    notes: "Your own category, discipline or sub-category."
  },
  {
    header: "unit_cost",
    label: "Unit cost",
    type: "number",
    required: false,
    notes: "Purchase or unit price. Digits only, at most 2 decimal places."
  },
  {
    header: "residual_value",
    label: "Residual value",
    type: "number",
    required: false,
    notes: "Residual value or net book value. Same number format as unit_cost."
  },
  {
    header: "currency",
    label: "Currency",
    type: "3 letters",
    required: false,
    notes: "ISO code for the money columns, e.g. GBP. Leave blank if your register does not say."
  },
  {
    header: "install_date",
    label: "Install date",
    type: "date",
    required: false,
    notes: "The date the asset was installed or deployed on site."
  },
  {
    header: "manufacture_date",
    label: "Manufacture date",
    type: "date",
    required: false,
    notes: "Manufactured. A 4-digit year on its own is fine here \u2014 many registers only have that."
  },
  {
    header: "warranty_end_date",
    label: "Warranty end date",
    type: "date",
    required: false,
    notes: "The date the warranty expires."
  },
  {
    header: "contract_end_date",
    label: "Contract end date",
    type: "date",
    required: false,
    notes: "The date the service contract ends."
  },
  {
    header: "condition",
    label: "Condition",
    type: "text",
    required: false,
    notes: "Condition, in your own words \u2014 we do not impose a scale."
  },
  {
    header: "customer_notes",
    label: "Notes",
    type: "text",
    required: false,
    notes: "Any free-text note you want kept against the asset."
  }
];
var ASSET_CSV_REFUSED_COLUMN_REASONS = /* @__PURE__ */ new Map([
  ["asset_name", "TwinLabs generates the unique display name from your `name` column."],
  ["capacity", "TwinLabs derives capacity from the matched catalogue product."],
  ["has_ref_data", "TwinLabs sets this when the asset resolves to a catalogue product."],
  ["reference_id", "TwinLabs sets this by matching against the equipment catalogue."],
  ["match_status", "TwinLabs sets this when it matches the asset, or when you resolve a match."],
  ["bid", "TwinLabs mints BIDs when the twin goes live; a supplied one would go stale."],
  ["org_bid", "Derived from the twin, so a supplied copy would drift."],
  ["twin_bid", "Derived from the twin, so a supplied copy would drift."],
  ["is_tracked", "Derived from whether an observer exists against the asset, never stored."],
  ["product", "TwinLabs sets the matched product; your own text goes in `model_raw`."],
  [
    "product_line",
    "TwinLabs sets the matched product line; your own text goes in `product_line_raw`."
  ],
  ["model_3d", "TwinLabs picks the 3D model from the matched catalogue product."],
  [
    "model_3d_source",
    "TwinLabs records whether the 3D model follows the catalogue or was chosen for the asset."
  ],
  [
    "capacity_source",
    "TwinLabs records whether capacity follows the catalogue or was set for the asset."
  ],
  ["proposed_reference_id", "TwinLabs stores its best guess here for you to confirm."],
  ["proposed_manufacturer", "TwinLabs stores its best guess here for you to confirm."],
  ["proposed_product_line", "TwinLabs stores its best guess here for you to confirm."],
  ["proposed_product", "TwinLabs stores its best guess here for you to confirm."],
  ["proposal_score", "TwinLabs stores how strong its best guess is."],
  ["proposal_basis", "TwinLabs stores which clue its best guess rests on."],
  ["proposal_mix_scope", "TwinLabs stores why its best guess follows your brands."],
  ["proposal_mix_kind", "TwinLabs stores why its best guess follows your brands."],
  ["proposal_mix_count", "TwinLabs stores why its best guess follows your brands."],
  ["proposal_mix_total", "TwinLabs stores why its best guess follows your brands."],
  ["keep_off_plan", "Marked in the register, on an identified asset, not set from a file."]
]);
var ASSET_CSV_GENERATED_COLUMNS = Array.from(
  ASSET_CSV_REFUSED_COLUMN_REASONS.keys()
);
var ASSET_CSV_MAX_BYTES = 10 * 1024 * 1024;
var ASSET_CSV_MAX_ROWS = 2e4;
var ASSET_CSV_ADOPTION_KEPT_COLUMNS = [
  {
    header: "bid",
    type: "text",
    required: false,
    notes: "The full entity BID we assigned, e.g. NPL-STN-E014. We store the code after the twin prefix and re-mint the same BID when the twin goes live, so the customer\u2019s records and BI keep working. A BID for a different twin is rejected, row by row."
  },
  {
    header: "reference_id",
    type: "text",
    required: false,
    notes: 'The catalogue product we already matched this asset to. Kept as given \u2014 the matcher does not run for the row \u2014 and recorded as "supplied" rather than as a hand-resolved match. An id no catalogue product has rejects that row.'
  },
  {
    header: "asset_name",
    type: "text",
    required: false,
    notes: "The display name we settled on. Kept verbatim. Where it is blank, the name column is the display name. Either way it must be unique within the twin, ignoring case; a clash rejects the row and names the asset already using it."
  },
  {
    header: "model_3d",
    type: "text",
    required: false,
    notes: "The 3D model file we chose, e.g. Benches+Bench.glb. Kept as an override of the catalogue\u2019s. Checked for shape only \u2014 the same rule the model picker applies."
  },
  {
    header: "capacity",
    type: "number",
    required: false,
    notes: 'Stations on the asset. A whole number greater than zero; blank means "unknown" and the catalogue default is used.'
  }
];
var ASSET_CSV_ADOPTION_IGNORED_COLUMNS = ASSET_CSV_GENERATED_COLUMNS.filter(
  (header) => !ASSET_CSV_ADOPTION_KEPT_COLUMNS.some((column) => column.header === header)
);
var assetColumnFileNote = (column) => column.fileNote ? `${column.notes} ${column.fileNote}` : column.notes;

// src/equipment/parseAssetCsv.ts
var AssetCsvReadError = class extends Error {
  constructor(message, line) {
    super(message);
    this.name = "AssetCsvReadError";
    this.line = line;
  }
};
var COMMENT_LINE = /^# /;
var BOM = "\uFEFF";
var isBlank = (cells) => !cells.some((cell) => cell.trim() !== "");
var isSkippableLeading = (cells) => COMMENT_LINE.test((cells[0] ?? "").trim()) || isBlank(cells) || (cells[0] ?? "").trim() === "#" && cells.slice(1).every((cell) => cell.trim() === "");
var parseAssetCsv = (content) => {
  const text4 = content.startsWith(BOM) ? content.slice(BOM.length) : content;
  const rows = [];
  const rowLines = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  let line = 1;
  let rowStartLine = 1;
  for (let i = 0; i < text4.length; i++) {
    const char = text4[i];
    if (inQuotes) {
      if (char === '"') {
        if (text4[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        if (char === "\n") line += 1;
        field += char;
      }
      continue;
    }
    if (char === '"' && field === "") {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      rowLines.push(rowStartLine);
      row = [];
      field = "";
      line += 1;
      rowStartLine = line;
    } else if (char !== "\r") {
      field += char;
    }
  }
  if (inQuotes) {
    throw new AssetCsvReadError(
      `a quoted value starting on line ${rowStartLine} is never closed. Check for an unmatched double quote \u2014 a measurement like 10" needs to be written as "10""" or the inch mark removed`,
      rowStartLine
    );
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
    rowLines.push(rowStartLine);
  }
  let headerIndex = 0;
  while (headerIndex < rows.length && isSkippableLeading(rows[headerIndex])) headerIndex += 1;
  if (headerIndex === rows.length) return { headers: [], records: [] };
  const headers = rows[headerIndex].map((header) => header.trim());
  const records = [];
  for (let index = headerIndex + 1; index < rows.length; index++) {
    const cells = rows[index];
    if (isBlank(cells)) continue;
    records.push({
      line: rowLines[index],
      cells: headers.map((_, cellIndex) => cells[cellIndex] ?? "")
    });
  }
  return { headers, records };
};

// src/equipment/assetCsvColumnMap.ts
var LEAVE_OUT = "__leave_out__";
var NOTES_COLUMN = "customer_notes";
var normaliseHeader = (value) => {
  const squeezed = value.toLowerCase().replace(/[^a-z0-9]/g, "");
  return squeezed.length > 3 && squeezed.endsWith("raw") ? squeezed.slice(0, -3) : squeezed;
};
var COLUMN_SYNONYMS = {
  name: [
    "assetname",
    "equipmentname",
    "equipment",
    "item",
    "itemname",
    "description",
    "assetdescription"
  ],
  customer_asset_identifier: [
    "assetno",
    "assetnumber",
    "assetid",
    "assettag",
    "tag",
    "tagnumber",
    "barcode",
    "serial",
    "serialnumber",
    "assetcode",
    "ref",
    "reference"
  ],
  source_asset_id: ["qrcode", "qr", "recordid", "systemid", "sourceid"],
  manufacturer_raw: ["brand", "make", "maker", "supplier", "vendor"],
  product_line_raw: ["range", "series", "line", "productrange", "family"],
  model_raw: ["modelname", "modelno", "modelnumber"],
  manufacturer_sku_raw: ["sku", "partnumber", "partno", "productcode"],
  category_raw: ["type", "equipmenttype", "assettype", "kind", "group"],
  unit_cost: ["cost", "price", "purchaseprice", "purchasecost", "value"],
  residual_value: ["residual", "bookvalue", "netbookvalue"],
  install_date: ["installed", "installeddate", "installationdate", "datefitted", "fitted"],
  manufacture_date: ["manufactured", "yearofmanufacture", "builddate"],
  warranty_end_date: ["warranty", "warrantyend", "warrantyexpiry", "warrantyexpires"],
  contract_end_date: ["contractend", "contractexpiry", "serviceend"],
  condition: ["state", "status"],
  customer_notes: ["note", "comments", "comment", "remarks"]
};
var ALIAS = { manufacturer: "manufacturer_raw" };
var TEMPLATE_ORDER = ASSET_CSV_COLUMNS.map((column) => column.header);
var ADOPTION_COLUMNS = ASSET_CSV_ADOPTION_KEPT_COLUMNS.map(
  (column) => column.header
);
var WRITE_ORDER = [...TEMPLATE_ORDER, ...ADOPTION_COLUMNS];
var orderFor = (contract) => contract === "adopt" ? WRITE_ORDER : TEMPLATE_ORDER;
var ADOPTION_LABELS = {
  bid: "BID",
  reference_id: "Reference ID",
  asset_name: "Asset name",
  model_3d: "3D model",
  capacity: "Capacity"
};
var LABEL_BY_COLUMN = new Map([
  ...ASSET_CSV_COLUMNS.map((column) => [column.header, column.label]),
  ...Object.entries(ADOPTION_LABELS)
]);
var columnLabel = (column) => LABEL_BY_COLUMN.get(column) ?? column;
var EXACT_BY_NORMALISED = /* @__PURE__ */ new Map();
for (const column of ASSET_CSV_COLUMNS) {
  EXACT_BY_NORMALISED.set(normaliseHeader(column.header), column.header);
  if (column.label) EXACT_BY_NORMALISED.set(normaliseHeader(column.label), column.header);
}
Object.entries(ALIAS).forEach(
  ([alias, column]) => EXACT_BY_NORMALISED.set(normaliseHeader(alias), column)
);
var CLOSE_BY_NORMALISED = /* @__PURE__ */ new Map();
Object.entries(COLUMN_SYNONYMS).forEach(
  ([column, synonyms]) => synonyms.forEach((synonym) => CLOSE_BY_NORMALISED.set(synonym, column))
);
var refusedReason = (header, contract = "customer") => {
  const key = header.trim().toLowerCase();
  if (contract === "adopt") {
    if (ADOPTION_COLUMNS.includes(key)) return void 0;
    const product = Object.prototype.hasOwnProperty.call(ADOPTION_PRODUCT_REASONS, key) ? ADOPTION_PRODUCT_REASONS[key] : void 0;
    if (product !== void 0) return product;
  }
  return ASSET_CSV_REFUSED_COLUMN_REASONS.get(key);
};
var ADOPTION_PRODUCT_REASONS = {
  product: "An adoption file does not set the product here: it comes from BI, then reference_id, then a lookup.",
  product_line: "An adoption file does not set the product line here: it follows the product, which comes from BI, then reference_id, then a lookup."
};
var DEFAULT_LEFT_OUT = /* @__PURE__ */ new Set(["problems"]);
var columnLetter = (index) => {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};
var suggestColumnMap = (parsed, contract = "customer") => {
  const listed = [];
  parsed.headers.forEach((header, index) => {
    const firstValue = parsed.records.find((record) => (record.cells[index] ?? "").trim() !== "")?.cells[index] ?? null;
    if (header === "" && firstValue === null) return;
    const entry = {
      index,
      header,
      display: header === "" ? `Column ${columnLetter(index)}` : header,
      state: "decide",
      column: null,
      firstValue
    };
    const reason = refusedReason(header, contract);
    if (reason !== void 0) {
      listed.push({ entry: { ...entry, state: "out", reason }, candidate: null });
      return;
    }
    const adopted = contract === "adopt" ? header.trim().toLowerCase() : "";
    if (ADOPTION_COLUMNS.includes(adopted)) {
      listed.push({ entry, candidate: { column: adopted, strength: 2 } });
      return;
    }
    const normalised = normaliseHeader(header);
    if (DEFAULT_LEFT_OUT.has(normalised)) {
      listed.push({ entry: { ...entry, state: "out" }, candidate: null });
      return;
    }
    const exact = normalised === "" ? void 0 : EXACT_BY_NORMALISED.get(normalised);
    const close = exact === void 0 ? CLOSE_BY_NORMALISED.get(normalised) : void 0;
    const candidate = exact ? { column: exact, strength: 2 } : close ? { column: close, strength: 1 } : null;
    listed.push({ entry, candidate });
  });
  const holder = /* @__PURE__ */ new Map();
  listed.forEach(({ candidate }, position) => {
    if (!candidate || candidate.column === NOTES_COLUMN) return;
    const current = holder.get(candidate.column);
    if (current === void 0 || listed[current].candidate.strength < candidate.strength) {
      holder.set(candidate.column, position);
    }
  });
  return listed.map(({ entry, candidate }, position) => {
    if (!candidate) return entry;
    const holds = candidate.column === NOTES_COLUMN || holder.get(candidate.column) === position;
    if (!holds) return entry;
    return {
      ...entry,
      state: candidate.strength === 2 ? "exact" : "close",
      column: candidate.column
    };
  });
};
var decideColumn = (entries, index, choice, contract = "customer") => {
  const position = entries.findIndex((entry2) => entry2.index === index);
  if (position === -1) return entries;
  const entry = entries[position];
  let decided;
  if (choice === LEAVE_OUT) {
    if (entry.state === "out") return entries;
    decided = {
      ...entry,
      state: "out",
      column: null,
      reason: refusedReason(entry.header, contract)
    };
  } else {
    if (!orderFor(contract).includes(choice) || entry.column === choice) return entries;
    const heldElsewhere = choice !== NOTES_COLUMN && entries.some((other) => other.index !== index && other.column === choice);
    if (heldElsewhere) return entries;
    decided = { ...entry, state: "chosen", column: choice, reason: void 0 };
  }
  const next = entries.slice();
  next[position] = decided;
  return next;
};
var columnOptions = (entries, index, contract = "customer") => {
  const ordered = [NOTES_COLUMN, ...orderFor(contract).filter((column) => column !== NOTES_COLUMN)];
  const options = ordered.map((column) => {
    const other = column === NOTES_COLUMN ? void 0 : entries.find((entry) => entry.index !== index && entry.column === column);
    return {
      value: column,
      column,
      // R7's *Customer notes*: the column's own label (*Notes*) is what a header is matched
      // against; as a destination for extra identifiers it reads as the customer's notes.
      label: column === NOTES_COLUMN ? "Customer notes" : columnLabel(column),
      ...other ? { usedBy: other.display } : {},
      disabled: other !== void 0
    };
  });
  options.push({ value: LEAVE_OUT, column: null, label: "Leave out", disabled: false });
  return options;
};
var columnMapCounts = (entries) => ({
  listed: entries.length,
  mapped: entries.filter((entry) => entry.column !== null).length,
  undecided: entries.filter((entry) => entry.state === "decide").map((entry) => entry.display),
  nameMapped: entries.some((entry) => entry.column === "name"),
  identifierMapped: entries.some((entry) => entry.column === ASSET_CSV_IDENTIFIER_HEADER)
});
var columnMapReady = (entries) => entries.every((entry) => entry.state !== "decide") && entries.some((entry) => entry.column === "name");
var holdersOf = (entries, column) => entries.filter((entry) => entry.column === column);
var mappedCell = (cells, holders) => {
  if (holders.length === 1) return cells[holders[0].index] ?? "";
  return holders.map((holder) => ({ holder, value: cells[holder.index] ?? "" })).filter(({ value }) => value.trim() !== "").map(({ holder, value }) => `${holder.display}: ${value}`).join("; ");
};
var mappedColumns = (entries) => WRITE_ORDER.map((column) => ({ column, holders: holdersOf(entries, column) })).filter(
  ({ holders }) => holders.length > 0
);
var csvField = (value) => /[",\r\n]|^\s|\s$/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
var editKey = (line, column) => `${line}:${column}`;
var NOTHING_LEFT_OUT = { has: () => false };
var buildMappedCsv = (parsed, entries, edits = /* @__PURE__ */ new Map(), leftOut = NOTHING_LEFT_OUT) => {
  const columns = withEditedColumns(mappedColumns(entries), edits);
  const lines = [columns.map(({ column }) => column).join(",")];
  const lineMap = /* @__PURE__ */ new Map();
  let nextLine = 2;
  for (const record of parsed.records) {
    if (leftOut.has(record.line)) continue;
    const row = columns.map(
      ({ column, holders }) => csvField(edits.get(editKey(record.line, column)) ?? mappedCell(record.cells, holders))
    ).join(",");
    lineMap.set(nextLine, record.line);
    nextLine += 1 + (row.match(/\n/g)?.length ?? 0);
    lines.push(row);
  }
  return { text: `${lines.join("\r\n")}\r
`, lineMap };
};
var withEditedColumns = (columns, edits) => {
  const edited = new Set(Array.from(edits.keys(), (key) => key.slice(key.indexOf(":") + 1)));
  const missing = WRITE_ORDER.filter(
    (column) => edited.has(column) && !columns.some((mapped) => mapped.column === column)
  );
  if (missing.length === 0) return columns;
  const all = [...columns, ...missing.map((column) => ({ column, holders: [] }))];
  return all.sort((a, b) => WRITE_ORDER.indexOf(a.column) - WRITE_ORDER.indexOf(b.column));
};
var translateLine = (lineMap, line) => lineMap.get(line) ?? line;
var ON_LINE = /(" is already used on line )(\d+)(?=\. )/g;
var translateAnswerLines = (answer, lineMap) => {
  const relined = (rows) => rows?.map((row) => ({
    ...row,
    line: row.line === null ? null : translateLine(lineMap, row.line),
    ...typeof row.reason === "string" ? {
      reason: row.reason.replace(
        ON_LINE,
        (_, lead, n) => `${lead}${translateLine(lineMap, Number(n))}`
      )
    } : {}
  }));
  const translated = { ...answer };
  if (answer.rejected) translated.rejected = relined(answer.rejected);
  if (answer.renamed) translated.renamed = relined(answer.renamed);
  return translated;
};
var blankMappedRecords = (parsed, entries) => {
  const columns = mappedColumns(entries);
  if (columns.length === 0) return parsed.records.length;
  return parsed.records.filter(
    (record) => columns.every(({ holders }) => mappedCell(record.cells, holders).trim() === "")
  ).length;
};
var PREVIEW_COLUMNS = [
  "name",
  "customer_asset_identifier",
  "manufacturer_raw",
  "product_line_raw",
  "model_raw",
  "category_raw"
];
var previewRows = (parsed, entries, n = 6) => {
  const columns = PREVIEW_COLUMNS.map((column) => ({
    column,
    holders: holdersOf(entries, column)
  })).filter(({ holders }) => holders.length > 0);
  return {
    columns: columns.map(({ column }) => column),
    rows: parsed.records.slice(0, n).map((record) => columns.map(({ holders }) => mappedCell(record.cells, holders)))
  };
};

// src/equipment/assetCsvValues.ts
import { modelReferenceProblem } from "@repo/frontend-types";

// src/twin-planner/nameFamily.ts
var nameKey = (name) => name.trim().toLowerCase();
var escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
var isHeld = (name, index) => (index.byKey.get(nameKey(name)) ?? []).length > 0;
var lowestFreeNumber = (base, index, start, reserved = /* @__PURE__ */ new Set()) => {
  for (let n = start; ; n++) {
    if (!reserved.has(n) && !isHeld(`${base}-${n}`, index)) return n;
  }
};
var nameFamily = (base, index) => {
  const key = nameKey(base);
  const member = new RegExp(`^${escapeRegExp(key)}-(\\d+)$`);
  const bare = index.byKey.get(key) ?? [];
  const members = [];
  for (const [k, entries] of index.byKey) {
    const match = member.exec(k);
    if (match) for (const entry of entries) members.push({ n: Number(match[1]), entry });
  }
  return { bare, members };
};
function decideNewcomerName(base, kind, index) {
  const trimmed3 = base.trim();
  const { bare, members } = nameFamily(trimmed3, index);
  if (bare.length === 0 && members.length === 0) return { name: trimmed3 };
  const holder = bare.length === 1 && bare[0].kind === kind ? bare[0] : void 0;
  if (holder && kind !== "floor") {
    const holderN = lowestFreeNumber(trimmed3, index, 1);
    const newcomerN = lowestFreeNumber(trimmed3, index, 2, /* @__PURE__ */ new Set([holderN]));
    return {
      name: `${trimmed3}-${newcomerN}`,
      renameHolder: { entry: holder, to: `${trimmed3}-${holderN}` }
    };
  }
  return { name: `${trimmed3}-${lowestFreeNumber(trimmed3, index, 2)}` };
}

// src/equipment/assetCsvValues.ts
var MAX_DECIMAL_INTEGER_DIGITS = 10;
var MAX_DECIMAL_PLACES = 2;
var MIN_YEAR = 1900;
var MAX_YEAR = 2200;
var CURRENCY_SYMBOL_RE = /^[£$€¥]\s*/;
var THOUSANDS_GROUPED_RE = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;
var MONEY_RE = /^(-?)(\d+)(?:\.(\d+))?$/;
var DATE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
var DATE_YEAR_ONLY_RE = /^(\d{4})$/;
var CURRENCY_RE = /^[A-Z]{3}$/;
var MONEY_COLUMNS = ["unit_cost", "residual_value"];
var DATE_COLUMNS = [
  "install_date",
  "manufacture_date",
  "warranty_end_date",
  "contract_end_date"
];
var DATE_RIGHT_FORM = "Use 2025-04-03, or a year on its own (2023)";
var MONEY_RIGHT_FORM = "Use a plain number; the currency goes in its own column";
var NAME_RIGHT_FORM = "Type a name, or leave the rows out";
var CURRENCY_RIGHT_FORM = "Use a 3-letter code such as GBP, EUR or USD";
var readNameCell = (raw) => {
  const value = raw.trim();
  return value === "" ? { ok: false, reason: "Every row needs a name." } : { ok: true, stored: value };
};
var readMoneyCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  let candidate = trimmed3.replace(CURRENCY_SYMBOL_RE, "").trim();
  if (THOUSANDS_GROUPED_RE.test(candidate)) candidate = candidate.replace(/,/g, "");
  const match = MONEY_RE.exec(candidate);
  if (!match) return { ok: false, reason: `"${trimmed3}" is not a number. ${MONEY_RIGHT_FORM}.` };
  const [, sign, integerDigits, decimalDigits] = match;
  if (sign === "-") return { ok: false, reason: `"${trimmed3}" is negative.` };
  if ((decimalDigits?.length ?? 0) > MAX_DECIMAL_PLACES) {
    return { ok: false, reason: `"${trimmed3}" has more than 2 decimal places.` };
  }
  if (integerDigits.replace(/^0+(?=\d)/, "").length > MAX_DECIMAL_INTEGER_DIGITS) {
    return { ok: false, reason: `"${trimmed3}" is too large. The most is 9999999999.99.` };
  }
  return { ok: true, stored: Number(candidate) };
};
var readCurrencyCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  const upper = trimmed3.toUpperCase();
  return CURRENCY_RE.test(upper) ? { ok: true, stored: upper } : { ok: false, reason: `"${trimmed3}" is not a currency code. ${CURRENCY_RIGHT_FORM}.` };
};
var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var isLeapYear = (year) => year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
var isRealCalendarDate = (year, month, day) => {
  if (month < 1 || month > 12 || day < 1) return false;
  return day <= (month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]);
};
var yearInRange = (year) => year >= MIN_YEAR && year <= MAX_YEAR;
var readDateCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  const yearOnly = DATE_YEAR_ONLY_RE.exec(trimmed3);
  if (yearOnly) {
    return yearInRange(Number(yearOnly[1])) ? { ok: true, stored: `${yearOnly[1]}-01-01` } : { ok: false, reason: `"${trimmed3}" is outside 1900\u20132200.` };
  }
  const iso = DATE_ISO_RE.exec(trimmed3);
  if (!iso) return { ok: false, reason: `"${trimmed3}" can't be read. ${DATE_RIGHT_FORM}.` };
  const [, yearText, monthText, dayText] = iso;
  if (!yearInRange(Number(yearText))) {
    return { ok: false, reason: `"${trimmed3}" is outside 1900\u20132200.` };
  }
  if (!isRealCalendarDate(Number(yearText), Number(monthText), Number(dayText))) {
    return { ok: false, reason: `"${trimmed3}" is not a real date.` };
  }
  return { ok: true, stored: trimmed3 };
};
var MAX_ADOPTED_CODE_LENGTH = 64;
var MAX_REFERENCE_ID_LENGTH = 64;
var MAX_ADOPTED_CAPACITY = 2147483647;
var CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
var readAdoptedBidCell = (raw, twinBid) => {
  const bid = raw.trim();
  if (bid === "") return { ok: true, stored: null };
  const upper = bid.toUpperCase();
  const twin = twinBid.trim().toUpperCase();
  if (twin === "") {
    return { ok: false, reason: "This twin has no BID yet, so no BID in the file can be checked." };
  }
  const prefix = `${twin}-`;
  if (!upper.startsWith(prefix)) {
    return {
      ok: false,
      reason: `"${bid}" is not a BID for this twin. It must start with ${prefix}`
    };
  }
  const tail = upper.slice(prefix.length).trim();
  if (tail === "") return { ok: false, reason: `"${bid}" has no code after the twin's BID.` };
  if (tail.length > MAX_ADOPTED_CODE_LENGTH) {
    return {
      ok: false,
      reason: `"${bid}" has a code longer than ${MAX_ADOPTED_CODE_LENGTH} characters.`
    };
  }
  if (CONTROL_CHARACTERS.test(tail)) {
    return { ok: false, reason: `"${bid}" contains a control character.` };
  }
  if (/\s/.test(tail)) return { ok: false, reason: `"${bid}" has a space inside its code.` };
  if (tail.includes("-")) {
    return { ok: false, reason: `"${bid}" has more than one part after ${prefix}` };
  }
  return { ok: true, stored: upper };
};
var readReferenceIdCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  if (trimmed3.length > MAX_REFERENCE_ID_LENGTH) {
    return {
      ok: false,
      reason: `"${trimmed3}" is longer than ${MAX_REFERENCE_ID_LENGTH} characters.`
    };
  }
  if (CONTROL_CHARACTERS.test(trimmed3)) {
    return { ok: false, reason: `"${trimmed3}" contains a control character.` };
  }
  return { ok: true, stored: trimmed3 };
};
var readCapacityCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  if (!/^\d+$/.test(trimmed3)) {
    return { ok: false, reason: `"${trimmed3}" is not a whole number of stations.` };
  }
  const value = Number(trimmed3);
  if (value <= 0) {
    return { ok: false, reason: `"${trimmed3}" is not a capacity: leave it blank if unknown.` };
  }
  if (value > MAX_ADOPTED_CAPACITY) {
    return { ok: false, reason: `"${trimmed3}" is too large. The most is ${MAX_ADOPTED_CAPACITY}.` };
  }
  return { ok: true, stored: value };
};
var readModel3dCell = (raw) => {
  const trimmed3 = raw.trim();
  if (trimmed3 === "") return { ok: true, stored: null };
  const problem = modelReferenceProblem(trimmed3);
  return problem === null ? { ok: true, stored: trimmed3 } : { ok: false, reason: `"${trimmed3}" is not a model file name: it ${problem}` };
};
var ADOPTED_RULES = {
  bid: (raw, adoption) => readAdoptedBidCell(raw, adoption.twinBid),
  reference_id: (raw) => readReferenceIdCell(raw),
  capacity: (raw) => readCapacityCell(raw),
  model_3d: (raw) => readModel3dCell(raw)
};
var readValueCell = (column, raw, adoption) => {
  const adopted = adoption ? ADOPTED_RULES[column] : void 0;
  if (adopted && adoption) return adopted(raw, adoption);
  if (column === "name") return readNameCell(raw);
  if (column === "currency") return readCurrencyCell(raw);
  if (MONEY_COLUMNS.includes(column)) return readMoneyCell(raw);
  if (DATE_COLUMNS.includes(column)) return readDateCell(raw);
  const value = raw.trim();
  return { ok: true, stored: value === "" ? null : value };
};
var VALUE_PROBLEM_KINDS = [
  "date-day-first",
  "date-month-first",
  "date-order-unknown",
  "date-unreadable",
  "money-sign",
  "money-unreadable",
  "currency-code",
  "name-missing",
  "identifier-repeated",
  "adopted-bid",
  "adopted-reference-id",
  "adopted-capacity",
  "adopted-model-3d",
  "server-refused"
];
var DAY_MONTH_RE = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4})$/;
var decideDateOrder = (cells) => {
  let firstOver12 = false;
  let secondOver12 = false;
  for (const cell of cells) {
    const match = DAY_MONTH_RE.exec(cell.trim());
    if (!match) continue;
    if (Number(match[1]) > 12 && convertDayMonth(match, "day-first") !== null) firstOver12 = true;
    if (Number(match[3]) > 12 && convertDayMonth(match, "month-first") !== null) {
      secondOver12 = true;
    }
  }
  if (firstOver12 && !secondOver12) return "day-first";
  if (secondOver12 && !firstOver12) return "month-first";
  return "unsettled";
};
var pad2 = (value) => value.padStart(2, "0");
var convertDayMonth = (match, order) => {
  const [, first, , second, year] = match;
  const [day, month] = order === "day-first" ? [first, second] : [second, first];
  const iso = `${year}-${pad2(month)}-${pad2(day)}`;
  return readDateCell(iso).ok ? iso : null;
};
var identifierKey = (value) => {
  const key = value.replace(/^ +| +$/g, "").toUpperCase();
  return key === "" ? null : key;
};
var SIGN_FILLS = { "\xA3": "GBP", "\u20AC": "EUR" };
var plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
var KIND_TITLES = {
  "date-day-first": (n) => `${plural(n, "date is", "dates are")} written day first`,
  "date-month-first": (n) => `${plural(n, "date is", "dates are")} written month first`,
  "date-order-unknown": (n) => `${plural(n, "date", "dates")} could be read two ways`,
  "date-unreadable": (n) => `${plural(n, "date", "dates")} can't be read`,
  "money-sign": (n) => `${plural(n, "cost has", "costs have")} a currency sign`,
  "money-unreadable": (n) => `${plural(n, "cost", "costs")} can't be read`,
  "currency-code": (n) => `${plural(n, "currency isn't a", "currencies aren't")} 3-letter code${n === 1 ? "" : "s"}`,
  "name-missing": (n) => `${plural(n, "row has", "rows have")} no name`,
  "identifier-repeated": (n) => `${plural(n, "Asset Identifier repeats", "Asset Identifiers repeat")}`,
  "adopted-bid": (n) => `${plural(n, "BID isn't", "BIDs aren't")} this twin's`,
  "adopted-reference-id": (n) => `${plural(n, "reference id can't", "reference ids can't")} be read`,
  "adopted-capacity": (n) => `${plural(n, "capacity isn't a", "capacities aren't")} whole number${n === 1 ? "" : "s"} above 0`,
  "adopted-model-3d": (n) => `${plural(n, "3D model isn't a", "3D models aren't")} model file name${n === 1 ? "" : "s"}`,
  "server-refused": (n) => `${plural(n, "row", "rows")} would be refused`
};
var linesText = (lines) => {
  const unique = Array.from(new Set(lines));
  if (unique.length === 1) return `line ${unique[0]}`;
  if (unique.length <= 3) {
    return `lines ${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
  }
  return `lines ${unique.slice(0, 3).join(", ")} and ${unique.length - 3} more`;
};
var rightFormOf = (kind, lines) => {
  switch (kind) {
    case "date-day-first":
    case "date-month-first":
    case "date-unreadable":
      return DATE_RIGHT_FORM;
    case "date-order-unknown":
      return `Nothing in the file shows whether the day or the month comes first. ${DATE_RIGHT_FORM}`;
    case "money-sign":
      return lines.some((line) => /^[$¥]/.test(line.found.trim())) ? `${MONEY_RIGHT_FORM}. $ and \xA5 name several currencies, so the currency is left as it is` : MONEY_RIGHT_FORM;
    case "money-unreadable":
      return MONEY_RIGHT_FORM;
    case "currency-code":
      return CURRENCY_RIGHT_FORM;
    case "name-missing":
      return NAME_RIGHT_FORM;
    case "identifier-repeated":
      return "Give each row its own, or leave the repeats out";
    case "adopted-bid":
      return "Use this twin's BID, a dash and the code (NPL-STN-E014), or leave it blank";
    case "adopted-reference-id":
      return "Use a catalogue reference id of up to 64 characters, or leave it blank";
    case "adopted-capacity":
      return `Use a whole number from 1 to ${MAX_ADOPTED_CAPACITY}, or leave it blank`;
    case "adopted-model-3d":
      return "Use a model file name such as Benches+Bench.glb, or leave it blank";
    case "server-refused":
      return lines[0]?.reason ?? null;
  }
};
var groupOf = (kind, lines) => {
  const sorted = lines.slice().sort((a, b) => a.line - b.line);
  const columns = Array.from(
    new Set(sorted.flatMap((line) => line.column === null ? [] : [columnLabel(line.column)]))
  );
  const count = kind === "server-refused" ? new Set(sorted.map((line) => line.line)).size : sorted.length;
  const first = sorted[0];
  const use = first.fix && first.column !== null ? first.fix[first.column] : void 0;
  return {
    kind,
    lines: sorted,
    title: KIND_TITLES[kind](count),
    where: [columns.join(", "), linesText(sorted.map((line) => line.line)), rightFormOf(kind, sorted)].filter((part) => !!part).join(" \xB7 "),
    found: first.found,
    ...use !== void 0 ? { use } : {}
  };
};
var recordViews = (parsed, entries) => {
  const columns = mappedColumns(entries);
  return parsed.records.map((record) => {
    const values = /* @__PURE__ */ new Map();
    for (const { column, holders } of columns)
      values.set(column, mappedCell(record.cells, holders));
    return { line: record.line, values };
  });
};
var withEdits = (views, edits) => {
  if (edits.size === 0) return views;
  const byLine = /* @__PURE__ */ new Map();
  for (const [key, value] of edits) {
    const at = key.indexOf(":");
    const line = Number(key.slice(0, at));
    byLine.set(line, [...byLine.get(line) ?? [], [key.slice(at + 1), value]]);
  }
  return views.map((view) => {
    const own = byLine.get(view.line);
    if (!own) return view;
    const values = new Map(view.values);
    for (const [column, value] of own) values.set(column, value);
    return { line: view.line, values };
  });
};
var isBlank2 = (view) => {
  for (const value of view.values.values()) if (value.trim() !== "") return false;
  return true;
};
var COLUMN_RULES = new Map([
  ["name", "name"],
  ["currency", "currency"],
  ...MONEY_COLUMNS.map((column) => [column, "money"]),
  ...DATE_COLUMNS.map((column) => [column, "date"])
]);
var ADOPTION_COLUMN_RULES = new Map([
  ...Array.from(COLUMN_RULES),
  ...Object.keys(ADOPTED_RULES).map((column) => [column, "adopted"])
]);
var ADOPTED_KINDS = {
  bid: "adopted-bid",
  reference_id: "adopted-reference-id",
  capacity: "adopted-capacity",
  model_3d: "adopted-model-3d"
};
var checkAssetValues = (parsed, entries, edits = /* @__PURE__ */ new Map(), leftOut = /* @__PURE__ */ new Map(), serverRefused = [], adoption, register = []) => {
  const rules = adoption ? ADOPTION_COLUMN_RULES : COLUMN_RULES;
  const original = recordViews(parsed, entries).filter((view) => !leftOut.has(view.line));
  const views = withEdits(original, edits).filter((view) => !isBlank2(view));
  const dateCells = [];
  for (const view of original) {
    for (const column of DATE_COLUMNS) {
      const cell = view.values.get(column);
      if (cell !== void 0) dateCells.push(cell);
    }
  }
  const order = decideDateOrder(dateCells);
  const byKind = /* @__PURE__ */ new Map();
  const add2 = (kind, problem) => {
    const list = byKind.get(kind);
    if (list) list.push(problem);
    else byKind.set(kind, [problem]);
  };
  const troubled = /* @__PURE__ */ new Set();
  for (const view of views) {
    const nameCell = view.values.get("name") ?? "";
    const name = nameCell.trim() === "" ? null : nameCell.trim();
    const currencyBlank = (view.values.get("currency") ?? "").trim() === "";
    let rowFill;
    const signFixes = [];
    const problem = (column, found, reason, fix) => ({
      line: view.line,
      column,
      name,
      found,
      reason,
      ...fix ? { fix } : {}
    });
    for (const [column, raw] of view.values) {
      const rule = rules.get(column);
      if (rule === void 0) continue;
      const trimmed3 = raw.trim();
      if (rule === "adopted") {
        const verdict = ADOPTED_RULES[column](raw, adoption);
        if (verdict.ok) continue;
        add2(ADOPTED_KINDS[column], problem(column, raw, verdict.reason));
      } else if (rule === "name") {
        const verdict = readNameCell(raw);
        if (verdict.ok) continue;
        add2("name-missing", problem(column, raw, verdict.reason));
      } else if (rule === "money") {
        const verdict = readMoneyCell(raw);
        if (!verdict.ok) {
          add2("money-unreadable", problem(column, raw, verdict.reason));
        } else if (/^[£$€¥]/.test(trimmed3)) {
          const fix = { [column]: String(verdict.stored) };
          const fill = SIGN_FILLS[trimmed3[0]];
          if (fill && currencyBlank) {
            rowFill = rowFill === void 0 || rowFill === fill ? fill : null;
            fix.currency = fill;
            signFixes.push(fix);
          }
          add2("money-sign", problem(column, raw, `"${trimmed3}" has a currency sign.`, fix));
        } else {
          continue;
        }
      } else if (rule === "currency") {
        const verdict = readCurrencyCell(raw);
        if (verdict.ok) continue;
        const fill = SIGN_FILLS[trimmed3];
        add2(
          "currency-code",
          problem(column, raw, verdict.reason, fill ? { currency: fill } : void 0)
        );
      } else {
        const dayMonth = DAY_MONTH_RE.exec(trimmed3);
        if (dayMonth === null) {
          const verdict = readDateCell(raw);
          if (verdict.ok) continue;
          add2("date-unreadable", problem(column, raw, verdict.reason));
        } else if (order === "unsettled") {
          if (convertDayMonth(dayMonth, "day-first") === null && convertDayMonth(dayMonth, "month-first") === null) {
            add2("date-unreadable", problem(column, raw, `"${trimmed3}" is not a real date.`));
          } else {
            add2(
              "date-order-unknown",
              problem(column, raw, `"${trimmed3}" could be the day or the month first.`)
            );
          }
        } else {
          const iso = convertDayMonth(dayMonth, order);
          if (iso === null) {
            add2("date-unreadable", problem(column, raw, `"${trimmed3}" is not a real date.`));
          } else {
            const kind = order === "day-first" ? "date-day-first" : "date-month-first";
            const reason = `"${trimmed3}" is written ${order === "day-first" ? "day" : "month"} first.`;
            add2(kind, problem(column, raw, reason, { [column]: iso }));
          }
        }
      }
      troubled.add(view.line);
    }
    if (rowFill === null) for (const fix of signFixes) delete fix.currency;
  }
  const firstLineFor = /* @__PURE__ */ new Map();
  for (const view of views) {
    if (troubled.has(view.line)) continue;
    const raw = view.values.get("customer_asset_identifier");
    const key = raw === void 0 ? null : identifierKey(raw);
    if (key === null) continue;
    const earlier = firstLineFor.get(key);
    if (earlier === void 0) {
      firstLineFor.set(key, view.line);
      continue;
    }
    const nameCell = (view.values.get("name") ?? "").trim();
    add2("identifier-repeated", {
      line: view.line,
      column: "customer_asset_identifier",
      name: nameCell === "" ? null : nameCell,
      found: raw ?? "",
      reason: `"${(raw ?? "").trim()}" is already used on line ${earlier}.`
    });
  }
  const held = serverRefused.length === 0 ? /* @__PURE__ */ new Set() : new Set(
    Array.from(byKind.values()).flatMap(
      (lines) => lines.map((line) => `${line.line}:${line.column}`)
    )
  );
  const viewByLine = new Map(
    serverRefused.length === 0 ? [] : views.map((view) => [view.line, view])
  );
  for (const refusal of serverRefused) {
    if (held.has(`${refusal.line}:${refusal.column}`)) continue;
    const view = viewByLine.get(refusal.line);
    if (view === void 0) continue;
    const nameCell = (view.values.get("name") ?? "").trim();
    add2("server-refused", {
      line: refusal.line,
      column: refusal.column,
      name: nameCell === "" ? null : nameCell,
      found: refusal.column === null ? "" : view.values.get(refusal.column) ?? "",
      reason: refusal.reason
    });
  }
  return {
    kinds: VALUE_PROBLEM_KINDS.filter((kind) => byKind.has(kind)).map(
      (kind) => groupOf(kind, byKind.get(kind))
    ),
    repeatedNames: repeatedNamesOf(views, register),
    rows: views.length,
    order
  };
};
var isDerivedAssetName = (assetName, name) => {
  if (assetName == null || assetName === "") return true;
  const base = name.trim();
  if (assetName === base) return true;
  if (new RegExp(`^${escapeRegExp(base)} \\d+$`).test(assetName)) return true;
  return new RegExp(`^${escapeRegExp(nameKey(base))}-\\d+$`).test(nameKey(assetName));
};
var keptNumberedSlot = (existing, newName) => {
  const assetName = existing.assetName;
  if (assetName == null || nameKey(existing.name ?? "") !== nameKey(newName)) return newName;
  const slot = new RegExp(`^${escapeRegExp(nameKey(newName))}(-| )(\\d+)$`).exec(
    nameKey(assetName)
  );
  return slot ? `${newName.trim()}${slot[1]}${slot[2]}` : newName;
};
var updateNameBase = (existing, newName) => {
  const rederive = existing.assetName == null || (existing.name ?? "") !== newName && isDerivedAssetName(existing.assetName, existing.name ?? "") && newName.trim() !== existing.assetName;
  return rederive ? keptNumberedSlot(existing, newName) : null;
};
var updatesOf = (rows, register) => {
  const updates = /* @__PURE__ */ new Map();
  if (register.length === 0) return updates;
  const byKey = /* @__PURE__ */ new Map();
  for (const row of register) {
    const key = row.identifier == null ? null : identifierKey(row.identifier);
    if (key !== null) byKey.set(key, row);
  }
  const claimed = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const key = row.identifier == null ? null : identifierKey(row.identifier);
    const target = key === null ? void 0 : byKey.get(key);
    if (target === void 0 || claimed.has(target.id)) continue;
    claimed.add(target.id);
    updates.set(row.line, target);
  }
  return updates;
};
var numberFileNames = (rows, register = []) => {
  const slots = /* @__PURE__ */ new Map();
  const slotOf = (key) => {
    let slot = slots.get(key);
    if (!slot) {
      slot = { bare: [], members: 0, taken: /* @__PURE__ */ new Set(), from1: 1, from2: 2 };
      slots.set(key, slot);
    }
    return slot;
  };
  const hold = (name, holder) => {
    const key = nameKey(name);
    if (key === "") return;
    slotOf(key).bare.push(holder);
    const member = /^(.+)-(\d+)$/.exec(key);
    if (!member) return;
    const stem = slotOf(member[1]);
    stem.members += 1;
    if (String(Number(member[2])) === member[2]) stem.taken.add(Number(member[2]));
  };
  const lowest = (slot, start, skip) => {
    let n = start === 1 ? slot.from1 : slot.from2;
    while (slot.taken.has(n)) n += 1;
    if (start === 1) slot.from1 = n;
    else slot.from2 = n;
    while (n === skip || slot.taken.has(n)) n += 1;
    return n;
  };
  const release = (name, holder) => {
    const bare = slots.get(nameKey(name))?.bare;
    const at = bare ? bare.indexOf(holder) : -1;
    if (at !== -1) bare.splice(at, 1);
  };
  const names = /* @__PURE__ */ new Map();
  const updates = updatesOf(rows, register);
  const requests = [];
  const renaming = /* @__PURE__ */ new Set();
  const statedLine = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const target = updates.get(row.line);
    if (target === void 0) {
      requests.push({ line: row.line, base: row.name });
      continue;
    }
    const base = updateNameBase(target, row.name);
    if (base === null) {
      statedLine.set(target.id, row.line);
      names.set(row.line, target.assetName || target.name || row.name);
    } else {
      renaming.add(target.id);
      requests.push({ line: row.line, base });
    }
  }
  const ownOf = /* @__PURE__ */ new Map();
  for (const row of register) {
    if (!row.assetName) continue;
    const holder = {
      line: statedLine.get(row.id) ?? null,
      kind: renaming.has(row.id) ? "pending" : "machine"
    };
    ownOf.set(row.id, { name: row.assetName, holder });
    hold(row.assetName, holder);
  }
  for (const request of requests) {
    const target = updates.get(request.line);
    if (target !== void 0) request.own = ownOf.get(target.id);
  }
  const keeps = (request) => request.own !== void 0 && nameKey(request.own.name) === nameKey(request.base);
  const ordered = register.length === 0 ? requests : [
    ...requests.filter(keeps),
    ...requests.filter((request) => updates.has(request.line) && !keeps(request)),
    ...requests.filter((request) => !updates.has(request.line))
  ];
  for (const request of ordered) {
    const base = request.base.trim();
    const own = request.own;
    const keeping = own !== void 0 && nameKey(own.name) === nameKey(base);
    if (own) {
      if (keeping) release(own.name, own.holder);
      else own.holder.kind = "vacated";
    }
    const slot = slots.get(nameKey(base));
    let name = base;
    if (slot && (slot.bare.length > 0 || slot.members > 0)) {
      const holder = slot.bare.length === 1 && slot.bare[0].kind === "machine" ? slot.bare[0] : void 0;
      if (holder) {
        const holderN = lowest(slot, 1);
        const to = `${base}-${holderN}`;
        name = `${base}-${lowest(slot, 2, holderN)}`;
        if (holder.line !== null) names.set(holder.line, to);
        holder.kind = "vacated";
        hold(to, { line: holder.line, kind: "renamed" });
      } else {
        name = `${base}-${lowest(slot, 2)}`;
      }
    }
    if (own && keeping && nameKey(name) !== nameKey(base)) {
      hold(own.name, { line: own.holder.line, kind: "vacated" });
    }
    hold(name, { line: request.line, kind: "machine" });
    names.set(request.line, name);
  }
  return names;
};
var repeatedNamesOf = (views, register) => {
  const named = [];
  for (const view of views) {
    const name = (view.values.get("name") ?? "").trim();
    if (name === "") continue;
    const identifier = view.values.get("customer_asset_identifier") ?? null;
    named.push({ line: view.line, name, key: nameKey(name), identifier });
  }
  const counts = /* @__PURE__ */ new Map();
  for (const row of named) counts.set(row.key, (counts.get(row.key) ?? 0) + 1);
  const repeated = new Set(
    Array.from(counts).filter(([, n]) => n > 1).map(([key]) => key)
  );
  const onRegister = /* @__PURE__ */ new Set();
  for (const row of register) {
    if (!row.assetName) continue;
    const key = nameKey(row.assetName);
    onRegister.add(key);
    const stem = /^(.+)-\d+$/.exec(key)?.[1];
    if (stem !== void 0) onRegister.add(stem);
  }
  onRegister.forEach((key) => repeated.add(key));
  if (repeated.size === 0) return null;
  const familyOf = (key) => {
    if (repeated.has(key)) return key;
    const stem = /^(.+)-\d+$/.exec(key)?.[1];
    return stem !== void 0 && repeated.has(stem) ? stem : void 0;
  };
  const willBe = numberFileNames(named, register);
  const updates = updatesOf(named, register);
  const keepsStoredName = (line) => {
    const stored = updates.get(line)?.assetName;
    return !!stored && nameKey(stored) === nameKey(willBe.get(line));
  };
  const lines = named.filter(
    (row) => familyOf(row.key) !== void 0 && willBe.get(row.line) !== row.name && !keepsStoredName(row.line)
  ).map((row) => ({ line: row.line, asWritten: row.name, willBe: willBe.get(row.line) }));
  if (lines.length === 0) return null;
  const families = /* @__PURE__ */ new Map();
  for (const line of lines) {
    const key = familyOf(nameKey(line.asWritten)) ?? nameKey(line.asWritten);
    const members = families.get(key);
    if (members) members.push(line);
    else families.set(key, [line]);
  }
  const described = Array.from(families).map(([key, members]) => {
    const base = members[0].asWritten;
    const numbers = members.map((member) => member.willBe.slice(member.willBe.lastIndexOf("-")));
    const range = `${base}${numbers[0]} \u2026 ${numbers[numbers.length - 1]}`;
    const registered = onRegister.has(key);
    return {
      // Repeats within the file alone: *Rower ×2* … *Rower-1 … -2*, as the card always said.
      fileOnly: !registered && members.length > 1,
      registered,
      said: `${base} \xD7${members.length}`,
      become: range,
      /*
       * Said plainly where the name clashes with the register, or one row is numbered: no ×1 and
       * no one-number range (`equipment-page-reimport`, R3 — the file-only wording assumes the
       * register is empty).
       */
      plain: registered ? `${base} is already on the register: ${members.length === 1 ? `becomes ${members[0].willBe}` : `${members.length} become ${range}`}` : members.length === 1 ? `${base} becomes ${members[0].willBe}` : `${base} \xD7${members.length} become ${range}`
    };
  });
  const shown = described.slice(0, 3);
  const more = described.length > 3 ? ` and ${described.length - 3} more` : "";
  const anyRegistered = described.some((d) => d.registered);
  const title = !anyRegistered ? `${plural(lines.length, "name repeats", "names repeat")}, so they will be numbered` : described.every((d) => d.registered) ? `${plural(lines.length, "name is", "names are")} already on the register, so ${lines.length === 1 ? "it" : "they"} will be numbered` : `${plural(lines.length, "name", "names")} repeat or are already on the register, so they will be numbered`;
  const where = described.every((d) => d.fileOnly) ? `${shown.map((d) => d.said).join(", ")}${more} become ${shown.map((d) => d.become).join(", ")}` : `${shown.map((d) => d.plain).join("; ")}${more}`;
  return { rows: lines.length, title, where: `${where} \xB7 Nothing to do`, lines };
};
var applyFixes = (edits, group) => {
  const next = new Map(edits);
  for (const line of group.lines) {
    for (const [column, value] of Object.entries(line.fix ?? {})) {
      next.set(editKey(line.line, column), value);
    }
  }
  return next;
};
var FIXED_NOUNS = {
  "date-day-first": ["date", "dates"],
  "date-month-first": ["date", "dates"],
  "date-order-unknown": ["date", "dates"],
  "date-unreadable": ["date", "dates"],
  "money-sign": ["cost", "costs"],
  "money-unreadable": ["cost", "costs"],
  "currency-code": ["currency", "currencies"],
  "name-missing": ["name", "names"],
  "identifier-repeated": ["Asset Identifier", "Asset Identifiers"],
  "adopted-bid": ["BID", "BIDs"],
  "adopted-reference-id": ["reference id", "reference ids"],
  "adopted-capacity": ["capacity", "capacities"],
  "adopted-model-3d": ["3D model", "3D models"],
  "server-refused": ["row", "rows"]
};
var describeFixedKind = (kind, seen, edits, leftOut) => {
  const valueOf = (line) => line.column === null ? void 0 : edits.get(editKey(line.line, line.column));
  const out = seen.filter((line) => leftOut.has(line.line));
  const kept = seen.filter((line) => !leftOut.has(line.line) && valueOf(line) !== void 0);
  if (out.length === 0 && kept.length === 0) return null;
  const converted = new Set(
    kept.filter(
      (line) => line.fix && line.column !== null && valueOf(line) === line.fix[line.column]
    )
  );
  const inPlace = kept.filter((line) => !converted.has(line));
  const [one, many] = FIXED_NOUNS[kind];
  const title = converted.size > 0 ? `${plural(converted.size, one, many)} converted` : inPlace.length > 0 ? `${plural(inPlace.length, one, many)} fixed in place` : `${plural(new Set(out.map((line) => line.line)).size, "row", "rows")} left out`;
  const changed = [...converted, ...inPlace].sort((a, b) => a.line - b.line);
  const parts2 = [];
  if (changed.length > 0) {
    const shown = changed.slice(0, 3).map((line) => {
      const value = valueOf(line) ?? "";
      return `Line ${line.line}: "${line.found.trim()}" is now ${value.trim() === "" ? "blank" : value}`;
    });
    parts2.push(shown.join("; ") + (changed.length > 3 ? ` and ${changed.length - 3} more` : ""));
  }
  if (out.length > 0) {
    const text4 = linesText(out.map((line) => line.line));
    parts2.push(`${text4[0].toUpperCase()}${text4.slice(1)} will not be uploaded`);
  }
  return { title, where: parts2.join(". ") };
};
var LEFT_OUT_NAME_MAX = 255;
var leftOutEntries = (parsed, entries, edits, leftOut) => {
  if (leftOut.size === 0) return [];
  const holders = mappedColumns(entries).find(({ column }) => column === "name")?.holders ?? [];
  return parsed.records.filter((record) => leftOut.has(record.line)).map((record) => {
    const cell = edits.get(editKey(record.line, "name")) ?? (holders.length > 0 ? mappedCell(record.cells, holders) : "");
    const name = cell.trim();
    return {
      line: record.line,
      // Cut as the server stores it, so the field stays within the route's limit: whole
      // characters, since half a surrogate pair is JSON Postgres refuses.
      name: name === "" ? null : Array.from(name).slice(0, LEFT_OUT_NAME_MAX).join(""),
      problems: [...leftOut.get(record.line) ?? []]
    };
  });
};
var problemSentences = (check) => {
  const byLine = /* @__PURE__ */ new Map();
  for (const group of check.kinds) {
    for (const line of group.lines) {
      const sentence = line.column === null ? line.reason : `${columnLabel(line.column)}: ${line.reason}`;
      byLine.set(line.line, [...byLine.get(line.line) ?? [], sentence]);
    }
  }
  return byLine;
};
var LEFT_OUT_SENTENCE = "Left out of this upload";
var downloadProblemSentences = (parsed, entries, edits, leftOut, serverRefused = [], adoption) => {
  const byLine = problemSentences(
    checkAssetValues(parsed, entries, edits, leftOut, serverRefused, adoption)
  );
  if (leftOut.size === 0) return byLine;
  const whole = problemSentences(
    checkAssetValues(parsed, entries, edits, /* @__PURE__ */ new Map(), serverRefused, adoption)
  );
  for (const line of Array.from(leftOut.keys())) {
    byLine.set(line, [LEFT_OUT_SENTENCE, ...whole.get(line) ?? []]);
  }
  return byLine;
};
var buildProblemsCsv = (parsed, entries, edits, problems) => {
  const holderOf = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    if (entry.column !== null && !holderOf.has(entry.column))
      holderOf.set(entry.column, entry.index);
  }
  const editedColumns = Array.from(
    new Set(Array.from(edits.keys(), (key) => key.slice(key.indexOf(":") + 1)))
  );
  const extra = editedColumns.filter((column) => !holderOf.has(column));
  const header = [...parsed.headers, ...extra.map(columnLabel), "Problems"];
  const rows = parsed.records.map((record) => {
    const cells = parsed.headers.map((_, index) => record.cells[index] ?? "");
    for (const [column, index] of holderOf) {
      const edited = edits.get(editKey(record.line, column));
      if (edited !== void 0 && column !== "customer_notes") cells[index] = edited;
    }
    const added = extra.map((column) => edits.get(editKey(record.line, column)) ?? "");
    return [...cells, ...added, (problems.get(record.line) ?? []).join("; ")];
  });
  return `${[header, ...rows].map((row) => row.map(csvField).join(",")).join("\r\n")}\r
`;
};

// src/equipment/registerRowLabels.ts
var registerRowName = (row) => row.asset_name || row.name;
var catalogueMatchText = (row) => row.match_status === "unmatched" || !row.product ? null : `${row.manufacturer ? `${toDisplayManufacturer(row.manufacturer)} ` : ""}${row.product}`;

// src/equipment/assetCoverage.ts
import { normaliseAssetUid as normaliseAssetUid4 } from "@repo/frontend-types";

// src/twin-planner/entityTracking.ts
var nonBlank = (value) => typeof value === "string" && value.trim() !== "";
var trackedPlanEntities = (layout) => {
  const surfaces = /* @__PURE__ */ new Set();
  const items = /* @__PURE__ */ new Set();
  const designs = layoutCurrentDesigns(layout ?? null);
  for (let d = 0; d < designs.length; d += 1) {
    const observers = asArray(designs[d]?.observers).filter(isLive);
    for (let o = 0; o < observers.length; o += 1) {
      const { targetKind, targetRefid } = observers[o];
      if (!nonBlank(targetRefid)) continue;
      if (targetKind === "surface") surfaces.add(targetRefid);
      else if (targetKind === "item") items.add(targetRefid);
    }
  }
  const composites = asArray(layout?.composites).filter(isLive);
  for (let c = 0; c < composites.length; c += 1) {
    const composite = composites[c];
    if (composite.excluded === true) continue;
    const target = composite.target;
    if (target?.kind !== "entity" || !nonBlank(target.refid)) continue;
    if (target.entityKind === "surface") surfaces.add(target.refid);
    else if (target.entityKind === "item") items.add(target.refid);
  }
  return { surfaces, items };
};

// src/equipment/assetCoverage.ts
var buildAssetCoverageIndex = (layout, assets, options = {}) => {
  const designs = layoutCurrentDesigns(layout);
  const items = designs.flatMap((d) => asArray(d?.items)).filter(isLive);
  const planKnown = layout != null || options.planAbsent === true;
  const trackedItems = trackedPlanEntities(layout).items;
  const placementsByAssetKey = /* @__PURE__ */ new Map();
  let orphanPlacementCount = 0;
  for (const item of items) {
    const key = normaliseAssetUid4(item.assetUid);
    if (key === void 0) {
      orphanPlacementCount += 1;
      continue;
    }
    const refid = typeof item.refid === "string" ? item.refid : "";
    const existing = placementsByAssetKey.get(key);
    if (existing) existing.push(refid);
    else placementsByAssetKey.set(key, [refid]);
  }
  const statusByAssetKey = /* @__PURE__ */ new Map();
  let trackedCount = 0;
  let onPlanCount = 0;
  const claimedKeys = /* @__PURE__ */ new Set();
  for (const asset of assets) {
    const key = normaliseAssetUid4(asset.id);
    if (key === void 0) continue;
    claimedKeys.add(key);
    const placements = placementsByAssetKey.get(key);
    if (!placements || placements.length === 0) {
      statusByAssetKey.set(key, "not-on-plan");
      continue;
    }
    onPlanCount += 1;
    const tracked = placements.some((refid) => trackedItems.has(refid));
    statusByAssetKey.set(key, tracked ? "on-plan-tracked" : "on-plan-untracked");
    if (tracked) trackedCount += 1;
  }
  placementsByAssetKey.forEach((placements, key) => {
    if (!claimedKeys.has(key)) orphanPlacementCount += placements.length;
  });
  return {
    statusFor: (assetId) => {
      const key = normaliseAssetUid4(assetId);
      return (key !== void 0 ? statusByAssetKey.get(key) : void 0) ?? "not-on-plan";
    },
    placementsFor: (assetId) => {
      const key = normaliseAssetUid4(assetId);
      if (key === void 0 || !claimedKeys.has(key)) return [];
      return placementsByAssetKey.get(key) ?? [];
    },
    isPlacementKnown: (assetId) => {
      if (!planKnown) return false;
      const key = normaliseAssetUid4(assetId);
      return key !== void 0 && claimedKeys.has(key);
    },
    trackedCount,
    onPlanCount,
    orphanPlacementCount,
    planKnown
  };
};
var ASSET_COVERAGE_LABEL = {
  "not-on-plan": "Not on plan",
  "on-plan-untracked": "On plan, not tracked",
  "on-plan-tracked": "On plan & tracked"
};

// src/equipment/mirrorRegisterOntoPlan.ts
import {
  normaliseAssetUid as normaliseAssetUid5
} from "@repo/frontend-types";
var mirrorIndexCache = /* @__PURE__ */ new WeakMap();
var mirrorIndexFor = (rows) => {
  const cached = mirrorIndexCache.get(rows);
  if (cached) return cached;
  const index = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = normaliseAssetUid5(row?.id);
    if (key !== void 0) index.set(key, row);
  }
  mirrorIndexCache.set(rows, index);
  return index;
};
var patchFor = (item, row) => {
  const patch = {};
  let changed = false;
  if (row.assetName !== null && row.assetName.trim() !== "" && row.assetName !== item.name) {
    patch.name = row.assetName;
    changed = true;
  }
  if (row.modelFile !== null && row.modelFile !== (item.modelFile ?? null)) {
    patch.modelFile = row.modelFile;
    changed = true;
  }
  return changed ? patch : null;
};
var withPatch = (item, patch) => ({
  ...item,
  ...patch
});
var mirrorRegisterOntoPlan = (layout, rows) => {
  if (!layout) return layout;
  const floors = asArray(layout.floors);
  if (floors.length === 0 || rows.length === 0) return layout;
  const rowsByAssetKey = mirrorIndexFor(rows);
  if (rowsByAssetKey.size === 0) return layout;
  let layoutChanged = false;
  const nextFloors = floors.map((floor) => {
    const designs = asArray(floor?.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) return floor;
    const items = asArray(design.items);
    let designChanged = false;
    const nextItems = items.map((item) => {
      if (!isLive(item)) return item;
      const key = normaliseAssetUid5(item.assetUid);
      if (key === void 0) return item;
      const row = rowsByAssetKey.get(key);
      if (!row) return item;
      const patch = patchFor(item, row);
      if (!patch) return item;
      designChanged = true;
      return withPatch(item, patch);
    });
    if (!designChanged) return floor;
    layoutChanged = true;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = { ...design, items: nextItems };
    return { ...floor, designs: nextDesigns };
  });
  return layoutChanged ? { ...layout, floors: nextFloors } : layout;
};

// src/equipment/joinRegisterOntoPlan.ts
import {
  normaliseAssetUid as normaliseAssetUid6
} from "@repo/frontend-types";
var reservedRegisterIdentities = (rows, twinBid) => {
  const reserved = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const identity2 = entityIdentityFor(twinBid, row?.entity_code);
    if (identity2 !== "") reserved.add(identity2);
  }
  return reserved;
};
var registerIndexCache = /* @__PURE__ */ new WeakMap();
var registerIndexFor = (rows, twinKey) => {
  const cached = registerIndexCache.get(rows);
  if (cached && cached.twinKey === twinKey) return cached.index;
  const byIdentity = /* @__PURE__ */ new Map();
  const contested = /* @__PURE__ */ new Set();
  const keys = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const assetKey = normaliseAssetUid6(row?.id);
    if (assetKey === void 0) continue;
    keys.add(assetKey);
    const identity2 = entityIdentityFor(twinKey, row?.entity_code);
    if (identity2 === "") continue;
    if (contested.has(identity2)) continue;
    const existing = byIdentity.get(identity2);
    if (existing !== void 0 && existing !== assetKey) {
      byIdentity.delete(identity2);
      contested.add(identity2);
      continue;
    }
    byIdentity.set(identity2, assetKey);
  }
  const index = { byIdentity, contested, keys };
  registerIndexCache.set(rows, { twinKey, index });
  return index;
};
var planIdentities = (layout, twinKey) => {
  const byItem = /* @__PURE__ */ new Map();
  const seen = /* @__PURE__ */ new Set();
  const contested = /* @__PURE__ */ new Set();
  const claimed = /* @__PURE__ */ new Set();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const item of asArray(design?.items)) {
      if (!isLive(item)) continue;
      const existing = normaliseAssetUid6(item.assetUid);
      if (existing !== void 0) claimed.add(existing);
      const identity2 = entityIdentityFor(twinKey, item.entityCode);
      if (identity2 === "") continue;
      byItem.set(item, identity2);
      if (seen.has(identity2)) contested.add(identity2);
      else seen.add(identity2);
    }
  }
  return { byItem, contested, claimed };
};
var joinRegisterOntoPlan = (layout, rows, twinBid, { repairStaleLinks = false } = {}) => {
  if (!layout) return layout;
  const twinKey = entityIdentityKey(twinBid);
  if (twinKey === "") return layout;
  const floors = asArray(layout.floors);
  if (floors.length === 0 || rows.length === 0) return layout;
  const index = registerIndexFor(rows, twinKey);
  if (index.byIdentity.size === 0) return layout;
  const plan = planIdentities(layout, twinKey);
  if (plan.byItem.size === 0) return layout;
  let layoutChanged = false;
  const nextFloors = floors.map((floor) => {
    const designs = asArray(floor?.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) return floor;
    const items = asArray(design.items);
    let designChanged = false;
    const nextItems = items.map((item) => {
      if (!isLive(item)) return item;
      const existing = normaliseAssetUid6(item.assetUid);
      if (existing !== void 0 && (!repairStaleLinks || index.keys.has(existing))) return item;
      const identity2 = plan.byItem.get(item);
      if (identity2 === void 0) return item;
      if (plan.contested.has(identity2)) return item;
      const assetKey = index.byIdentity.get(identity2);
      if (assetKey === void 0) return item;
      if (plan.claimed.has(assetKey)) return item;
      designChanged = true;
      return { ...item, assetUid: assetKey };
    });
    if (!designChanged) return floor;
    layoutChanged = true;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = { ...design, items: nextItems };
    return { ...floor, designs: nextDesigns };
  });
  return layoutChanged ? { ...layout, floors: nextFloors } : layout;
};
var EMPTY_DRIFT = {
  missing: [],
  missingCount: 0,
  contestedOnPlan: 0,
  contestedInRegister: 0,
  rowAlreadyPlaced: 0,
  staleLink: 0,
  staleLinkNoIdentity: 0,
  unaccountable: 0
};
var diagnosePlanRegisterDrift = (layout, rows, twinBid) => {
  if (!layout) return EMPTY_DRIFT;
  const twinKey = entityIdentityKey(twinBid);
  if (twinKey === "") return EMPTY_DRIFT;
  const floors = asArray(layout.floors);
  if (floors.length === 0) return EMPTY_DRIFT;
  const index = registerIndexFor(rows, twinKey);
  const plan = planIdentities(layout, twinKey);
  const missing = [];
  const seenIdentities = /* @__PURE__ */ new Set();
  let missingCount = 0;
  let contestedOnPlan = 0;
  let contestedInRegister = 0;
  let rowAlreadyPlaced = 0;
  let staleLink = 0;
  let staleLinkNoIdentity = 0;
  let unaccountable = 0;
  for (const design of layoutCurrentDesigns(layout)) {
    for (const item of asArray(design?.items)) {
      if (!isLive(item)) continue;
      const existing = normaliseAssetUid6(item.assetUid);
      if (existing !== void 0) {
        if (index.keys.has(existing)) continue;
        staleLink += 1;
        if (plan.byItem.get(item) === void 0) staleLinkNoIdentity += 1;
        continue;
      }
      const identity2 = plan.byItem.get(item);
      if (identity2 === void 0) {
        unaccountable += 1;
        continue;
      }
      if (plan.contested.has(identity2)) {
        contestedOnPlan += 1;
        continue;
      }
      if (index.contested.has(identity2)) {
        contestedInRegister += 1;
        continue;
      }
      if (index.byIdentity.get(identity2) !== void 0) {
        rowAlreadyPlaced += 1;
        continue;
      }
      const name = typeof item.name === "string" ? item.name.trim() : "";
      if (name === "") {
        unaccountable += 1;
        continue;
      }
      missingCount += 1;
      if (seenIdentities.has(identity2)) continue;
      seenIdentities.add(identity2);
      const rawModel = typeof item.modelFile === "string" ? item.modelFile.trim() : "";
      missing.push({ identity: identity2, name, modelFile: rawModel === "" ? null : rawModel });
    }
  }
  return {
    missing,
    missingCount,
    contestedOnPlan,
    contestedInRegister,
    rowAlreadyPlaced,
    staleLink,
    staleLinkNoIdentity,
    unaccountable
  };
};

// src/equipment/mirrorRegisterThroughJoin.ts
var mirrorRegisterThroughJoin = (layout, joinRows, mirrorRows, twinBid, options = {}) => {
  if (!layout) return layout;
  const view = joinRegisterOntoPlan(layout, joinRows, twinBid, options);
  const mirrored = mirrorRegisterOntoPlan(view, mirrorRows);
  if (!view || !mirrored || mirrored === view) return layout;
  const viewFloors = asArray(view.floors);
  const mirroredFloors = asArray(mirrored.floors);
  let layoutChanged = false;
  const nextFloors = asArray(layout.floors).map((floor, f) => {
    if (mirroredFloors[f] === viewFloors[f]) return floor;
    const designs = asArray(floor?.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) return floor;
    const viewItems = asArray(asArray(viewFloors[f]?.designs)[designIndex]?.items);
    const mirroredItems = asArray(asArray(mirroredFloors[f]?.designs)[designIndex]?.items);
    let designChanged = false;
    const nextItems = asArray(design.items).map((item, i) => {
      const after = mirroredItems[i];
      if (!isLive(item) || after === viewItems[i] || !after) return item;
      const nameChanged = after.name !== item.name;
      const modelChanged = (after.modelFile ?? null) !== (item.modelFile ?? null);
      if (!nameChanged && !modelChanged) return item;
      designChanged = true;
      const patch = {
        ...nameChanged ? { name: after.name } : {},
        ...modelChanged ? { modelFile: after.modelFile } : {}
      };
      return withPatch(item, patch);
    });
    if (!designChanged) return floor;
    layoutChanged = true;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = { ...design, items: nextItems };
    return { ...floor, designs: nextDesigns };
  });
  return layoutChanged ? { ...layout, floors: nextFloors } : layout;
};

// src/equipment/clearPlanModelThroughJoin.ts
import {
  normaliseAssetUid as normaliseAssetUid7
} from "@repo/frontend-types";
var clearPlanModelThroughJoin = (layout, joinRows, twinBid, assetId, options = {}) => {
  if (!layout) return layout;
  const target = normaliseAssetUid7(assetId);
  if (target === void 0) return layout;
  const view = joinRegisterOntoPlan(layout, joinRows, twinBid, options);
  const viewFloors = asArray(view?.floors);
  let layoutChanged = false;
  const nextFloors = asArray(layout.floors).map((floor, f) => {
    const designs = asArray(floor?.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) return floor;
    const viewItems = asArray(asArray(viewFloors[f]?.designs)[designIndex]?.items);
    let designChanged = false;
    const nextItems = asArray(design.items).map((item, i) => {
      if (!isLive(item) || (item.modelFile ?? null) === null) return item;
      if (normaliseAssetUid7(viewItems[i]?.assetUid) !== target) return item;
      designChanged = true;
      return { ...item, modelFile: null };
    });
    if (!designChanged) return floor;
    layoutChanged = true;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = { ...design, items: nextItems };
    return { ...floor, designs: nextDesigns };
  });
  return layoutChanged ? { ...layout, floors: nextFloors } : layout;
};

// src/equipment/registerRowsForMirror.ts
import { isModelReference } from "@repo/frontend-types";
var flatShareFilename = (value) => {
  if (typeof value !== "string") return null;
  const name = value.trim();
  if (name === "") return null;
  return isModelReference(name) ? name : null;
};
var displayName = (value) => {
  if (typeof value !== "string") return null;
  return value.trim() === "" ? null : value;
};
var registerRowsForMirror = (rows) => rows.map((row) => ({
  id: row.id,
  assetName: displayName(row.asset_name),
  // `flatShareFilename` already answers `null` for an absent value, so this keeps the
  // "never `undefined`" contract `FloorPlannerItem.modelFile` depends on.
  modelFile: flatShareFilename(row.model_3d)
}));

// src/twin-planner/entityBidMint.ts
var MAX_ENTITY_SEQUENCE = 999;
var TAIL = /^([A-Z])(\d{1,})$/;
var claimOf = (raw, twinBid) => {
  if (typeof raw !== "string") return null;
  const identity2 = entityIdentityFor(twinBid, raw);
  if (identity2 === "") return null;
  const twin = entityIdentityKey(twinBid);
  const tail = twin === "" ? identity2 : stripTwinBidPrefix(identity2, twin, twin);
  const match = TAIL.exec(tail);
  if (!match) return null;
  const seq = Number(match[2]);
  return Number.isSafeInteger(seq) && seq > 0 ? { letter: match[1], seq } : null;
};
var raise = (mark, raw, twinBid) => {
  const claim = claimOf(raw, twinBid);
  if (!claim) return;
  const current = mark[claim.letter];
  if (current === void 0 || claim.seq > current) mark[claim.letter] = claim.seq;
};
var highWaterMarkFor = (layout, liveEntityBids, reservedIdentities, twinBid) => {
  const mark = /* @__PURE__ */ Object.create(null);
  const floorsInPlay = [
    ...asArray(layout?.floors),
    ...asArray(layout?.removedFloors).map((record) => record?.floor)
  ];
  for (const floor of floorsInPlay) {
    for (const design of asArray(floor?.designs)) {
      const entries = [
        ...asArray(design?.surfaces),
        ...asArray(design?.items)
      ].filter(isLive);
      for (const entry of entries) {
        raise(mark, entry.entityCode, twinBid);
        raise(mark, entry.refid, twinBid);
      }
    }
  }
  Array.from(liveEntityBids ?? []).forEach((bid) => raise(mark, bid, twinBid));
  Array.from(reservedIdentities ?? []).forEach((identity2) => raise(mark, identity2, twinBid));
  asArray(layout?.retiredEntityCodes).forEach((code) => raise(mark, code, twinBid));
  return mark;
};
var describeEntityExhaustion = (twin, letter) => `${twin || "this twin"} has used every ${letter} number up to ${letter}${MAX_ENTITY_SEQUENCE}, so no new one can be created. Retired numbers are never reused.`;
var mintEntityBid = (twinBid, letter, mark) => {
  const prefix = entityIdentityKey(letter);
  if (!/^[A-Z]$/.test(prefix)) {
    throw new Error(
      `mintEntityBid: letter must be a single letter, got ${JSON.stringify(letter)}. The legal letters are Z (zone), S (section), A (area) and E (equipment).`
    );
  }
  const next = (mark[prefix] ?? 0) + 1;
  if (next > MAX_ENTITY_SEQUENCE) {
    return {
      ok: false,
      reason: "exhausted",
      message: describeEntityExhaustion(entityIdentityKey(twinBid), prefix)
    };
  }
  const bid = entityIdentityFor(twinBid, `${prefix}${String(next).padStart(3, "0")}`);
  return { ok: true, bid };
};

// src/twin-planner/unverifiedMintBlock.ts
var planIdentities2 = (layout, twinBid) => {
  const identities = /* @__PURE__ */ new Set();
  for (const floor of asArray(layout?.floors)) {
    for (const design of asArray(floor?.designs)) {
      const entries = [
        ...asArray(design?.surfaces),
        ...asArray(design?.items)
      ].filter(isLive);
      for (const entry of entries) {
        const identity2 = entityIdentityFor(twinBid ?? "", entry.entityCode || entry.refid);
        if (identity2 !== "") identities.add(entityIdentityKey(identity2));
      }
    }
  }
  return identities;
};
var unverifiedMintBlock = (state2, twinBid) => {
  const stillOnPlan = planIdentities2(state2.layout, twinBid);
  const collided = state2.collidingMints.filter((bid) => stillOnPlan.has(entityIdentityKey(bid)));
  if (collided.length > 0) {
    const named = collided.slice(0, 3).join(", ");
    const more = collided.length > 3 ? ` and ${collided.length - 3} more` : "";
    return {
      reason: "collided",
      message: `This twin already has equipment using ${named}${more}, so saving would merge two machines into one. Change the identity on the newly placed equipment, then save.`
    };
  }
  if (state2.unverifiedMints.length > 0) {
    return {
      reason: "unverified",
      message: "This twin's existing equipment could not be read, so newly placed equipment may reuse an identity that already exists. Retry, then save."
    };
  }
  return null;
};

// src/twin-planner/emptyLayout.ts
var DEFAULT_FLOOR_HEIGHT = 3;
var FLOOR_NAMES_BY_LEVEL = [
  "Ground floor",
  "First floor",
  "Second floor",
  "Third floor",
  "Fourth floor",
  "Fifth floor",
  "Sixth floor",
  "Seventh floor",
  "Eighth floor",
  "Ninth floor",
  "Tenth floor"
];
var defaultFloorName = (level) => FLOOR_NAMES_BY_LEVEL[level] ?? `Floor ${level + 1}`;
function createEmptyFloor(level, id, opts) {
  return {
    id,
    name: opts?.name ?? defaultFloorName(level),
    level,
    height: opts?.height ?? DEFAULT_FLOOR_HEIGHT,
    designs: [
      {
        name: "Current",
        areas: [],
        surfaces: [],
        items: [],
        walls: [],
        dimensions: [],
        labels: [],
        lines: []
      }
    ]
  };
}
function createEmptyLayout(name) {
  return {
    name,
    public: false,
    floors: [createEmptyFloor(0, 1)]
  };
}

// src/twin-planner/planEntitySearch.ts
var text2 = (v) => typeof v === "string" ? v.trim() : "";
var lowerFields = (values) => {
  const out = [];
  for (const v of values) {
    const t = text2(v).toLowerCase();
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
};
var planSearchEntry = (entry) => {
  const { searchable, ...rest } = entry;
  return { ...rest, fields: lowerFields(searchable) };
};
function buildPlanSearchIndex(layout, {
  registerByAssetUid,
  catalogueByReference,
  include = ["surface", "item", "camera"],
  extra = [],
  register
} = {}) {
  const entries = [];
  const floors = asArray(layout?.floors);
  floors.forEach((floor, floorIndex) => {
    if (!floor) return;
    const design = findLatestDesign(asArray(floor.designs));
    if (!design) return;
    const floorName = text2(floor.name) || null;
    if (include.includes("surface")) {
      for (const surface of asArray(design.surfaces).filter(isLive)) {
        if (typeof surface.refid !== "string" || !surface.refid) continue;
        const code = text2(surface.entityCode);
        entries.push({
          refid: surface.refid,
          kind: "surface",
          floorIndex,
          floorName,
          label: surfaceDisplayName(surface),
          code,
          detail: "",
          fields: lowerFields([surface.name, surface.customName])
        });
      }
    }
    if (include.includes("item")) {
      for (const item of asArray(design.items).filter(isLive)) {
        if (typeof item.refid !== "string" || !item.refid) continue;
        const code = text2(item.entityCode);
        const row = item.assetUid ? registerByAssetUid?.get(String(item.assetUid)) : void 0;
        const facts = registerFacts(row, catalogueByReference);
        entries.push({
          refid: item.refid,
          kind: "item",
          floorIndex,
          floorName,
          label: itemDisplayName(item),
          code,
          detail: facts.type || facts.manufacturer,
          customerId: facts.customerId,
          fields: lowerFields([item.name, ...facts.searchable])
        });
      }
    }
    if (include.includes("camera")) {
      asArray(design.cameras).forEach((camera, index) => {
        if (!isLive(camera) || typeof camera.refid !== "string" || !camera.refid) return;
        const label = cameraDisplayName(camera, index);
        entries.push({
          refid: camera.refid,
          kind: "camera",
          floorIndex,
          floorName,
          label,
          code: "",
          detail: "",
          // `cameraHasStream`'s test (in @repo/ui, which this package does not depend on).
          linked: camera.rtspEndpointId != null,
          fields: lowerFields([label])
        });
      });
    }
  });
  if (register) {
    for (const row of register.rows) {
      const state2 = register.planStateOf(row);
      if (state2 !== "not_on_plan" && state2 !== "keep_off_plan") continue;
      entries.push(registerEntry(row, state2, catalogueByReference));
    }
  }
  return entries.concat(extra);
}
var registerEntry = (row, planState, catalogueByReference) => {
  const facts = registerFacts(row, catalogueByReference);
  return {
    refid: `register:${row.id}`,
    kind: "register",
    floorIndex: null,
    floorName: null,
    label: text2(row.asset_name) || text2(row.name),
    code: text2(row.entity_code),
    detail: facts.type || text2(row.product) || facts.manufacturer,
    customerId: facts.customerId,
    sourceId: text2(row.source_asset_id),
    planState,
    fields: lowerFields([
      row.asset_name,
      row.name,
      row.product_line_raw,
      row.model_raw,
      ...facts.searchable
    ])
  };
};
var registerFacts = (row, catalogueByReference) => {
  const reference = text2(row?.reference_id);
  const catalogue = reference ? catalogueByReference?.get(reference) : void 0;
  const manufacturer = text2(row?.manufacturer) || text2(row?.manufacturer_raw);
  return {
    manufacturer,
    type: text2(catalogue?.equipment_type) || text2(row?.category_raw),
    customerId: text2(row?.customer_asset_identifier),
    searchable: [
      manufacturer,
      row?.manufacturer_raw,
      row?.product_line,
      row?.product,
      row?.category_raw,
      catalogue?.equipment_type,
      catalogue?.equipment_variation,
      row?.customer_asset_identifier,
      row?.source_asset_id
    ]
  };
};
var NATURAL = new Intl.Collator(void 0, { numeric: true, sensitivity: "base" });
var normalisePlanQuery = (query) => query.trim().replace(/\s+/g, " ").toLowerCase();
var codeTail = (raw, twinPrefix) => {
  let tail = raw.toLowerCase();
  while (twinPrefix && tail.startsWith(twinPrefix)) tail = tail.slice(twinPrefix.length);
  return tail;
};
function matchPlanEntities(index, query, { twinBid, limit = 50 } = {}) {
  const q = normalisePlanQuery(query);
  if (!q) return { hits: [], total: 0 };
  const words2 = q.split(" ");
  const identity2 = (raw) => twinBid ? entityIdentityFor(twinBid, raw) : entityIdentityKey(raw);
  const queryIdentity = identity2(q);
  const twinPrefix = twinBid ? `${twinBid.trim().toLowerCase()}-` : "";
  const wordTails = words2.map((w) => codeTail(w, twinPrefix));
  const scored = [];
  for (const entry of index) {
    const codeKey = entry.code ? entityIdentityKey(entry.code) : "";
    const tail = entry.code ? codeTail(entry.code, twinPrefix) : "";
    let codeWord = false;
    let all = true;
    for (let i = 0; i < words2.length; i++) {
      const word = words2[i];
      const inCode = !!tail && !!wordTails[i] && tail.includes(wordTails[i]);
      if (inCode) {
        codeWord = true;
        continue;
      }
      if (!entry.fields.some((f) => f.includes(word))) {
        all = false;
        break;
      }
    }
    if (!all) continue;
    const tier = codeKey && queryIdentity === codeKey ? 0 : entry.label.toLowerCase().startsWith(q) ? 1 : codeWord ? 2 : 3;
    scored.push({ entry, tier });
  }
  const registerLast = (e) => e.kind === "register" ? 1 : 0;
  scored.sort(
    (a, b) => registerLast(a.entry) - registerLast(b.entry) || a.tier - b.tier || (a.entry.floorIndex ?? Number.MAX_SAFE_INTEGER) - (b.entry.floorIndex ?? Number.MAX_SAFE_INTEGER) || NATURAL.compare(a.entry.label, b.entry.label)
  );
  return { hits: scored.slice(0, limit).map((s) => s.entry), total: scored.length };
}

// src/twin-planner/floorTargets.ts
var FLOOR_REFID_PREFIX = "floor:";
var floorTargetRefid = (floorId) => `${FLOOR_REFID_PREFIX}${floorId}`;
var floorIdFromTargetRefid = (refid) => {
  if (typeof refid !== "string" || !refid.startsWith(FLOOR_REFID_PREFIX)) return null;
  const tail = refid.slice(FLOOR_REFID_PREFIX.length);
  if (!/^\d+$/.test(tail)) return null;
  return Number(tail);
};
var floorLaunchBid = (twinBid, floorId) => `${twinBid}-floor-${floorId}`;
var isFloorLaunched = (floor, twinBid, live2) => {
  const twinKey = typeof twinBid === "string" ? twinBid.trim() : "";
  if (twinKey === "") return "unknown";
  if (live2.status !== "ready") return "unknown";
  if (live2.bids.has(floorLaunchBid(twinBid, floor.id))) return true;
  const idPrefix = `${twinBid}-floor-`;
  let idKeyed = false;
  live2.bids.forEach((bid) => {
    if (!idKeyed && bid.startsWith(idPrefix)) idKeyed = true;
  });
  if (idKeyed || live2.bids.size === 0) return false;
  const legacy = floorEntityBid(twinBid, floor.name);
  return legacy !== null && live2.bids.has(legacy) ? true : "unknown";
};
var floorEntityBid = (twinBid, floorName) => {
  const name = (floorName ?? "").trim();
  if (!name || !twinBid) return null;
  return `${twinBid}-${name}`;
};
var fileableFloorBids = (layout, twinBid) => {
  const byFold = /* @__PURE__ */ new Map();
  const bids = /* @__PURE__ */ new Map();
  for (const floor of asArray(layout?.floors)) {
    if (typeof floor?.id !== "number") continue;
    const bid = floorEntityBid(twinBid, floor.name);
    if (!bid) continue;
    bids.set(floor.id, bid);
    const fold = bid.toUpperCase();
    byFold.set(fold, [...byFold.get(fold) ?? [], floor.id]);
  }
  byFold.forEach((ids) => {
    if (ids.length > 1) ids.forEach((id) => bids.delete(id));
  });
  return bids;
};
var withFloorTargetBids = (refidToBid, layout, twinBid) => {
  const withFloors = { ...refidToBid };
  fileableFloorBids(layout, twinBid).forEach((bid, floorId) => {
    withFloors[floorTargetRefid(floorId)] = bid;
  });
  return withFloors;
};
var observerTargetNamesByRefid = (layout) => {
  const names = entityNamesByRefid(layout);
  for (const floor of asArray(layout?.floors)) {
    if (typeof floor?.id !== "number") continue;
    const name = typeof floor.name === "string" ? floor.name.trim() : "";
    if (name) names.set(floorTargetRefid(floor.id), name);
  }
  return names;
};

// src/twin-planner/planHierarchy.ts
var KIND_LETTER = {
  zone: ENTITY_TYPES.ZONE,
  section: ENTITY_TYPES.SECTION,
  area: ENTITY_TYPES.AREA
};
var kindOfLetter = (letter) => {
  switch (letter) {
    case ENTITY_TYPES.ZONE:
      return "zone";
    case ENTITY_TYPES.SECTION:
      return "section";
    case ENTITY_TYPES.AREA:
      return "area";
    default:
      return null;
  }
};
var kindInside = (kind) => kind === "zone" ? "section" : kind === "section" ? "area" : null;
var TAIL2 = /(?:^|-)([A-Z])(\d+)$/;
var letterOfCode = (code) => {
  const key = entityIdentityKey(code);
  if (key === "") return null;
  const match = TAIL2.exec(key);
  return match ? match[1] : "?";
};
var cache2 = /* @__PURE__ */ new WeakMap();
var resolvePlanHierarchy = (design) => {
  if (design) {
    const cached = cache2.get(design);
    if (cached) return cached;
  }
  const result = derive2(design);
  if (design) cache2.set(design, result);
  return result;
};
var derive2 = (design) => {
  const parentage = resolvePlanParentage(design);
  const surfaces = firstLiveSurfaces(design?.surfaces);
  const byRefid = /* @__PURE__ */ new Map();
  surfaces.forEach((s) => byRefid.set(s.refid, s));
  const answers = /* @__PURE__ */ new Map();
  const visiting = /* @__PURE__ */ new Set();
  const answerFor = (refid) => {
    const known = answers.get(refid);
    if (known) return known;
    const surface = byRefid.get(refid);
    if (!surface || visiting.has(refid)) {
      return { kind: null, placeKind: null, letter: "A" };
    }
    visiting.add(refid);
    const parent = parentage.surface(refid)?.effectiveParent ?? null;
    let placeKind;
    if (parent === null) {
      placeKind = "zone";
    } else {
      const parentAnswer = answerFor(parent);
      placeKind = kindInside(parentAnswer.kind ?? parentAnswer.placeKind);
    }
    const letter = letterOfCode(surface.entityCode);
    const kind = letter === null ? placeKind : kindOfLetter(letter);
    const answer = {
      kind,
      placeKind,
      letter: placeKind ? KIND_LETTER[placeKind] : KIND_LETTER.area
    };
    visiting.delete(refid);
    answers.set(refid, answer);
    return answer;
  };
  const problems = [];
  surfaces.forEach((s) => {
    const answer = answerFor(s.refid);
    const letter = letterOfCode(s.entityCode);
    if (letter !== null && kindOfLetter(letter) === null) {
      problems.push({ code: "invalid-letter", refid: s.refid, entity: "surface" });
    }
    if (answer.placeKind === null) {
      problems.push({ code: "too-deep", refid: s.refid, entity: "surface" });
    } else if (answer.kind !== null && letter !== null && answer.kind !== answer.placeKind) {
      problems.push({ code: "letter-mismatch", refid: s.refid, entity: "surface" });
    }
  });
  const itemParents = /* @__PURE__ */ new Map();
  asArray(design?.items).forEach((it) => {
    if (!isLive(it)) return;
    const parent = parentage.item(it)?.effectiveParent ?? null;
    itemParents.set(it, { parent });
    const letter = letterOfCode(it.entityCode);
    if (letter !== null && letter !== ENTITY_TYPES.EQUIPMENT) {
      problems.push({ code: "invalid-letter", refid: it.refid, entity: "item" });
    }
    if (parent === null) {
      problems.push({ code: "equipment-on-floor", refid: it.refid, entity: "item" });
    }
  });
  return {
    surface: (refid) => byRefid.has(refid) ? answerFor(refid) : void 0,
    item: (it) => itemParents.get(it),
    problems
  };
};
var PROSPECTIVE_REFID = "\0prospective-space";
var kindOfNewSpace = (design, poly) => {
  const prospective = { refid: PROSPECTIVE_REFID, poly };
  return kindUnder(
    resolvePlanHierarchy(design),
    spatialParentOfSurface(prospective, firstLiveSurfaces(design?.surfaces))
  );
};
var kindUnder = (hierarchy, parent) => {
  if (parent === null) return "zone";
  const answer = hierarchy.surface(parent);
  return answer ? kindInside(answer.kind ?? answer.placeKind) : null;
};
var allowedLetterFor = (hierarchy, target) => "item" in target ? ENTITY_TYPES.EQUIPMENT : hierarchy?.surface(target.surfaceRefid)?.letter ?? KIND_LETTER.area;
var LEGAL_TAIL = /(?:^|-)([A-Z])(\d{3})$/;
var isCompleteWrongLetter = (value, allowedLetter) => {
  const match = LEGAL_TAIL.exec(entityIdentityKey(value));
  return !!match && match[1] !== allowedLetter;
};
var checkTypedEntityCode = (value, allowedLetter, kindLabel, twinBid) => {
  const key = entityIdentityKey(value);
  if (key === "") return null;
  const twin = entityIdentityKey(twinBid);
  const tail = /^([A-Z])(\d{3})$/;
  const short = twin && key.startsWith(`${twin}-`) ? key.slice(twin.length + 1) : key;
  const match = twin ? tail.exec(short) : LEGAL_TAIL.exec(key);
  if (match && match[1] === allowedLetter) return null;
  const example = `${twin ? `${twin}-` : ""}${allowedLetter}001`;
  const article = /^[aeiou]/i.test(kindLabel) ? "an" : "a";
  const capitalised = article.charAt(0).toUpperCase() + article.slice(1);
  return `${value.trim()} isn't ${article} ${kindLabel} BID. ${capitalised} ${kindLabel}'s BID is the twin's BID, then ${allowedLetter} and three digits, such as ${example}.`;
};

// src/twin-planner/compositeRefs.ts
var SEP = String.fromCharCode(31);
var encodeRef = (ref) => {
  if (ref == null || typeof ref !== "object") return "";
  if (ref.kind === "twin") return "twin";
  if (ref.kind === "floor") {
    return Number.isInteger(ref.floorId) ? `floor${SEP}${ref.floorId}` : "";
  }
  if (ref.kind === "entity") {
    if (typeof ref.refid !== "string" || ref.refid === "") return "";
    if (ref.entityKind !== "surface" && ref.entityKind !== "item") return "";
    return `entity${SEP}${ref.entityKind}${SEP}${ref.refid}`;
  }
  return "";
};
var decodeRef = (value) => {
  if (value === "twin") return { kind: "twin" };
  const first = value.indexOf(SEP);
  if (first < 0) return null;
  const kind = value.slice(0, first);
  const rest = value.slice(first + 1);
  if (kind === "floor") {
    const floorId = Number(rest);
    return Number.isInteger(floorId) && rest.trim() !== "" ? { kind: "floor", floorId } : null;
  }
  if (kind === "entity") {
    const second = rest.indexOf(SEP);
    if (second < 0) return null;
    const entityKind = rest.slice(0, second);
    const refid = rest.slice(second + 1);
    if (refid === "") return null;
    if (entityKind !== "surface" && entityKind !== "item") return null;
    return { kind: "entity", refid, entityKind };
  }
  return null;
};
var LIVE_ENTRY_MARKER = ":live-";
var isLoadedComposite = (composite) => typeof composite?.refid === "string" && composite.refid.includes(LIVE_ENTRY_MARKER);
var observerTargetRef = (observer) => {
  if (typeof observer.targetRefid !== "string" || !observer.targetRefid) return null;
  if (observer.targetKind === "floor") {
    const floorId = floorIdFromTargetRefid(observer.targetRefid);
    return floorId === null ? null : { kind: "floor", floorId };
  }
  return {
    kind: "entity",
    refid: observer.targetRefid,
    entityKind: observer.targetKind === "item" ? "item" : "surface"
  };
};
var isFileableObserver = (observer) => (observer.points?.length ?? 0) >= 3 && observer.rtspEndpointId != null;
var newCompositeRefid = () => newClientId({ uuidPrefix: "cmp:", fallbackPrefix: "cmp:" });
var sameRefList = (a, b) => a.length === b.length && a.every((ref, i) => encodeRef(ref) === encodeRef(b[i]));

// src/twin-planner/removePlacementsFromLayout.ts
var NOTHING = {
  items: 0,
  observers: 0,
  compositesAmended: 0,
  compositesRemoved: 0
};
var refersToRemoved = (ref, { refids, floorIds }) => !!ref && (ref.kind === "entity" && refids.has(ref.refid) || ref.kind === "floor" && !!floorIds && floorIds.has(ref.floorId));
var pruneCompositesForRemovedRefs = (composites, removedRefs, {
  isLoadedComposite: isLoadedComposite2 = isLoadedComposite
} = {}) => {
  const input = asArray(composites);
  let amended = 0;
  let removed = 0;
  const out = [];
  for (const composite of input) {
    if (!isLive(composite)) {
      out.push(composite);
      continue;
    }
    const inputs = asArray(composite.inputs);
    const keptInputs = inputs.filter((i) => !refersToRemoved(i, removedRefs));
    const lostInputs = keptInputs.length < inputs.length;
    const targetGone = refersToRemoved(composite.target, removedRefs);
    const emptied = lostInputs && keptInputs.length === 0 && composite.includesOwnObservers !== true;
    if (targetGone || emptied) {
      removed += 1;
      if (isLoadedComposite2(composite)) out.push({ ...composite, _deleted: true });
      continue;
    }
    if (lostInputs) {
      amended += 1;
      out.push({ ...composite, inputs: keptInputs });
      continue;
    }
    out.push(composite);
  }
  const changed = amended > 0 || removed > 0;
  return { composites: changed ? out : input, amended, removed, changed };
};
var pruneObserversForRemovedRefs = (observers, refids) => asArray(observers).filter(
  (observer) => !(isLive(observer) && (observer.targetKind === "item" || observer.targetKind === "surface") && refids.has(observer.targetRefid))
);
var observersOnRemovedCamera = (layout, cameraRefid) => {
  const designs = layoutCurrentDesigns(layout);
  const cameras = designs.flatMap((d) => asArray(d?.cameras).filter(isLive));
  const camera = cameras.find((c) => c.refid === cameraRefid);
  const endpointId = camera?.rtspEndpointId;
  if (endpointId == null) return [];
  if (cameras.some((c) => c !== camera && c.rtspEndpointId === endpointId)) return [];
  return designs.flatMap(
    (d) => asArray(d?.observers).filter((o) => isLive(o) && o.rtspEndpointId === endpointId)
  );
};
var removeCameraFromLayout = (layout, cameraRefid) => {
  if (!layout) return { layout, observersRemoved: 0 };
  const designs = layoutCurrentDesigns(layout);
  const found = designs.some(
    (d) => asArray(d?.cameras).some((c) => isLive(c) && c.refid === cameraRefid)
  );
  if (!found) return { layout, observersRemoved: 0 };
  const doomed = new Set(observersOnRemovedCamera(layout, cameraRefid));
  const floors = asArray(layout.floors).map((floor) => {
    const floorDesigns = asArray(floor?.designs);
    const design = findLatestDesign(floorDesigns);
    const designIndex = floorDesigns.indexOf(design);
    if (designIndex < 0) return floor;
    const cameras = asArray(design.cameras);
    const keptCameras = cameras.filter((c) => !(isLive(c) && c.refid === cameraRefid));
    const observers = asArray(design.observers);
    const keptObservers = observers.filter((o) => !doomed.has(o));
    if (keptCameras.length === cameras.length && keptObservers.length === observers.length) {
      return floor;
    }
    const nextDesigns = [...floorDesigns];
    nextDesigns[designIndex] = {
      ...design,
      ...design.cameras ? { cameras: keptCameras } : {},
      ...design.observers ? { observers: keptObservers } : {}
    };
    return { ...floor, designs: nextDesigns };
  });
  return { layout: { ...layout, floors }, observersRemoved: doomed.size };
};
var removePlacementsFromLayout = (layout, refids, { isLoadedComposite: isLoadedComposite2, dryRun = false }) => {
  if (!layout || refids.size === 0) return { layout, removed: NOTHING };
  let items = 0;
  let observers = 0;
  let floorsChanged = false;
  const nextFloors = asArray(layout.floors).map((floor) => {
    const designs = asArray(floor?.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) return floor;
    const designItems = asArray(design.items);
    const keptItems = designItems.filter((item) => !(isLive(item) && refids.has(item.refid)));
    const designObservers = asArray(design.observers);
    const keptObservers = pruneObserversForRemovedRefs(designObservers, refids);
    const itemsGone = designItems.length - keptItems.length;
    const observersGone = designObservers.length - keptObservers.length;
    if (itemsGone === 0 && observersGone === 0) return floor;
    items += itemsGone;
    observers += observersGone;
    floorsChanged = true;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = {
      ...design,
      items: keptItems,
      ...observersGone > 0 ? { observers: keptObservers } : {}
    };
    return { ...floor, designs: nextDesigns };
  });
  const pruned = pruneCompositesForRemovedRefs(
    layout.composites,
    { refids },
    { isLoadedComposite: isLoadedComposite2 }
  );
  const compositesAmended = pruned.amended;
  const compositesRemoved = pruned.removed;
  const compositesChanged2 = pruned.changed;
  const composites = pruned.composites;
  const removed = {
    items,
    observers,
    compositesAmended,
    compositesRemoved
  };
  if (dryRun || !floorsChanged && !compositesChanged2) return { layout, removed };
  return {
    layout: {
      ...layout,
      ...floorsChanged ? { floors: nextFloors } : {},
      ...compositesChanged2 ? { composites } : {}
    },
    removed
  };
};

// src/twin-planner/twinNames.ts
import {
  normaliseAssetUid as normaliseAssetUid8
} from "@repo/frontend-types";
var add = (byKey, entry) => {
  const key = nameKey(entry.name);
  if (!key) return;
  const list = byKey.get(key);
  if (list) list.push(entry);
  else byKey.set(key, [entry]);
};
var twinNameEntryFor = (entity, kind) => {
  if (kind === "space") {
    const name = entity.name?.trim() || entity.customName?.trim() || "";
    return { name, kind, ref: { type: "surface", refid: entity.refid } };
  }
  const assetUid = normaliseAssetUid8(entity.assetUid);
  return {
    name: entity.name ?? "",
    kind,
    ref: { type: "item", refid: entity.refid },
    ...assetUid !== void 0 ? { assetUid } : {}
  };
};
function twinNameSignature(layout) {
  const parts2 = [];
  for (const floor of asArray(layout?.floors)) {
    if (!floor) continue;
    parts2.push(`f${floor.id}:${floor.name ?? ""}`);
    const designs = asArray(floor.designs);
    if (designs.length === 0) continue;
    const design = findLatestDesign(designs);
    for (const s of asArray(design?.surfaces).filter(isLive)) {
      parts2.push(`s${s.refid}:${s.name ?? ""}:${s.customName ?? ""}`);
    }
    for (const i of asArray(design?.items).filter(isLive)) {
      parts2.push(`i${i.refid}:${i.name ?? ""}:${i.assetUid ?? ""}`);
    }
  }
  return parts2.join("\0");
}
function buildTwinNameIndex(layout, registerRows = []) {
  const byKey = /* @__PURE__ */ new Map();
  const placedUids = /* @__PURE__ */ new Set();
  for (const floor of asArray(layout?.floors)) {
    if (!floor) continue;
    add(byKey, {
      name: floor.name ?? "",
      kind: "floor",
      ref: { type: "floor", floorId: floor.id }
    });
    const designs = asArray(floor.designs);
    if (designs.length === 0) continue;
    const design = findLatestDesign(designs);
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      add(byKey, twinNameEntryFor(surface, "space"));
    }
    for (const item of asArray(design?.items).filter(isLive)) {
      const entry = twinNameEntryFor(item, "machine");
      if (entry.assetUid !== void 0) placedUids.add(entry.assetUid);
      add(byKey, entry);
    }
  }
  for (const row of registerRows) {
    const uid = normaliseAssetUid8(row.id);
    if (uid !== void 0 && placedUids.has(uid)) continue;
    add(byKey, {
      name: row.asset_name ?? "",
      kind: "machine",
      ref: { type: "register", assetId: row.id },
      ...uid !== void 0 ? { assetUid: uid } : {}
    });
  }
  return { byKey };
}
var isSelf = (entry, self) => {
  if (!self) return false;
  if (self.assetUid !== void 0 && entry.assetUid === self.assetUid) return true;
  const a = entry.ref;
  const b = self.ref;
  if (a.type !== b.type) return false;
  switch (a.type) {
    case "floor":
      return a.floorId === b.floorId;
    case "register":
      return a.assetId === b.assetId;
    default:
      return a.refid === b.refid;
  }
};
var isNameTaken = (name, index, self) => (index.byKey.get(nameKey(name)) ?? []).some((entry) => !isSelf(entry, self));
function nameForNewcomer(base, kind, index) {
  return decideNewcomerName(base, kind, index);
}
function nextFreeName(typed, index) {
  const trimmed3 = typed.trim();
  const split = /^(.+)-(\d+)$/.exec(trimmed3);
  const stem = split ? split[1] : trimmed3;
  const { bare, members } = nameFamily(stem, index);
  const spelled = bare[0]?.name.trim() ?? [...members].sort((a, b) => a.n - b.n)[0]?.entry.name.trim().replace(/-\d+$/, "") ?? stem;
  return `${spelled}-${lowestFreeNumber(spelled, index, 2)}`;
}
var KIND_ORDER = { floor: 0, space: 1, machine: 2 };
var refSortKey = (ref) => {
  switch (ref.type) {
    case "floor":
      return `0:${String(ref.floorId).padStart(10, "0")}`;
    case "register":
      return `2:${String(ref.assetId).padStart(12, "0")}`;
    default:
      return `1:${ref.refid}`;
  }
};
function findDuplicateNames(index) {
  const groups = [];
  for (const entries of index.byKey.values()) {
    if (entries.length < 2) continue;
    groups.push(
      [...entries].sort(
        (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || refSortKey(a.ref).localeCompare(refSortKey(b.ref))
      )
    );
  }
  return groups.sort((a, b) => {
    const x = a[0];
    const y = b[0];
    return KIND_ORDER[x.kind] - KIND_ORDER[y.kind] || nameKey(x.name).localeCompare(nameKey(y.name));
  });
}
function renameInLayout(layout, ref, name) {
  let changed = false;
  const floors = asArray(layout.floors).map((floor) => {
    const designs = asArray(floor?.designs);
    if (designs.length === 0) return floor;
    const current = findLatestDesign(designs);
    const key = ref.type === "item" ? "items" : "surfaces";
    const list = asArray(current?.[key]);
    if (!list.some((entry) => entry?.refid === ref.refid)) return floor;
    changed = true;
    const renamed = list.map((entry) => entry?.refid === ref.refid ? { ...entry, name } : entry);
    return {
      ...floor,
      designs: designs.map((d) => d === current ? { ...d, [key]: renamed } : d)
    };
  });
  return changed ? { ...layout, floors } : layout;
}

// src/twin-planner/floorRemoval.ts
var floorRefids = (floor) => {
  const refids = /* @__PURE__ */ new Set();
  for (const design of asArray(floor.designs)) {
    for (const s of asArray(design?.surfaces)) if (s?.refid) refids.add(s.refid);
    for (const i of asArray(design?.items)) if (i?.refid) refids.add(i.refid);
  }
  return refids;
};
var floorRemovedRefs = (floor) => ({
  refids: floorRefids(floor),
  floorIds: typeof floor.id === "number" ? /* @__PURE__ */ new Set([floor.id]) : void 0
});
var cascadeFloorCameras = (layout, floor) => {
  let cascaded = layout;
  for (const camera of asArray(findLatestDesign(asArray(floor.designs)).cameras)) {
    if (camera && isLive(camera) && camera.refid)
      cascaded = removeCameraFromLayout(cascaded, camera.refid).layout;
  }
  return cascaded;
};
var otherFloorObserversTaken = (layout, index) => {
  const floors = asArray(layout.floors);
  const floor = floors[index];
  if (!floor) return [];
  const cascaded = cascadeFloorCameras(layout, floor);
  if (cascaded === layout) return [];
  const out = [];
  floors.forEach((other, i) => {
    if (i === index || !other) return;
    const before2 = asArray(findLatestDesign(asArray(other.designs)).observers);
    const afterFloor = asArray(cascaded.floors)[i];
    const after = new Set(asArray(findLatestDesign(asArray(afterFloor?.designs)).observers));
    const taken = before2.filter((o) => !after.has(o));
    if (taken.length > 0) out.push({ floorId: other.id, observers: taken });
  });
  return out;
};
var compositesChanged = (layout, floor) => {
  const before2 = asArray(layout.composites);
  const pruned = pruneCompositesForRemovedRefs(before2, floorRemovedRefs(floor));
  if (!pruned.changed) return [];
  const afterByRefid = new Map(pruned.composites.map((c) => [c.refid, c]));
  const out = [];
  for (const composite of before2) {
    if (!isLive(composite)) continue;
    const after = afterByRefid.get(composite.refid);
    if (after === composite) continue;
    out.push({ before: composite, removed: !after || after._deleted === true });
  }
  return out;
};
var countFloorContents = (layout, index) => {
  const empty = {
    zones: 0,
    sections: 0,
    areas: 0,
    machines: 0,
    cameras: 0,
    composites: 0,
    otherFloorObservers: 0
  };
  const floor = layout ? asArray(layout.floors)[index] : void 0;
  if (!layout || !floor) return empty;
  const counts = { ...empty };
  const design = findLatestDesign(asArray(floor.designs));
  const hierarchy = resolvePlanHierarchy(design);
  for (const surface of asArray(design.surfaces).filter(isLive)) {
    const kind = hierarchy.surface(surface.refid)?.kind ?? hierarchy.surface(surface.refid)?.placeKind;
    if (kind === "zone") counts.zones += 1;
    else if (kind === "section") counts.sections += 1;
    else counts.areas += 1;
  }
  counts.machines = asArray(design.items).filter(isLive).length;
  counts.cameras = asArray(design.cameras).filter(isLive).length;
  counts.composites = compositesChanged(layout, floor).length;
  counts.otherFloorObservers = otherFloorObserversTaken(layout, index).reduce(
    (n, f) => n + f.observers.length,
    0
  );
  return counts;
};
var KIND_WORDS = [
  ["zones", "zone", "zones"],
  ["sections", "section", "sections"],
  ["areas", "area", "areas"],
  ["machines", "machine", "machines"],
  ["cameras", "camera", "cameras"]
];
var parts = (counts) => KIND_WORDS.filter(([key]) => counts[key] > 0).map(
  ([key, one, many]) => `${counts[key]} ${counts[key] === 1 ? one : many}`
);
var floorDeleteSentence = (counts) => {
  const words2 = parts(counts);
  if (words2.length === 0) return null;
  if (words2.length === 1) return words2[0];
  return `${words2.slice(0, -1).join(", ")} and ${words2[words2.length - 1]}`;
};
var floorCountsLine = (counts) => {
  const words2 = parts(counts);
  return words2.length === 0 ? "Nothing drawn" : words2.join(" \xB7 ");
};
var nextFloorId = (layout) => {
  let max = layout?.floorIdHighWater ?? 0;
  for (const f of asArray(layout?.floors))
    if (f && typeof f.id === "number") max = Math.max(max, f.id);
  for (const r of asArray(layout?.removedFloors))
    if (r?.floor && typeof r.floor.id === "number") max = Math.max(max, r.floor.id);
  return max + 1;
};
var buildFloorRemovalRecord = (layout, index, removedAt = (/* @__PURE__ */ new Date()).toISOString()) => {
  const floor = layout ? asArray(layout.floors)[index] : void 0;
  if (!layout || !floor) return null;
  return {
    floor,
    level: index,
    removedAt,
    composites: compositesChanged(layout, floor),
    otherFloorObservers: otherFloorObserversTaken(layout, index)
  };
};
var identityTail = (raw) => {
  const key = raw.trim().toUpperCase();
  const m = /([A-Z])0*(\d+)$/.exec(key);
  return m ? `${m[1]}${m[2]}` : key;
};
var identitiesOn = (floor) => {
  const out = /* @__PURE__ */ new Map();
  const design = findLatestDesign(asArray(floor.designs));
  for (const e of [...asArray(design.surfaces), ...asArray(design.items)]) {
    const raw = e && isLive(e) ? (e.entityCode || e.refid || "").trim() : "";
    if (raw) out.set(identityTail(raw), raw);
  }
  return out;
};
var sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
var liveRefidsOf = (layout) => {
  const refids = /* @__PURE__ */ new Set();
  for (const f of asArray(layout.floors)) {
    const design = findLatestDesign(asArray(f?.designs));
    for (const s of asArray(design.surfaces).filter(isLive)) refids.add(s.refid);
    for (const i of asArray(design.items).filter(isLive)) refids.add(i.refid);
  }
  return refids;
};
var isFloorRecordRestored = (layout, record) => {
  const held = asArray(layout?.removedFloors).some(
    (r) => r?.floor?.id === record.floor.id && r?.removedAt === record.removedAt
  );
  if (held) return false;
  const same = asArray(layout?.floors).find((f) => f?.id === record.floor.id);
  if (!same) return false;
  const recorded = floorRemovedRefs(record.floor).refids;
  if (recorded.size === 0) return (same.name ?? "") === (record.floor.name ?? "");
  const design = findLatestDesign(asArray(same.designs));
  return [...asArray(design.surfaces), ...asArray(design.items)].some(
    (e) => !!e && recorded.has(e.refid)
  );
};
var reFloorRef = (ref, from, to) => ref.kind === "floor" && ref.floorId === from ? { ...ref, floorId: to } : ref;
var restoreFloorIntoLayout = (layout, record, registerRows = []) => {
  if (isFloorRecordRestored(layout, record)) return null;
  const floors = asArray(layout.floors);
  const inUse = /* @__PURE__ */ new Set();
  for (const f of floors) identitiesOn(f).forEach((_raw, key) => inUse.add(key));
  const blocked = [];
  identitiesOn(record.floor).forEach((raw, key) => {
    if (inUse.has(key)) blocked.push(raw);
  });
  if (blocked.length > 0) return { blocked };
  const skipped = { composites: 0, observers: 0 };
  const names = buildTwinNameIndex(layout, registerRows);
  const oldName = record.floor.name ?? "";
  const taken = oldName.trim() !== "" && isNameTaken(oldName, names);
  const restoredName = taken ? nameForNewcomer(oldName, "floor", names).name : oldName;
  const oldId = record.floor.id;
  const id = floors.some((f) => f?.id === oldId) ? nextFloorId(layout) : oldId;
  const moved = id !== oldId;
  const oldTarget = floorTargetRefid(oldId);
  const newTarget = floorTargetRefid(id);
  const restored = moved ? {
    ...record.floor,
    id,
    name: restoredName,
    designs: asArray(record.floor.designs).map((d) => ({
      ...d,
      ...d.observers ? {
        observers: d.observers.map(
          (o) => o && o.targetKind === "floor" && o.targetRefid === oldTarget ? { ...o, targetRefid: newTarget } : o
        )
      } : {}
    }))
  } : { ...record.floor, name: restoredName };
  const at = Math.max(0, Math.min(record.level, floors.length));
  const withFloor = [...floors.slice(0, at), restored, ...floors.slice(at)].map(
    (f, i) => f.level === i ? f : { ...f, level: i }
  );
  const live2 = liveRefidsOf({ ...layout, floors: withFloor });
  const nextFloors = withFloor.map((f) => {
    if (f === restored) return f;
    const owed = asArray(record.otherFloorObservers).find((o) => o.floorId === f.id);
    if (!owed) return f;
    const designs = asArray(f.designs);
    const design = findLatestDesign(designs);
    const designIndex = designs.indexOf(design);
    if (designIndex < 0) {
      skipped.observers += owed.observers.length;
      return f;
    }
    const present = new Set(asArray(design.observers).map((o) => o?.refid));
    const back = [];
    for (const o of owed.observers) {
      if (present.has(o.refid)) continue;
      const targetGone = (o.targetKind === "item" || o.targetKind === "surface") && !live2.has(o.targetRefid);
      if (targetGone) {
        skipped.observers += 1;
        continue;
      }
      back.push(o);
    }
    if (back.length === 0) return f;
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = { ...design, observers: [...asArray(design.observers), ...back] };
    return { ...f, designs: nextDesigns };
  });
  for (const owed of asArray(record.otherFloorObservers)) {
    if (!withFloor.some((f) => f !== restored && f.id === owed.floorId))
      skipped.observers += owed.observers.length;
  }
  const tookFromFloor = floorRemovedRefs(record.floor);
  const reFloor = (ref) => moved ? reFloorRef(ref, oldId, id) : ref;
  const floorIdsBack = new Set(nextFloors.map((f) => f.id));
  const pointsAtLive = (ref) => ref.kind === "twin" || ref.kind === "entity" && live2.has(ref.refid) || ref.kind === "floor" && floorIdsBack.has(ref.floorId);
  let composites = asArray(layout.composites);
  const compositesTouched = asArray(record.composites).length > 0;
  if (compositesTouched) composites = [...composites];
  for (const { before: before2, removed } of asArray(record.composites)) {
    const pos = composites.findIndex((c) => c?.refid === before2.refid);
    const current = pos >= 0 ? composites[pos] : void 0;
    if (removed) {
      if (current && !current._deleted) continue;
      const target = reFloor(before2.target);
      if (!pointsAtLive(target)) {
        skipped.composites += 1;
        continue;
      }
      const whole = {
        ...before2,
        target,
        inputs: asArray(before2.inputs).map(reFloor).filter(pointsAtLive)
      };
      if (pos >= 0) composites[pos] = whole;
      else composites.push(whole);
      continue;
    }
    if (!current || current._deleted) {
      skipped.composites += 1;
      continue;
    }
    const inputs = asArray(current.inputs);
    const back = asArray(before2.inputs).filter((i) => refersToRemoved(i, tookFromFloor)).map(reFloor).filter(pointsAtLive).filter((i) => !inputs.some((c) => sameJson(c, i)));
    if (back.length > 0) composites[pos] = { ...current, inputs: [...inputs, ...back] };
  }
  const remaining = asArray(layout.removedFloors).filter((r) => r?.floor?.id !== oldId);
  const next = {
    ...layout,
    floors: nextFloors,
    ...compositesTouched ? { composites } : {},
    floorIdHighWater: Math.max(layout.floorIdHighWater ?? 0, id, nextFloorId(layout) - 1)
  };
  if (remaining.length > 0) next.removedFloors = remaining;
  else delete next.removedFloors;
  return {
    layout: next,
    index: at,
    restoredName,
    ...taken ? { renamedFrom: oldName } : {},
    skipped
  };
};

// src/twin-planner/modelDiff.ts
var STRUCTURAL_TYPES = /* @__PURE__ */ new Set(["wall", "floor"]);
var STORED_UNITS_TO_M = 100;
var MOVE_MIN_M = 0.1;
var ROTATE_MIN_DEG = 1;
var num = (v) => typeof v === "number" && !Number.isNaN(v) ? v : null;
var trimmed = (v) => typeof v === "string" ? v.trim() : "";
var typeName = (node) => typeof node.type?.name === "string" ? node.type.name.trim().toLowerCase() : "";
var displayName2 = (node, bID) => trimmed(node.name) || bID;
var positionOf = (node) => {
  const p = node.location ?? node.centre;
  if (!p) return null;
  const lat = num(p.latitude);
  const lng = num(p.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};
var angularDelta = (a, b) => {
  const d = Math.abs((a - b) % 360 + 360) % 360;
  return d > 180 ? 360 - d : d;
};
var round1 = (n) => Math.round(n * 10) / 10;
var collect = (root) => {
  const byBid = /* @__PURE__ */ new Map();
  const visit = (node, isRoot) => {
    if (!node) return;
    const bID = typeof node.bID === "string" ? node.bID : "";
    if (bID && !isRoot && !STRUCTURAL_TYPES.has(typeName(node)) && !byBid.has(bID)) {
      byBid.set(bID, node);
    }
    for (const child of asArray(node.children)) visit(child, false);
  };
  visit(root, true);
  return byBid;
};
var collectFloors = (root) => {
  const byBid = /* @__PURE__ */ new Map();
  const visit = (node) => {
    if (!node) return;
    if (typeName(node) === "floor" && typeof node.bID === "string" && node.bID)
      byBid.set(node.bID, node);
    for (const child of asArray(node.children)) visit(child);
  };
  visit(root);
  return byBid;
};
var placedFloor = (floor) => ({
  bID: floor.bID,
  location: positionOf(floor) ? floor.location : { latitude: 0, longitude: 0 },
  rotationY: num(floor.rotationY) ?? 0,
  // Finite only: a non-finite altitude reads as 0 m, as adoption reads it — never "moved up Infinity m".
  altitude: Number.isFinite(floor.altitude) ? floor.altitude : 0
});
var elevationChange = (live2, next) => {
  const d = Math.round(((next.altitude ?? 0) - (live2.altitude ?? 0)) * 1e3) / 1e3;
  if (Math.abs(d) < MOVE_MIN_M) return null;
  return { kind: "elevated", detail: `moved ${d > 0 ? "up" : "down"} ${round1(Math.abs(d))} m` };
};
var floorPlacementChanges = (live2, planned) => {
  const liveFloors = collectFloors(live2);
  const out = [];
  for (const [bID, node] of Array.from(collectFloors(planned).entries())) {
    const liveNode = liveFloors.get(bID);
    if (!liveNode) continue;
    const from = placedFloor(liveNode);
    const to = placedFloor(node);
    const changes = describeChanges(from, to);
    const elevated = elevationChange(from, to);
    if (elevated) changes.push(elevated);
    if (changes.length > 0) out.push([bID, node, changes]);
  }
  return out;
};
var describeChanges = (live2, next) => {
  const changes = [];
  const fromName = trimmed(live2.name);
  const toName = trimmed(next.name);
  if (fromName && toName && fromName !== toName) {
    changes.push({ kind: "renamed", detail: `renamed \u201C${fromName}\u201D \u2192 \u201C${toName}\u201D` });
  }
  const fromType = typeName(live2);
  const toType = typeName(next);
  if (fromType && toType && fromType !== toType) {
    changes.push({ kind: "retyped", detail: `re-typed ${fromType} \u2192 ${toType}` });
  }
  const fromPos = positionOf(live2);
  const toPos = positionOf(next);
  if (fromPos && toPos) {
    const metres = Math.hypot(fromPos.lat - toPos.lat, fromPos.lng - toPos.lng) * STORED_UNITS_TO_M;
    if (metres >= MOVE_MIN_M) {
      changes.push({ kind: "moved", detail: `moved ${round1(metres)} m` });
    }
  }
  const fromRot = num(live2.rotationY);
  const toRot = num(next.rotationY);
  if (fromRot != null && toRot != null) {
    const deg = angularDelta(fromRot, toRot);
    if (deg >= ROTATE_MIN_DEG) {
      changes.push({ kind: "rotated", detail: `rotated ${Math.round(deg)}\xB0` });
    }
  }
  return changes;
};
var computeModelDiff = (live2, planned) => {
  const liveByBid = collect(live2);
  const plannedByBid = collect(planned);
  const added = [];
  const changed = [];
  for (const [bID, node] of Array.from(plannedByBid.entries())) {
    const liveNode = liveByBid.get(bID);
    if (!liveNode) {
      added.push({ bID, name: displayName2(node, bID) });
      continue;
    }
    const changes = describeChanges(liveNode, node);
    if (changes.length > 0) {
      changed.push({ bID, name: displayName2(node, bID), changes });
    }
  }
  for (const [bID, node, changes] of floorPlacementChanges(live2, planned)) {
    changed.push({ bID, name: displayName2(node, bID), changes });
  }
  const removed = [];
  for (const [bID, node] of Array.from(liveByBid.entries())) {
    if (!plannedByBid.has(bID)) removed.push({ bID, name: displayName2(node, bID) });
  }
  return { added, removed, changed };
};
var isEmptyModelDiff = (diff) => diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0;
var CHANGE_KIND_TO_KEYS = {
  renamed: ["name"],
  retyped: ["type"],
  // Placement lives in `location` (items) or `centre` (areas) — only one is present, so marking
  // both is safe and avoids branching on geometry here.
  moved: ["location", "centre"],
  rotated: ["rotationY"],
  elevated: ["altitude"]
};
var computeChangedFieldsByBid = (live2, planned) => {
  const liveByBid = collect(live2);
  const plannedByBid = collect(planned);
  const byBid = /* @__PURE__ */ new Map();
  for (const [bID, node] of Array.from(plannedByBid.entries())) {
    const liveNode = liveByBid.get(bID);
    if (!liveNode) continue;
    const changes = describeChanges(liveNode, node);
    if (changes.length === 0) continue;
    const keys = /* @__PURE__ */ new Set();
    for (const c of changes) for (const key of CHANGE_KIND_TO_KEYS[c.kind]) keys.add(key);
    byBid.set(bID, keys);
  }
  for (const [bID, , changes] of floorPlacementChanges(live2, planned)) {
    const keys = /* @__PURE__ */ new Set();
    for (const c of changes) for (const key of CHANGE_KIND_TO_KEYS[c.kind]) keys.add(key);
    byBid.set(bID, keys);
  }
  return byBid;
};

// src/twin-planner/pairOneToOne.ts
function pairOneToOne(saved, current, passes) {
  const savedTaken = /* @__PURE__ */ new Set();
  const pairedCurrent = /* @__PURE__ */ new Map();
  for (const keyOf of passes) {
    const queues = /* @__PURE__ */ new Map();
    saved.forEach((entry, index) => {
      if (savedTaken.has(index)) return;
      const key = keyOf(entry, "saved");
      if (key === null) return;
      const queue = queues.get(key);
      if (queue) queue.push(index);
      else queues.set(key, [index]);
    });
    current.forEach((entry, index) => {
      if (pairedCurrent.has(index)) return;
      const key = keyOf(entry, "current");
      if (key === null) return;
      const savedIndex = queues.get(key)?.shift();
      if (savedIndex === void 0) return;
      savedTaken.add(savedIndex);
      pairedCurrent.set(index, savedIndex);
    });
  }
  const pairs = [];
  const currentOnly = [];
  current.forEach((entry, index) => {
    const savedIndex = pairedCurrent.get(index);
    if (savedIndex === void 0) currentOnly.push(entry);
    else pairs.push([saved[savedIndex], entry]);
  });
  const savedOnly = saved.filter((_, index) => !savedTaken.has(index));
  return { pairs, savedOnly, currentOnly };
}

// src/twin-planner/versionDelta.ts
var POSITION_EPSILON = 1e-3;
var ROTATION_EPSILON = 0.1;
function buildItemMaps(items) {
  const byRefid = /* @__PURE__ */ new Map();
  const byName2 = /* @__PURE__ */ new Map();
  const seenNames = /* @__PURE__ */ new Set();
  for (const item of items) {
    if (item._deleted) continue;
    if (item.refid) byRefid.set(item.refid, item);
    const nameKey2 = item.name?.trim();
    if (!nameKey2) continue;
    if (seenNames.has(nameKey2)) byName2.delete(nameKey2);
    else {
      seenNames.add(nameKey2);
      byName2.set(nameKey2, item);
    }
  }
  return { byRefid, byName: byName2 };
}
function buildIdentityMap(entities, twinBid) {
  const byIdentity = /* @__PURE__ */ new Map();
  for (const entity of entities) {
    if (entity._deleted) continue;
    const identity2 = entityIdentityFor(twinBid, entity.entityCode);
    if (identity2 && !byIdentity.has(identity2)) byIdentity.set(identity2, entity);
  }
  return byIdentity;
}
function deriveLabel(item) {
  return (item.name?.trim() || item.refid).trim();
}
function deriveSurfaceLabel(surface) {
  return surfaceName(surface) || surface.refid;
}
function snapshot(item) {
  return { x: item.x, y: item.y, rotation: item.rotation };
}
var exceeds = (value, threshold, inclusive) => inclusive ? value >= threshold : value > threshold;
function classifyItemChange(saved, current, options = {}) {
  const positionEpsilon = options.positionEpsilon ?? POSITION_EPSILON;
  const rotationEpsilon = options.rotationEpsilon ?? ROTATION_EPSILON;
  const wrapRotation = options.wrapRotation ?? false;
  const dx = current.x - saved.x;
  const dy = current.y - saved.y;
  const dist = Math.hypot(dx, dy);
  const rot = wrapRotation ? angularDelta(current.rotation, saved.rotation) : Math.abs(current.rotation - saved.rotation);
  const moved = exceeds(dist, positionEpsilon, options.positionEpsilon != null);
  const rotated = exceeds(rot, rotationEpsilon, options.rotationEpsilon != null);
  if (moved && rotated) return { changeType: "moved-and-rotated", distance: dist, rotation: rot };
  if (moved) return { changeType: "moved", distance: dist };
  if (rotated) return { changeType: "rotated", rotation: rot };
  return null;
}
var relativeTo = (p, c) => ({
  x: p.x - c.x,
  y: p.y - c.y
});
function classifySurfaceChange(saved, current, options = {}) {
  if (saved.length < 3 || current.length < 3) return null;
  const positionEpsilon = options.positionEpsilon ?? POSITION_EPSILON;
  const savedCentroid = polygonCentroid(saved);
  const currentCentroid = polygonCentroid(current);
  const centroidShift = distance(savedCentroid, currentCentroid);
  const inclusive = options.positionEpsilon != null;
  const moved = exceeds(centroidShift, positionEpsilon, inclusive);
  let shapeChange = null;
  if (saved.length !== current.length) {
    shapeChange = "reshaped";
  } else if (saved.some(
    (p, i) => exceeds(
      distance(relativeTo(p, savedCentroid), relativeTo(current[i], currentCentroid)),
      positionEpsilon,
      inclusive
    )
  )) {
    shapeChange = "resized";
  }
  if (shapeChange && moved) return { changeType: "moved-and-reshaped", distance: centroidShift };
  if (shapeChange) return { changeType: shapeChange };
  if (moved) return { changeType: "moved", distance: centroidShift };
  return null;
}
function designsFor(layout, floorIndex) {
  if (floorIndex === "all") {
    return layout.floors.filter((floor2) => floor2?.designs?.length).map((floor2) => findLatestDesign(floor2.designs));
  }
  const floor = layout.floors[floorIndex] ?? layout.floors[0];
  return floor?.designs.length ? [findLatestDesign(floor.designs)] : [];
}
function computeVersionDelta(saved, current, floorIndex = 0, options = {}) {
  const classify = {
    // Passed through undefaulted: the classifiers default them, and whether a value was SUPPLIED
    // is what decides the boundary (see `exceeds`).
    positionEpsilon: options.positionEpsilon,
    rotationEpsilon: options.rotationEpsilon,
    wrapRotation: options.wrapRotation ?? false
  };
  const byIdentity = options.matchBy === "identity";
  const savedDesigns = designsFor(saved, floorIndex);
  const currentDesigns = designsFor(current, floorIndex);
  const savedItems = savedDesigns.flatMap((d) => d?.items ?? []);
  const currentItems = currentDesigns.flatMap((d) => d?.items ?? []);
  const savedSurfaces = savedDesigns.flatMap((d) => d?.surfaces ?? []);
  const currentSurfaces = currentDesigns.flatMap((d) => d?.surfaces ?? []);
  if (options.oneToOne || options.compareSurfaces && options.surfacePresence) {
    return oneToOneDelta(
      savedItems,
      currentItems,
      savedSurfaces,
      currentSurfaces,
      options,
      classify
    );
  }
  const savedMaps = buildItemMaps(savedItems);
  const currentMaps = buildItemMaps(currentItems);
  const savedByIdentity = buildIdentityMap(savedItems, options.twinBid);
  const currentByIdentity = buildIdentityMap(currentItems, options.twinBid);
  const items = [];
  const matchedSavedRefids = /* @__PURE__ */ new Set();
  for (const cur of currentItems) {
    if (cur._deleted) continue;
    const nameKey2 = cur.name?.trim() || void 0;
    let match;
    if (byIdentity) {
      const identity2 = entityIdentityFor(options.twinBid, cur.entityCode);
      match = identity2 && currentByIdentity.get(identity2) === cur ? savedByIdentity.get(identity2) : void 0;
      match ?? (match = savedMaps.byRefid.get(cur.refid));
    } else {
      match = savedMaps.byRefid.get(cur.refid) ?? (nameKey2 && currentMaps.byName.get(nameKey2) === cur ? savedMaps.byName.get(nameKey2) : void 0);
    }
    if (!match) {
      items.push({
        refid: cur.refid,
        label: deriveLabel(cur),
        modelFile: cur.modelFile,
        changeType: "removed",
        current: snapshot(cur)
      });
      continue;
    }
    matchedSavedRefids.add(match.refid);
    const change = classifyItemChange(snapshot(match), snapshot(cur), classify);
    if (!change) continue;
    items.push({
      refid: cur.refid,
      label: deriveLabel(cur),
      modelFile: cur.modelFile ?? match.modelFile,
      changeType: change.changeType,
      saved: snapshot(match),
      current: snapshot(cur),
      ...change.distance != null ? { distance: change.distance } : {},
      ...change.rotation != null ? { rotation: change.rotation } : {}
    });
  }
  for (const old of savedItems) {
    if (old._deleted) continue;
    if (matchedSavedRefids.has(old.refid)) continue;
    if (byIdentity) {
      const identity2 = entityIdentityFor(options.twinBid, old.entityCode);
      const reverseHit = identity2 && savedByIdentity.get(identity2) === old ? currentByIdentity.get(identity2) : void 0;
      if (reverseHit && !reverseHit._deleted) continue;
    } else {
      const nameKey2 = old.name?.trim() || void 0;
      const reverseHit = nameKey2 && savedMaps.byName.get(nameKey2) === old ? currentMaps.byName.get(nameKey2) : void 0;
      if (reverseHit && !reverseHit._deleted) continue;
    }
    items.push({
      refid: old.refid,
      label: deriveLabel(old),
      modelFile: old.modelFile,
      changeType: "added",
      saved: snapshot(old)
    });
  }
  const surfaces = [];
  const savedSurfacesByRefid = /* @__PURE__ */ new Map();
  for (const s of savedSurfaces) if (!s._deleted && s.refid) savedSurfacesByRefid.set(s.refid, s);
  const savedSurfacesByIdentity = buildIdentityMap(savedSurfaces, options.twinBid);
  const currentSurfacesByIdentity = buildIdentityMap(currentSurfaces, options.twinBid);
  for (const cur of options.compareSurfaces ? currentSurfaces : []) {
    if (cur._deleted) continue;
    let match;
    if (byIdentity) {
      const identity2 = entityIdentityFor(options.twinBid, cur.entityCode);
      match = identity2 && currentSurfacesByIdentity.get(identity2) === cur ? savedSurfacesByIdentity.get(identity2) : void 0;
    }
    match ?? (match = savedSurfacesByRefid.get(cur.refid));
    if (!match) continue;
    const change = classifySurfaceChange(match.poly ?? [], cur.poly ?? [], classify);
    if (!change) continue;
    surfaces.push({
      refid: cur.refid,
      label: deriveSurfaceLabel(cur),
      changeType: change.changeType,
      ...change.distance != null ? { distance: change.distance } : {}
    });
  }
  return buildDelta(items, surfaces);
}
function buildDelta(items, surfaces, matches) {
  let added = 0;
  let removed = 0;
  let moved = 0;
  let rotated = 0;
  let movedAndRotated = 0;
  for (const d of items) {
    switch (d.changeType) {
      case "added":
        added++;
        break;
      case "removed":
        removed++;
        break;
      case "moved":
        moved++;
        break;
      case "rotated":
        rotated++;
        break;
      case "moved-and-rotated":
        movedAndRotated++;
        break;
    }
  }
  return {
    items,
    surfaces,
    ...matches ? { matches } : {},
    summary: {
      added,
      removed,
      moved,
      rotated,
      movedAndRotated,
      surfacesChanged: surfaces.length,
      total: items.length + surfaces.length
    }
  };
}
function keyCounts(entries, keyOf) {
  const counts = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    const key = keyOf(entry);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}
function contentKey(entry) {
  try {
    return `content:${JSON.stringify(entry)}`;
  } catch {
    return null;
  }
}
function entityPasses(saved, current, options, byName2) {
  const identityOf = (entry) => entityIdentityFor(options.twinBid, entry.entityCode) || null;
  const nameOf = (entry) => entry.name?.trim() || null;
  const unique = (keyOf) => {
    const counts = { saved: keyCounts(saved, keyOf), current: keyCounts(current, keyOf) };
    return (entry, side) => {
      const key = keyOf(entry);
      return key && counts[side].get(key) === 1 ? key : null;
    };
  };
  const refid = (entry) => entry.refid || null;
  const uniqueIdentity = unique(identityOf);
  const keyless = (entry, side) => !entry.refid && (options.matchBy !== "identity" || !uniqueIdentity(entry, side));
  const content = (entry, side) => keyless(entry, side) ? contentKey(entry) : null;
  if (options.matchBy === "identity") return [uniqueIdentity, refid, content];
  return byName2 ? [refid, unique(nameOf), content] : [refid, content];
}
var live = (entries) => entries.filter((entry) => !entry._deleted);
function oneToOneDelta(savedItems, currentItems, savedSurfaces, currentSurfaces, options, classify) {
  const saved = live(savedItems);
  const current = live(currentItems);
  const pairing = pairOneToOne(saved, current, entityPasses(saved, current, options, true));
  const partnerOf = new Map(pairing.pairs.map(([old, cur]) => [cur, old]));
  const items = [];
  const itemMatches = [];
  for (const cur of current) {
    const match = partnerOf.get(cur);
    if (!match) {
      items.push({
        refid: cur.refid,
        label: deriveLabel(cur),
        modelFile: cur.modelFile,
        changeType: "removed",
        current: snapshot(cur),
        entity: cur
      });
      continue;
    }
    const change = classifyItemChange(snapshot(match), snapshot(cur), classify);
    itemMatches.push({ saved: match, current: cur, change });
    if (!change) continue;
    items.push({
      refid: cur.refid,
      label: deriveLabel(cur),
      modelFile: cur.modelFile ?? match.modelFile,
      changeType: change.changeType,
      saved: snapshot(match),
      current: snapshot(cur),
      ...change.distance != null ? { distance: change.distance } : {},
      ...change.rotation != null ? { rotation: change.rotation } : {}
    });
  }
  for (const old of pairing.savedOnly) {
    items.push({
      refid: old.refid,
      label: deriveLabel(old),
      modelFile: old.modelFile,
      changeType: "added",
      saved: snapshot(old),
      entity: old
    });
  }
  const surfaces = [];
  const surfaceMatches = [];
  if (!options.compareSurfaces) {
    return buildDelta(items, surfaces, { items: itemMatches, surfaces: surfaceMatches });
  }
  const liveSaved = live(savedSurfaces);
  const liveCurrent = live(currentSurfaces);
  const surfacePairing = pairOneToOne(
    liveSaved,
    liveCurrent,
    entityPasses(liveSaved, liveCurrent, options, false)
  );
  for (const [old, cur] of surfacePairing.pairs) {
    const change = classifySurfaceChange(old.poly ?? [], cur.poly ?? [], classify);
    surfaceMatches.push({ saved: old, current: cur, change });
    if (!change) continue;
    surfaces.push({
      refid: cur.refid,
      label: deriveSurfaceLabel(cur),
      changeType: change.changeType,
      ...change.distance != null ? { distance: change.distance } : {}
    });
  }
  const delta = buildDelta(items, surfaces, { items: itemMatches, surfaces: surfaceMatches });
  if (!options.surfacePresence) return delta;
  const presence = (surface) => ({
    refid: surface.refid,
    label: deriveSurfaceLabel(surface),
    entity: surface
  });
  const added = surfacePairing.savedOnly.map(presence);
  const removed = surfacePairing.currentOnly.map(presence);
  return {
    ...delta,
    surfacePresence: { added, removed },
    summary: {
      ...delta.summary,
      surfacesAdded: added.length,
      surfacesRemoved: removed.length,
      total: delta.summary.total + added.length + removed.length
    }
  };
}

// src/twin-planner/planComparison.ts
var PLAN_KIND_STEP = {
  floors: "plan",
  zones: "plan",
  placedEquipment: "assets",
  cameras: "cameras",
  observers: "observers",
  compositeMeasures: "compositeMeasures"
};
var PLAN_STEP_IDS = [
  "assets",
  "plan",
  "cameras",
  "observers",
  "compositeMeasures"
];
var POINT_EPSILON = 1e-3;
var isObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var text3 = (value) => typeof value === "string" ? value : void 0;
var coordinate = (value) => typeof value === "number" ? value : NaN;
var optionalNumber = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
var json2 = (value) => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};
var liveEntries = (value, rebuild) => asArray(value).filter((entry) => isLive(entry)).map(
  (entry) => isObject(entry) ? rebuild(entry) : { ...rebuild({ name: "unreadable" }), unreadable: json2(entry) }
);
var points = (value) => asArray(value).filter(
  (p) => isObject(p) && typeof p.x === "number" && Number.isFinite(p.x) && typeof p.y === "number" && Number.isFinite(p.y)
).map((p) => ({ x: p.x, y: p.y }));
var sanitiseItem = (o) => ({
  refid: text3(o.refid) ?? "",
  name: text3(o.name),
  entityCode: text3(o.entityCode),
  x: coordinate(o.x),
  y: coordinate(o.y),
  z: 0,
  rotation: coordinate(o.rotation),
  parentSpaceBid: text3(o.parentSpaceBid),
  parentChosen: !!o.parentChosen,
  modelFile: text3(o.modelFile)
});
var sanitiseSurface = (o) => ({
  refid: text3(o.refid) ?? "",
  name: text3(o.name),
  customName: text3(o.customName),
  entityCode: text3(o.entityCode),
  poly: points(o.poly),
  parentSurfaceBid: text3(o.parentSurfaceBid),
  parentChosen: !!o.parentChosen
});
var sanitiseCamera = (o) => ({
  refid: text3(o.refid) ?? "",
  x: coordinate(o.x),
  y: coordinate(o.y),
  rotation: coordinate(o.rotation),
  cameraId: optionalNumber(o.cameraId),
  name: text3(o.name) ?? null,
  detectionDistance: optionalNumber(o.detectionDistance),
  fovAngle: optionalNumber(o.fovAngle),
  rtspEndpointId: optionalNumber(o.rtspEndpointId),
  rtspEndpointBid: text3(o.rtspEndpointBid) ?? null
});
var sanitiseObserver = (o) => ({
  refid: text3(o.refid) ?? "",
  targetRefid: text3(o.targetRefid) ?? "",
  targetKind: text3(o.targetKind),
  rtspEndpointId: optionalNumber(o.rtspEndpointId),
  rtspEndpointBid: text3(o.rtspEndpointBid) ?? null,
  points: points(o.points),
  trackSide: text3(o.trackSide)
});
var sanitise = (raw) => {
  const plan = isObject(raw) ? raw : {};
  const floorOf = /* @__PURE__ */ new WeakMap();
  const designOf = /* @__PURE__ */ new WeakMap();
  const wallsOf = /* @__PURE__ */ new WeakMap();
  const cameraIndex = /* @__PURE__ */ new WeakMap();
  const floors = asArray(plan.floors).filter(isObject).map((rawFloor) => {
    const picked = findLatestDesign(asArray(rawFloor.designs));
    const rawDesign = isObject(picked) ? picked : {};
    const design = {
      name: "Current",
      areas: [],
      walls: [],
      items: liveEntries(rawDesign.items, sanitiseItem),
      surfaces: liveEntries(rawDesign.surfaces, sanitiseSurface),
      cameras: liveEntries(rawDesign.cameras, sanitiseCamera),
      observers: liveEntries(rawDesign.observers, sanitiseObserver)
    };
    const floor = {
      id: Number.isInteger(rawFloor.id) ? rawFloor.id : null,
      name: text3(rawFloor.name) ?? "",
      level: optionalNumber(rawFloor.level),
      height: optionalNumber(rawFloor.height),
      // As the store reads it: absent is the identity, and parts are made safe and whole-degree.
      placement: cleanPlacement(isObject(rawFloor.placement) ? rawFloor.placement : {}),
      capacity: optionalNumber(rawFloor.capacity),
      designs: [design]
    };
    wallsOf.set(floor, asArray(rawDesign.walls));
    for (const entity of [
      ...design.items,
      ...design.surfaces,
      ...design.cameras ?? [],
      ...design.observers ?? []
    ]) {
      floorOf.set(entity, floor);
      designOf.set(entity, design);
    }
    (design.cameras ?? []).forEach((camera, index) => cameraIndex.set(camera, index));
    return floor;
  });
  const composites = liveEntries(
    plan.composites,
    (c) => ({
      refid: text3(c.refid) ?? "",
      target: c.target,
      inputs: asArray(c.inputs),
      includesOwnObservers: !!c.includesOwnObservers,
      excluded: !!c.excluded
    })
  ).filter((c) => !c.excluded).map(({ excluded: _excluded, ...c }) => c);
  return { layout: { name: "", floors, composites }, floorOf, designOf, wallsOf, cameraIndex };
};
var designsOf = (side) => side.layout.floors.map((f) => f.designs[0]);
var countsOf = (side) => {
  const designs = designsOf(side);
  const sum = (pick) => designs.reduce((n, d) => n + (pick(d)?.length ?? 0), 0);
  return {
    floors: side.layout.floors.length,
    zones: sum((d) => d.surfaces),
    placedEquipment: sum((d) => d.items),
    cameras: sum((d) => d.cameras),
    observers: sum((d) => d.observers),
    compositeMeasures: side.layout.composites.length
  };
};
var floorRef = (floor) => floor ? { id: floor.id, name: floor.name } : null;
var sameNumber = (a, b) => (a ?? null) === (b ?? null);
var wallWithoutId = (wall) => {
  if (typeof wall !== "object" || wall === null) return wall;
  const { id: _id, ...rest } = wall;
  return rest;
};
var wallsJson = (walls) => {
  try {
    return JSON.stringify(Array.isArray(walls) ? walls.map(wallWithoutId) : walls ?? []);
  } catch {
    return null;
  }
};
var streamKey = (entity) => entity.rtspEndpointBid ? `bid:${entity.rtspEndpointBid}` : entity.rtspEndpointId != null ? `id:${entity.rtspEndpointId}` : "none";
var itemChanges = (change) => change.changeType === "moved-and-rotated" ? ["moved", "rotated"] : [change.changeType];
var surfaceChanges = (change) => change.changeType === "moved-and-reshaped" ? ["moved", "reshaped"] : [change.changeType];
var emptyKind = () => ({ added: [], removed: [], changed: [] });
function comparePlans(base, other, options) {
  const twinBid = typeof options?.twinBid === "string" ? options.twinBid : null;
  const b = sanitise(base);
  const o = sanitise(other);
  const result = {
    floors: emptyKind(),
    zones: emptyKind(),
    placedEquipment: emptyKind(),
    cameras: emptyKind(),
    observers: emptyKind(),
    compositeMeasures: emptyKind()
  };
  const byId = pairOneToOne(b.layout.floors, o.layout.floors, [
    (f) => f.id === null ? null : `${f.id}`
  ]);
  const nameKey2 = (f) => f.name.trim().toLowerCase();
  const uniqueNames = (floors) => {
    const seen = /* @__PURE__ */ new Map();
    for (const f of floors) seen.set(nameKey2(f), (seen.get(nameKey2(f)) ?? 0) + 1);
    return seen;
  };
  const baseNames = uniqueNames(byId.savedOnly);
  const otherNames = uniqueNames(byId.currentOnly);
  const byName2 = pairOneToOne(byId.savedOnly, byId.currentOnly, [
    (f, side) => {
      const key = nameKey2(f);
      return key && (side === "saved" ? baseNames : otherNames).get(key) === 1 ? key : null;
    }
  ]);
  const byOrder = pairOneToOne(byName2.savedOnly, byName2.currentOnly, [
    (f) => f.id === null ? "no-id" : null
  ]);
  const floorPairs = [...byId.pairs, ...byName2.pairs, ...byOrder.pairs];
  const floorKey = /* @__PURE__ */ new Map();
  floorPairs.forEach(([bf, of], n) => {
    floorKey.set(bf, `fp${n}`);
    floorKey.set(of, `fp${n}`);
  });
  b.layout.floors.forEach((f, n) => floorKey.has(f) || floorKey.set(f, `fb${n}`));
  o.layout.floors.forEach((f, n) => floorKey.has(f) || floorKey.set(f, `fo${n}`));
  for (const [bf, of] of floorPairs) {
    const changes = [];
    if (bf.name.trim() !== of.name.trim()) changes.push("renamed");
    const placementDiffers = !samePlacement(bf.placement, of.placement);
    const bw = wallsJson(b.wallsOf.get(bf));
    const ow = wallsJson(o.wallsOf.get(of));
    if (!sameNumber(bf.level, of.level) || !sameNumber(bf.height, of.height) || !sameNumber(bf.capacity, of.capacity) || placementDiffers || bw === null || ow === null || bw !== ow) {
      changes.push("changed");
    }
    if (changes.length) {
      result.floors.changed.push({ refid: null, label: of.name, floor: floorRef(of), changes });
    }
  }
  for (const f of byOrder.savedOnly) {
    result.floors.removed.push({ refid: null, label: f.name, floor: floorRef(f) });
  }
  for (const f of byOrder.currentOnly) {
    result.floors.added.push({ refid: null, label: f.name, floor: floorRef(f) });
  }
  const delta = computeVersionDelta(o.layout, b.layout, "all", {
    matchBy: "identity",
    twinBid,
    compareSurfaces: true,
    surfacePresence: true,
    oneToOne: true
  });
  const itemPairs = (delta.matches?.items ?? []).map((m) => ({
    base: m.current,
    other: m.saved,
    change: m.change
  }));
  const zonePairs = (delta.matches?.surfaces ?? []).map((m) => ({
    base: m.current,
    other: m.saved,
    change: m.change
  }));
  const pairKey = /* @__PURE__ */ new Map();
  itemPairs.forEach((p, n) => {
    pairKey.set(p.base, `ip${n}`);
    pairKey.set(p.other, `ip${n}`);
  });
  zonePairs.forEach((p, n) => {
    pairKey.set(p.base, `zp${n}`);
    pairKey.set(p.other, `zp${n}`);
  });
  const entitiesByRefid = (side) => {
    const map = /* @__PURE__ */ new Map();
    for (const design of designsOf(side)) {
      for (const entity of [...design.surfaces, ...design.items]) {
        if (entity.refid && !map.has(entity.refid)) map.set(entity.refid, entity);
      }
    }
    return map;
  };
  const baseEntities = entitiesByRefid(b);
  const otherEntities = entitiesByRefid(o);
  const surfaceIndex = /* @__PURE__ */ new WeakMap();
  const surfacesByRefid = (design) => {
    let map = surfaceIndex.get(design);
    if (!map) {
      map = /* @__PURE__ */ new Map();
      for (const s of design.surfaces) if (s.refid && !map.has(s.refid)) map.set(s.refid, s);
      surfaceIndex.set(design, map);
    }
    return map;
  };
  const parentKey = (side, entity, isItem) => {
    const design = side.designOf.get(entity);
    if (!design) return "none";
    const parentage = resolvePlanParentage(design);
    const parent = isItem ? parentage.item(entity)?.effectiveParent : parentage.surface(entity.refid)?.effectiveParent;
    if (parent == null) return `floor:${floorKey.get(side.floorOf.get(entity)) ?? ""}`;
    const zone = surfacesByRefid(design).get(parent);
    return zone && pairKey.get(zone) || `ref:${parent}`;
  };
  const pushPairChanges = (kind, pairs) => {
    const isItem = kind === "placedEquipment";
    for (const pair of pairs) {
      const changes = pair.change ? isItem ? itemChanges(pair.change) : surfaceChanges(pair.change) : [];
      if (parentKey(b, pair.base, isItem) !== parentKey(o, pair.other, isItem)) {
        changes.push("rezoned");
      }
      if (!changes.length) continue;
      const change = pair.change;
      result[kind].changed.push({
        refid: pair.other.refid || null,
        label: isItem ? deriveLabel(pair.other) : deriveSurfaceLabel(pair.other),
        floor: floorRef(o.floorOf.get(pair.other)),
        changes,
        ...change?.distance != null ? { distance: change.distance } : {},
        ...change?.rotation != null ? { rotation: change.rotation } : {}
      });
    }
  };
  pushPairChanges("placedEquipment", itemPairs);
  pushPairChanges("zones", zonePairs);
  for (const d of delta.items) {
    if (!d.entity || d.changeType !== "added" && d.changeType !== "removed") continue;
    const side = d.changeType === "added" ? o : b;
    result.placedEquipment[d.changeType].push({
      refid: d.entity.refid || null,
      label: deriveLabel(d.entity),
      floor: floorRef(side.floorOf.get(d.entity))
    });
  }
  for (const [list, side, into] of [
    [delta.surfacePresence?.added ?? [], o, result.zones.added],
    [delta.surfacePresence?.removed ?? [], b, result.zones.removed]
  ]) {
    for (const z of list) {
      into.push({
        refid: z.entity.refid || null,
        label: z.label,
        floor: floorRef(side.floorOf.get(z.entity))
      });
    }
  }
  const floorsById = (side) => {
    const map = /* @__PURE__ */ new Map();
    for (const f of side.layout.floors) if (f.id !== null && !map.has(f.id)) map.set(f.id, f);
    return map;
  };
  const baseFloorsById = floorsById(b);
  const otherFloorsById = floorsById(o);
  const floorTarget = (side, floorId) => {
    const floor = (side === b ? baseFloorsById : otherFloorsById).get(floorId);
    return floor ? `floor:${floorKey.get(floor)}` : `floor-raw:${floorId}`;
  };
  const entityTarget = (side, refid) => {
    const entity = (side === b ? baseEntities : otherEntities).get(refid);
    return entity && pairKey.get(entity) || `ref:${refid}`;
  };
  const refTarget = (side, ref) => {
    const encoded = encodeRef(ref);
    if (!encoded) return `raw:${json2(ref)}`;
    if (ref.kind === "twin") return "twin";
    if (ref.kind === "floor") return floorTarget(side, ref.floorId);
    return entityTarget(side, ref.refid);
  };
  const observerTarget = (side, observer) => {
    const floorId = floorIdFromTargetRefid(observer.targetRefid);
    if (floorId !== null) return floorTarget(side, floorId);
    return observer.targetRefid ? entityTarget(side, observer.targetRefid) : "raw:";
  };
  const cameras = (side) => designsOf(side).flatMap((d) => d.cameras ?? []);
  const cameraLabel = (side, camera) => cameraDisplayName(camera, side.cameraIndex.get(camera) ?? 0);
  const cameraPairing = pairOneToOne(cameras(b), cameras(o), [
    (c) => c.refid || null,
    (c) => c.refid ? null : `content:${json2(c)}`
  ]);
  for (const [bc, oc] of cameraPairing.pairs) {
    const changes = [];
    const moved = classifyItemChange(bc, oc);
    const otherFloor = floorKey.get(b.floorOf.get(bc)) !== floorKey.get(o.floorOf.get(oc));
    if (moved || otherFloor) changes.push("moved");
    if (streamKey(bc) !== streamKey(oc)) changes.push("relinked");
    if (!sameNumber(bc.cameraId, oc.cameraId) || (bc.name?.trim() ?? "") !== (oc.name?.trim() ?? "") || !sameNumber(bc.detectionDistance, oc.detectionDistance) || !sameNumber(bc.fovAngle, oc.fovAngle)) {
      changes.push("settings");
    }
    if (!changes.length) continue;
    result.cameras.changed.push({
      refid: oc.refid || null,
      label: cameraLabel(o, oc),
      floor: floorRef(o.floorOf.get(oc)),
      changes,
      ...moved?.distance != null ? { distance: moved.distance } : {},
      ...moved?.rotation != null ? { rotation: moved.rotation } : {}
    });
  }
  for (const c of cameraPairing.savedOnly) {
    result.cameras.removed.push({
      refid: c.refid || null,
      label: cameraLabel(b, c),
      floor: floorRef(b.floorOf.get(c))
    });
  }
  for (const c of cameraPairing.currentOnly) {
    result.cameras.added.push({
      refid: c.refid || null,
      label: cameraLabel(o, c),
      floor: floorRef(o.floorOf.get(c))
    });
  }
  const observers = (side) => designsOf(side).flatMap((d) => d.observers ?? []);
  const groupOf2 = /* @__PURE__ */ new Map();
  for (const side of [b, o]) {
    for (const observer of observers(side)) {
      groupOf2.set(observer, `${observerTarget(side, observer)}|${streamKey(observer)}`);
    }
  }
  const observerPairing = pairOneToOne(observers(b), observers(o), [
    (observer) => observer.refid ? `${groupOf2.get(observer)}|r:${observer.refid}` : null,
    (observer) => groupOf2.get(observer) ?? null
  ]);
  const baseTargetNames = observerTargetNamesByRefid(b.layout);
  const otherTargetNames = observerTargetNamesByRefid(o.layout);
  const observerEntry = (side, observer) => ({
    refid: observer.refid || null,
    label: (side === b ? baseTargetNames : otherTargetNames).get(observer.targetRefid) ?? observer.targetRefid,
    floor: floorRef(side.floorOf.get(observer))
  });
  for (const [bo, oo] of observerPairing.pairs) {
    const pointsDiffer = bo.points.length !== oo.points.length || bo.points.some(
      (p, i) => Math.hypot(p.x - oo.points[i].x, p.y - oo.points[i].y) > POINT_EPSILON
    );
    if (pointsDiffer || (bo.trackSide ?? "TORSO") !== (oo.trackSide ?? "TORSO")) {
      result.observers.changed.push({ ...observerEntry(o, oo), changes: ["changed"] });
    }
  }
  for (const ob of observerPairing.savedOnly) result.observers.removed.push(observerEntry(b, ob));
  for (const ob of observerPairing.currentOnly) result.observers.added.push(observerEntry(o, ob));
  const baseEntityNames = entityNamesByRefid(b.layout);
  const otherEntityNames = entityNamesByRefid(o.layout);
  const compositeEntry = (side, composite) => {
    const ref = composite.target;
    const encoded = encodeRef(ref);
    let label = "unreadable";
    let floor = null;
    if (encoded && ref?.kind === "twin") label = "the whole twin";
    else if (encoded && ref?.kind === "floor") {
      const f = (side === b ? baseFloorsById : otherFloorsById).get(ref.floorId);
      label = f?.name || `floor ${ref.floorId}`;
      floor = floorRef(f);
    } else if (encoded && ref?.kind === "entity") {
      label = (side === b ? baseEntityNames : otherEntityNames).get(ref.refid) ?? ref.refid;
      const entity = (side === b ? baseEntities : otherEntities).get(ref.refid);
      floor = floorRef(entity && side.floorOf.get(entity));
    }
    return { refid: composite.refid || null, label, floor };
  };
  const inputSet = (side, composite) => [...new Set(composite.inputs.map((input) => refTarget(side, input)))].sort().join("\0");
  const targetOf = /* @__PURE__ */ new Map();
  for (const c of b.layout.composites) targetOf.set(c, refTarget(b, c.target));
  for (const c of o.layout.composites) targetOf.set(c, refTarget(o, c.target));
  const compositePairing = pairOneToOne(b.layout.composites, o.layout.composites, [
    (c) => targetOf.get(c) ?? null
  ]);
  for (const [bc, oc] of compositePairing.pairs) {
    if (inputSet(b, bc) !== inputSet(o, oc) || !!bc.includesOwnObservers !== !!oc.includesOwnObservers) {
      result.compositeMeasures.changed.push({ ...compositeEntry(o, oc), changes: ["changed"] });
    }
  }
  for (const c of compositePairing.savedOnly) {
    result.compositeMeasures.removed.push(compositeEntry(b, c));
  }
  for (const c of compositePairing.currentOnly) {
    result.compositeMeasures.added.push(compositeEntry(o, c));
  }
  const byStep = Object.fromEntries(
    PLAN_STEP_IDS.map((step) => [step, { added: 0, removed: 0, changed: 0, total: 0 }])
  );
  let total = 0;
  for (const kind of Object.keys(PLAN_KIND_STEP)) {
    const totals = byStep[PLAN_KIND_STEP[kind]];
    const { added, removed, changed } = result[kind];
    totals.added += added.length;
    totals.removed += removed.length;
    totals.changed += changed.length;
    const n = added.length + removed.length + changed.length;
    totals.total += n;
    total += n;
  }
  return { ...result, byStep, total, counts: { base: countsOf(b), other: countsOf(o) } };
}

// src/twin-planner/draftEntityChanges.ts
var INDEXED_KINDS = [
  "zones",
  "placedEquipment",
  "cameras",
  "observers",
  "compositeMeasures"
];
var isKey = (refid) => typeof refid === "string" && refid !== "";
var draftEntityChanges = (comparison, renumbered) => {
  const index = /* @__PURE__ */ new Map();
  const entryFor = (refid, kind) => {
    let entry = index.get(refid);
    if (!entry) {
      entry = {
        kind,
        step: PLAN_KIND_STEP[kind],
        added: false,
        moved: false,
        rezoned: false,
        renumbered: null
      };
      index.set(refid, entry);
    }
    return entry;
  };
  if (comparison) {
    INDEXED_KINDS.forEach((kind) => {
      comparison[kind].added.forEach((added) => {
        if (isKey(added.refid)) entryFor(added.refid, kind).added = true;
      });
      comparison[kind].changed.forEach((changed) => {
        const moved = changed.changes.includes("moved");
        const rezoned = changed.changes.includes("rezoned");
        if (!isKey(changed.refid) || !moved && !rezoned) return;
        const entry = entryFor(changed.refid, kind);
        entry.moved = entry.moved || moved;
        entry.rezoned = entry.rezoned || rezoned;
      });
    });
  }
  renumbered.forEach((change) => {
    if (!change || !isKey(change.refid)) return;
    const entry = entryFor(change.refid, "zones");
    if (!entry.renumbered) entry.renumbered = { from: change.from, to: change.to };
  });
  return index;
};

// src/twinsense/resolveActiveConfigId.ts
var resolveActiveConfigId = (configs) => {
  if (!Array.isArray(configs) || configs.length === 0) return null;
  const byIdDescending = (a, b) => b.id - a.id;
  const actives = configs.filter((c) => c.is_active).sort(byIdDescending);
  if (actives.length > 0) {
    return {
      id: actives[0].id,
      // Ascending, so a rendered list reads in the order a person expects.
      activeIds: actives.map((c) => c.id).reverse()
    };
  }
  const newest = configs.slice().sort(byIdDescending)[0];
  return { id: newest.id, activeIds: [] };
};

// src/twin-planner/activationDelta.ts
var STRUCTURAL_TYPES2 = /* @__PURE__ */ new Set(["wall", "floor"]);
var collectEntities = (root) => {
  const entities = /* @__PURE__ */ new Map();
  const structural = /* @__PURE__ */ new Map();
  const visit = (node, isRoot) => {
    if (!node) return;
    const bID = typeof node.bID === "string" ? node.bID : "";
    const typeName2 = typeof node.type?.name === "string" ? node.type.name.toLowerCase() : "";
    if (bID && !isRoot) {
      const name = typeof node.name === "string" && node.name.trim() ? node.name.trim() : bID;
      if (STRUCTURAL_TYPES2.has(typeName2)) {
        if (!structural.has(bID)) structural.set(bID, { bID, name });
      } else if (!entities.has(bID)) {
        entities.set(bID, { bID, name, kind: typeName2 === "asset" ? "machine" : "area" });
      }
    }
    for (const child of asArray(node.children)) visit(child, false);
  };
  visit(root, true);
  return { entities, structural };
};
var computeActivationDelta = (live2, planned) => {
  const liveSide = collectEntities(live2);
  const plannedSide = collectEntities(planned);
  const added = [];
  const kept = [];
  for (const bID of Array.from(plannedSide.entities.keys())) {
    if (liveSide.entities.has(bID)) kept.push(bID);
    else added.push(bID);
  }
  const removed = Array.from(liveSide.entities.values()).filter(
    (e) => !plannedSide.entities.has(e.bID)
  );
  const removedStructural = Array.from(liveSide.structural.values()).filter(
    (e) => !plannedSide.structural.has(e.bID)
  );
  return { added, kept, removed, removedStructural };
};
var withoutRenumbered = (delta, changes) => {
  if (changes.length === 0) return delta;
  const renumbered = new Set(changes.map((c) => entityIdentityKey(c.from)));
  return {
    ...delta,
    removed: delta.removed.filter((e) => !renumbered.has(entityIdentityKey(e.bID)))
  };
};
var liveEntityBidsOf = (root) => Array.from(collectEntities(root).entities.keys());

// src/twin-planner/entityCodeChanges.ts
var ENTITY_TAIL = /^[ZSAE]\d+$/;
function isMintedIdentity(id, twinBid) {
  const twin = entityIdentityKey(twinBid);
  const tail = twin === "" ? id : stripTwinBidPrefix(id, twin, twin);
  return ENTITY_TAIL.test(tail);
}
var identity = (twinBid, raw) => typeof raw === "string" ? entityIdentityFor(twinBid, raw) : "";
var recordCodeChange = (changes, args) => {
  const list = asArray(changes).filter(
    (c) => !!c && typeof c.refid === "string"
  );
  const to = identity(args.twinBid, args.to);
  const existing = list.find((c) => c.refid === args.refid);
  const others2 = list.filter((c) => c.refid !== args.refid);
  if (existing) {
    const from2 = identity(args.twinBid, existing.from);
    if (from2 === "" || to === "" || from2 === to) return others2;
    return [...others2, { refid: args.refid, from: from2, to }];
  }
  if (args.live === false) return others2;
  const from = identity(args.twinBid, args.from);
  if (from === "" || to === "" || from === to) return others2;
  return [...others2, { refid: args.refid, from, to }];
};
var pendingCodeChanges = (layout, twinBid) => {
  const changes = asArray(layout?.entityCodeChanges);
  if (changes.length === 0) return [];
  const codeByRefid = /* @__PURE__ */ new Map();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      if (typeof surface.refid === "string") {
        codeByRefid.set(surface.refid, identity(twinBid, surface.entityCode));
      }
    }
  }
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const change of changes) {
    if (!change || typeof change.refid !== "string" || seen.has(change.refid)) continue;
    const from = identity(twinBid, change.from);
    const to = identity(twinBid, change.to);
    if (from === "" || to === "" || from === to) continue;
    if (!isMintedIdentity(from, twinBid) || !isMintedIdentity(to, twinBid)) continue;
    if (codeByRefid.get(change.refid) !== to) continue;
    seen.add(change.refid);
    out.push({ refid: change.refid, from, to });
  }
  return out;
};
var effectiveCodeChanges = (layout, liveBids, twinBid) => {
  const known = /* @__PURE__ */ new Set();
  Array.from(liveBids ?? []).forEach((bid) => {
    const id = identity(twinBid, bid);
    if (id) known.add(id);
  });
  asArray(layout?.retiredEntityCodes).forEach((code) => {
    const id = identity(twinBid, code);
    if (id) known.add(id);
  });
  return pendingCodeChanges(layout, twinBid).filter((c) => known.has(c.from));
};
var originalIdentityOf = (layout, refid) => {
  if (typeof refid !== "string") return void 0;
  const entry = asArray(layout?.entityCodeChanges).find((c) => c?.refid === refid);
  return typeof entry?.from === "string" && entry.from.trim() !== "" ? entry.from : void 0;
};
var renamesOf = (changes) => new Map(changes.map((c) => [entityIdentityKey(c.from), entityIdentityKey(c.to)]));
var renumberBid = (bid, renames) => {
  if (renames.size === 0 || typeof bid !== "string") return bid;
  const folded = entityIdentityKey(bid);
  const exact = renames.get(folded);
  if (exact) return exact;
  const dot2 = folded.lastIndexOf(".");
  if (dot2 > 0 && /^\d+$/.test(folded.slice(dot2 + 1))) {
    const base = renames.get(folded.slice(0, dot2));
    if (base) return `${base}${folded.slice(dot2)}`;
  }
  return bid;
};
var retiredAfterLaunch = (live2, launched, existing, twinBid) => {
  const out = /* @__PURE__ */ new Set();
  asArray(existing).forEach((code) => {
    const id = identity(twinBid, code);
    if (id) out.add(id);
  });
  computeActivationDelta(live2, launched).removed.forEach(({ bID }) => {
    const id = identity(twinBid, bID);
    if (id && isMintedIdentity(id, twinBid)) out.add(id);
  });
  return Array.from(out).sort();
};

// src/twin-planner/stampEntityCodes.ts
var trimmed2 = (value) => typeof value === "string" ? value.trim() : "";
var stampEntityCodes = (layout, refidToEntityCode, twinBid) => {
  const floors = asArray(layout?.floors);
  const minted = refidToEntityCode ?? {};
  const normalised = (value) => entityIdentityFor(twinBid ?? "", typeof value === "string" ? value : "");
  if (!layout || floors.length === 0) {
    return { layout, changed: false, collisions: [] };
  }
  const taken = /* @__PURE__ */ new Set();
  for (const floor of floors) {
    const design = findLatestDesign(asArray(floor?.designs));
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      const code = normalised(surface.entityCode);
      if (code) taken.add(code);
    }
    for (const item of asArray(design?.items).filter(isLive)) {
      const code = normalised(item.entityCode);
      if (code) taken.add(code);
    }
  }
  let changed = false;
  const collisions = [];
  const stamp = (entry) => {
    if (!isLive(entry)) return entry;
    if (trimmed2(entry.entityCode)) return entry;
    const code = Object.prototype.hasOwnProperty.call(minted, entry.refid) ? normalised(minted[entry.refid]) : "";
    if (!code) return entry;
    if (taken.has(code)) {
      collisions.push(code);
      return entry;
    }
    taken.add(code);
    changed = true;
    return { ...entry, entityCode: code };
  };
  const nextFloors = floors.map((floor) => {
    const designs = asArray(floor?.designs);
    const latest = findLatestDesign(designs);
    const designIndex = designs.indexOf(latest);
    if (designIndex === -1) return floor;
    const nextDesign = {
      ...latest,
      surfaces: asArray(latest?.surfaces).map(stamp),
      items: asArray(latest?.items).map(stamp)
    };
    const nextDesigns = [...designs];
    nextDesigns[designIndex] = nextDesign;
    return { ...floor, designs: nextDesigns };
  });
  return {
    layout: changed ? { ...layout, floors: nextFloors } : layout,
    changed,
    collisions
  };
};

// src/twin-planner/itemBaseName.ts
var resolveItemBaseName = (parts2) => {
  for (const candidate of [parts2.product, parts2.productLine, parts2.manufacturer]) {
    const trimmed3 = (candidate ?? "").trim();
    if (trimmed3) return trimmed3;
  }
  return "Asset";
};

// src/twin-planner/planItemIds.ts
var newWallId = () => newClientId({ uuidPrefix: "wall-", fallbackPrefix: "wall-" });
var isEntry = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function mapKeepingIdentity(list, visit) {
  let changed = false;
  const next = list.map((value, at) => {
    const visited = visit(value, at);
    if (visited !== value) changed = true;
    return visited;
  });
  return changed ? next : list;
}
function walkFloor(floor, floorAt, visit) {
  if (!isEntry(floor) || !Array.isArray(floor.designs)) return floor;
  const floorKey = Number.isFinite(floor.id) ? String(floor.id) : `p${floorAt}`;
  const designs = mapKeepingIdentity(floor.designs, (design) => {
    if (!isEntry(design) || !Array.isArray(design.walls)) return design;
    const walls = mapKeepingIdentity(
      design.walls,
      (wall, wallAt) => isEntry(wall) ? visit(wall, floorKey, wallAt) : wall
    );
    return walls === design.walls ? design : { ...design, walls };
  });
  return designs === floor.designs ? floor : { ...floor, designs };
}
function walkWalls(layout, visit) {
  const { floors, removedFloors } = layout;
  const nextFloors = Array.isArray(floors) ? mapKeepingIdentity(floors, (floor, at) => walkFloor(floor, at, visit)) : floors;
  const nextRemoved = Array.isArray(removedFloors) ? mapKeepingIdentity(removedFloors, (record, at) => {
    if (!isEntry(record)) return record;
    const floor = walkFloor(record.floor, at, visit);
    return floor === record.floor ? record : { ...record, floor };
  }) : removedFloors;
  if (nextFloors === floors && nextRemoved === removedFloors) return layout;
  return {
    ...layout,
    floors: nextFloors,
    ...nextRemoved === removedFloors ? {} : { removedFloors: nextRemoved }
  };
}
function ensurePlanItemIds(layout) {
  if (!isEntry(layout)) return layout;
  const taken = /* @__PURE__ */ new Set();
  const keeps = [];
  walkWalls(layout, (wall) => {
    const { id } = wall;
    const usable = typeof id === "string" && id.trim() !== "" && !taken.has(id);
    if (usable) taken.add(id);
    keeps.push(usable);
    return wall;
  });
  let slot = 0;
  return walkWalls(layout, (wall, floorKey, wallAt) => {
    if (keeps[slot++]) return wall;
    const computed = `wall-${floorKey}-${wallAt}`;
    let id = computed;
    for (let n = 2; taken.has(id); n += 1) id = `${computed}-${n}`;
    taken.add(id);
    return { ...wall, id };
  });
}

// src/twin-planner/wallRun.ts
function wallRunPresentIds(run, walls) {
  const onPlan = new Set(walls.map((wall) => wall.id));
  return run.committed.filter((id) => onPlan.has(id));
}
function wallRunPresent(run, walls) {
  return wallRunPresentIds(run, walls).length;
}
function wallRunCurrent(run, walls) {
  const newest = wallRunPresentIds(run, walls).at(-1);
  if (newest === void 0) return run.start;
  return walls.find((wall) => wall.id === newest)?.b ?? run.start;
}

// src/twin-planner/wallSnap.ts
var CORNER_PX = 14;
var WALL_PX = 10;
var AXIS_PX = 8;
var JOINT_EPSILON_M = 1e-3;
function closestOnSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return { x: a.x, y: a.y };
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return { x: a.x + t * dx, y: a.y + t * dy };
}
function axisCrossing(anchor, axis, a, b) {
  const [fixed, from, to] = axis === "h" ? [anchor.y, a.y, b.y] : [anchor.x, a.x, b.x];
  if (from === to) return null;
  const t = (fixed - from) / (to - from);
  if (t < 0 || t > 1) return null;
  return axis === "h" ? { x: a.x + t * (b.x - a.x), y: anchor.y } : { x: anchor.x, y: a.y + t * (b.y - a.y) };
}
function nearest(raw, ctx, reach, candidates) {
  let best = null;
  let bestDistance = reach;
  ctx.walls.forEach((wall, wallIndex) => {
    if (ctx.exclude?.has(wallIndex)) return;
    for (const at of candidates(wall)) {
      if (!at) continue;
      const d = distance(raw, at);
      if (d <= bestDistance) {
        bestDistance = d;
        best = { at: { x: at.x, y: at.y }, wallIndex };
      }
    }
  });
  return best;
}
function snapWallPoint(raw, ctx) {
  if (ctx.free) return { point: raw, snap: { kind: "free" } };
  const corner = nearest(raw, ctx, CORNER_PX * ctx.metresPerScreenPx, (w) => [w.a, w.b]);
  if (corner) return { point: corner.at, snap: { kind: "corner", at: corner.at } };
  const anchor = ctx.anchor ?? null;
  const axisReach = AXIS_PX * ctx.metresPerScreenPx;
  let axis = null;
  if (anchor) {
    const offH = Math.abs(raw.y - anchor.y);
    const offV = Math.abs(raw.x - anchor.x);
    if (Math.min(offH, offV) <= axisReach) axis = offH <= offV ? "h" : "v";
  }
  const wallReach = WALL_PX * ctx.metresPerScreenPx;
  if (anchor && axis) {
    const lockedAxis = axis;
    const crossing = nearest(raw, ctx, wallReach, (w) => [
      axisCrossing(anchor, lockedAxis, w.a, w.b)
    ]);
    if (crossing) {
      return {
        point: crossing.at,
        snap: {
          kind: "axis-wall",
          axis,
          from: anchor,
          wallIndex: crossing.wallIndex,
          at: crossing.at
        }
      };
    }
  }
  const onWall = nearest(raw, ctx, wallReach, (w) => [closestOnSegment(raw, w.a, w.b)]);
  if (onWall) {
    return { point: onWall.at, snap: { kind: "wall", wallIndex: onWall.wallIndex, at: onWall.at } };
  }
  const gridded = snapPlannerPoint(raw, {
    gridSnap: ctx.gridSnap,
    gridSizeCm: ctx.gridSizeCm,
    free: false
  });
  if (anchor && axis) {
    const point = axis === "h" ? { x: gridded.x, y: anchor.y } : { x: anchor.x, y: gridded.y };
    return { point, snap: { kind: "axis", axis, from: anchor } };
  }
  return { point: gridded, snap: { kind: "grid" } };
}
function jointEndsAt(walls, point) {
  const ends = [];
  walls.forEach((wall, index) => {
    if (distance(wall.a, point) <= JOINT_EPSILON_M) ends.push({ index, end: "a" });
    if (distance(wall.b, point) <= JOINT_EPSILON_M) ends.push({ index, end: "b" });
  });
  return ends;
}

// src/twin-planner/planOutline.ts
var NEAR_LINE_RATIO = 0.15;
var OUTLINE_STEP_M = 0.1;
var MAX_OUTLINE_SAMPLES = 512;
function scatterEigenvalues(points2) {
  const n = points2.length;
  if (n < 2) return { larger: 0, smaller: 0 };
  let mx = 0;
  let my = 0;
  for (const [x, y] of points2) {
    mx += x / n;
    my += y / n;
  }
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const [x, y] of points2) {
    const dx = x - mx;
    const dy = y - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  const mean = (sxx + syy) / 2;
  const spread = Math.sqrt(((sxx - syy) / 2) ** 2 + sxy * sxy);
  return { larger: mean + spread, smaller: Math.max(0, mean - spread) };
}
function collinearityRatio(points2) {
  const { larger, smaller } = scatterEigenvalues(points2);
  if (!(larger > 0)) return 0;
  return Math.sqrt(smaller / larger);
}
function footprintCorners(item, size) {
  const rad = (item.rotation ?? 0) * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const hw = size.width / 2;
  const hd = size.depth / 2;
  return [
    [-hw, -hd],
    [hw, -hd],
    [hw, hd],
    [-hw, hd]
  ].map(([lx, ly]) => [item.x + lx * cos - ly * sin, item.y + lx * sin + ly * cos]);
}
var FOOTPRINT_OUTLINE_STEP_M = 0.25;
function sampleRing(corners, step) {
  const out = [];
  corners.forEach((a, k) => {
    const b = corners[(k + 1) % corners.length];
    const n = Math.min(
      MAX_OUTLINE_SAMPLES,
      Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / step))
    );
    for (let i = 0; i < n; i++) {
      const t = i / n;
      out.push({ x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t });
    }
  });
  return out;
}
var hullCross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
function convexHull(points2) {
  const pts = [...points2].sort((a, b) => a.x - b.x || a.y - b.y);
  const unique = [];
  for (const p of pts) {
    const last = unique[unique.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) unique.push(p);
  }
  if (unique.length < 3) return unique;
  const lower = [];
  for (const p of unique) {
    while (lower.length >= 2 && hullCross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = unique.length - 1; i >= 0; i--) {
    const p = unique[i];
    while (upper.length >= 2 && hullCross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}
function clipToTile(polygon, rect) {
  const [rx, ry, w, h] = rect;
  const lerp = (a, b, t) => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t
  });
  const edges = [
    { inside: (p) => p.x >= 0, cut: (a, b) => lerp(a, b, (0 - a.x) / (b.x - a.x)) },
    { inside: (p) => p.x <= w, cut: (a, b) => lerp(a, b, (w - a.x) / (b.x - a.x)) },
    { inside: (p) => p.y >= 0, cut: (a, b) => lerp(a, b, (0 - a.y) / (b.y - a.y)) },
    { inside: (p) => p.y <= h, cut: (a, b) => lerp(a, b, (h - a.y) / (b.y - a.y)) }
  ];
  let output = polygon.map((p) => ({ x: p.x - rx, y: p.y - ry }));
  for (const edge of edges) {
    const input = output;
    output = [];
    for (let i = 0; i < input.length; i++) {
      const cur = input[i];
      const prev = input[(i + input.length - 1) % input.length];
      if (edge.inside(cur)) {
        if (!edge.inside(prev)) output.push(edge.cut(prev, cur));
        output.push(cur);
      } else if (edge.inside(prev)) {
        output.push(edge.cut(prev, cur));
      }
    }
    if (output.length === 0) break;
  }
  return output.map((p) => ({ x: p.x + rx, y: p.y + ry }));
}
function ringArea(ring) {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % ring.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}
function footprintOutlineInPicture(corners, project, rect) {
  if (corners.length === 0) return [];
  const samples = sampleRing(corners, FOOTPRINT_OUTLINE_STEP_M).map((p) => project(p)).filter((p) => p !== null);
  const outline = clipToTile(convexHull(samples), rect);
  return outline.length < 3 || !(ringArea(outline) > 0) ? [] : outline;
}
function projectPlanOutline(polygon, project) {
  if (polygon.length === 0) return { closed: true, paths: [] };
  const runs = [[]];
  let dropped = false;
  polygon.forEach((a, k) => {
    const b = polygon[(k + 1) % polygon.length];
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const samples = Math.min(MAX_OUTLINE_SAMPLES, Math.max(1, Math.ceil(length / OUTLINE_STEP_M)));
    for (let i = 0; i < samples; i++) {
      const t = i / samples;
      const q = project({ x: a[0] + t * (b[0] - a[0]), y: a[1] + t * (b[1] - a[1]) });
      if (q && Number.isFinite(q.x) && Number.isFinite(q.y)) {
        runs[runs.length - 1].push(q);
      } else {
        dropped = true;
        if (runs[runs.length - 1].length > 0) runs.push([]);
      }
    }
  });
  if (!dropped) return { closed: true, paths: runs };
  const first = project({ x: polygon[0][0], y: polygon[0][1] });
  if (first && runs.length > 1 && runs[0].length > 0) {
    const last = runs.pop();
    runs[0] = [...last, ...runs[0]];
  }
  return { closed: false, paths: runs.filter((r) => r.length >= 2) };
}
var atX = (a, b, x) => ({
  x,
  y: a.y + (x - a.x) / (b.x - a.x) * (b.y - a.y)
});
var atY = (a, b, y) => ({
  x: a.x + (y - a.y) / (b.y - a.y) * (b.x - a.x),
  y
});
function clipPolygonToRect(polygon, rect) {
  const [left, top, width, height] = rect;
  const right = left + width;
  const bottom = top + height;
  const edges = [
    { inside: (p) => p.x >= left, cross: (a, b) => atX(a, b, left) },
    { inside: (p) => p.x <= right, cross: (a, b) => atX(a, b, right) },
    { inside: (p) => p.y >= top, cross: (a, b) => atY(a, b, top) },
    { inside: (p) => p.y <= bottom, cross: (a, b) => atY(a, b, bottom) }
  ];
  let out = polygon.map(({ x, y }) => ({ x, y }));
  for (const edge of edges) {
    const input = out;
    out = [];
    input.forEach((a, k) => {
      const b = input[(k + 1) % input.length];
      const aIn = edge.inside(a);
      if (aIn) out.push(a);
      if (aIn !== edge.inside(b)) out.push(edge.cross(a, b));
    });
    if (out.length === 0) break;
  }
  return out;
}
function clipPolylineToRect(path, rect) {
  const [left, top, width, height] = rect;
  const right = left + width;
  const bottom = top + height;
  const out = [];
  let current = [];
  const close = () => {
    if (current.length >= 2) out.push(current);
    current = [];
  };
  for (let k = 0; k + 1 < path.length; k++) {
    const a = path[k];
    const b = path[k + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    let t0 = 0;
    let t1 = 1;
    const sides = [
      [-dx, a.x - left],
      [dx, right - a.x],
      [-dy, a.y - top],
      [dy, bottom - a.y]
    ];
    let visible = true;
    for (const [p, q] of sides) {
      if (p === 0) {
        if (q < 0) visible = false;
      } else {
        const t = q / p;
        if (p < 0) t0 = Math.max(t0, t);
        else t1 = Math.min(t1, t);
      }
      if (t0 > t1) visible = false;
    }
    if (!visible) {
      close();
      continue;
    }
    const start = { x: a.x + t0 * dx, y: a.y + t0 * dy };
    const end = { x: a.x + t1 * dx, y: a.y + t1 * dy };
    if (t0 > 0) close();
    if (current.length === 0) current.push(start);
    current.push(end);
    if (t1 < 1) close();
  }
  close();
  return out;
}

// src/model-file/modelFootprint.ts
var MODEL_BOX_PADDING_M = 0.02;
var MODEL_FOOTPRINT_FALLBACK = {
  widthM: 1,
  depthM: 1.2,
  centreXM: 0,
  centreZM: 0
};
var GLB_MAGIC = 1179937895;
var GLB_CHUNK_JSON = 1313821514;
var GLB_HEADER_BYTES = 12;
var GLB_PREFIX_BYTES = 20;
var MAX_GRAPH_VISITS = 1e6;
function readGlbJson(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.byteLength >= 4 && view.getUint32(0, true) !== GLB_MAGIC) {
    return { ok: false, reason: "not-glb" };
  }
  if (bytes.byteLength >= 8 && view.getUint32(4, true) !== 2) {
    return { ok: false, reason: "unsupported-version" };
  }
  if (bytes.byteLength < GLB_PREFIX_BYTES) {
    return { ok: false, reason: "truncated", needBytes: GLB_PREFIX_BYTES };
  }
  const jsonChunkLength = view.getUint32(GLB_HEADER_BYTES, true);
  if (view.getUint32(GLB_HEADER_BYTES + 4, true) !== GLB_CHUNK_JSON) {
    return { ok: false, reason: "no-json-chunk" };
  }
  const needBytes = GLB_PREFIX_BYTES + jsonChunkLength;
  if (needBytes > view.getUint32(8, true)) {
    return { ok: false, reason: "no-json-chunk" };
  }
  if (bytes.byteLength < needBytes) {
    return { ok: false, reason: "truncated", needBytes };
  }
  const text4 = new TextDecoder("utf-8").decode(bytes.subarray(GLB_PREFIX_BYTES, needBytes));
  let json3;
  try {
    json3 = JSON.parse(text4);
  } catch {
    return { ok: false, reason: "invalid-json" };
  }
  if (!isRecord(json3)) return { ok: false, reason: "invalid-json" };
  return { ok: true, json: json3 };
}
function modelFootprint(gltfJson) {
  if (!isRecord(gltfJson)) return { ok: false, reason: "no-scene" };
  const scenes = gltfJson.scenes;
  const sceneIndex = gltfJson.scene ?? 0;
  if (!Array.isArray(scenes) || !isIndex(sceneIndex, scenes.length)) {
    return { ok: false, reason: "no-scene" };
  }
  const scene = scenes[sceneIndex];
  if (!isRecord(scene)) return { ok: false, reason: "no-scene" };
  const nodes = Array.isArray(gltfJson.nodes) ? gltfJson.nodes : [];
  const meshes = Array.isArray(gltfJson.meshes) ? gltfJson.meshes : [];
  const accessors = Array.isArray(gltfJson.accessors) ? gltfJson.accessors : [];
  const movedLater = /* @__PURE__ */ new Set();
  for (let i = sceneIndex + 1; i < scenes.length; i += 1) {
    const later = scenes[i];
    if (isRecord(later) && Array.isArray(later.nodes)) later.nodes.forEach((n) => movedLater.add(n));
  }
  const meshScan = /* @__PURE__ */ new Map();
  let last;
  let candidateCount = 0;
  let visitsLeft = MAX_GRAPH_VISITS;
  const walk = (sceneDef, select) => {
    if (!isRecord(sceneDef)) return "invalid-graph";
    const roots = sceneDef.nodes ?? [];
    if (!Array.isArray(roots)) return "invalid-graph";
    const stack = [...roots].reverse();
    const visited = /* @__PURE__ */ new Set();
    while (stack.length > 0) {
      const index = stack.pop();
      visitsLeft -= 1;
      if (visitsLeft < 0) return "invalid-graph";
      if (!isIndex(index, nodes.length) || visited.has(index)) return "invalid-graph";
      visited.add(index);
      const node = nodes[index];
      if (!isRecord(node)) return "invalid-graph";
      const moved = select && movedLater.has(index);
      if (node.mesh !== void 0) {
        if (!isIndex(node.mesh, meshes.length)) return "invalid-graph";
        let scan = meshScan.get(node.mesh);
        if (!scan) {
          const mesh = meshes[node.mesh];
          const primitives = isRecord(mesh) ? mesh.primitives : void 0;
          if (!Array.isArray(primitives)) return "invalid-graph";
          scan = { count: 0 };
          for (const primitive of primitives) {
            if (!isRecord(primitive)) continue;
            if (!isSupportedMode(primitive.mode)) return "invalid-graph";
            if (isMeshMode(primitive.mode)) {
              scan.last = primitive;
              scan.count += 1;
            }
          }
          meshScan.set(node.mesh, scan);
        }
        if (select && !moved) {
          if (scan.last) last = scan.last;
          candidateCount += scan.count;
        }
      }
      const children = node.children ?? [];
      if (!Array.isArray(children)) return "invalid-graph";
      for (let i = children.length - 1; i >= 0; i -= 1) {
        if (moved) movedLater.add(children[i]);
        stack.push(children[i]);
      }
    }
    return void 0;
  };
  const failed = walk(scene, true);
  if (failed) return { ok: false, reason: failed };
  for (let i = 0; i < scenes.length; i += 1) {
    if (i === sceneIndex) continue;
    const otherFailed = walk(scenes[i], false);
    if (otherFailed) return { ok: false, reason: otherFailed };
  }
  if (!last) return { ok: false, reason: "no-mesh" };
  const attributes = last.attributes;
  const position = isRecord(attributes) ? attributes.POSITION : void 0;
  if (!isIndex(position, accessors.length)) return { ok: false, reason: "no-position" };
  const box = accessorBox(accessors[position]);
  if (!box) return { ok: false, reason: "no-bounds" };
  const [min, max] = box;
  if (last.targets !== void 0) {
    if (!Array.isArray(last.targets)) return { ok: false, reason: "invalid-graph" };
    const displacement = [0, 0, 0];
    for (const target of last.targets) {
      const targetPosition = isRecord(target) ? target.POSITION : void 0;
      if (targetPosition === void 0) continue;
      if (!isIndex(targetPosition, accessors.length)) {
        return { ok: false, reason: "invalid-graph" };
      }
      const targetAccessor = accessors[targetPosition];
      if (isUnloadableNormalized(targetAccessor)) return { ok: false, reason: "no-bounds" };
      const targetBox = accessorBox(targetAccessor);
      if (!targetBox) {
        const carriesBounds = isRecord(targetAccessor) && targetAccessor.min !== void 0 && targetAccessor.max !== void 0;
        if (carriesBounds) return { ok: false, reason: "no-bounds" };
        continue;
      }
      for (let axis = 0; axis < 3; axis += 1) {
        const extent = Math.max(Math.abs(targetBox[0][axis]), Math.abs(targetBox[1][axis]));
        displacement[axis] = Math.max(displacement[axis], extent);
      }
    }
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] -= displacement[axis];
      max[axis] += displacement[axis];
    }
  }
  const footprint = {
    widthM: max[0] - min[0] + MODEL_BOX_PADDING_M,
    heightM: max[1] - min[1] + MODEL_BOX_PADDING_M,
    depthM: max[2] - min[2] + MODEL_BOX_PADDING_M,
    centreXM: (min[0] + max[0]) / 2,
    centreZM: (min[2] + max[2]) / 2,
    bounds: { min, max },
    candidateCount
  };
  const sane = [0, 1, 2].every((axis) => max[axis] >= min[axis]) && [
    footprint.widthM,
    footprint.heightM,
    footprint.depthM,
    footprint.centreXM,
    footprint.centreZM
  ].every(Number.isFinite);
  if (!sane) return { ok: false, reason: "no-bounds" };
  return { ok: true, footprint };
}
function modelFootprintPlanCentre(item, footprint) {
  const rotation = item.rotation ?? 0;
  let mirrorX = 1;
  let mirrorZ = 1;
  if (Array.isArray(item.mirrored)) {
    const [horizontal, vertical] = item.mirrored;
    if (Math.abs(rotation % 180) === 90) {
      if (horizontal === 1) mirrorX = -1;
      if (vertical === 1) mirrorZ = -1;
    } else {
      if (horizontal === 1) mirrorZ = -1;
      if (vertical === 1) mirrorX = -1;
    }
  }
  const localX = footprint.centreXM * mirrorX;
  const localZ = footprint.centreZM * mirrorZ;
  const turn = rotation * Math.PI / 180;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  return {
    x: item.x + localX * cos + localZ * sin,
    y: item.y + localX * sin - localZ * cos
  };
}
var NORMALIZED_SCALE = {
  5120: 1 / 127,
  // BYTE
  5121: 1 / 255,
  // UNSIGNED_BYTE
  5122: 1 / 32767,
  // SHORT
  5123: 1 / 65535
  // UNSIGNED_SHORT
};
function accessorBox(accessor) {
  if (!isRecord(accessor) || !isVec3(accessor.min) || !isVec3(accessor.max)) return void 0;
  let scale = 1;
  if (accessor.normalized === true) {
    const componentType = accessor.componentType;
    if (typeof componentType !== "number" || !(componentType in NORMALIZED_SCALE)) {
      return void 0;
    }
    scale = NORMALIZED_SCALE[componentType];
  }
  const min = [
    accessor.min[0] * scale,
    accessor.min[1] * scale,
    accessor.min[2] * scale
  ];
  const max = [
    accessor.max[0] * scale,
    accessor.max[1] * scale,
    accessor.max[2] * scale
  ];
  return [min, max];
}
function isUnloadableNormalized(accessor) {
  if (!isRecord(accessor) || accessor.normalized !== true) return false;
  const componentType = accessor.componentType;
  return typeof componentType !== "number" || !(componentType in NORMALIZED_SCALE);
}
function isSupportedMode(mode) {
  return mode === void 0 || typeof mode === "number" && Number.isInteger(mode) && mode >= 0 && mode <= 6;
}
function isMeshMode(mode) {
  return mode === void 0 || mode === 4 || mode === 5 || mode === 6;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isIndex(value, length) {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value < length;
}
function isVec3(value) {
  return Array.isArray(value) && value.length >= 3 && value.slice(0, 3).every((v) => typeof v === "number" && Number.isFinite(v));
}

// src/twin-planner/cameraModel.ts
var isObserverPair = (pair) => pair.source === "observer";
var NEWTON_MAX_ITERATIONS = 40;
var NEWTON_TOLERANCE = 1e-12;
var SOLVER_MAX_ITERATIONS = 200;
var START_HEADINGS = 12;
var SIGMA_PX_RATIO = 5e-3;
var PRIOR_POSITION_SIGMA_M = 1.5;
var PRIOR_HEIGHT_M = 3;
var PRIOR_HEIGHT_SIGMA_M = 1.5;
var HEIGHT_MIN_M = 1.5;
var HEIGHT_MAX_M = 10;
var PRIOR_NADIR_SIGMA_DEG = 10;
var PRIOR_FOCAL_SIGMA_RATIO = 0.25;
var PRIOR_K_SIGMA = 0.1;
var START_PITCH_DEG = 50;
var PRIOR_OBSERVER_HEIGHT_M = 0.8;
var PRIOR_OBSERVER_HEIGHT_SIGMA_M = 0.4;
var OBSERVER_HEIGHT_MIN_M = 0;
var OBSERVER_HEIGHT_MAX_M = 1.5;
var OBSERVER_BOUND_SIGMA_M = 0.01;
var MIN_FIT_PAIRS = 4;
var MIN_DISAGREE_PAIRS = 5;
var DEG = Math.PI / 180;
var dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
var cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0]
];
function cameraAxes(model) {
  const yaw = model.yaw * DEG;
  const pitch = model.pitch * DEG;
  const roll = model.roll * DEG;
  const forward = [
    Math.sin(pitch) * Math.cos(yaw),
    Math.sin(pitch) * Math.sin(yaw),
    -Math.cos(pitch)
  ];
  const up = [
    Math.cos(pitch) * Math.cos(yaw),
    Math.cos(pitch) * Math.sin(yaw),
    Math.sin(pitch)
  ];
  const down0 = [-up[0], -up[1], -up[2]];
  const right0 = cross(down0, forward);
  const c = Math.cos(roll);
  const s = Math.sin(roll);
  return {
    right: [
      c * right0[0] + s * down0[0],
      c * right0[1] + s * down0[1],
      c * right0[2] + s * down0[2]
    ],
    down: [
      -s * right0[0] + c * down0[0],
      -s * right0[1] + c * down0[1],
      -s * right0[2] + c * down0[2]
    ],
    forward
  };
}
var lensPolynomial = (model, v) => v + model.k1 * v ** 3 + model.k2 * v ** 5;
var lensSlope = (model, v) => 1 + 3 * model.k1 * v ** 2 + 5 * model.k2 * v ** 4;
function polynomialLimit(model) {
  const a = 5 * model.k2;
  const b = 3 * model.k1;
  let u = Infinity;
  if (a === 0) {
    if (b < 0) u = -1 / b;
  } else {
    const disc = b * b - 4 * a;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      for (const root of [(-b - sq) / (2 * a), (-b + sq) / (2 * a)]) {
        if (root > 0 && root < u) u = root;
      }
    }
  }
  return Math.sqrt(u);
}
function lensLimit(model) {
  const v = polynomialLimit(model);
  return model.lens === "fisheye" ? Math.min(Math.PI, v) : Math.min(Math.PI / 2, Math.atan(v));
}
function lensRadius(model, theta) {
  const v = model.lens === "fisheye" ? theta : Math.tan(theta);
  return model.f * lensPolynomial(model, v);
}
function inverseLens(model, radiusPx) {
  if (!(model.f > 0) || !(radiusPx >= 0)) return null;
  const target = radiusPx / model.f;
  const vLimit = model.lens === "fisheye" ? Math.min(Math.PI, polynomialLimit(model)) : polynomialLimit(model);
  if (Number.isFinite(vLimit) && !(target < lensPolynomial(model, vLimit))) return null;
  if (target === 0) return 0;
  let lo = 0;
  let hi = vLimit;
  let v = target;
  if (model.k1 > 0) v = Math.min(v, Math.cbrt(target / model.k1));
  if (model.k2 > 0) v = Math.min(v, (target / model.k2) ** 0.2);
  if (v >= hi) v = hi / 2;
  const angle = (value) => model.lens === "fisheye" ? value : Math.atan(value);
  for (let k = 0; k < NEWTON_MAX_ITERATIONS; k++) {
    const g = lensPolynomial(model, v) - target;
    if (g === 0) return angle(v);
    if (g > 0) hi = Math.min(hi, v);
    else lo = Math.max(lo, v);
    const slope = lensSlope(model, v);
    let next = slope > 0 ? v - g / slope : NaN;
    if (!(next >= lo && next <= hi)) next = Number.isFinite(hi) ? (lo + hi) / 2 : 2 * v;
    const step = Math.abs(next - v);
    v = next;
    if (step <= NEWTON_TOLERANCE * Math.max(1, v)) return angle(v);
  }
  return null;
}
function toCamera(model, point, z = 0, axes = cameraAxes(model)) {
  const d = [point.x - model.x, point.y - model.y, z - model.height];
  return [dot(d, axes.right), dot(d, axes.down), dot(d, axes.forward)];
}
function projectWith(model, axes, limit, point, z) {
  const c = toCamera(model, point, z, axes);
  const rho = Math.hypot(c[0], c[1]);
  const theta = Math.atan2(rho, c[2]);
  if (!(theta < limit)) return null;
  if (rho === 0) return { x: model.cx, y: model.cy };
  const r = lensRadius(model, theta);
  return { x: model.cx + r * c[0] / rho, y: model.cy + r * c[1] / rho };
}
function planProjector(model) {
  const axes = cameraAxes(model);
  const limit = lensLimit(model);
  return (point) => projectWith(model, axes, limit, point, 0);
}
function planToPicture(model, point) {
  return levelToPicture(model, point, 0);
}
function levelToPicture(model, point, z) {
  return projectWith(model, cameraAxes(model), lensLimit(model), point, z);
}
function pictureRay(model, point) {
  const dx = point.x - model.cx;
  const dy = point.y - model.cy;
  const rho = Math.hypot(dx, dy);
  if (rho === 0) return [0, 0, 1];
  const theta = inverseLens(model, rho);
  if (theta === null) return null;
  const s = Math.sin(theta);
  return [s * dx / rho, s * dy / rho, Math.cos(theta)];
}
function pictureToPlan(model, point) {
  return pictureToLevel(model, point, 0);
}
function pictureToLevel(model, point, z) {
  if (!(model.height > z)) return null;
  const ray = pictureRay(model, point);
  if (!ray) return null;
  const axes = cameraAxes(model);
  const dz = axes.right[2] * ray[0] + axes.down[2] * ray[1] + axes.forward[2] * ray[2];
  if (!(dz < -1e-12)) return null;
  const t = (model.height - z) / -dz;
  const dx = axes.right[0] * ray[0] + axes.down[0] * ray[1] + axes.forward[0] * ray[2];
  const dy = axes.right[1] * ray[0] + axes.down[1] * ray[1] + axes.forward[1] * ray[2];
  const hit = { x: model.x + t * dx, y: model.y + t * dy };
  return Number.isFinite(hit.x) && Number.isFinite(hit.y) ? hit : null;
}
var wrapDegrees = (deg) => {
  const w = ((deg + 180) % 360 + 360) % 360 - 180;
  return w === -180 ? 180 : w;
};
function pictureUpHeading(model) {
  const step = Math.max(1, model.f * 0.01);
  const centre = pictureToPlan(model, { x: model.cx, y: model.cy });
  const above = pictureToPlan(model, { x: model.cx, y: model.cy - step });
  const heading = centre && above && (above.x !== centre.x || above.y !== centre.y) ? Math.atan2(above.y - centre.y, above.x - centre.x) / DEG : model.yaw;
  return (heading % 360 + 360) % 360;
}
function priorFocal(lens, rect, fovAngle) {
  const [, , w, h] = rect;
  if (lens === "fisheye") {
    const half2 = Math.min(Math.max(fovAngle, 1), 180) / 2 * DEG;
    return Math.min(w, h) / 2 / half2;
  }
  const half = Math.min(Math.max(fovAngle, 1), 170) / 2 * DEG;
  return w / 2 / Math.tan(half);
}
var toModel = (p, problem) => ({
  x: p[0],
  y: p[1],
  height: p[2],
  yaw: p[3] / DEG,
  pitch: p[4] / DEG,
  roll: p[5] / DEG,
  f: p[6] * problem.f0,
  cx: problem.cx,
  cy: problem.cy,
  k1: p[7],
  k2: p[8],
  lens: problem.input.lens
});
var toParams = (m, f0) => [
  m.x,
  m.y,
  m.height,
  m.yaw * DEG,
  m.pitch * DEG,
  m.roll * DEG,
  m.f / f0,
  m.k1,
  m.k2
];
var UNPROJECTABLE_RESIDUAL = 10;
var LIMIT_BARRIER_FROM = 0.97;
var LIMIT_BARRIER_RAD = 0.01;
function projectForFit(model, axes, point, z, limit) {
  const c = toCamera(model, point, z, axes);
  const rho = Math.hypot(c[0], c[1]);
  const theta = Math.atan2(rho, c[2]);
  if (!(theta < limit)) return { q: null, theta };
  if (rho === 0) return { q: { x: model.cx, y: model.cy }, theta };
  const r = lensRadius(model, theta);
  return { q: { x: model.cx + r * c[0] / rho, y: model.cy + r * c[1] / rho }, theta };
}
function cauchy(e0, e1) {
  const n2 = e0 * e0 + e1 * e1;
  if (n2 === 0) return [0, 0];
  const w = Math.sqrt(Math.log1p(n2) / n2);
  return [e0 * w, e1 * w];
}
function smallestEigenvalue(m) {
  const p1 = m[0][1] ** 2 + m[0][2] ** 2 + m[1][2] ** 2;
  const q = (m[0][0] + m[1][1] + m[2][2]) / 3;
  if (p1 === 0) return Math.min(m[0][0], m[1][1], m[2][2]);
  const p2 = (m[0][0] - q) ** 2 + (m[1][1] - q) ** 2 + (m[2][2] - q) ** 2 + 2 * p1;
  const p = Math.sqrt(p2 / 6);
  const b = m.map((row, i) => row.map((v, j) => (v - (i === j ? q : 0)) / p));
  const det = b[0][0] * (b[1][1] * b[2][2] - b[1][2] * b[2][1]) - b[0][1] * (b[1][0] * b[2][2] - b[1][2] * b[2][0]) + b[0][2] * (b[1][0] * b[2][1] - b[1][1] * b[2][0]);
  const r = Math.max(-1, Math.min(1, det / 2));
  const phi = Math.acos(r) / 3;
  return q + 2 * p * Math.cos(phi + 2 * Math.PI / 3);
}
function edgeResidualPx(model, edge) {
  const rays = edge.map(([x, y]) => pictureRay(model, { x, y })).filter((r) => r !== null);
  if (rays.length < 3) return null;
  const m = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0]
  ];
  for (const r of rays)
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) m[i][j] += r[i] * r[j];
  return Math.sqrt(Math.max(0, smallestEigenvalue(m))) * model.f;
}
function residuals(p, problem) {
  const { input, sigmaPx } = problem;
  const model = toModel(p, problem);
  const observerHeight = problem.hasObservers ? p[9] : 0;
  const limit = lensLimit(model);
  const axes = cameraAxes(model);
  const out = [];
  for (const pair of input.pairs) {
    const z = isObserverPair(pair) ? observerHeight : 0;
    const { q, theta } = projectForFit(model, axes, { x: pair.plan[0], y: pair.plan[1] }, z, limit);
    const barrier = Math.max(0, theta - LIMIT_BARRIER_FROM * limit) / LIMIT_BARRIER_RAD;
    if (!q || !Number.isFinite(q.x) || !Number.isFinite(q.y)) {
      out.push(UNPROJECTABLE_RESIDUAL, UNPROJECTABLE_RESIDUAL, barrier);
      continue;
    }
    const [a, b] = cauchy((q.x - pair.picture[0]) / sigmaPx, (q.y - pair.picture[1]) / sigmaPx);
    out.push(a, b, barrier);
  }
  for (const edge of input.edges ?? []) {
    const e = edgeResidualPx(model, edge);
    out.push(e === null ? UNPROJECTABLE_RESIDUAL : e / sigmaPx);
  }
  out.push(
    (model.x - input.marker.x) / PRIOR_POSITION_SIGMA_M,
    (model.y - input.marker.y) / PRIOR_POSITION_SIGMA_M,
    (model.height - PRIOR_HEIGHT_M) / PRIOR_HEIGHT_SIGMA_M,
    (p[6] - 1) / PRIOR_FOCAL_SIGMA_RATIO,
    model.k1 / PRIOR_K_SIGMA,
    model.k2 / PRIOR_K_SIGMA
  );
  if (problem.nadirPrior) out.push(model.pitch / PRIOR_NADIR_SIGMA_DEG);
  if (problem.hasObservers) {
    const below = Math.max(0, OBSERVER_HEIGHT_MIN_M - observerHeight);
    const above = Math.max(0, observerHeight - OBSERVER_HEIGHT_MAX_M);
    out.push(
      (observerHeight - PRIOR_OBSERVER_HEIGHT_M) / PRIOR_OBSERVER_HEIGHT_SIGMA_M,
      (below + above) / OBSERVER_BOUND_SIGMA_M
    );
  }
  return out;
}
var sumSquares = (r) => r.reduce((acc, v) => acc + v * v, 0);
function solve(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  let scale = 0;
  for (const row of A) for (const v of row) scale = Math.max(scale, Math.abs(v));
  if (!(scale > 0)) return null;
  for (let c = 0; c < n; c++) {
    let pivot = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[pivot][c])) pivot = r;
    if (Math.abs(M[pivot][c]) <= 1e-15 * scale) return null;
    [M[c], M[pivot]] = [M[pivot], M[c]];
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}
function levenbergMarquardt(fn, p0, maxIterations) {
  let p = [...p0];
  let r = fn(p);
  let cost = sumSquares(r);
  const startCost = cost;
  let lambda = 1e-3;
  let iterations = 0;
  const n = p.length;
  if (!Number.isFinite(cost)) return { params: p, cost, startCost, iterations };
  outer: while (iterations < maxIterations) {
    const J = r.map(() => new Array(n).fill(0));
    for (let j = 0; j < n; j++) {
      const h = 1e-7 * Math.max(1, Math.abs(p[j]));
      const q = [...p];
      q[j] += h;
      const rq = fn(q);
      for (let i = 0; i < r.length; i++) J[i][j] = (rq[i] - r[i]) / h;
    }
    const A = Array.from({ length: n }, () => new Array(n).fill(0));
    const g = new Array(n).fill(0);
    for (let i = 0; i < r.length; i++) {
      const row = J[i];
      for (let a = 0; a < n; a++) {
        if (row[a] === 0) continue;
        g[a] += row[a] * r[i];
        for (let b = 0; b < n; b++) A[a][b] += row[a] * row[b];
      }
    }
    const maxDiag = Math.max(...A.map((row, i) => row[i]));
    for (; ; ) {
      if (iterations >= maxIterations) break outer;
      iterations += 1;
      const damped = A.map(
        (row, i) => row.map((v, j) => i === j ? v + lambda * Math.max(v, 1e-9 * maxDiag) : v)
      );
      const delta = solve(
        damped,
        g.map((v) => -v)
      );
      if (delta) {
        const next = p.map((v, k) => v + delta[k]);
        const rn = fn(next);
        const cn = sumSquares(rn);
        if (Number.isFinite(cn) && cn < cost) {
          const gain = cost - cn;
          p = next;
          r = rn;
          cost = cn;
          lambda = Math.max(lambda / 10, 1e-12);
          if (gain <= 1e-12 * Math.max(1, cost)) break outer;
          break;
        }
      }
      lambda *= 10;
      if (lambda > 1e12) break outer;
    }
  }
  return { params: p, cost, startCost, iterations };
}
function canonical(model) {
  let { yaw, pitch, roll } = model;
  pitch = wrapDegrees(pitch);
  if (pitch < 0) {
    pitch = -pitch;
    yaw += 180;
    roll += 180;
  }
  return { ...model, yaw: (yaw % 360 + 360) % 360, pitch, roll: wrapDegrees(roll) };
}
var isFullCircle = (fovAngle) => fovAngle >= 360;
function problemFor(input) {
  const [rx, ry, w, h] = input.rect;
  return {
    input,
    cx: rx + w / 2,
    cy: ry + h / 2,
    f0: priorFocal(input.lens, input.rect, input.marker.fovAngle),
    sigmaPx: SIGMA_PX_RATIO * Math.hypot(w, h),
    nadirPrior: input.layout === "single" && isFullCircle(input.marker.fovAngle),
    hasObservers: input.pairs.some(isObserverPair)
  };
}
function defaultStarts(problem) {
  const { input } = problem;
  const pitch = problem.nadirPrior ? 0 : START_PITCH_DEG;
  return Array.from({ length: START_HEADINGS }, (_, k) => ({
    x: input.marker.x,
    y: input.marker.y,
    height: PRIOR_HEIGHT_M,
    yaw: k * (360 / START_HEADINGS),
    pitch,
    roll: 0,
    f: problem.f0,
    cx: problem.cx,
    cy: problem.cy,
    k1: 0,
    k2: 0,
    lens: input.lens
  }));
}
function fitCameraModel(input, options = {}) {
  if (input.pairs.length < MIN_FIT_PAIRS) return { verdict: "too-few" };
  const sides = [
    ["picture", input.pairs.map((p) => p.picture)],
    ["plan", input.pairs.map((p) => p.plan)]
  ];
  for (const [side, points2] of sides) {
    if (collinearityRatio(points2) < NEAR_LINE_RATIO) return { verdict: "near-line", side };
  }
  const problem = problemFor(input);
  const maxIterations = options.maxIterations ?? SOLVER_MAX_ITERATIONS;
  const starts = (options.starts ?? defaultStarts(problem)).map((s) => ({
    ...s,
    cx: problem.cx,
    cy: problem.cy,
    lens: input.lens
  }));
  const fn = (p) => residuals(p, problem);
  const observerStart = options.observerHeight ?? PRIOR_OBSERVER_HEIGHT_M;
  const paramsOf = (start) => problem.hasObservers ? [...toParams(start, problem.f0), observerStart] : toParams(start, problem.f0);
  let best = null;
  const counts = [];
  for (const start of starts) {
    const result = levenbergMarquardt(fn, paramsOf(start), maxIterations);
    counts.push(result.iterations);
    const converged = Number.isFinite(result.cost) && result.cost < result.startCost;
    if (converged && (!best || result.cost < best.cost)) best = result;
  }
  if (!best) return { verdict: "no-convergence" };
  const model = canonical(toModel(best.params, problem));
  if (!(model.height > 0)) return { verdict: "under-floor", model };
  if (model.height < HEIGHT_MIN_M || model.height > HEIGHT_MAX_M) {
    return { verdict: "height-out-of-bounds", model };
  }
  return {
    verdict: "ok",
    model,
    iterations: counts.reduce((a, b) => a + b, 0),
    starts: counts,
    cost: best.cost,
    observerHeight: problem.hasObservers ? best.params[9] : null
  };
}
var FIRST_GUESS_HEIGHT_M = PRIOR_HEIGHT_M;
var FIRST_GUESS_PITCH_DEG = START_PITCH_DEG;
function firstGuessParams(marker, rect, lens) {
  const [rx, ry, w, h] = rect;
  return {
    x: marker.x,
    y: marker.y,
    height: FIRST_GUESS_HEIGHT_M,
    yaw: marker.rotation,
    pitch: FIRST_GUESS_PITCH_DEG,
    roll: 0,
    f: priorFocal(lens, rect, marker.fovAngle),
    cx: rx + w / 2,
    cy: ry + h / 2,
    k1: 0,
    k2: 0,
    lens
  };
}
var GUIDED_VIEW_MARGIN = 0.04;
var FIRST_GUESS_VIEW_ALLOWANCE = 0.25;
function projectsInsideView(model, point, rect, margin = GUIDED_VIEW_MARGIN) {
  const q = planToPicture(model, point);
  if (!q || !Number.isFinite(q.x) || !Number.isFinite(q.y)) return false;
  const [rx, ry, w, h] = rect;
  const mx = margin * w;
  const my = margin * h;
  return q.x >= rx + mx && q.x <= rx + w - mx && q.y >= ry + my && q.y <= ry + h - my;
}
function residualJacobian(p, problem, rows) {
  const r0 = residuals(p, problem).slice(0, rows);
  const J = r0.map(() => new Array(p.length).fill(0));
  for (let j = 0; j < p.length; j++) {
    const h = 1e-7 * Math.max(1, Math.abs(p[j]));
    const q = [...p];
    q[j] += h;
    const rq = residuals(q, problem);
    for (let i = 0; i < r0.length; i++) J[i][j] = (rq[i] - r0[i]) / h;
  }
  return J;
}
function normalMatrix(J, n) {
  const A = Array.from({ length: n }, () => new Array(n).fill(0));
  for (const row of J) {
    for (let a = 0; a < n; a++) {
      if (row[a] === 0) continue;
      for (let b = 0; b < n; b++) A[a][b] += row[a] * row[b];
    }
  }
  return A;
}
function paramsFor(model, problem, observerHeight) {
  const p = toParams(model, problem.f0);
  return problem.hasObservers ? [...p, observerHeight ?? PRIOR_OBSERVER_HEIGHT_M] : p;
}
function fitInformation(input, model, observerHeight = null) {
  const problem = problemFor({ ...input, edges: [] });
  const p = paramsFor(model, problem, observerHeight);
  return normalMatrix(residualJacobian(p, problem), p.length);
}
function logDeterminant(A) {
  const n = A.length;
  const M = A.map((row) => [...row]);
  let sum = 0;
  for (let c = 0; c < n; c++) {
    let pivot = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[pivot][c])) pivot = r;
    if (!(Math.abs(M[pivot][c]) > 0)) return -Infinity;
    [M[c], M[pivot]] = [M[pivot], M[c]];
    sum += Math.log(Math.abs(M[c][c]));
    for (let r = c + 1; r < n; r++) {
      const f = M[r][c] / M[c][c];
      if (f === 0) continue;
      for (let k = c; k < n; k++) M[r][k] -= f * M[c][k];
    }
  }
  return sum;
}
var trace = (A) => A.reduce((s, row, i) => s + row[i], 0);
var TARGET_RIDGE_RATIO = 1e-6;
function bestNextTarget(input, model, candidates, observerHeight = null) {
  const problem = problemFor({ ...input, edges: [] });
  const p = paramsFor(model, problem, observerHeight);
  const n = p.length;
  const base = fitInformation(input, model, observerHeight);
  const at = toModel(p, problem);
  const scored = [];
  for (const candidate of candidates) {
    const q = planToPicture(at, candidate.plan);
    if (!q || !Number.isFinite(q.x) || !Number.isFinite(q.y)) continue;
    const pair = {
      picture: [q.x, q.y],
      plan: [candidate.plan.x, candidate.plan.y],
      source: "clicked"
    };
    const one = { ...problem, input: { ...problem.input, pairs: [pair] } };
    scored.push({ candidate, info: normalMatrix(residualJacobian(p, one, 3), n) });
  }
  if (scored.length === 0) return null;
  const scale = trace(base) > 0 ? trace(base) : Math.max(...scored.map((s) => trace(s.info)));
  const lambda = TARGET_RIDGE_RATIO * scale / n;
  let best = null;
  let bestValue = -Infinity;
  for (const { candidate, info } of scored) {
    const A = base.map((row, a) => row.map((v, b) => v + info[a][b] + (a === b ? lambda : 0)));
    const value = logDeterminant(A);
    if (best === null || value > bestValue) {
      best = candidate;
      bestValue = value;
    }
  }
  return best;
}
function cameraErrors(model, pairs, observerHeight = null, rect) {
  const sigma = SIGMA_PX_RATIO * (rect ? Math.hypot(rect[2], rect[3]) : Math.hypot(model.cx * 2, model.cy * 2));
  const errors = pairs.map((pair) => {
    const z = isObserverPair(pair) ? observerHeight ?? 0 : 0;
    const q = levelToPicture(model, { x: pair.plan[0], y: pair.plan[1] }, z);
    const g = pictureToLevel(model, { x: pair.picture[0], y: pair.picture[1] }, z);
    return {
      px: q ? Math.hypot(q.x - pair.picture[0], q.y - pair.picture[1]) : null,
      m: g ? Math.hypot(g.x - pair.plan[0], g.y - pair.plan[1]) : null
    };
  });
  const px = errors.map((e) => e.px).filter((v) => v !== null);
  const ms = errors.map((e) => e.m).filter((v) => v !== null);
  let worstPxIndex = -1;
  errors.forEach((e, k) => {
    const cur = worstPxIndex < 0 ? -1 : errors[worstPxIndex].px ?? Infinity;
    if ((e.px ?? Infinity) > cur) worstPxIndex = k;
  });
  let worstMIndex = -1;
  errors.forEach((e, k) => {
    if (e.m !== null && (worstMIndex < 0 || e.m > errors[worstMIndex].m))
      worstMIndex = k;
  });
  const sorted = [...ms].sort((a, b) => a - b);
  const median = sorted.length === 0 ? null : sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
  const costs = errors.map(
    (e) => e.px === null ? Math.log1p(2 * UNPROJECTABLE_RESIDUAL ** 2) : Math.log1p((e.px / sigma) ** 2)
  );
  const total = costs.reduce((a, b) => a + b, 0);
  return {
    pairs: errors,
    rmsPx: px.length ? Math.sqrt(px.reduce((a, v) => a + v * v, 0) / px.length) : 0,
    worstPx: worstPxIndex < 0 ? 0 : errors[worstPxIndex].px ?? Infinity,
    worstPxIndex,
    rmsM: ms.length ? Math.sqrt(ms.reduce((a, v) => a + v * v, 0) / ms.length) : null,
    medianM: median,
    worstM: worstMIndex < 0 ? null : errors[worstMIndex].m,
    worstMIndex,
    costShare: costs.map((c) => total > 0 ? c / total : 0)
  };
}
var LEAVE_ONE_OUT_MAX_ITERATIONS = 40;
var REFIT_BUDGET = START_HEADINGS * SOLVER_MAX_ITERATIONS;
function disagreement(input, fit) {
  const n = input.pairs.length;
  if (n < MIN_DISAGREE_PAIRS) return { index: null, iterations: 0 };
  const remaining = Math.max(0, REFIT_BUDGET - fit.iterations);
  const allowance = Math.min(LEAVE_ONE_OUT_MAX_ITERATIONS, Math.floor(remaining / n));
  const full = cameraErrors(fit.model, input.pairs, fit.observerHeight, input.rect).rmsPx;
  let index = null;
  let bestDrop = 0;
  let iterations = 0;
  input.pairs.forEach((_, k) => {
    const rest = input.pairs.filter((__, j) => j !== k);
    const refit = fitCameraModel(
      { ...input, pairs: rest },
      { starts: [fit.model], observerHeight: fit.observerHeight, maxIterations: allowance }
    );
    if (refit.verdict !== "ok") {
      iterations += allowance;
      return;
    }
    iterations += refit.iterations;
    const drop = full - cameraErrors(refit.model, rest, refit.observerHeight, input.rect).rmsPx;
    if (drop > bestDrop) {
      bestDrop = drop;
      index = k;
    }
  });
  return { index, iterations };
}
var disagreeingPair = (input, fit) => disagreement(input, fit).index;
function fittedHeading(model) {
  return model.lens === "fisheye" ? pictureUpHeading(model) : (model.yaw % 360 + 360) % 360;
}
function markerAgreement(model, marker) {
  const heading = fittedHeading(model);
  return {
    distanceM: Math.hypot(model.x - marker.x, model.y - marker.y),
    headingDeg: isFullCircle(marker.fovAngle) ? null : wrapDegrees(heading - marker.rotation),
    fittedHeading: heading
  };
}

// src/twin-planner/cameraCalibration.ts
var isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);
var isNumbers = (v, length) => Array.isArray(v) && v.length === length && v.every(isFiniteNumber);
var isObject2 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var isNullableNumber = (v) => v === null || isFiniteNumber(v);
var LENSES = /* @__PURE__ */ new Set(["standard", "fisheye"]);
var CAMERA_NUMBERS = ["x", "y", "height", "yaw", "pitch", "roll", "f", "cx", "cy", "k1", "k2"];
function cameraProblem(camera) {
  if (camera === null) return null;
  if (!isObject2(camera)) return "camera";
  const bad = CAMERA_NUMBERS.find((k) => !isFiniteNumber(camera[k]));
  if (bad) return `camera.${bad}`;
  if (!(camera.height > 0)) return "camera.height";
  if (!(camera.f > 0)) return "camera.f";
  if (!LENSES.has(camera.lens)) return "camera.lens";
  return null;
}
var isValidTarget = (t) => t === void 0 || isObject2(t) && typeof t.itemRefid === "string" && t.itemRefid !== "" && (t.corner === 0 || t.corner === 1 || t.corner === 2 || t.corner === 3);
function viewShapeProblem(v) {
  if (!isObject2(v)) return "view";
  if (!isNumbers(v.rect, 4)) return "rect";
  if (!Array.isArray(v.pairs) || !v.pairs.every(
    (p) => isObject2(p) && isNumbers(p.picture, 2) && isNumbers(p.plan, 2) && isValidTarget(p.target)
  )) {
    return "pairs";
  }
  if (!Array.isArray(v.edges) || !v.edges.every((e) => Array.isArray(e) && e.every((p) => isNumbers(p, 2)))) {
    return "edges";
  }
  return null;
}
function viewProblem(v) {
  const shape = viewShapeProblem(v);
  if (shape) return shape;
  const view = v;
  if (view.camera === void 0) return "camera";
  const camera = cameraProblem(view.camera);
  if (camera) return camera;
  for (const k of ["rmsErrorPx", "rmsErrorM", "worstErrorM", "observerHeight"]) {
    if (!isNullableNumber(view[k])) return k;
  }
  return null;
}
var VIEW_COUNT = {
  single: 1,
  "grid-2x2": 4
};
var SOURCES = /* @__PURE__ */ new Set(["clicked", "observers", "mixed"]);
var isPositive = (v) => isFiniteNumber(v) && v > 0;
function headerProblem(value) {
  if (typeof value.rtspEndpointBid !== "string" || value.rtspEndpointBid === "") {
    return "rtspEndpointBid";
  }
  if (!isPositive(value.frameWidth)) return "frameWidth";
  if (!isPositive(value.frameHeight)) return "frameHeight";
  if (value.layout !== "single" && value.layout !== "grid-2x2") return "layout";
  if (!SOURCES.has(value.source)) return "source";
  if (typeof value.fittedAt !== "string") return "fittedAt";
  if (!Array.isArray(value.views) || value.views.length !== VIEW_COUNT[value.layout]) {
    return "views";
  }
  return null;
}
function calibrationProblem(value) {
  if (!isObject2(value)) return "calibration";
  if (value.version !== 2) return "version";
  const header = headerProblem(value);
  if (header) return header;
  if (!LENSES.has(value.lensModel)) return "lensModel";
  const views = value.views;
  for (let k = 0; k < views.length; k++) {
    const problem = viewProblem(views[k]);
    if (problem) return `views[${k}].${problem}`;
  }
  if (!views.some((v) => isObject2(v) && v.camera !== null)) return "views (none fitted)";
  return null;
}
function isValidCalibration(value) {
  return calibrationProblem(value) === null;
}
var STALE_CORNER_M = 0.3;
function calibrationTargetsMoved(calibration, plan) {
  const live2 = new Map(plan.items.filter((item) => !item._deleted).map((item) => [item.refid, item]));
  for (const view of calibration.views) {
    for (const pair of view.pairs) {
      const target = pair.target;
      if (!target) continue;
      const item = live2.get(target.itemRefid);
      if (!item) return true;
      const footprint = plan.footprintOf(item);
      if (!footprint) continue;
      const corner = footprintCorners(
        { ...item, ...modelFootprintPlanCentre(item, footprint) },
        { width: footprint.widthM, depth: footprint.depthM }
      )[target.corner];
      if (Math.hypot(corner[0] - pair.plan[0], corner[1] - pair.plan[1]) > STALE_CORNER_M) {
        return true;
      }
    }
  }
  return false;
}
function isCalibrationCurrent(camera, frameSize, plan) {
  const calibration = camera.calibration;
  if (!isValidCalibration(calibration)) return false;
  if (calibration.rtspEndpointBid !== camera.rtspEndpointBid) return false;
  if (frameSize && (frameSize.width !== calibration.frameWidth || frameSize.height !== calibration.frameHeight)) {
    return false;
  }
  if (plan && calibrationTargetsMoved(calibration, plan)) return false;
  return true;
}
function calibrationViewRects(layout, frameWidth, frameHeight) {
  if (layout === "single") return [[0, 0, frameWidth, frameHeight]];
  const left = Math.floor(frameWidth / 2);
  const top = Math.floor(frameHeight / 2);
  return [
    [0, 0, left, top],
    [left, 0, frameWidth - left, top],
    [0, top, left, frameHeight - top],
    [left, top, frameWidth - left, frameHeight - top]
  ];
}
function viewIndexAt(rects, point) {
  if (rects.length === 0) return -1;
  const columns = [...new Set(rects.map((r) => r[0]))].sort((a, b) => a - b);
  const rows = [...new Set(rects.map((r) => r[1]))].sort((a, b) => a - b);
  const right = Math.max(...rects.map((r) => r[0] + r[2]));
  const bottom = Math.max(...rects.map((r) => r[1] + r[3]));
  if (!(point.x >= columns[0] && point.x < right && point.y >= rows[0] && point.y < bottom)) {
    return -1;
  }
  const bandOf = (starts, v) => starts.reduce((band, start, k) => start <= v ? k : band, 0);
  const column = bandOf(columns, point.x);
  const row = bandOf(rows, point.y);
  const index = row * columns.length + column;
  return index < rects.length ? index : -1;
}
function pictureToPlanAt(calibration, point) {
  const index = viewIndexAt(
    calibration.views.map((v) => v.rect),
    point
  );
  const view = index < 0 ? null : calibration.views[index];
  if (!view?.camera) return null;
  return pictureToPlan(view.camera, point);
}
function planToPictureIn(view, point) {
  if (!view.camera) return null;
  return planToPicture(view.camera, point);
}

// src/twin-planner/duplicateEntityCodes.ts
var findDuplicateEntityCodes = (layout, twinBid) => {
  const seen = /* @__PURE__ */ new Map();
  for (const floor of asArray(layout?.floors)) {
    const design = findLatestDesign(asArray(floor?.designs));
    const entries = [
      ...asArray(design?.surfaces),
      ...asArray(design?.items)
    ].filter(isLive);
    for (const entry of entries) {
      const code = entityIdentityFor(
        twinBid ?? "",
        typeof entry.entityCode === "string" ? entry.entityCode : ""
      );
      if (!code) continue;
      const refids = seen.get(code) ?? [];
      refids.push(entry.refid);
      seen.set(code, refids);
    }
  }
  const codes = /* @__PURE__ */ new Set();
  const byRefid = /* @__PURE__ */ new Map();
  for (const [code, refids] of Array.from(seen.entries())) {
    if (refids.length < 2) continue;
    codes.add(code);
    for (const refid of refids) byRefid.set(refid, code);
  }
  return { codes, byRefid, inUse: new Set(seen.keys()) };
};

// src/twin-planner/deleteMachineSummary.ts
var deleteMachineSummary = (layout, design, refid) => deleteSummaryFor(layout, design, /* @__PURE__ */ new Set([refid]));
var deleteSummaryFor = (layout, design, removed) => {
  const live2 = asArray(design?.observers).filter(isLive);
  const surviving = new Set(pruneObserversForRemovedRefs(live2, removed));
  const observers = live2.filter((observer) => !surviving.has(observer));
  const composites = asArray(layout?.composites).filter(
    (composite) => isLive(composite) && pruneCompositesForRemovedRefs([composite], { refids: removed }).changed
  );
  const refs = [
    ...observers.map((observer) => `observer:${observer.refid}`),
    ...composites.map((composite) => `composite:${composite.refid}`)
  ].sort();
  return { observers: observers.length, composites: composites.length, refs };
};
var deleteSpaceSummary = (layout, design, removed, recodes, alsoRemovedObservers = /* @__PURE__ */ new Set()) => {
  const lost = deleteSummaryFor(layout, design, removed);
  const pruned = pruneCompositesForRemovedRefs(asArray(layout?.composites), { refids: removed });
  const surviving = layout ? { ...layout, composites: pruned.composites } : layout;
  const survivingDesign = design ? {
    ...design,
    observers: asArray(design.observers).filter((o) => !alsoRemovedObservers.has(o))
  } : design;
  const follow = deleteSummaryFor(surviving, survivingDesign, new Set(recodes.map((r) => r.refid)));
  const key = [
    ...lost.refs.map((r) => `lost:${r}`),
    ...follow.refs.map((r) => `follow:${r}`),
    ...recodes.map((r) => `recode:${r.refid}:${r.from}>${r.to}`)
  ].join("|");
  return { lost, follow, key };
};

// src/twin-planner/promoteAfterRemoval.ts
var MAX_PASSES = 4;
var descendantsOf = (design, roots) => {
  const parentage = resolvePlanParentage(design);
  const out = /* @__PURE__ */ new Set();
  firstLiveSurfaces(design.surfaces).forEach((s) => {
    const seen = /* @__PURE__ */ new Set([s.refid]);
    let parent = parentage.surface(s.refid)?.effectiveParent ?? null;
    while (parent !== null && !seen.has(parent)) {
      if (roots.has(parent)) {
        out.add(s.refid);
        return;
      }
      seen.add(parent);
      parent = parentage.surface(parent)?.effectiveParent ?? null;
    }
  });
  roots.forEach((r) => out.delete(r));
  return out;
};
var mismatched = (design) => new Set(
  resolvePlanHierarchy(design).problems.filter((p) => p.code === "letter-mismatch" && p.entity === "surface").map((p) => p.refid)
);
var promoteAfterRemoval = (before2, after, removed, inputs) => {
  const rewired = /* @__PURE__ */ new Set();
  const beforeParents = /* @__PURE__ */ new Map();
  firstLiveSurfaces(before2.surfaces).forEach((s) => beforeParents.set(s.refid, s));
  firstLiveSurfaces(after.surfaces).forEach((s) => {
    const was = beforeParents.get(s.refid);
    if (!was) return;
    if ((was.parentSurfaceBid || void 0) !== (s.parentSurfaceBid || void 0) || !!was.parentChosen !== !!s.parentChosen) {
      rewired.add(s.refid);
    }
  });
  const affected = /* @__PURE__ */ new Set([
    ...Array.from(descendantsOf(before2, removed)),
    ...Array.from(rewired),
    ...Array.from(descendantsOf(after, rewired))
  ]);
  const wrongBefore = mismatched(before2);
  const beforeByRefid = /* @__PURE__ */ new Map();
  firstLiveSurfaces(before2.surfaces).forEach((s) => beforeByRefid.set(s.refid, s));
  const mark = { ...inputs.mark };
  let design = after;
  const codes = /* @__PURE__ */ new Map();
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const hierarchy = resolvePlanHierarchy(design);
    const parentage = resolvePlanParentage(design);
    const candidates = /* @__PURE__ */ new Map();
    firstLiveSurfaces(design.surfaces).forEach((s) => {
      if (!affected.has(s.refid) || wrongBefore.has(s.refid)) return;
      const letter = letterOfCode(s.entityCode);
      if (letter === null) return;
      const answer = hierarchy.surface(s.refid);
      const kind = kindOfLetter(letter);
      if (!answer || answer.placeKind === null || kind === null || kind === answer.placeKind)
        return;
      candidates.set(s.refid, answer.placeKind);
    });
    const ready = Array.from(candidates.keys()).filter((refid) => {
      const parent = parentage.surface(refid)?.effectiveParent ?? null;
      return parent === null || !candidates.has(parent);
    });
    if (ready.length === 0) break;
    const next = /* @__PURE__ */ new Map();
    for (const refid of ready) {
      const kind = candidates.get(refid);
      const letter = KIND_LETTER[kind];
      const outcome = mintEntityBid(inputs.twinBid, letter, mark);
      if (!outcome.ok) return outcome;
      mark[letter] = (mark[letter] ?? 0) + 1;
      next.set(refid, outcome.bid);
      const original = codes.get(refid)?.from ?? beforeByRefid.get(refid)?.entityCode;
      codes.set(refid, { from: original, to: outcome.bid, kind });
    }
    design = {
      ...design,
      surfaces: design.surfaces.map(
        (s) => isLive(s) && next.has(s.refid) ? { ...s, entityCode: next.get(s.refid) } : s
      )
    };
  }
  const finalParentage = resolvePlanParentage(design);
  const recodes = [];
  codes.forEach((code, refid) => {
    const was = beforeByRefid.get(refid);
    recodes.push({
      refid,
      name: (was?.name ?? "").trim(),
      from: code.from,
      to: code.to,
      kind: code.kind,
      parentRefid: finalParentage.surface(refid)?.effectiveParent ?? null,
      live: inputs.launched({ refid, entityCode: code.from })
    });
  });
  const kindBefore = resolvePlanHierarchy(before2);
  const kindAfter = resolvePlanHierarchy(design);
  const moved = [];
  firstLiveSurfaces(design.surfaces).forEach((s) => {
    if (!affected.has(s.refid)) return;
    const was = kindBefore.surface(s.refid)?.kind ?? null;
    const now = kindAfter.surface(s.refid)?.kind ?? null;
    if (now === null || now === was) return;
    moved.push({
      refid: s.refid,
      name: (s.name ?? "").trim(),
      kind: now,
      recoded: codes.has(s.refid),
      parentRefid: finalParentage.surface(s.refid)?.effectiveParent ?? null
    });
  });
  const beforeParentage = resolvePlanParentage(before2);
  const finalItems = /* @__PURE__ */ new Map();
  asArray(design.items).filter(isLive).forEach((it) => {
    const copies = finalItems.get(it.refid);
    if (copies) copies.push(it);
    else finalItems.set(it.refid, [it]);
  });
  const seenCopies = /* @__PURE__ */ new Map();
  const survivorOf = (it) => {
    const n = seenCopies.get(it.refid) ?? 0;
    seenCopies.set(it.refid, n + 1);
    return finalItems.get(it.refid)?.[n];
  };
  const machineMoves = [];
  const stays = /* @__PURE__ */ new Map();
  let chosenReplaced = 0;
  asArray(before2.items).filter(isLive).forEach((it) => {
    const parent = beforeParentage.item(it)?.effectiveParent ?? null;
    const now = survivorOf(it);
    const to = now ? finalParentage.item(now)?.effectiveParent ?? null : null;
    if (!now) return;
    if (it.parentChosen && !now.parentChosen) chosenReplaced += 1;
    if (parent !== null && removed.has(parent) || parent !== to) {
      machineMoves.push({ refid: it.refid, from: parent, to });
    } else if (parent !== null && codes.has(parent)) {
      stays.set(parent, (stays.get(parent) ?? 0) + 1);
    }
  });
  const finalSurfaces = /* @__PURE__ */ new Map();
  firstLiveSurfaces(design.surfaces).forEach((s) => finalSurfaces.set(s.refid, s));
  beforeByRefid.forEach((was, refid) => {
    const now = finalSurfaces.get(refid);
    if (now && was.parentChosen && !now.parentChosen) chosenReplaced += 1;
  });
  return {
    ok: true,
    design,
    recodes,
    moved,
    machineMoves,
    machineStays: Array.from(stays, ([refid, count]) => ({ refid, count })),
    chosenReplaced
  };
};

// src/twin-planner/directChildSpaces.ts
var surfaceRef = (refid) => ({
  kind: "entity",
  refid,
  entityKind: "surface"
});
var hierarchyCache = /* @__PURE__ */ new WeakMap();
var directChildSpaces = (layout) => {
  if (layout) {
    const cached = hierarchyCache.get(layout);
    if (cached) return cached;
  }
  const result = deriveHierarchy(layout);
  if (layout) hierarchyCache.set(layout, result);
  return result;
};
var deriveHierarchy = (layout) => {
  const childSpaces = /* @__PURE__ */ new Map();
  const equipment = /* @__PURE__ */ new Map();
  const push = (map, key, value) => {
    const bucket = map.get(key);
    if (bucket) bucket.push(value);
    else map.set(key, [value]);
  };
  const floors = asArray(layout?.floors);
  const designs = layoutCurrentDesigns(layout ?? null);
  const fileable = new Set(fileableFloorBids(layout, "_").keys());
  const unfileableFloorIds = [];
  const twinKey = encodeRef({ kind: "twin" });
  floors.forEach((floor) => {
    if (typeof floor?.id !== "number") return;
    if (fileable.has(floor.id)) push(childSpaces, twinKey, { kind: "floor", floorId: floor.id });
    else unfileableFloorIds.push(floor.id);
  });
  designs.forEach((design, index) => {
    const floor = floors[index];
    if (typeof floor?.id !== "number") return;
    const floorKey = encodeRef({ kind: "floor", floorId: floor.id });
    const surfaces = asArray(design?.surfaces).filter(
      (s) => isLive(s) && typeof s.refid === "string" && s.refid !== ""
    );
    const parentage = resolvePlanParentage(design);
    for (const surface of surfaces) {
      const parent = parentage.surface(surface.refid)?.effectiveParent ?? null;
      push(
        childSpaces,
        parent === null ? floorKey : encodeRef(surfaceRef(parent)),
        surfaceRef(surface.refid)
      );
    }
    for (const item of asArray(design?.items)) {
      if (!isLive(item) || typeof item.refid !== "string" || item.refid === "") continue;
      const parent = parentage.item(item)?.effectiveParent ?? null;
      push(equipment, parent === null ? floorKey : encodeRef(surfaceRef(parent)), item);
    }
  });
  return { childSpaces, equipment, fileableFloorIds: fileable, unfileableFloorIds };
};

// src/twin-planner/equipmentDropHint.ts
var equipmentDropHint = (point, design) => {
  if (!design) return null;
  const surfaces = firstLiveSurfaces(design.surfaces);
  const refid = spatialParentOfItem(point, surfaces);
  if (refid === null) return { floor: true };
  const surface = surfaces.find((s) => s.refid === refid);
  const name = (surface?.customName || surface?.name || "").trim();
  return {
    floor: false,
    refid,
    name: name === "" ? "this space" : name,
    kind: resolvePlanHierarchy(design).surface(refid)?.kind ?? null
  };
};

// src/twin-planner/filedByDrawing.ts
var filedByDrawing = (before2, after, refid) => {
  const itemParentBefore = /* @__PURE__ */ new Map();
  (before2?.items ?? []).forEach((item) => {
    if (isLive(item)) itemParentBefore.set(item.refid, item.parentSpaceBid || void 0);
  });
  const surfaceParentBefore = /* @__PURE__ */ new Map();
  (before2?.surfaces ?? []).forEach((surface) => {
    if (isLive(surface))
      surfaceParentBefore.set(surface.refid, surface.parentSurfaceBid || void 0);
  });
  const machines = [];
  (after?.items ?? []).forEach((item) => {
    if (!isLive(item) || item.parentSpaceBid !== refid) return;
    if (itemParentBefore.get(item.refid) === refid) return;
    machines.push(item.refid);
  });
  const hierarchy = resolvePlanHierarchy(after);
  const spaces = [];
  (after?.surfaces ?? []).forEach((surface) => {
    if (!isLive(surface) || surface.refid === refid || surface.parentSurfaceBid !== refid) return;
    if (surfaceParentBefore.get(surface.refid) === refid) return;
    spaces.push({ refid: surface.refid, kind: hierarchy.surface(surface.refid)?.kind ?? null });
  });
  return { machines, spaces };
};

// src/twin-planner/planCapacity.ts
import { normaliseAssetUid as normaliseAssetUid9 } from "@repo/frontend-types";
var UNKNOWN = "unknown";
var NO_ROW = { row: false };
var UNKNOWN_LOOKUP = () => UNKNOWN;
var lookupCache = /* @__PURE__ */ new WeakMap();
var registerCapacityLookup = (rows, known) => {
  if (!known) return UNKNOWN_LOOKUP;
  const cached = lookupCache.get(rows);
  if (cached) return cached;
  const index = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key = normaliseAssetUid9(row?.id);
    if (key) index.set(key, isEntityCapacity(row.capacity) ? row.capacity : void 0);
  }
  const lookup = (assetUid) => {
    const key = normaliseAssetUid9(assetUid);
    if (!key || !index.has(key)) return NO_ROW;
    const capacity = index.get(key);
    return capacity === void 0 ? { row: true } : { row: true, capacity };
  };
  lookupCache.set(rows, lookup);
  return lookup;
};
var entryOf = (resolved) => ({
  value: resolved?.value,
  missing: resolved?.source === "calculated" ? resolved.missing : 0
});
var sumOf = (entries) => {
  let total = 0;
  let missing = 0;
  let unknown = false;
  for (const { value, missing: carried } of entries) {
    if (value === "unknown") unknown = true;
    else if (value === void 0) missing += 1;
    else total += value;
    missing += carried;
  }
  return { contents: unknown ? "unknown" : total, missing };
};
var container = (holder, sum) => {
  const reviewed = isEntityCapacity(holder.capacityReviewedContents) ? { reviewedContents: holder.capacityReviewedContents } : {};
  if (isEntityCapacity(holder.capacity)) {
    return { value: holder.capacity, source: "hand", ...sum, ...reviewed };
  }
  return { value: sum.contents, source: "calculated", ...sum };
};
var resolveMachineCapacity = (item, registerCapacityOf) => {
  const register = registerCapacityOf(item.assetUid);
  if (register === "unknown") return { value: "unknown", source: "product", missing: 0 };
  if (register.row) return { value: register.capacity, source: "product", missing: 0 };
  return {
    value: isEntityCapacity(item.capacity) ? item.capacity : void 0,
    source: "plan-copy",
    missing: 0
  };
};
var containerProblems = (resolved, entity, refid, push) => {
  if (resolved.source !== "hand" || typeof resolved.value !== "number") return;
  const { contents, missing } = resolved;
  if (typeof contents !== "number" || missing > 0) return;
  if (resolved.value < contents) {
    push({ code: "capacity-below-contents", entity, refid, capacity: resolved.value, contents });
  } else if (resolved.reviewedContents !== void 0 && resolved.reviewedContents !== contents) {
    push({ code: "capacity-contents-changed", entity, refid, capacity: resolved.value, contents });
  }
};
var derive3 = (layout, registerCapacityOf) => {
  const surfaces = /* @__PURE__ */ new Map();
  const surfacesByFloor = /* @__PURE__ */ new Map();
  const items = /* @__PURE__ */ new Map();
  const floors = /* @__PURE__ */ new Map();
  const problems = [];
  const seen = /* @__PURE__ */ new Map();
  const push = (problem, floorId) => {
    const key = `${problem.code}${problem.entity}${problem.refid}`;
    const known2 = seen.get(key);
    if (known2) {
      if (floorId !== void 0 && known2.floorIds && !known2.floorIds.includes(floorId)) {
        known2.floorIds.push(floorId);
      }
      return;
    }
    const stored = floorId === void 0 ? problem : { ...problem, floorIds: [floorId] };
    seen.set(key, stored);
    problems.push(stored);
  };
  let known = true;
  const floorValues = [];
  for (const floor of asArray(layout?.floors)) {
    if (!floor) continue;
    const design = findLatestDesign(asArray(floor.designs));
    const spaces = firstLiveSurfaces(design?.surfaces);
    const machines = asArray(design?.items).filter(isLive);
    const parentage = resolvePlanParentage(design);
    const spaceRefids = new Set(spaces.map((s) => s.refid));
    const parentOf = (refid) => refid && spaceRefids.has(refid) ? refid : null;
    const childSpaces = /* @__PURE__ */ new Map();
    const childItems = /* @__PURE__ */ new Map();
    const add2 = (map, key, value) => {
      const list = map.get(key);
      if (list) list.push(value);
      else map.set(key, [value]);
    };
    for (const space of spaces) {
      add2(childSpaces, parentOf(parentage.surface(space.refid)?.effectiveParent), space);
    }
    for (const item of machines) {
      const resolved = resolveMachineCapacity(item, registerCapacityOf);
      if (resolved.value === "unknown") known = false;
      if (resolved.value === void 0 && typeof item.refid === "string") {
        push({ code: "capacity-missing", entity: "item", refid: item.refid }, floor.id);
      }
      items.set(item, resolved);
      add2(childItems, parentOf(parentage.item(item)?.effectiveParent), item);
    }
    const local = /* @__PURE__ */ new Map();
    const visiting = /* @__PURE__ */ new Set();
    const childValues = (key) => [
      ...asArray(childSpaces.get(key)).map((child) => entryOf(resolveSpace(child))),
      ...asArray(childItems.get(key)).map((child) => entryOf(items.get(child)))
    ];
    const resolveSpace = (space) => {
      const done = local.get(space.refid);
      if (done) return done;
      if (visiting.has(space.refid)) return { value: "unknown", source: "calculated", missing: 0 };
      visiting.add(space.refid);
      const values = childValues(space.refid);
      const resolved = container(space, sumOf(values));
      visiting.delete(space.refid);
      local.set(space.refid, resolved);
      if (values.length === 0) {
        push({ code: "space-empty", entity: "surface", refid: space.refid }, floor.id);
      }
      containerProblems(resolved, "surface", space.refid, (problem) => push(problem, floor.id));
      return resolved;
    };
    for (const space of spaces) {
      const resolved = resolveSpace(space);
      if (!surfaces.has(space.refid)) surfaces.set(space.refid, resolved);
    }
    if (!surfacesByFloor.has(floor.id)) surfacesByFloor.set(floor.id, local);
    const floorResolved = container(floor, sumOf(childValues(null)));
    containerProblems(floorResolved, "floor", String(floor.id), push);
    if (!floors.has(floor.id)) floors.set(floor.id, floorResolved);
    floorValues.push(entryOf(floorResolved));
  }
  const twinResolved = container(layout ?? {}, sumOf(floorValues));
  containerProblems(twinResolved, "twin", "", push);
  return {
    surface: (refid, floorId) => floorId === void 0 ? surfaces.get(refid) : surfacesByFloor.get(floorId)?.get(refid),
    item: (item) => items.get(item),
    floor: (id) => floors.get(id),
    twin: () => twinResolved,
    problems,
    known
  };
};
var cache3 = /* @__PURE__ */ new WeakMap();
var resolvePlanCapacity = (layout, registerCapacityOf) => {
  if (!layout) return derive3(layout, registerCapacityOf);
  let byLookup = cache3.get(layout);
  if (!byLookup) {
    byLookup = /* @__PURE__ */ new WeakMap();
    cache3.set(layout, byLookup);
  }
  const cached = byLookup.get(registerCapacityOf);
  if (cached) return cached;
  const result = derive3(layout, registerCapacityOf);
  byLookup.set(registerCapacityOf, result);
  return result;
};
var sortOne = (entity, resolved) => {
  if (!resolved || resolved.source !== "hand" || !isEntityCapacity(entity.capacity)) return entity;
  const { capacity, capacityReviewedContents: _previous, ...rest } = entity;
  if (typeof resolved.contents === "number" && resolved.missing === 0 && resolved.contents === capacity) {
    return rest;
  }
  return {
    ...rest,
    capacity,
    ...typeof resolved.contents === "number" ? { capacityReviewedContents: resolved.contents } : {}
  };
};
var sortCapacitySources = (layout, registerCapacityOf, resolveOver = layout) => {
  if (!layout || layout.capacitySources === 1) return null;
  const capacity = resolvePlanCapacity(resolveOver ?? layout, registerCapacityOf);
  if (!capacity.known) return null;
  const floors = asArray(layout.floors).map((floor) => {
    if (!floor) return floor;
    const designs = asArray(floor.designs);
    const current = findLatestDesign(designs);
    const sortedDesigns = designs.includes(current) ? designs.map(
      (design) => design === current ? {
        ...design,
        surfaces: (() => {
          const first = new Set(firstLiveSurfaces(design.surfaces));
          return asArray(design.surfaces).map(
            (surface) => first.has(surface) ? sortOne(surface, capacity.surface(surface.refid, floor.id)) : surface
          );
        })()
      } : design
    ) : floor.designs;
    return { ...sortOne(floor, capacity.floor(floor.id)), designs: sortedDesigns };
  });
  return { ...sortOne(layout, capacity.twin()), floors, capacitySources: 1 };
};
var withCapacityReview = (capacity, contents) => ({
  capacity,
  capacityReviewedContents: capacity !== void 0 && typeof contents === "number" ? contents : void 0
});

// src/twin-planner/planChecks.ts
import { normaliseAssetUid as normaliseAssetUid10 } from "@repo/frontend-types";
var CHECK_LEVELS = {
  "adoption-skipped": "error",
  "machine-not-in-register": "error",
  "machine-register-row-gone": "error",
  "capacity-below-contents": "error",
  "capacity-missing": "error",
  "equipment-on-floor": "error",
  "too-deep": "error",
  "letter-mismatch": "error",
  "invalid-letter": "error",
  "duplicate-bid": "error",
  "duplicate-name": "error",
  "capacity-contents-changed": "check",
  "space-empty": "check",
  "parent-chosen-differs": "check",
  "to-place": "place"
};
var BID_SHARED_WITH = "BID shared with";
var NAME_SHARED_WITH = "Name shared with";
var others = (names) => names.length === 1 ? names[0] : `${names.length} others`;
var CHECK_SENTENCES = {
  machineNotInRegister: "On the plan, not in the register",
  machineRegisterRowGone: "Linked to a register row that no longer exists",
  capacityBelowContents: (capacity, contents) => `Capacity ${capacity ?? "\u2013"} \xB7 less than contents ${contents ?? "\u2013"}`,
  capacityMissing: "No capacity: no product and no register number",
  equipmentOnFloor: "On the floor, not in a zone",
  tooDeep: "Inside an area: areas hold only equipment",
  letterMismatch: (letterKind, placeKind) => `BID says ${letterKind}, drawn as a ${placeKind}`,
  invalidLetter: (letter, what) => letter === "?" ? `BID has no kind letter a ${what} can have` : `BID letter ${letter} is not one a ${what} can have`,
  /** What `duplicateBid` says before the holder (`duplicate-holder-links`: the name is a control). */
  duplicateBidPrefix: BID_SHARED_WITH,
  duplicateBid: (names) => `${BID_SHARED_WITH} ${others(names)}`,
  /** The same entity drawn more than once: its copies share the BID, and nothing else does. */
  duplicateBidCopies: "Drawn more than once with this BID",
  /** What `duplicateName` says before the holder. */
  duplicateNamePrefix: NAME_SHARED_WITH,
  duplicateName: (names) => `${NAME_SHARED_WITH} ${others(names)}`,
  capacityContentsChanged: (capacity) => `Set by hand to ${capacity ?? "\u2013"} \xB7 contents changed`,
  spaceEmpty: "Nothing inside",
  parentChosenDiffers: "Chosen parent differs from the drawing",
  toPlace: "Not on plan"
};
var zeroCounts = () => ({ error: 0, check: 0, identify: 0, place: 0 });
var IDENTIFY_STATES = /* @__PURE__ */ new Set(["to_identify", "to_confirm", "generic"]);
var pushTo = (map, key, value) => {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
};
var walkFloor2 = (floor, index) => {
  const designs = asArray(floor?.designs);
  const design = designs.length > 0 ? findLatestDesign(designs) : void 0;
  const parentage = resolvePlanParentage(design);
  const order = /* @__PURE__ */ new Map();
  const surfaceByRefid = /* @__PURE__ */ new Map();
  const itemsByRefid = /* @__PURE__ */ new Map();
  asArray(design?.surfaces).filter(isLive).forEach((s) => {
    if (typeof s.refid !== "string" || s.refid === "") return;
    if (!order.has(`s:${s.refid}`)) order.set(`s:${s.refid}`, order.size);
    if (!surfaceByRefid.has(s.refid)) surfaceByRefid.set(s.refid, s);
  });
  asArray(design?.items).filter(isLive).forEach((it) => {
    if (typeof it.refid !== "string" || it.refid === "") return;
    if (!order.has(`i:${it.refid}`)) order.set(`i:${it.refid}`, order.size);
    pushTo(itemsByRefid, it.refid, it);
  });
  const depths = /* @__PURE__ */ new Map();
  const visiting = /* @__PURE__ */ new Set();
  const depthOfSurface = (refid) => {
    const known = depths.get(refid);
    if (known !== void 0) return known;
    if (visiting.has(refid)) return 1;
    visiting.add(refid);
    const parent = parentage.surface(refid)?.effectiveParent ?? null;
    const depth = parent === null ? 1 : 1 + depthOfSurface(parent);
    visiting.delete(refid);
    depths.set(refid, depth);
    return depth;
  };
  const depthOfItem = (item) => {
    const parent = parentage.item(item)?.effectiveParent ?? null;
    return parent === null ? 1 : 1 + depthOfSurface(parent);
  };
  return {
    floor,
    index,
    design,
    parentage,
    order,
    depthOfSurface,
    depthOfItem,
    surfaceByRefid,
    itemsByRefid
  };
};
var ENTITY_RANK = {
  surface: 0,
  item: 1,
  floor: 2,
  twin: 3,
  register: 4
};
var compareDeepestFirst = (a, b) => Number(!!b.first) - Number(!!a.first) || b.depth - a.depth || a.floorIndex - b.floorIndex || ENTITY_RANK[a.entity] - ENTITY_RANK[b.entity] || a.planOrder - b.planOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
var strip = ({ floorIndex: _f, planOrder: _p, first: _first, ...check }) => check;
var naturalName = (a, b) => a.name.localeCompare(b.name, void 0, { numeric: true, sensitivity: "base" }) || Number(a.ref) - Number(b.ref);
var entityKeyOf = (entry) => {
  const ref = entry.ref;
  switch (ref.type) {
    case "floor":
      return `floor:${ref.floorId}`;
    case "register":
      return `register:${ref.assetId}`;
    default:
      return `${ref.type}:${ref.refid}`;
  }
};
var launchedFor = (live2, twinBid, layout) => (entity) => {
  const twinKey = typeof twinBid === "string" ? twinBid.trim() : "";
  if (live2.status === "loading" || live2.status === "failed") return "unknown";
  if (live2.status === "idle") return twinKey === "" ? false : "unknown";
  if (twinKey === "") return "unknown";
  const current = entityIdentityFor(twinBid, entity.entityCode);
  const original = entityIdentityFor(twinBid, originalIdentityOf(layout, entity.refid));
  if (current === "" && original === "") return false;
  return original !== "" && live2.bids.has(original) || current !== "" && live2.bids.has(current);
};
var notLoaded = (input, failed) => ({
  known: false,
  failed,
  registerKnown: false,
  errors: [],
  toCheck: [],
  identify: [],
  toPlace: [],
  counts: { twin: zeroCounts(), byFloor: /* @__PURE__ */ new Map() },
  score: input.identify.score,
  marks: () => ({}),
  checksFor: () => [],
  refidsOfGroup: () => [],
  refidOfRow: () => null,
  launched: launchedFor(input.live, input.twinBid, input.layout)
});
var build = (input, layout) => {
  const { twinBid, register, identify, live: live2 } = input;
  const twinKey = typeof twinBid === "string" ? twinBid.trim() : "";
  const registerKnown = register.readState === "ready" && twinKey !== "";
  const walks = asArray(layout.floors).filter((f) => !!f).map((f, i) => walkFloor2(f, i));
  const walkById = /* @__PURE__ */ new Map();
  walks.forEach((w) => walkById.set(w.floor.id, w));
  const floorsOfSurface = /* @__PURE__ */ new Map();
  const floorsOfItem = /* @__PURE__ */ new Map();
  walks.forEach((w) => {
    w.surfaceByRefid.forEach((_s, refid) => pushTo(floorsOfSurface, refid, w));
    w.itemsByRefid.forEach((_i, refid) => pushTo(floorsOfItem, refid, w));
  });
  const layoutIndexOf = /* @__PURE__ */ new Map();
  asArray(layout.floors).forEach((f, i) => {
    if (f) layoutIndexOf.set(f, i);
  });
  const holderFloor = (holderWalks, at) => {
    const on = holderWalks.includes(at) ? at : holderWalks[0];
    return on ? layoutIndexOf.get(on.floor) ?? null : null;
  };
  const placed = /* @__PURE__ */ new Map();
  const add2 = (check) => {
    const id = `${check.code}:${check.floorId ?? ""}:${check.entity}:${check.ref}`;
    if (placed.has(id)) return;
    placed.set(id, { ...check, id, kind: CHECK_LEVELS[check.code] });
  };
  const surfaceName2 = (s) => twinNameEntryFor(s, "space").name;
  const itemName = (it) => twinNameEntryFor(it, "machine").name;
  const bidOf = (entityCode) => entityIdentityFor(twinBid, entityCode);
  const atSurface = (w, refid, code, sentence, fix, holders) => {
    const s = w.surfaceByRefid.get(refid);
    if (!s) return;
    add2({
      ...holders ? { holders } : {},
      code,
      entity: "surface",
      ref: refid,
      floorId: w.floor.id,
      name: surfaceName2(s),
      bid: bidOf(s.entityCode),
      sentence,
      fix,
      depth: w.depthOfSurface(refid),
      floorIndex: w.index,
      planOrder: w.order.get(`s:${refid}`) ?? 0
    });
  };
  const atItem = (w, item, code, sentence, fix, holders) => {
    add2({
      ...holders ? { holders } : {},
      code,
      entity: "item",
      ref: item.refid,
      floorId: w.floor.id,
      name: itemName(item),
      bid: bidOf(item.entityCode),
      sentence,
      fix,
      depth: w.depthOfItem(item),
      floorIndex: w.index,
      planOrder: w.order.get(`i:${item.refid}`) ?? 0
    });
  };
  const copyWhere = (w, refid, holds) => (w.itemsByRefid.get(refid) ?? []).find(holds);
  const groupOfRow = /* @__PURE__ */ new Map();
  const identifyGroups = registerKnown ? identify.groups.filter((g) => IDENTIFY_STATES.has(g.state)) : [];
  identifyGroups.forEach((g) => g.rowIds.forEach((id) => groupOfRow.set(id, g.key)));
  const groupOfItem = (item) => {
    const uid = normaliseAssetUid10(item.assetUid);
    if (uid === void 0) return null;
    return groupOfRow.get(Number(uid)) ?? null;
  };
  const linkStateOf = itemRegisterStateResolver(register.rows, register.readState);
  walks.forEach((w) => {
    const hierarchy = resolvePlanHierarchy(w.design);
    hierarchy.problems.forEach((p) => {
      if (p.entity === "surface") {
        const s = w.surfaceByRefid.get(p.refid);
        const answer = hierarchy.surface(p.refid);
        if (!s || !answer) return;
        const letter = letterOfCode(s.entityCode) ?? "?";
        if (p.code === "too-deep") {
          atSurface(w, p.refid, p.code, CHECK_SENTENCES.tooDeep, { type: "choose-parent" });
        } else if (p.code === "letter-mismatch") {
          atSurface(
            w,
            p.refid,
            p.code,
            CHECK_SENTENCES.letterMismatch(kindOfLetter(letter) ?? letter, answer.placeKind ?? ""),
            { type: "reissue-bid", letter: answer.letter }
          );
        } else if (p.code === "invalid-letter") {
          atSurface(w, p.refid, p.code, CHECK_SENTENCES.invalidLetter(letter, "space"), {
            type: "reissue-bid",
            letter: answer.letter
          });
        }
        return;
      }
      if (p.code === "equipment-on-floor") {
        const item = copyWhere(w, p.refid, (it) => hierarchy.item(it)?.parent === null);
        if (item) {
          atItem(w, item, p.code, CHECK_SENTENCES.equipmentOnFloor, { type: "choose-parent" });
        }
      } else if (p.code === "invalid-letter") {
        const item = copyWhere(w, p.refid, (it) => {
          const letter = letterOfCode(it.entityCode);
          return letter !== null && letter !== "E";
        });
        if (item) {
          const letter = letterOfCode(item.entityCode) ?? "?";
          atItem(w, item, p.code, CHECK_SENTENCES.invalidLetter(letter, "machine"), {
            type: "reissue-bid",
            letter: "E"
          });
        }
      }
    });
    const kept = (entity, drawn) => entity.parentKeptOver !== void 0 && entity.parentKeptOver === drawn;
    w.surfaceByRefid.forEach((s, refid) => {
      const p = w.parentage.surface(refid);
      if (!p?.disagrees || kept(s, p.spatialParent)) return;
      atSurface(w, refid, "parent-chosen-differs", CHECK_SENTENCES.parentChosenDiffers, {
        type: "parent",
        drawn: p.spatialParent
      });
    });
    w.itemsByRefid.forEach((_copies, refid) => {
      const item = copyWhere(w, refid, (it) => {
        const p = w.parentage.item(it);
        return !!p?.disagrees && !kept(it, p.spatialParent);
      });
      if (!item) return;
      atItem(w, item, "parent-chosen-differs", CHECK_SENTENCES.parentChosenDiffers, {
        type: "parent",
        drawn: w.parentage.item(item)?.spatialParent ?? null
      });
    });
    if (registerKnown) {
      w.itemsByRefid.forEach((_copies, refid) => {
        const unlinked = copyWhere(w, refid, (it) => linkStateOf(it).kind === "legacy");
        if (unlinked) {
          atItem(w, unlinked, "machine-not-in-register", CHECK_SENTENCES.machineNotInRegister, {
            type: "link-or-add"
          });
        }
        const gone = copyWhere(w, refid, (it) => linkStateOf(it).kind === "orphan");
        if (gone) {
          atItem(w, gone, "machine-register-row-gone", CHECK_SENTENCES.machineRegisterRowGone, {
            type: "link-or-add"
          });
        }
      });
    }
  });
  const capacity = resolvePlanCapacity(layout, input.registerCapacityOf);
  const containerCheck = (code, r) => {
    const value = typeof r?.value === "number" ? r.value : void 0;
    const contents = typeof r?.contents === "number" ? r.contents : void 0;
    return {
      sentence: code === "capacity-below-contents" ? CHECK_SENTENCES.capacityBelowContents(value, contents) : CHECK_SENTENCES.capacityContentsChanged(value),
      fix: {
        type: "capacity",
        calculated: contents ?? "unknown",
        contents: contents ?? "unknown",
        keep: code === "capacity-contents-changed"
      }
    };
  };
  capacity.problems.forEach((p) => {
    if (p.entity === "surface") {
      (p.floorIds ?? []).forEach((floorId) => {
        const w = walkById.get(floorId);
        if (!w) return;
        if (p.code === "space-empty") {
          atSurface(w, p.refid, p.code, CHECK_SENTENCES.spaceEmpty, { type: "open" });
          return;
        }
        const { sentence, fix } = containerCheck(p.code, capacity.surface(p.refid, floorId));
        atSurface(w, p.refid, p.code, sentence, fix);
      });
    } else if (p.entity === "item") {
      if (!registerKnown) return;
      (p.floorIds ?? []).forEach((floorId) => {
        const w = walkById.get(floorId);
        const item = w && copyWhere(w, p.refid, (it) => capacity.item(it)?.value === void 0);
        if (!w || !item) return;
        atItem(w, item, p.code, CHECK_SENTENCES.capacityMissing, {
          type: "machine-capacity",
          groupKey: groupOfItem(item)
        });
      });
    } else if (p.entity === "floor") {
      const w = walkById.get(Number(p.refid));
      if (!w) return;
      const { sentence, fix } = containerCheck(p.code, capacity.floor(w.floor.id));
      add2({
        code: p.code,
        entity: "floor",
        ref: p.refid,
        floorId: w.floor.id,
        name: w.floor.name ?? "",
        bid: "",
        sentence,
        fix,
        depth: 0,
        floorIndex: w.index,
        planOrder: 0
      });
    } else {
      const { sentence, fix } = containerCheck(p.code, capacity.twin());
      add2({
        code: p.code,
        entity: "twin",
        ref: "",
        floorId: null,
        name: layout.name ?? "",
        bid: "",
        sentence,
        fix,
        depth: -1,
        floorIndex: Number.MAX_SAFE_INTEGER,
        planOrder: 0
      });
    }
  });
  const duplicates = findDuplicateEntityCodes(layout, twinBid);
  if (duplicates.byRefid.size > 0) {
    const holders = /* @__PURE__ */ new Map();
    walks.forEach((w) => {
      const note = (refid, holder) => {
        const code = duplicates.byRefid.get(refid);
        if (!code) return;
        const byRefid = holders.get(code) ?? /* @__PURE__ */ new Map();
        if (!byRefid.has(refid)) byRefid.set(refid, holder);
        holders.set(code, byRefid);
      };
      w.surfaceByRefid.forEach(
        (s, refid) => note(refid, { name: surfaceName2(s), entity: "surface" })
      );
      w.itemsByRefid.forEach(
        (copies, refid) => note(refid, { name: itemName(copies[0]), entity: "item" })
      );
    });
    const othersOf = (refid) => {
      const code = duplicates.byRefid.get(refid) ?? "";
      return [...(holders.get(code) ?? /* @__PURE__ */ new Map()).entries()].filter(
        ([holder]) => holder !== refid
      );
    };
    const holdersAt = (others2, w) => {
      if (others2.length !== 1) return [];
      const [[ref, { name, entity }]] = others2;
      const on = (entity === "surface" ? floorsOfSurface : floorsOfItem).get(ref) ?? [];
      return [{ ref, entity, floorIndex: holderFloor(on, w), name }];
    };
    duplicates.byRefid.forEach((_code, refid) => {
      const others2 = othersOf(refid);
      const sentence = others2.length > 0 ? CHECK_SENTENCES.duplicateBid(others2.map(([, h]) => h.name)) : CHECK_SENTENCES.duplicateBidCopies;
      (floorsOfSurface.get(refid) ?? []).forEach(
        (w) => atSurface(w, refid, "duplicate-bid", sentence, { type: "open" }, holdersAt(others2, w))
      );
      (floorsOfItem.get(refid) ?? []).forEach((w) => {
        const item = w.itemsByRefid.get(refid)?.[0];
        if (item)
          atItem(w, item, "duplicate-bid", sentence, { type: "open" }, holdersAt(others2, w));
      });
    });
  }
  const nameIndex = buildTwinNameIndex(layout, registerKnown ? register.rows : []);
  findDuplicateNames(nameIndex).forEach((group) => {
    const entities = /* @__PURE__ */ new Map();
    group.forEach((entry) => {
      const key = entityKeyOf(entry);
      if (!entities.has(key)) entities.set(key, entry);
    });
    if (entities.size < 2) return;
    const suggestion = nextFreeName(group[0].name, nameIndex);
    const pair = entities.size === 2 ? [...entities.values()] : null;
    const othersOf = (entry) => pair ? [pair[0] === entry ? pair[1].name : pair[0].name] : new Array(entities.size - 1);
    const holdersAt = (entry, w) => {
      if (!pair) return [];
      const other = pair[0] === entry ? pair[1] : pair[0];
      const { name } = other;
      const ref = other.ref;
      switch (ref.type) {
        case "surface":
          return [
            {
              ref: ref.refid,
              entity: "surface",
              floorIndex: holderFloor(floorsOfSurface.get(ref.refid) ?? [], w),
              name
            }
          ];
        case "item":
          return [
            {
              ref: ref.refid,
              entity: "item",
              floorIndex: holderFloor(floorsOfItem.get(ref.refid) ?? [], w),
              name
            }
          ];
        case "floor": {
          const on = walkById.get(ref.floorId);
          const floorIndex = on ? layoutIndexOf.get(on.floor) ?? null : null;
          return [{ ref: String(ref.floorId), entity: "floor", floorIndex, name }];
        }
        case "register":
          return [{ ref: String(ref.assetId), entity: "register", floorIndex: null, name }];
      }
    };
    entities.forEach((entry) => {
      const sentence = CHECK_SENTENCES.duplicateName(othersOf(entry));
      const fix = { type: "rename", suggestion };
      const ref = entry.ref;
      if (ref.type === "surface") {
        (floorsOfSurface.get(ref.refid) ?? []).forEach(
          (w) => atSurface(w, ref.refid, "duplicate-name", sentence, fix, holdersAt(entry, w))
        );
      } else if (ref.type === "item") {
        (floorsOfItem.get(ref.refid) ?? []).forEach((w) => {
          const item = w.itemsByRefid.get(ref.refid)?.[0];
          if (item) atItem(w, item, "duplicate-name", sentence, fix, holdersAt(entry, w));
        });
      } else if (ref.type === "floor") {
        const w = walkById.get(ref.floorId);
        if (!w) return;
        add2({
          code: "duplicate-name",
          entity: "floor",
          ref: String(ref.floorId),
          floorId: ref.floorId,
          name: entry.name,
          bid: "",
          sentence,
          fix,
          holders: holdersAt(entry, w),
          depth: 0,
          floorIndex: w.index,
          planOrder: 0
        });
      }
    });
  });
  if (input.skipped && input.skipped.count > 0) {
    add2({
      code: "adoption-skipped",
      entity: "twin",
      ref: "",
      floorId: null,
      name: layout.name ?? "",
      bid: "",
      sentence: input.skipped.summary,
      fix: { type: "none" },
      // A twin-level row like any other (depth −1), sorted ahead of everything by `first`.
      depth: -1,
      floorIndex: -1,
      planOrder: 0,
      first: true
    });
  }
  const toPlace = [];
  if (registerKnown) {
    const seen = /* @__PURE__ */ new Set();
    register.rows.forEach((row) => {
      if (!row || seen.has(row.id)) return;
      seen.add(row.id);
      const state2 = identify.stateOf(row.id);
      if (identify.planStateOf(row.id) !== "not_on_plan" || !state2 || !isPlaceableIdentity(state2)) {
        return;
      }
      const ref = String(row.id);
      toPlace.push({
        id: `to-place::register:${ref}`,
        kind: "place",
        code: "to-place",
        entity: "register",
        ref,
        floorId: null,
        name: row.asset_name ?? row.name ?? "",
        bid: "",
        sentence: CHECK_SENTENCES.toPlace,
        fix: { type: "place", assetId: row.id },
        depth: 0
      });
    });
    toPlace.sort(naturalName);
  }
  const all = [...placed.values()].sort(compareDeepestFirst);
  const errors = all.filter((c) => c.kind === "error").map(strip);
  const toCheck = all.filter((c) => c.kind === "check").map(strip);
  const identifyRefids = (w) => [...w.itemsByRefid].filter(([, copies]) => copies.some((it) => groupOfItem(it) !== null)).map(([refid]) => refid);
  const twin = zeroCounts();
  const byFloor = /* @__PURE__ */ new Map();
  walks.forEach((w) => byFloor.set(w.floor.id, zeroCounts()));
  all.forEach((c) => {
    twin[c.kind] += 1;
    if (c.floorId !== null) {
      const counts = byFloor.get(c.floorId);
      if (counts) counts[c.kind] += 1;
    }
  });
  identifyGroups.forEach((g) => {
    twin.identify += g.rowIds.length;
  });
  twin.place = toPlace.length;
  walks.forEach((w) => {
    const counts = byFloor.get(w.floor.id);
    if (counts) counts.identify += identifyRefids(w).length;
  });
  const byEntity = /* @__PURE__ */ new Map();
  [...errors, ...toCheck, ...toPlace].forEach(
    (c) => pushTo(byEntity, `${c.floorId ?? ""}|${c.entity}|${c.ref}`, c)
  );
  const marksCache = /* @__PURE__ */ new Map();
  const marks = (floorId) => {
    const cached = marksCache.get(floorId);
    if (cached) return cached;
    const w = walkById.get(floorId);
    if (!w) return {};
    const machines = /* @__PURE__ */ new Map();
    const spaces = /* @__PURE__ */ new Map();
    const rank = { error: 3, check: 2, identify: 1 };
    const raise2 = (map, refid, state2) => {
      const current = map.get(refid);
      if (current === void 0 || rank[state2] > rank[current]) map.set(refid, state2);
    };
    [...errors, ...toCheck].forEach((c) => {
      if (c.floorId !== floorId) return;
      const state2 = c.kind === "error" ? "error" : "check";
      if (c.entity === "item") raise2(machines, c.ref, state2);
      else if (c.entity === "surface") raise2(spaces, c.ref, state2);
    });
    identifyRefids(w).forEach((refid) => raise2(machines, refid, "identify"));
    const result = { machines, spaces };
    marksCache.set(floorId, result);
    return result;
  };
  const refidsOfGroup = (groupKey2) => {
    const group = identifyGroups.find((g) => g.key === groupKey2);
    const placement = group ? placementIndexFor(layout) : null;
    if (!group || !placement) return [];
    return group.rowIds.map((id) => placement.byAssetUid.get(String(id))).filter((refid) => typeof refid === "string");
  };
  const refidOfRow = (rowId) => placementIndexFor(layout)?.byAssetUid.get(String(rowId)) ?? null;
  return {
    known: true,
    failed: false,
    registerKnown,
    errors,
    toCheck,
    identify: identifyGroups,
    toPlace,
    counts: { twin, byFloor },
    score: identify.score,
    marks,
    checksFor: (floorId, entity, ref) => byEntity.get(`${floorId ?? ""}|${entity}|${ref}`) ?? [],
    refidsOfGroup,
    refidOfRow,
    launched: launchedFor(live2, twinBid, layout)
  };
};
var memo = /* @__PURE__ */ new WeakMap();
var MEMO_PER_LAYOUT = 4;
var sameInput = (a, b) => a.twinBid === b.twinBid && a.register.rows === b.register.rows && a.register.readState === b.register.readState && a.registerCapacityOf === b.registerCapacityOf && a.identify === b.identify && a.live.status === b.live.status && a.live.bids === b.live.bids && (a.skipped?.count ?? 0) === (b.skipped?.count ?? 0) && (a.skipped?.summary ?? "") === (b.skipped?.summary ?? "");
var buildPlanChecks = (input) => {
  const layout = input.layout;
  if (layout == null || typeof layout !== "object") return notLoaded(input, false);
  const entries = memo.get(layout) ?? [];
  const hit = entries.find((e) => sameInput(e.input, input));
  if (hit) return hit.result;
  let result;
  try {
    result = build(input, layout);
  } catch {
    result = notLoaded(input, true);
  }
  entries.unshift({ input, result });
  if (entries.length > MEMO_PER_LAYOUT) entries.length = MEMO_PER_LAYOUT;
  memo.set(layout, entries);
  return result;
};

// src/twin-planner/reconcileGeneratedComposites.ts
var generationTargets = (layout) => new Set(wantedTargets(layout).keys());
var wantedTargets = (layout) => {
  const wanted = /* @__PURE__ */ new Map();
  if (!layout) return wanted;
  const hierarchy = directChildSpaces(layout);
  const observed = /* @__PURE__ */ new Set();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const observer of asArray(design?.observers).filter(isLive)) {
      if (!isFileableObserver(observer)) continue;
      const key = encodeRef(observerTargetRef(observer));
      if (key) observed.add(key);
    }
  }
  hierarchy.childSpaces.forEach((children, key) => {
    if (children.length === 0 || observed.has(key)) return;
    const target = decodeRef(key);
    if (!target) return;
    if (target.kind === "floor" && !hierarchy.fileableFloorIds.has(target.floorId)) return;
    wanted.set(key, { target, inputs: children });
  });
  return wanted;
};
var reconcileGeneratedComposites = (layout, { newRefid }) => {
  const current = asArray(layout?.composites);
  if (!layout) return current;
  const handMade = /* @__PURE__ */ new Set();
  for (const composite of current) {
    if (!isLive(composite) || composite.generated === true) continue;
    const key = encodeRef(composite.target);
    if (key) handMade.add(key);
  }
  const wanted = wantedTargets(layout);
  handMade.forEach((key) => wanted.delete(key));
  let changed = false;
  const settled = /* @__PURE__ */ new Set();
  const next = [];
  for (const composite of current) {
    if (!isLive(composite) || composite.generated !== true) {
      next.push(composite);
      continue;
    }
    const key = encodeRef(composite.target);
    const want = key ? wanted.get(key) : void 0;
    if (want && !settled.has(key)) {
      settled.add(key);
      if (sameRefList(asArray(composite.inputs), want.inputs)) {
        next.push(composite);
      } else {
        changed = true;
        next.push({ ...composite, inputs: want.inputs });
      }
      continue;
    }
    changed = true;
    if (isLoadedComposite(composite)) next.push({ ...composite, _deleted: true });
  }
  wanted.forEach((want, key) => {
    if (settled.has(key)) return;
    changed = true;
    next.push({ refid: newRefid(), target: want.target, inputs: want.inputs, generated: true });
  });
  return changed ? next : current;
};
var isFiledSelfOf = (generated, loaded, layout) => {
  if (encodeRef(generated.target) !== encodeRef(loaded.target)) return false;
  if (generated.includesOwnObservers === true !== (loaded.includesOwnObservers === true))
    return false;
  if (generated.excluded === true !== (loaded.excluded === true)) return false;
  const ours = asArray(generated.inputs).map(encodeRef);
  const theirs = asArray(loaded.inputs).map(encodeRef);
  let i = 0;
  const missing = [];
  for (const key of ours) {
    if (i < theirs.length && theirs[i] === key) i += 1;
    else missing.push(key);
  }
  if (i !== theirs.length) return false;
  if (missing.length === 0) return true;
  const measured = /* @__PURE__ */ new Set();
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const observer of asArray(design?.observers).filter(isLive)) {
      if (!isFileableObserver(observer)) continue;
      const key = encodeRef(observerTargetRef(observer));
      if (key) measured.add(key);
    }
  }
  for (const composite of asArray(layout?.composites)) {
    if (isLive(composite)) measured.add(encodeRef(composite.target));
  }
  return missing.every((key) => !measured.has(key));
};

// src/equipment/registerImportState.ts
var awaitingCustomerRegisterImport = (mode, rows) => {
  if (mode !== "UPDATE") return false;
  const list = rows ?? [];
  return !list.some((row) => (row?.customer_asset_identifier ?? "").trim() !== "");
};
var preImportWarningText = (missingCount) => {
  const lead = "This twin\u2019s own asset register has not been imported yet.";
  const tail = "TwinLabs-named entries stand in for equipment the customer\u2019s spreadsheet would otherwise bring in under their own names and Asset Identifiers \u2014 import it if it is coming.";
  if (missingCount <= 0) return `${lead} ${tail}`;
  return `${lead} Adding ${missingCount} ${missingCount === 1 ? "row" : "rows"} now means those ` + tail;
};

// src/table/sortRows.ts
var compareValues = (a, b, direction) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const factor = direction === "desc" ? -1 : 1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
  return String(a).localeCompare(String(b), void 0, { numeric: true, sensitivity: "base" }) * factor;
};
var sortRows = (rows, field, direction, accessors) => {
  const accessor = field !== null && Object.prototype.hasOwnProperty.call(accessors, field) ? accessors[field] : void 0;
  if (accessor === void 0) return [...rows];
  return rows.map((row, index) => ({ row, index })).sort((left, right) => {
    const primary = compareValues(accessor(left.row), accessor(right.row), direction);
    return primary !== 0 ? primary : left.index - right.index;
  }).map((entry) => entry.row);
};
var nextSort = (current, field) => current.field === field ? { field, direction: current.direction === "asc" ? "desc" : "asc" } : { field, direction: "asc" };

// src/rovingIndexForKey.ts
var rovingIndexForKey = (key, at, count) => {
  if (count <= 0) return -1;
  switch (key) {
    case "ArrowRight":
    case "ArrowDown":
      return (at + 1) % count;
    case "ArrowLeft":
    case "ArrowUp":
      return (at - 1 + count) % count;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return -1;
  }
};

// src/heatGrid/decodeHeatGridCells.ts
var HEAT_GRID_CELL_ENCODING = "i16i16i32-le-b64";
var BYTES_PER_CELL = 8;
var base64ToBytes = (data) => {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};
var decodeHeatGridCells = (encoding, data, cells) => {
  if (encoding !== HEAT_GRID_CELL_ENCODING) {
    throw new Error(`decodeHeatGridCells: unknown encoding "${encoding}"`);
  }
  const bytes = base64ToBytes(data);
  if (!Number.isInteger(cells) || cells < 0 || bytes.byteLength !== cells * BYTES_PER_CELL) {
    throw new Error(
      `decodeHeatGridCells: ${bytes.byteLength} bytes cannot hold ${cells} cells of ${BYTES_PER_CELL}`
    );
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const cx = new Int16Array(cells);
  const cy = new Int16Array(cells);
  const n = new Int32Array(cells);
  for (let i = 0; i < cells; i += 1) {
    const at = i * BYTES_PER_CELL;
    cx[i] = view.getInt16(at, true);
    cy[i] = view.getInt16(at + 2, true);
    n[i] = view.getInt32(at + 4, true);
  }
  return { cx, cy, n };
};

// src/heatGrid/heatBlobs.ts
var SIGMA_CELLS = 1;
var KERNEL_RADIUS = 3;
var PERCENTILE = 0.99;
var MIN_BLOB_CELLS = 3;
var MAX_OUTLINE_CORNERS = 16;
var SEPARATE_PROMINENCE = 0.3;
var MIN_PEAK_SEPARATION_CELLS = 2;
var gaussianKernel = () => {
  const k = [];
  for (let i = -KERNEL_RADIUS; i <= KERNEL_RADIUS; i += 1) {
    k.push(Math.exp(-(i * i) / (2 * SIGMA_CELLS * SIGMA_CELLS)));
  }
  return k;
};
var blur = (raw, w, h) => {
  const k = gaussianKernel();
  const pass = (src, horizontal) => {
    const out = new Float64Array(src.length);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        let sum = 0;
        let weight = 0;
        for (let i = -KERNEL_RADIUS; i <= KERNEL_RADIUS; i += 1) {
          const xx = horizontal ? x + i : x;
          const yy = horizontal ? y : y + i;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          const kw = k[i + KERNEL_RADIUS];
          sum += src[yy * w + xx] * kw;
          weight += kw;
        }
        out[y * w + x] = weight > 0 ? sum / weight : 0;
      }
    }
    return out;
  };
  return pass(pass(raw, true), false);
};
var components = (hot, w, h) => {
  const seen = new Uint8Array(hot.length);
  const out = [];
  for (let start = 0; start < hot.length; start += 1) {
    if (!hot[start] || seen[start]) continue;
    const comp = [];
    const stack = [start];
    seen[start] = 1;
    while (stack.length > 0) {
      const i = stack.pop();
      comp.push(i);
      const x = i % w;
      const y = (i - x) / w;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const xx = x + dx;
          const yy = y + dy;
          if (dx === 0 && dy === 0 || xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          const j = yy * w + xx;
          if (hot[j] && !seen[j]) {
            seen[j] = 1;
            stack.push(j);
          }
        }
      }
    }
    out.push(comp);
  }
  return out;
};
var neighbours8 = (i, w, h) => {
  const x = i % w;
  const y = (i - x) / w;
  const out = [];
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const xx = x + dx;
      const yy = y + dy;
      if ((dx !== 0 || dy !== 0) && xx >= 0 && yy >= 0 && xx < w && yy < h) out.push(yy * w + xx);
    }
  }
  return out;
};
var hotterFirst = (heat) => (a, b) => heat[b] - heat[a] || a - b;
var CellHeap = class {
  constructor(before2) {
    this.before = before2;
    this.items = [];
  }
  get size() {
    return this.items.length;
  }
  push(cell) {
    const items = this.items;
    items.push(cell);
    let i = items.length - 1;
    while (i > 0) {
      const up = i - 1 >> 1;
      if (this.before(items[i], items[up]) >= 0) break;
      [items[i], items[up]] = [items[up], items[i]];
      i = up;
    }
  }
  pop() {
    const items = this.items;
    const top = items[0];
    const last = items.pop();
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (; ; ) {
        const l = 2 * i + 1;
        const r = l + 1;
        let best = i;
        if (l < items.length && this.before(items[l], items[best]) < 0) best = l;
        if (r < items.length && this.before(items[r], items[best]) < 0) best = r;
        if (best === i) break;
        [items[i], items[best]] = [items[best], items[i]];
        i = best;
      }
    }
    return top;
  }
};
var separatingPeaks = (comp, heat, w, h) => {
  const order = [...comp].sort(hotterFirst(heat));
  const parent = /* @__PURE__ */ new Map();
  const peakOf = /* @__PURE__ */ new Map();
  const prominence = /* @__PURE__ */ new Map();
  const find = (cell) => {
    let root = cell;
    while (parent.get(root) !== root) root = parent.get(root);
    for (let c = cell; c !== root; ) {
      const next = parent.get(c);
      parent.set(c, root);
      c = next;
    }
    return root;
  };
  for (const cell of order) {
    const roots = [];
    for (const n of neighbours8(cell, w, h)) {
      if (!parent.has(n)) continue;
      const root = find(n);
      if (!roots.includes(root)) roots.push(root);
    }
    parent.set(cell, cell);
    if (roots.length === 0) {
      peakOf.set(cell, cell);
      continue;
    }
    roots.sort((a, b) => hotterFirst(heat)(peakOf.get(a), peakOf.get(b)));
    const [winner, ...losers] = roots;
    parent.set(cell, winner);
    for (const loser of losers) {
      const peak = peakOf.get(loser);
      prominence.set(peak, heat[peak] - heat[cell]);
      parent.set(loser, winner);
    }
  }
  const peaks = [...peakOf.values()].sort(hotterFirst(heat));
  const kept = [];
  for (const peak of peaks) {
    const rise = prominence.get(peak) ?? heat[peak];
    if (kept.length > 0 && (heat[peak] <= 0 || rise < SEPARATE_PROMINENCE * heat[peak])) continue;
    const px = peak % w;
    const py = (peak - px) / w;
    const tooClose = kept.some(
      (k) => Math.hypot(k % w - px, Math.floor(k / w) - py) < MIN_PEAK_SEPARATION_CELLS
    );
    if (!tooClose) kept.push(peak);
  }
  return kept;
};
var partsOf = (comp, heat, w, h, exterior) => {
  const seeds = separatingPeaks(comp, heat, w, h);
  if (seeds.length < 2) return [comp];
  const inComp = new Set(comp);
  const label = /* @__PURE__ */ new Map();
  const heap = new CellHeap(hotterFirst(heat));
  seeds.forEach((seed, k) => {
    label.set(seed, k);
    heap.push(seed);
  });
  while (heap.size > 0) {
    const cell = heap.pop();
    const l = label.get(cell);
    for (const n of neighbours8(cell, w, h)) {
      if (inComp.has(n) && !label.has(n)) {
        label.set(n, l);
        heap.push(n);
      }
    }
  }
  const parts2 = seeds.map(() => []);
  for (const cell of comp) parts2[label.get(cell)].push(cell);
  const reachesOut = (part) => part.some((cell) => {
    const x = cell % w;
    const y = (cell - x) / w;
    return [
      [x - 1, y],
      [x + 1, y],
      [x, y - 1],
      [x, y + 1]
    ].some(([xx, yy]) => xx < 0 || yy < 0 || xx >= w || yy >= h || exterior[yy * w + xx] === 1);
  });
  for (; ; ) {
    const live2 = parts2.flatMap((p, k) => p.length > 0 ? [k] : []);
    if (live2.length < 2) break;
    const small = live2.filter((k) => parts2[k].length < MIN_BLOB_CELLS || !reachesOut(parts2[k])).sort((a, b) => parts2[a].length - parts2[b].length || a - b)[0];
    if (small === void 0) break;
    const shared = /* @__PURE__ */ new Map();
    for (const cell of parts2[small]) {
      for (const n of neighbours8(cell, w, h)) {
        const other = label.get(n);
        if (other === void 0 || other === small) continue;
        const side = Math.abs(n - cell) === 1 || Math.abs(n - cell) === w;
        shared.set(other, (shared.get(other) ?? 0) + (side ? 1 : 1e-3));
      }
    }
    let into = -1;
    for (const [k, count] of shared) {
      const best = into < 0 ? -1 : shared.get(into);
      if (count > best || count === best && parts2[k].length > parts2[into].length) into = k;
    }
    if (into < 0) break;
    for (const cell of parts2[small]) label.set(cell, into);
    parts2[into].push(...parts2[small]);
    parts2[small] = [];
  }
  return parts2.filter((p) => p.length > 0);
};
var walkOuterBoundary = (comp, w) => {
  const inComp = new Set(comp);
  const out = /* @__PURE__ */ new Map();
  const key = (v) => `${v[0]},${v[1]}`;
  const add2 = (from, to, inside, outside) => {
    const edge = { from, to, inside, outside };
    const list = out.get(key(from));
    if (list) list.push(edge);
    else out.set(key(from), [edge]);
  };
  for (const i of comp) {
    const x = i % w;
    const y = (i - x) / w;
    const has = (xx, yy) => xx >= 0 && xx < w && inComp.has(yy * w + xx);
    if (!has(x, y - 1)) add2([x, y], [x + 1, y], [x, y], [x, y - 1]);
    if (!has(x + 1, y)) add2([x + 1, y], [x + 1, y + 1], [x, y], [x + 1, y]);
    if (!has(x, y + 1)) add2([x + 1, y + 1], [x, y + 1], [x, y], [x, y + 1]);
    if (!has(x - 1, y)) add2([x, y + 1], [x, y], [x, y], [x - 1, y]);
  }
  const first = Math.min(...comp);
  const fx = first % w;
  const fy = (first - fx) / w;
  const start = (out.get(key([fx, fy])) ?? []).find((e) => e.to[0] === fx + 1 && e.to[1] === fy);
  if (!start) return [];
  const used = /* @__PURE__ */ new Set([start]);
  const sides = [start];
  let cur = start;
  for (let guard = 0; guard < comp.length * 4 + 4; guard += 1) {
    const dx = cur.to[0] - cur.from[0];
    const dy = cur.to[1] - cur.from[1];
    const options = (out.get(key(cur.to)) ?? []).filter((e) => !used.has(e) || e === start);
    if (options.length === 0) break;
    const cross2 = (e) => dx * (e.to[1] - e.from[1]) - dy * (e.to[0] - e.from[0]);
    const next = options.sort((a, b) => cross2(a) - cross2(b))[0];
    if (next === start) break;
    used.add(next);
    sides.push(next);
    cur = next;
  }
  return sides;
};
var contourPoint = (side, smoothed, w, h, threshold, limit, hot) => {
  const [ix, iy] = side.inside;
  const [ox, oy] = side.outside;
  const beyond = ox < 0 || oy < 0 || ox >= w || oy >= h;
  const vi = smoothed[iy * w + ix];
  const vo = beyond ? 0 : smoothed[oy * w + ox];
  if (!beyond && hot[oy * w + ox]) return [(ix + ox) / 2 + 0.5, (iy + oy) / 2 + 0.5];
  const f = (vi - threshold) / (vi - vo);
  const cx = Math.min(ix + 0.5, limit[0]);
  const cy = Math.min(iy + 0.5, limit[1]);
  return [cx + f * (ox + 0.5 - cx), cy + f * (oy + 0.5 - cy)];
};
var distanceToSegment = (p, a, b) => {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2));
  return Math.hypot(p[0] - (a[0] + t * vx), p[1] - (a[1] + t * vy));
};
var douglasPeucker = (line, tolerance) => {
  if (line.length <= 2) return line;
  let worst = 0;
  let at = 0;
  for (let i = 1; i < line.length - 1; i += 1) {
    const d = distanceToSegment(line[i], line[0], line[line.length - 1]);
    if (d > worst) {
      worst = d;
      at = i;
    }
  }
  if (worst <= tolerance) return [line[0], line[line.length - 1]];
  const left = douglasPeucker(line.slice(0, at + 1), tolerance);
  const right = douglasPeucker(line.slice(at), tolerance);
  return [...left.slice(0, -1), ...right];
};
var simplifyRing = (ring, tolerance) => {
  if (ring.length <= 3) return ring;
  let far = 0;
  let farDist = -1;
  ring.forEach((p, i) => {
    const d = Math.hypot(p[0] - ring[0][0], p[1] - ring[0][1]);
    if (d > farDist) {
      farDist = d;
      far = i;
    }
  });
  const a = douglasPeucker(ring.slice(0, far + 1), tolerance);
  const b = douglasPeucker([...ring.slice(far), ring[0]], tolerance);
  return [...a.slice(0, -1), ...b.slice(0, -1)];
};
var ringArea2 = (ring) => Math.abs(
  ring.reduce((sum, [x, y], i) => {
    const [nx, ny] = ring[(i + 1) % ring.length];
    return sum + x * ny - nx * y;
  }, 0)
) / 2;
var spreadCorners = (ring, count) => Array.from({ length: count }, (_, i) => ring[Math.floor(i * ring.length / count)]);
var before = (a, b) => a[0] < b[0] || a[0] === b[0] && a[1] < b[1];
var MAX_CUT_CORNERS = 6;
var simplifyCut = (chain, step) => {
  const flip = before(chain[chain.length - 1], chain[0]);
  const line = flip ? [...chain].reverse() : chain;
  let simple = douglasPeucker(line, step);
  for (let tolerance = 2 * step; simple.length > MAX_CUT_CORNERS; tolerance += step) {
    simple = douglasPeucker(line, tolerance);
  }
  return flip ? simple.reverse() : simple;
};
var cutChains = (ring, key) => {
  const n = ring.length;
  const start = key.findIndex((k, i) => k >= 0 && key[(i - 1 + n) % n] !== k);
  if (start < 0) return null;
  const at = (k) => (start + k) % n;
  const chains = [];
  for (let k = 0; k < n; ) {
    const part = key[at(k)];
    const run = [ring[at(k)]];
    for (k += 1; k < n && key[at(k)] === part; k += 1) run.push(ring[at(k)]);
    chains.push({ points: run, cut: true });
    const link = [run[run.length - 1]];
    for (; k < n && key[at(k)] < 0; k += 1) link.push(ring[at(k)]);
    link.push(ring[at(k)]);
    chains.push({ points: link, cut: false });
  }
  return chains;
};
function heatBlobOutlines(cells, picture, edge, cellPx, options = {}) {
  if (!(cellPx > 0)) return [];
  const toleranceStep = cellPx / 4;
  const w = Math.ceil(picture.width / cellPx);
  const h = Math.ceil(picture.height / cellPx);
  if (!(w > 0 && h > 0)) return [];
  const raw = new Float64Array(w * h);
  for (let i = 0; i < cells.n.length; i += 1) {
    const x = cells.cx[i];
    const y = cells.cy[i];
    const n = cells.n[i];
    if (n <= 0 || x < 0 || y < 0 || x >= w || y >= h) continue;
    raw[y * w + x] += n;
  }
  const smoothed = blur(raw, w, h);
  const heated = [];
  for (let i = 0; i < raw.length; i += 1) if (raw[i] > 0) heated.push(smoothed[i]);
  if (heated.length === 0) return [];
  heated.sort((a, b) => a - b);
  const p99 = heated[Math.floor(PERCENTILE * (heated.length - 1))];
  const threshold = Math.min(100, Math.max(1, edge)) / 100 * p99;
  if (!(threshold > 0)) return [];
  const hot = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) if (smoothed[i] >= threshold) hot[i] = 1;
  const limit = [picture.width / cellPx, picture.height / cellPx];
  const clip = (ring) => ring.map(([x, y]) => [
    Math.min(picture.width, Math.max(0, x)),
    Math.min(picture.height, Math.max(0, y))
  ]);
  const finish = (ring) => {
    if (ring.length < 3) return [];
    let tolerance = toleranceStep;
    let simple = simplifyRing(ring, tolerance);
    if (simple.length < 3) simple = ring;
    while (simple.length > MAX_OUTLINE_CORNERS) {
      const next = simplifyRing(ring, tolerance + toleranceStep);
      if (next.length < 3) break;
      tolerance += toleranceStep;
      simple = next;
    }
    if (simple.length > MAX_OUTLINE_CORNERS) simple = spreadCorners(simple, MAX_OUTLINE_CORNERS);
    return clip(simple);
  };
  const finishShared = (ring, key) => {
    const chains = ring.length >= 3 ? cutChains(ring, key) : null;
    if (!chains) return finish(ring);
    const cuts = chains.map((c) => c.cut ? simplifyCut(c.points, toleranceStep) : c.points);
    for (let tolerance = toleranceStep; ; tolerance += toleranceStep) {
      const simples = chains.map(
        (c, k) => c.cut ? cuts[k] : douglasPeucker(c.points, tolerance)
      );
      const joined = simples.flatMap((s) => s.slice(0, -1));
      if (joined.length <= MAX_OUTLINE_CORNERS) {
        return joined.length >= 3 && ringArea2(joined) >= ringArea2(ring) / 2 ? clip(joined) : finish(ring);
      }
      if (chains.every((c, k) => c.cut || simples[k].length <= 2)) return finish(ring);
    }
  };
  const outlines = [];
  const blobs = components(hot, w, h).filter((comp) => comp.length >= MIN_BLOB_CELLS);
  let pieces = blobs;
  if (options.separatePeaks) {
    const exterior = new Uint8Array(raw.length);
    const stack = [];
    for (let i = 0; i < raw.length; i += 1) {
      const x = i % w;
      const y = (i - x) / w;
      if (!hot[i] && (x === 0 || y === 0 || x === w - 1 || y === h - 1)) {
        exterior[i] = 1;
        stack.push(i);
      }
    }
    while (stack.length > 0) {
      const i = stack.pop();
      const x = i % w;
      const y = (i - x) / w;
      for (const [xx, yy] of [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1]
      ]) {
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        const j = yy * w + xx;
        if (!hot[j] && !exterior[j]) {
          exterior[j] = 1;
          stack.push(j);
        }
      }
    }
    pieces = blobs.flatMap((comp) => partsOf(comp, raw, w, h, exterior));
  }
  const partOf = new Int32Array(raw.length).fill(-1);
  pieces.forEach((comp, p) => {
    for (const cell of comp) partOf[cell] = p;
  });
  for (const comp of pieces) {
    const sides = walkOuterBoundary(comp, w);
    const key = sides.map(
      ({ outside: [ox, oy] }) => ox >= 0 && oy >= 0 && ox < w && oy < h && hot[oy * w + ox] === 1 ? partOf[oy * w + ox] : -1
    );
    let points2 = finishShared(
      sides.map((side) => {
        const [x, y] = contourPoint(side, smoothed, w, h, threshold, limit, hot);
        return [x * cellPx, y * cellPx];
      }),
      key
    );
    if (ringArea2(points2) < cellPx * cellPx / 2) {
      points2 = finish(sides.map(({ from: [x, y] }) => [x * cellPx, y * cellPx]));
    }
    if (points2.length >= 3) outlines.push({ points: points2, cells: comp.length });
  }
  return outlines.sort((a, b) => b.cells - a.cells);
}

// src/timezone/zoneNames.ts
var LEGACY_ZONE_NAMES = Object.freeze({
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  // AR
  "America/Catamarca": "America/Argentina/Catamarca",
  // AR
  "America/Cordoba": "America/Argentina/Cordoba",
  // AR
  "America/Jujuy": "America/Argentina/Jujuy",
  // AR
  "America/Mendoza": "America/Argentina/Mendoza",
  // AR
  "America/Coral_Harbour": "America/Atikokan",
  // CA
  "Africa/Asmera": "Africa/Asmara",
  // ER
  "Pacific/Ponape": "Pacific/Pohnpei",
  // FM
  "Pacific/Truk": "Pacific/Chuuk",
  // FM
  "Atlantic/Faeroe": "Atlantic/Faroe",
  // FO
  "America/Godthab": "America/Nuuk",
  // GL
  "Asia/Calcutta": "Asia/Kolkata",
  // IN
  "Pacific/Enderbury": "Pacific/Kanton",
  // KI
  "Asia/Rangoon": "Asia/Yangon",
  // MM
  "Asia/Katmandu": "Asia/Kathmandu",
  // NP
  "Europe/Kiev": "Europe/Kyiv",
  // UA
  "America/Indianapolis": "America/Indiana/Indianapolis",
  // US
  "America/Louisville": "America/Kentucky/Louisville",
  // US
  "Asia/Saigon": "Asia/Ho_Chi_Minh"
  // VN
});
var normaliseZoneName = (name) => {
  const trimmed3 = name.trim();
  return Object.prototype.hasOwnProperty.call(LEGACY_ZONE_NAMES, trimmed3) ? LEGACY_ZONE_NAMES[trimmed3] : trimmed3;
};
var sameZone = (a, b) => {
  const left = normaliseZoneName(a);
  const right = normaliseZoneName(b);
  return left !== "" && right !== "" && left === right;
};

// src/timezone/countryZones.ts
var readEngineZones = (locale) => {
  const withZones = locale;
  if (typeof withZones.getTimeZones === "function") return withZones.getTimeZones() ?? [];
  if (Array.isArray(withZones.timeZones)) return withZones.timeZones;
  return null;
};
var toCountryCode = (input) => {
  const code = (input ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  try {
    return new Intl.Locale(`und-${code}`).region ?? code;
  } catch {
    return code;
  }
};
var countryZones = (country, read = readEngineZones) => {
  const code = toCountryCode(country);
  if (code === null) return [];
  let zones;
  try {
    zones = read(new Intl.Locale(`und-${code}`));
  } catch {
    return null;
  }
  if (zones === null) return null;
  return [...new Set(zones.map(normaliseZoneName))].sort();
};
var PRIMARY_ZONE_BY_COUNTRY = Object.freeze({
  DE: "Europe/Berlin",
  ES: "Europe/Madrid",
  PT: "Europe/Lisbon"
});

// src/timezone/deriveZone.ts
var loadTzLookup = () => import("@photostructure/tz-lookup").then(
  (mod) => mod.default ?? mod
);
var cachedLookup = null;
var warned = false;
var isUsablePosition = (position) => !!position && Number.isFinite(position.latitude) && Number.isFinite(position.longitude) && position.latitude >= -90 && position.latitude <= 90 && position.longitude >= -180 && position.longitude <= 180;
var lookupZone = async (position, load) => {
  try {
    const lookup = cachedLookup ?? await load();
    const zone = lookup(position.latitude, position.longitude);
    cachedLookup = lookup;
    warned = false;
    return { zone: normaliseZoneName(zone) };
  } catch (error) {
    if (!warned) {
      warned = true;
      const stage = cachedLookup ? "lookup" : "load";
      console.warn(`[zone-derivation] position lookup unavailable (${stage})`, error);
    }
    return { failed: true };
  }
};
var deriveZone = async (input, deps = {}) => {
  const code = toCountryCode(input.country);
  if (code === null) return { kind: "unknown", reason: "unknown-country" };
  const zones = countryZones(code, deps.read);
  if (zones === null) return { kind: "unknown", reason: "unsupported-engine" };
  if (zones.length === 0) return { kind: "unknown", reason: "unknown-country" };
  const notes = {};
  if (isUsablePosition(input.position)) {
    const found = await lookupZone(input.position, deps.load ?? loadTzLookup);
    if ("failed" in found) {
      notes.positionLookup = "failed";
    } else if (zones.includes(found.zone)) {
      return { kind: "derived", zone: found.zone, basis: "position" };
    } else {
      notes.positionZone = found.zone;
    }
  }
  if (zones.length === 1) return { kind: "derived", zone: zones[0], basis: "country", ...notes };
  const primary = notes.positionLookup === "failed" ? void 0 : PRIMARY_ZONE_BY_COUNTRY[code];
  if (primary) {
    return { kind: "derived", zone: primary, basis: "country", candidates: zones, ...notes };
  }
  return { kind: "choose", candidates: zones, ...notes };
};

// src/countries/countries.ts
var COUNTRY_ALIASES = Object.freeze({
  usa: "US",
  "united states of america": "US",
  uae: "AE",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  "northern ireland": "GB",
  holland: "NL"
});
var engineDisplayNames = () => {
  try {
    if (typeof Intl.DisplayNames !== "function") return null;
    return new Intl.DisplayNames(["en"], { type: "region", fallback: "none" });
  } catch {
    return null;
  }
};
var LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
var buildOptions = (engine = {}) => {
  const names = (engine.displayNames ?? engineDisplayNames)();
  if (!names) return [];
  const zonesOf = engine.zones ?? ((code) => countryZones(code));
  const options = [];
  for (const first of LETTERS) {
    for (const second of LETTERS) {
      const code = first + second;
      if (toCountryCode(code) !== code) continue;
      let name;
      try {
        name = names.of(code);
      } catch {
        name = void 0;
      }
      if (!name || name === code) continue;
      const zones = zonesOf(code);
      if (zones === null) return [];
      if (zones.length > 0) options.push({ code, name });
    }
  }
  return options.sort((a, b) => a.name.localeCompare(b.name, "en"));
};
var memo2 = null;
var countryOptions = (engine) => {
  if (engine) return buildOptions(engine);
  if (memo2 === null) memo2 = buildOptions();
  return memo2;
};
var byCode = null;
var byName = null;
var indexes = () => {
  if (!byCode || !byName) {
    const options = countryOptions();
    byCode = new Map(options.map((o) => [o.code, o.name]));
    byName = new Map(options.map((o) => [o.name.toLowerCase(), o.code]));
  }
  return { byCode, byName };
};
var countryName = (code) => indexes().byCode.get((code ?? "").trim().toUpperCase()) ?? null;
var countryCodeFor = (text4) => {
  const trimmed3 = (text4 ?? "").trim();
  if (trimmed3 === "") return null;
  const { byCode: codes, byName: names } = indexes();
  const code = toCountryCode(trimmed3);
  if (code !== null) return codes.has(code) ? code : null;
  const lower = trimmed3.toLowerCase();
  const named = names.get(lower);
  if (named) return named;
  const alias = COUNTRY_ALIASES[lower];
  return alias && codes.has(alias) ? alias : null;
};

// src/model-file/fetchModelFootprint.ts
var INITIAL_BYTES = 64 * 1024;
var MAX_HEADER_BYTES = 16 * 1024 * 1024;
var MODEL_FOOTPRINT_TIMEOUT_MS = 3e4;
async function fetchModelFootprint(url, {
  fetch: fetchImpl = globalThis.fetch,
  signal,
  timeoutMs = MODEL_FOOTPRINT_TIMEOUT_MS
} = {}) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener("abort", onCallerAbort);
  const aborted = new Promise((resolve) => {
    const answer = () => resolve({ ok: false, reason: timedOut ? "timeout" : "network" });
    if (controller.signal.aborted) answer();
    else controller.signal.addEventListener("abort", answer);
  });
  const read = (byteCount) => Promise.race([readPrefix(fetchImpl, url, byteCount, controller.signal), aborted]).catch(
    () => ({ ok: false, reason: timedOut ? "timeout" : "network" })
  );
  try {
    let bytes = await read(INITIAL_BYTES);
    if (!bytes.ok) return bytes;
    let json3 = readGlbJson(bytes.bytes);
    if (!json3.ok && json3.reason === "truncated") {
      if (json3.needBytes > MAX_HEADER_BYTES) return { ok: false, reason: "too-large" };
      bytes = await read(json3.needBytes);
      if (!bytes.ok) return bytes;
      json3 = readGlbJson(bytes.bytes);
      if (!json3.ok && json3.reason === "truncated") return { ok: false, reason: "http-error" };
    }
    if (!json3.ok) return { ok: false, reason: json3.reason };
    return modelFootprint(json3.json);
  } catch {
    return { ok: false, reason: timedOut ? "timeout" : "network" };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
    controller.abort();
  }
}
async function readPrefix(fetchImpl, url, byteCount, signal) {
  const response = await fetchImpl(url, {
    headers: { Range: `bytes=0-${byteCount - 1}` },
    signal
  });
  if (!response) return { ok: false, reason: "network" };
  if (response.status === 404) return { ok: false, reason: "not-found" };
  if (response.status !== 206 && response.status !== 200) {
    return { ok: false, reason: "http-error", status: response.status };
  }
  return { ok: true, bytes: await readUpTo(response, byteCount) };
}
async function readUpTo(response, byteCount) {
  const reader = response.body?.getReader();
  if (!reader) return new Uint8Array(await response.arrayBuffer()).subarray(0, byteCount);
  const chunks = [];
  let filled = 0;
  try {
    while (filled < byteCount) {
      const { done, value } = await reader.read();
      if (done) break;
      const take = Math.min(value.byteLength, byteCount - filled);
      chunks.push(value.subarray(0, take));
      filled += take;
    }
  } finally {
    reader.cancel().catch(() => void 0);
  }
  const out = new Uint8Array(filled);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.byteLength;
  }
  return out;
}

// src/model-file/modelURL.ts
import { resolveModelFileName } from "@repo/frontend-types";
var MODEL_URL_SESSION_CACHE_BUST = Math.floor(Date.now() / 6e4);
var secureFileURL = (path, source) => {
  const name = path ? resolveModelFileName(path) : "";
  const encodedPath = name.replace(/\+/g, "%2B").replace(/\s/g, "%20");
  let url = (source.baseURL || "") + encodedPath;
  const token = source.contextToken || source.fallbackToken || "";
  if (token) {
    if (token.startsWith("?") || token.startsWith("&")) {
      url += token;
    } else {
      url += (url.includes("?") ? "&" : "?") + token;
    }
  }
  return url;
};
var modelURL = (modelFileName, source, { cacheBust = true } = {}) => {
  if (!modelFileName) return "";
  let url = secureFileURL(modelFileName, source);
  if (cacheBust) {
    url += `${url.includes("?") ? "&" : "?"}t=${MODEL_URL_SESSION_CACHE_BUST}`;
  }
  return url;
};

// src/equipment/setRename.ts
var ASSET_NAME_MAX_LENGTH = 255;
var temporaryName = (id, to, free = () => true) => {
  const long = `${to.trim()}~${id}`;
  let candidate = long.length <= ASSET_NAME_MAX_LENGTH ? long : `~${id}~`;
  for (let n = 2; !free(candidate); n += 1) {
    const next = `${long}~${n}`;
    candidate = next.length <= ASSET_NAME_MAX_LENGTH ? next : `~${id}~${n}`;
  }
  return candidate;
};
var orderRenames = (moves, heldOutside = () => false) => {
  const pending = moves.filter((move) => move.from !== move.to).map((move, order) => ({
    order,
    id: move.id,
    from: move.from,
    to: move.to,
    done: false,
    parked: false
  }));
  const steps = [];
  const holders = /* @__PURE__ */ new Map();
  const waiters = /* @__PURE__ */ new Map();
  const hold = (move) => {
    const key = nameKey(move.from);
    const set = holders.get(key) ?? /* @__PURE__ */ new Set();
    set.add(move);
    holders.set(key, set);
  };
  const release = (move, from) => holders.get(nameKey(from))?.delete(move);
  const blockerOf = (move) => {
    const set = holders.get(nameKey(move.to));
    if (!set) return void 0;
    let other;
    set.forEach((holder) => {
      if (!other && holder !== move) other = holder;
    });
    return other;
  };
  const blocked = (move) => blockerOf(move) !== void 0;
  pending.forEach((move) => {
    hold(move);
    const key = nameKey(move.to);
    const list = waiters.get(key) ?? [];
    list.push(move);
    waiters.set(key, list);
  });
  const heap = [];
  const push = (move) => {
    heap.push(move);
    let i = heap.length - 1;
    while (i > 0) {
      const parent = i - 1 >> 1;
      if (heap[parent].order <= heap[i].order) break;
      [heap[parent], heap[i]] = [heap[i], heap[parent]];
      i = parent;
    }
  };
  const pop = () => {
    const top = heap[0];
    const last = heap.pop();
    if (heap.length > 0 && last) {
      heap[0] = last;
      let i = 0;
      for (; ; ) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < heap.length && heap[l].order < heap[m].order) m = l;
        if (r < heap.length && heap[r].order < heap[m].order) m = r;
        if (m === i) break;
        [heap[m], heap[i]] = [heap[i], heap[m]];
        i = m;
      }
    }
    return top;
  };
  const freed = (from) => (waiters.get(nameKey(from)) ?? []).forEach(push);
  pending.forEach((move) => {
    if (!blocked(move)) push(move);
  });
  let remaining = pending.length;
  let nextToPark = 0;
  while (remaining > 0) {
    const move = pop();
    if (move) {
      if (move.done || blocked(move)) continue;
      move.done = true;
      remaining -= 1;
      steps.push({ id: move.id, to: move.to });
      release(move, move.from);
      freed(move.from);
      continue;
    }
    while (nextToPark < pending.length && (pending[nextToPark].done || pending[nextToPark].parked)) {
      nextToPark += 1;
    }
    const start = pending[nextToPark];
    if (!start) break;
    const seen = /* @__PURE__ */ new Set();
    let onCycle = start;
    while (onCycle && !seen.has(onCycle)) {
      seen.add(onCycle);
      onCycle = blockerOf(onCycle);
    }
    const parked = onCycle && !onCycle.parked ? onCycle : start;
    parked.parked = true;
    const temporary = temporaryName(parked.id, parked.to, (name) => {
      const key = nameKey(name);
      return !heldOutside(key) && !holders.get(key)?.size && !waiters.has(key);
    });
    steps.push({ id: parked.id, to: temporary, temporary: true });
    const was = parked.from;
    release(parked, was);
    parked.from = temporary;
    hold(parked);
    freed(was);
    push(parked);
  }
  return steps;
};
var renameBase = (typed) => typed.trim().replace(/-\d+$/, "").trim();
var planSetRename = ({
  rows,
  base: typed,
  index,
  single
}) => {
  const base = renameBase(typed);
  if (!base) return { targets: /* @__PURE__ */ new Map(), steps: [], unchanged: [], empty: true };
  const selected = new Set(rows.map((row) => row.id));
  const selectedUids = new Set(rows.map((row) => String(row.id)));
  const outsideHolds = /* @__PURE__ */ new Set();
  index.byKey.forEach((entries, key) => {
    const outside = entries.some(
      (entry) => !(entry.ref.type === "register" && selected.has(entry.ref.assetId)) && !(entry.assetUid !== void 0 && selectedUids.has(entry.assetUid))
    );
    if (outside) outsideHolds.add(key);
  });
  const targets = /* @__PURE__ */ new Map();
  if (rows.length === 1) {
    targets.set(rows[0].id, (single ?? base).trim());
  } else {
    let m = 0;
    rows.forEach((row) => {
      do
        m += 1;
      while (outsideHolds.has(nameKey(`${base}-${m}`)));
      targets.set(row.id, `${base}-${m}`);
    });
  }
  const unchanged = rows.filter((row) => row.name === targets.get(row.id)).map((row) => row.id);
  const steps = orderRenames(
    rows.map((row) => ({ id: row.id, from: row.name, to: targets.get(row.id) })),
    (key) => outsideHolds.has(key)
  );
  return { targets, steps, unchanged, empty: false };
};

// src/equipment/keepOffPlanEligibility.ts
var classifyKeepOffPlan = (rows, planStateOf) => {
  const buckets = {
    eligible: [],
    onPlan: [],
    noProduct: [],
    notTracked: [],
    keptOff: [],
    unknown: []
  };
  rows.forEach((row) => {
    const identity2 = assetIdentityState(row);
    if (identity2 === "not_tracked") return void buckets.notTracked.push(row.id);
    if (identity2 === "to_identify" || identity2 === "to_confirm") {
      return void buckets.noProduct.push(row.id);
    }
    const plan = planStateOf(row.id);
    if (plan === void 0 || plan === "plan_not_loaded") return void buckets.unknown.push(row.id);
    if (plan === "on_plan") return void buckets.onPlan.push(row.id);
    if (plan === "not_on_plan") buckets.eligible.push(row.id);
    else buckets.keptOff.push(row.id);
  });
  return buckets;
};

// src/twin-planner/spaceTypeSuggestion.ts
var SPACE_TYPE_RULES_VERSION = "v4";
var DOMINANT_SHARE = 0.7;
var MIX_MIN_SHARE = 0.2;
var STRAY_MACHINE_MIN_SPACE = 20;
var HIGH_MIN_CAPACITY = 3;
var EQUIPMENT_CARDIO_CATEGORY_KEY = "eqt:6";
var EQUIPMENT_STRENGTH_CATEGORY_KEY = "eqt:42";
var EQUIPMENT_BENCHES_TYPE_KEY = "eqt:44";
var MOVABLE_BENCH_VARIATIONS = /^(bench|utility bench|storage bench)$/i;
var SPACE_TYPE_KEYS = {
  benches: "spt:10",
  dumbbellArea: "spt:12",
  mixedTrainingFloor: "spt:29",
  groupExercise: "spt:30",
  reformerPilates: "spt:37",
  reformerStudio: "spt:38",
  recoveryAndWellness: "spt:44"
};
var LABEL_KEYWORDS = [
  {
    family: "reception staff side",
    pattern: "(?:reception|desk)(?: desk)?\\W*(?:staff|team)(?: (?:side|area))?|staff side",
    spaceTypeKey: "spt:70",
    absorbs: ["reception", "staff"]
  },
  {
    family: "reception member side",
    pattern: "(?:reception|desk)(?: desk)?\\W*(?:members?|member side|customers?|clients?)(?: (?:side|area))?|member side",
    spaceTypeKey: "spt:69",
    absorbs: ["reception"]
  },
  { family: "reception", pattern: "reception|front desk", spaceTypeKey: "spt:68" },
  { family: "caf\xE9", pattern: "caf[e\xE9]|coffee|juice bar|kitchen", spaceTypeKey: "spt:63" },
  { family: "lounge", pattern: "lounge|hub|social|seating|snug", spaceTypeKey: "spt:64" },
  { family: "co-working", pattern: "co-?working|work ?space", spaceTypeKey: "spt:66" },
  {
    family: "drinks",
    pattern: "vending|drinks?( machines?)?|water|hydration",
    spaceTypeKey: "spt:65"
  },
  { family: "retail", pattern: "retail|shop", spaceTypeKey: "spt:67" },
  { family: "office", pattern: "office|meeting room", spaceTypeKey: "spt:72" },
  {
    family: "staff",
    pattern: "staff( room)?|pt( bar| desk| area| office)?|personal train(?:er|ing)",
    spaceTypeKey: "spt:71"
  },
  {
    family: "changing",
    pattern: "chang(?:e|ing)( rooms?)?|toilets?|showers?|wc",
    spaceTypeKey: "spt:74"
  },
  // Only beside a changing word: a "Women's Workout Space" is not a changing room.
  {
    family: "men's",
    pattern: "(?:men'?s|male|gents)(?=.*(?:chang|toilet|shower|wc))|(?<=(?:chang|toilet|shower|wc).*)(?:men'?s|male|gents)",
    spaceTypeKey: "spt:75"
  },
  {
    family: "women's",
    pattern: "(?:women'?s|ladies|female)(?=.*(?:chang|toilet|shower|wc))|(?<=(?:chang|toilet|shower|wc).*)(?:women'?s|ladies|female)",
    spaceTypeKey: "spt:76"
  },
  { family: "accessible", pattern: "accessible|family changing|disabled", spaceTypeKey: "spt:77" },
  { family: "lockers", pattern: "lockers?", spaceTypeKey: "spt:78" },
  {
    family: "walkways",
    pattern: "corridor|walkway|stairs?|stairway|lift( lobby)?|hallway|lobby|entrance|foyer",
    spaceTypeKey: "spt:81"
  },
  {
    family: "back of house",
    pattern: "plant( room)?|storage|store( room)?|deliver(y|ies)",
    spaceTypeKey: "spt:85"
  },
  { family: "sauna", pattern: "sauna", spaceTypeKey: "spt:49" },
  { family: "steam", pattern: "steam( room)?", spaceTypeKey: "spt:50" },
  { family: "pool", pattern: "pool|swim(ming)?", spaceTypeKey: "spt:52" },
  { family: "spa", pattern: "spa|hot tub|jacuzzi", spaceTypeKey: "spt:53" },
  { family: "massage", pattern: "massage|sports therapy", spaceTypeKey: "spt:56" },
  { family: "physio", pattern: "physio(therapy)?|clinic", spaceTypeKey: "spt:57" },
  {
    family: "body composition",
    pattern: "body comp(osition)?|inbody|evolt|assessment",
    spaceTypeKey: "spt:58"
  },
  { family: "red light", pattern: "red light", spaceTypeKey: "spt:59" },
  { family: "cryotherapy", pattern: "cryo(therapy)?|ice baths?", spaceTypeKey: "spt:60" },
  { family: "stretch", pattern: "stretch(ing)?|mobility|abs?|core", spaceTypeKey: "spt:46" },
  { family: "recovery", pattern: "recovery|wellness|power plates?", spaceTypeKey: "spt:45" },
  {
    family: "cycle",
    pattern: "cycle( studio)?|cycling|spin(ning)?|rpm|ride",
    spaceTypeKey: "spt:31",
    room: true
  },
  { family: "reformer", pattern: "reformers?", spaceTypeKey: "spt:38", room: true },
  { family: "pilates", pattern: "pilates", spaceTypeKey: "spt:37", room: true },
  {
    family: "yoga",
    pattern: "yoga|mind ?body|m ?& ?b|barre|hot studio",
    spaceTypeKey: "spt:34",
    room: true
  },
  { family: "hiit", pattern: "hiit|circuits?|blitz|small group", spaceTypeKey: "spt:35" },
  { family: "dance", pattern: "dance|aerobics", spaceTypeKey: "spt:36", room: true },
  {
    family: "group ex",
    pattern: "group ?ex(ercise)?|gx|class(es)?",
    spaceTypeKey: "spt:32",
    room: true
  },
  // A bare "studio" is not enough to overrule clear equipment ("Main Studio Space" of resistance).
  { family: "studio", pattern: "studio", spaceTypeKey: "spt:32" },
  { family: "boxing", pattern: "boxing ring|ring", spaceTypeKey: "spt:41", room: true },
  {
    family: "combat",
    pattern: "boxing|combat|martial arts?|mma",
    spaceTypeKey: "spt:42"
  },
  { family: "virtual", pattern: "virtual|on-?demand", spaceTypeKey: "spt:43", room: true },
  { family: "functional", pattern: "functional", spaceTypeKey: "spt:18" },
  { family: "turf", pattern: "turf", spaceTypeKey: "spt:21" },
  { family: "sled", pattern: "sled|battle ?ropes?|ropes?|tracks?", spaceTypeKey: "spt:22" },
  { family: "rigs", pattern: "rigs?|frames?", spaceTypeKey: "spt:28" },
  { family: "running track", pattern: "running track|running", spaceTypeKey: "spt:7" },
  { family: "free weights", pattern: "free ?weights?|weights", spaceTypeKey: "spt:9" },
  { family: "dumbbells", pattern: "dumb ?bells?|db", spaceTypeKey: "spt:12" },
  { family: "benches", pattern: "benche?s?", spaceTypeKey: "spt:10" },
  { family: "racks", pattern: "racks?|squat|smith", spaceTypeKey: "spt:11" },
  { family: "platforms", pattern: "platforms?|lifting|olympic", spaceTypeKey: "spt:13" },
  { family: "cardio", pattern: "cardio", spaceTypeKey: "spt:1" },
  { family: "powered cardio", pattern: "powered cardio|cardio machines?", spaceTypeKey: "spt:2" },
  { family: "treadmills", pattern: "treadmills?|skill ?runs?", spaceTypeKey: "spt:3" },
  {
    family: "bikes",
    pattern: "(?:stationary |upright |recumbent |recombent )?bikes?",
    spaceTypeKey: "spt:4"
  },
  {
    family: "climbers",
    pattern: "climbers?|steppers?|stair ?(?:masters?|climbers?)|climb ?mills?|step ?mills?",
    spaceTypeKey: "spt:5"
  },
  { family: "ellipticals", pattern: "ellipticals?|cross ?trainers?", spaceTypeKey: "spt:6" },
  { family: "rowers", pattern: "rowers?|rowing(?: machines?)?", spaceTypeKey: "spt:24" },
  { family: "ski ergs", pattern: "ski ?ergs?", spaceTypeKey: "spt:25" },
  { family: "air bikes", pattern: "(?:air|assault) ?bikes?", spaceTypeKey: "spt:26" },
  {
    family: "curved treadmills",
    pattern: "curved (?:treadmills?|runners?)",
    spaceTypeKey: "spt:27"
  },
  { family: "strength", pattern: "strength|gym floor", spaceTypeKey: "spt:8" },
  {
    family: "resistance",
    pattern: "resistance|resistance machines?|selectori[sz]ed|multi ?gym",
    spaceTypeKey: "spt:14"
  },
  {
    family: "cables",
    pattern: "cables?( stations?| machines?)?|pin ?loaded|pin selected",
    spaceTypeKey: "spt:15"
  },
  { family: "plate loaded", pattern: "plate ?loaded|plates", spaceTypeKey: "spt:16" }
];
var labelMatchers;
var labelKeywordMatchers = () => labelMatchers ?? (labelMatchers = LABEL_KEYWORDS.map((k) => ({
  ...k,
  regex: new RegExp(`(?<![\\p{L}\\p{N}])(?:${k.pattern})(?![\\p{L}\\p{N}])`, "iu")
})));
var PILATES_EQUIPMENT = /pilates|reformer/i;
var REFORMER_EQUIPMENT = /reformer/i;
function normaliseSpaceLabel(label) {
  let s = (label ?? "").toLowerCase().replace(/[_]+/g, " ").replace(/\s+/g, " ").trim();
  s = s.replace(
    /^(?:\d+\s?f|ground(?: floor)?|first floor|second floor|basement|level \d+|l\d+|gf)\b[\s\-:]*/u,
    ""
  );
  s = s.replace(/[\s\-#]*\d+[a-z]?$/u, "").trim();
  return s;
}
var isUncategorised = (key) => !key || key.startsWith("eqt:unknown");
var DROP = { high: "medium", medium: "low", low: "low" };
var pct = (share) => `${Math.round(share * 100)}%`;
var CONFIDENCE = { high: 0.9, medium: 0.6, low: 0.3 };
function equipmentWeighing(equipment) {
  const basis = equipment.some((e) => (e.trackedCapacity ?? 0) > 0) ? "tracked" : equipment.some((e) => (e.capacity ?? 0) > 0) ? "capacity" : "count";
  const weightOf = (e) => basis === "tracked" ? Math.max(0, e.trackedCapacity ?? 0) : basis === "capacity" ? Math.max(0, e.capacity ?? 0) : 1;
  return { basis, weightOf };
}
function commonPrefix(paths) {
  if (paths.length === 0) return [];
  const out = [];
  for (let i = 0; ; i++) {
    const k = paths[0][i];
    if (k === void 0 || paths.some((p) => p[i] !== k)) return out;
    out.push(k);
  }
}
function nodeLabelMatchers(nodes) {
  const out = [];
  for (const n of nodes) {
    const label = n.label.toLowerCase().trim();
    if (!label || /[/()]/.test(label) || n.node_type === "type" && !/\s/.test(label)) continue;
    const words2 = label.split(/\s+/).map((w) => {
      const stem = w.endsWith("s") && w.length > 3 ? w.slice(0, -1) : w;
      return `${stem.replace(/[.*+?^${}|[\]\\]/g, "\\$&")}s?`;
    });
    out.push({
      family: n.label,
      spaceTypeKey: n.node_key,
      regex: new RegExp(`(?<![\\p{L}\\p{N}])${words2.join("\\s+")}(?![\\p{L}\\p{N}])`, "iu")
    });
  }
  return out;
}
function suggestSpaceType(input) {
  const active = new Map(
    input.spaceTypes.filter((n) => n.status === "active").map((n) => [n.node_key, n])
  );
  const equipLabel = new Map(input.equipmentTypes.map((n) => [n.node_key, n.label]));
  const spaceLabel = (key) => active.get(key)?.label ?? key;
  const alignedTo = /* @__PURE__ */ new Map();
  for (const n of active.values())
    if (n.aligned_node_key) alignedTo.set(n.aligned_node_key, n.node_key);
  const ancestors = (key) => {
    const out = [];
    let n = active.get(key);
    while (n?.parent_key && active.has(n.parent_key)) {
      out.push(n.parent_key);
      n = active.get(n.parent_key);
    }
    return out;
  };
  const depth = (key) => ancestors(key).length;
  const rootPath = (key) => [...ancestors(key).reverse(), key];
  const inGroupExercise = (key) => key === SPACE_TYPE_KEYS.groupExercise || ancestors(key).includes(SPACE_TYPE_KEYS.groupExercise);
  const result = (spaceTypeKey, rule, band, reason, extra2 = {}) => ({
    spaceTypeKey,
    rule,
    confidence: spaceTypeKey ? CONFIDENCE[band] : 0,
    band,
    reason,
    rulesVersion: SPACE_TYPE_RULES_VERSION,
    ...extra2
  });
  const normalised = normaliseSpaceLabel(input.label);
  const labelMatch = (() => {
    if (!normalised) return null;
    const candidates = [...labelKeywordMatchers(), ...nodeLabelMatchers(active.values())];
    const found = candidates.filter((m) => active.has(m.spaceTypeKey)).map((m) => ({ m, at: m.regex.exec(normalised) })).filter((x) => x.at !== null).map(({ m, at }) => ({ ...m, start: at.index, end: at.index + at[0].length }));
    const hits = found.filter(
      (h) => !found.some(
        (o) => o !== h && o.start <= h.start && o.end >= h.end && o.end - o.start > h.end - h.start
      )
    );
    if (hits.length === 0) return null;
    const best = hits.reduce((a, b) => depth(b.spaceTypeKey) > depth(a.spaceTypeKey) ? b : a);
    const conflicting = hits.filter(
      (h) => h.spaceTypeKey !== best.spaceTypeKey && !(best.absorbs ?? []).includes(h.family) && !ancestors(best.spaceTypeKey).includes(h.spaceTypeKey) && !ancestors(h.spaceTypeKey).includes(best.spaceTypeKey)
    );
    const families = [...new Set([best, ...conflicting].map((h) => h.family))];
    const room = found.some((h) => h.room && h.spaceTypeKey === best.spaceTypeKey);
    return { best, conflicting, families, room };
  })();
  const labelSuggestion = (prefix, extra2) => {
    if (!labelMatch) return null;
    const { best, conflicting } = labelMatch;
    const families = labelMatch.families.map((f) => `"${f}"`).join(" and ");
    return result(
      best.spaceTypeKey,
      "label",
      conflicting.length > 0 ? "low" : "medium",
      `${prefix}The name matches ${families}${conflicting.length > 0 ? ", which disagree" : ""}.`,
      extra2
    );
  };
  const noName = () => normalised ? "Its name matches no keyword." : "It has no name.";
  const categorised = input.equipment.filter((e) => !isUncategorised(e.subcategoryKey));
  const uncategorisedCount = input.equipment.length - categorised.length;
  const { basis, weightOf } = equipmentWeighing(input.equipment);
  const total = categorised.reduce((s, e) => s + weightOf(e), 0);
  const uncategorisedWeight = input.equipment.filter((e) => isUncategorised(e.subcategoryKey)).reduce((s, e) => s + weightOf(e), 0);
  const mostlyUncategorised = uncategorisedWeight > total;
  const capBand = (band) => {
    let b = band;
    if (categorised.length < HIGH_MIN_CAPACITY && b === "high") b = "medium";
    if (basis !== "tracked" && b === "high") b = "medium";
    if (mostlyUncategorised) b = DROP[b];
    return b;
  };
  const basisNote = basis === "tracked" ? "tracked capacity" : basis === "capacity" ? "capacity (none of it is tracked)" : "machine count (no capacity recorded)";
  const namedRoom = (() => {
    if (!labelMatch?.room) return null;
    const all = [labelMatch.best, ...labelMatch.conflicting];
    if (!all.every((h) => inGroupExercise(h.spaceTypeKey))) return null;
    return result(
      labelMatch.best.spaceTypeKey,
      "label",
      "medium",
      `The name says it is a group-exercise room ("${labelMatch.best.family}"), whatever equipment is in it.`
    );
  })();
  const parentKey = input.parent?.spaceTypeKey ?? null;
  const parentRoom = (() => {
    if (!parentKey || !active.has(parentKey) || !inGroupExercise(parentKey)) return null;
    const why = `It sits inside a group-exercise room (${spaceLabel(parentKey)}), so it is part of that room`;
    const named = labelMatch?.best.spaceTypeKey;
    if (named && labelMatch?.conflicting.length === 0 && ancestors(named).includes(parentKey))
      return result(
        named,
        "label",
        "medium",
        `${why}, and the name narrows it to ${spaceLabel(named)}.`
      );
    return result(parentKey, "label", "medium", `${why}.`);
  })();
  if (total <= 0) {
    const why = input.equipment.length === 0 ? "The space has no equipment. " : basis === "tracked" ? `None of its tracked equipment is categorised (${uncategorisedCount} of ${input.equipment.length} machines are not). ` : `None of its ${uncategorisedCount} machines is categorised. `;
    return namedRoom ?? parentRoom ?? labelSuggestion(why, {}) ?? result(null, "unknown", "low", `${why}${noName()}`);
  }
  let pilatesWeight = 0;
  let hasReformer = false;
  for (const e of categorised) {
    const names = [e.typeKey, e.variationKey].map((k) => k ? equipLabel.get(k) ?? "" : "").join(" ");
    if (PILATES_EQUIPMENT.test(names)) {
      pilatesWeight += weightOf(e);
      if (REFORMER_EQUIPMENT.test(names)) hasReformer = true;
    }
  }
  const pilatesShare = pilatesWeight / total;
  if (pilatesShare >= DOMINANT_SHARE) {
    const fromLabel = labelSuggestion("", {});
    if (fromLabel?.spaceTypeKey && (fromLabel.spaceTypeKey === SPACE_TYPE_KEYS.recoveryAndWellness || ancestors(fromLabel.spaceTypeKey).includes(SPACE_TYPE_KEYS.recoveryAndWellness))) {
      return {
        ...fromLabel,
        reason: `${pct(pilatesShare)} of ${basisNote} is Pilates equipment, but ${fromLabel.reason[0].toLowerCase()}${fromLabel.reason.slice(1)}`
      };
    }
    const key = hasReformer && active.has(SPACE_TYPE_KEYS.reformerStudio) ? SPACE_TYPE_KEYS.reformerStudio : SPACE_TYPE_KEYS.reformerPilates;
    if (active.has(key)) {
      return result(
        key,
        "pilates",
        capBand("high"),
        `${pct(pilatesShare)} of ${basisNote} is Pilates equipment${hasReformer ? ", including reformers" : ""}.`
      );
    }
  }
  if (namedRoom) return namedRoom;
  if (parentRoom) return parentRoom;
  const withName = (eq, weak) => {
    if (!labelMatch || labelMatch.conflicting.length > 0 || !eq.spaceTypeKey) return eq;
    const named = labelMatch.best.spaceTypeKey;
    if (named === eq.spaceTypeKey || ancestors(eq.spaceTypeKey).includes(named)) return eq;
    if (ancestors(named).includes(eq.spaceTypeKey)) {
      return {
        ...eq,
        spaceTypeKey: named,
        reason: `${eq.reason.replace(/\.$/, "")}, and the name narrows it to ${spaceLabel(named)}.`
      };
    }
    if (weak && !(eq.rule === "mixed_floor" && !labelMatch.room)) {
      return result(
        named,
        "label",
        "medium",
        `${eq.reason.replace(/\.$/, "")}, which is not decisive, so the name decides: "${labelMatch.best.family}".`
      );
    }
    return {
      ...eq,
      band: DROP[eq.band],
      confidence: CONFIDENCE[DROP[eq.band]],
      reason: `${eq.reason.replace(/\.$/, "")}, though the name suggests ${spaceLabel(named)}.`
    };
  };
  const equipmentPath = (e) => [e.categoryKey, e.subcategoryKey, e.typeKey].filter(
    (k) => !!k && !isUncategorised(k)
  );
  const bySubcategory = /* @__PURE__ */ new Map();
  for (const e of categorised)
    bySubcategory.set(
      e.subcategoryKey,
      (bySubcategory.get(e.subcategoryKey) ?? 0) + 1
    );
  const strays = categorised.length >= STRAY_MACHINE_MIN_SPACE ? categorised.filter((e) => bySubcategory.get(e.subcategoryKey) === 1) : [];
  const core = strays.length === 1 ? categorised.filter((e) => !strays.includes(e)) : categorised;
  const untypedAsMany = uncategorisedCount > categorised.length;
  const fullShared = commonPrefix(core.map(equipmentPath));
  const shared = untypedAsMany ? fullShared.slice(0, 2) : fullShared;
  const machines = core.length === categorised.length ? `${categorised.length} machine${categorised.length === 1 ? "" : "s"}` : `${core.length} of its ${categorised.length} machines (one stray left out)`;
  let unlinked;
  if (shared.length > 0) {
    const deepest = shared[shared.length - 1];
    const linkedAt = [...shared].reverse().find((k) => alignedTo.has(k));
    if (linkedAt) {
      let key = alignedTo.get(linkedAt);
      let detail = shared.length < fullShared.length ? `Its ${machines} with a type are ${equipLabel.get(fullShared[fullShared.length - 1]) ?? deepest}, but ${uncategorisedCount} more have none, so only ${equipLabel.get(deepest) ?? deepest} is certain` : `All ${machines} are ${equipLabel.get(deepest) ?? deepest}`;
      if (linkedAt !== deepest) {
        unlinked = equipLabel.get(deepest) ?? deepest;
        detail += `, which has no space type of its own, so it takes ${spaceLabel(key)}`;
      }
      if (key === SPACE_TYPE_KEYS.benches && active.has(SPACE_TYPE_KEYS.dumbbellArea)) {
        const benches = categorised.filter((e) => e.typeKey === EQUIPMENT_BENCHES_TYPE_KEY);
        const movable = benches.filter(
          (e) => MOVABLE_BENCH_VARIATIONS.test(equipLabel.get(e.variationKey ?? "") ?? "")
        ).length;
        if (benches.length > 0 && movable * 2 >= benches.length) {
          key = SPACE_TYPE_KEYS.dumbbellArea;
          detail += `, and ${movable} of ${benches.length} are plain movable benches, so a dumbbell area`;
        }
      }
      const band = depth(key) >= 1 && !untypedAsMany ? "high" : "medium";
      return withName(
        result(
          key,
          "equipment_mix",
          capBand(band),
          `${detail}.`,
          unlinked ? { unlinkedEquipmentSubcategory: unlinked } : {}
        ),
        categorised.length === 1
      );
    }
    unlinked = equipLabel.get(deepest) ?? deepest;
  } else {
    const spacePaths = [];
    for (const e of categorised) {
      const linked = [...equipmentPath(e)].reverse().find((k) => alignedTo.has(k));
      if (!linked) {
        spacePaths.length = 0;
        break;
      }
      spacePaths.push(rootPath(alignedTo.get(linked)));
    }
    const sharedSpace = commonPrefix(spacePaths);
    if (sharedSpace.length > 0) {
      const key = sharedSpace[sharedSpace.length - 1];
      return withName(
        result(
          key,
          "equipment_mix",
          capBand("medium"),
          `Its ${machines} span several kinds of equipment that all fall under ${spaceLabel(key)}.`
        ),
        true
      );
    }
  }
  const byCategory = /* @__PURE__ */ new Map();
  const bySpaceCategory = /* @__PURE__ */ new Map();
  for (const e of categorised) {
    const w = weightOf(e);
    if (e.categoryKey) byCategory.set(e.categoryKey, (byCategory.get(e.categoryKey) ?? 0) + w);
    const linked = [...equipmentPath(e)].reverse().find((k) => alignedTo.has(k));
    if (linked) {
      const top = rootPath(alignedTo.get(linked))[0];
      bySpaceCategory.set(top, (bySpaceCategory.get(top) ?? 0) + w);
    }
  }
  const [topCategory, topWeight] = [...bySpaceCategory.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];
  if (unlinked === void 0 && topCategory && topWeight / total >= DOMINANT_SHARE) {
    return withName(
      result(
        topCategory,
        "equipment_mix",
        capBand("medium"),
        `${pct(topWeight / total)} of ${basisNote} is ${spaceLabel(topCategory)} equipment, with a mix inside it.`
      ),
      true
    );
  }
  if (unlinked === void 0) {
    const cardio = (byCategory.get(EQUIPMENT_CARDIO_CATEGORY_KEY) ?? 0) / total;
    const strength = (byCategory.get(EQUIPMENT_STRENGTH_CATEGORY_KEY) ?? 0) / total;
    if (cardio >= MIX_MIN_SHARE && strength >= MIX_MIN_SHARE && cardio + strength >= DOMINANT_SHARE && active.has(SPACE_TYPE_KEYS.mixedTrainingFloor)) {
      return withName(
        result(
          SPACE_TYPE_KEYS.mixedTrainingFloor,
          "mixed_floor",
          capBand("medium"),
          `No one kind of equipment decides it: ${pct(cardio)} cardio and ${pct(strength)} strength by ${basisNote}.`
        ),
        true
      );
    }
  }
  const lead = unlinked ? `All its machines are ${unlinked}, which no space type is linked to yet. ` : "Its equipment is too mixed to decide. ";
  const extra = unlinked ? { unlinkedEquipmentSubcategory: unlinked } : {};
  return labelSuggestion(lead, extra) ?? result(null, "unknown", "low", `${lead}${noName()}`, extra);
}
function spaceTypePath(spaceTypes, key) {
  const byKey = new Map(spaceTypes.map((n2) => [n2.node_key, n2]));
  const parts2 = [];
  let n = byKey.get(key);
  while (n) {
    parts2.unshift(n.label);
    n = n.parent_key ? byKey.get(n.parent_key) : void 0;
  }
  return parts2.join(" \u203A ");
}
export {
  ADOPTION_COLUMNS,
  ASSET_COVERAGE_LABEL,
  ASSET_CSV_ADOPTION_IGNORED_COLUMNS,
  ASSET_CSV_ADOPTION_KEPT_COLUMNS,
  ASSET_CSV_COLUMNS,
  ASSET_CSV_GENERATED_COLUMNS,
  ASSET_CSV_IDENTIFIER_HEADER,
  ASSET_CSV_MAX_BYTES,
  ASSET_CSV_MAX_ROWS,
  ASSET_CSV_REFUSED_COLUMN_REASONS,
  ASSET_NAME_MAX_LENGTH,
  AssetCsvReadError,
  BUILD_NUDGE_STEP_M,
  BUILD_ROTATION_SHIFT_STEP_DEG,
  CALENDAR_LABEL_COLUMN_WIDTH,
  CAMERA_COVERAGE_PRESETS,
  CAMERA_DISTANCE_MIN,
  CAMERA_FOV_MAX,
  CAMERA_FOV_MIN,
  CHECK_LEVELS,
  CHECK_SENTENCES,
  COUNTRY_ALIASES,
  DEFAULT_CAMERA_COVERAGE,
  DEFAULT_UNDERLAY_NAME,
  DEFAULT_UNDERLAY_OPACITY,
  DOMINANT_SHARE,
  EQUIPMENT_BENCHES_TYPE_KEY,
  EQUIPMENT_CARDIO_CATEGORY_KEY,
  EQUIPMENT_PHASES,
  EQUIPMENT_STRENGTH_CATEGORY_KEY,
  FIRST_GUESS_VIEW_ALLOWANCE,
  FLOOR_HEIGHT_M,
  FLOOR_SPACING_M,
  FOOTPRINT_OUTLINE_STEP_M,
  GENERIC_CATALOGUE_MANUFACTURER,
  GENERIC_GROUP,
  GUIDED_VIEW_MARGIN,
  HEAT_GRID_CELL_ENCODING,
  HIGH_MIN_CAPACITY,
  IDENTIFY_CARD_STATES,
  IDENTITY_STATE_LABELS,
  KIND_LETTER,
  LABEL_KEYWORDS,
  LABEL_TARGETS,
  LEAVE_OUT,
  LEFT_OUT_SENTENCE,
  LEGACY_ZONE_NAMES,
  LIVE_ENTRY_MARKER,
  MAX_ENTITY_CAPACITY,
  MAX_ENTITY_SEQUENCE,
  MAX_SERVER_MESSAGE_LENGTH,
  MIN_FIT_PAIRS,
  MIX_MIN_SHARE,
  MODEL_BOX_PADDING_M,
  MODEL_FOOTPRINT_FALLBACK,
  MODEL_URL_SESSION_CACHE_BUST,
  MOVABLE_BENCH_VARIATIONS,
  MOVE_MIN_M,
  NEAR_LINE_RATIO,
  NOT_IN_CATALOGUE_LABEL,
  NOT_TRACKED_HINT_CATEGORIES,
  NO_CATALOGUE_MATCH_GROUP,
  PIXELS_PER_METER,
  PLAN_KEY_PAN_KEEP,
  PLAN_KIND_STEP,
  PLAN_SCROLL_KEY_STEP,
  PLAN_SCROLL_MARGIN,
  PLAN_STATE_LABELS,
  PLAN_STEP_IDS,
  PRIMARY_ZONE_BY_COUNTRY,
  ROTATE_MIN_DEG,
  SPACE_TYPE_KEYS,
  SPACE_TYPE_RULES_VERSION,
  STALE_CORNER_M,
  STRAY_MACHINE_MIN_SPACE,
  UNDERLAY_CONTRAST_MAX,
  UNDERLAY_CONTRAST_MIN,
  UNDERLAY_WIDTH_MAX_M,
  UNDERLAY_WIDTH_MIN_M,
  VALUE_PROBLEM_KINDS,
  activeSceneBehaviourFor,
  addDataInterval,
  adjustColorByDeltaE,
  alignFloorDrawOrder,
  allowedLetterFor,
  angleDiff,
  applyFixes,
  asArray,
  assetColumnFileNote,
  assetIdentityState,
  assetPlanStateFor,
  assetSidebarKey,
  averageSelectedEquipmentValue,
  averageWeekValuesForBids,
  awaitingCustomerRegisterImport,
  beginEquipmentLoad,
  bestNextTarget,
  blankMappedRecords,
  buildAddressLine,
  buildAssetCoverageIndex,
  buildBaselineMetricList,
  buildBaselineWeekdayWindows,
  buildClearedWeek,
  buildEquipmentClassificationTrees,
  buildFloorFilterTree,
  buildFloorRemovalRecord,
  buildHeroMetric,
  buildIdentifyModel,
  buildMappedCsv,
  buildNameKeyedMetricRegistry,
  buildPlanChecks,
  buildPlanSearchIndex,
  buildProblemsCsv,
  buildTimeSeriesMetricList,
  buildTwinNameIndex,
  calcPercent,
  calculateTotalHours,
  calibrationProblem,
  calibrationTargetsMoved,
  calibrationViewRects,
  cameraDisplayName,
  cameraErrors,
  cameraVisibilityPolygon,
  capitaliseFirstLetter,
  cascadeFloorCameras,
  caseInsensitiveArrayIncludes,
  catalogueDisplayNames,
  catalogueMatchText,
  cellIdsToWeekdayWindows,
  centreViewOn,
  chartWidthCalc,
  chartYLabelWidthCalc,
  checkAssetValues,
  checkTypedEntityCode,
  classifyItemChange,
  classifyKeepOffPlan,
  classifySurfaceChange,
  cleanPlacement,
  cleanUnderlayName,
  clearOrganizationData,
  clearPhase,
  clearPlanModelThroughJoin,
  clipPolygonToRect,
  clipPolylineToRect,
  collectEquipmentEntities,
  collinearityRatio,
  columnLabel,
  columnMapCounts,
  columnMapReady,
  columnOptions,
  compareEntitiesForDisplay,
  comparePlans,
  compareValues,
  computeActivationDelta,
  computeAutoSelectBox,
  computeCellDimensions,
  computeChangedFieldsByBid,
  computeContiguousSelectionOutlines,
  computeDesignBounds,
  computeModelDiff,
  computeSelectionBoxFromPointer,
  computeTriStates,
  computeVersionDelta,
  convertDateTimeISOToUTC,
  convertIntervalToNumber,
  convertUnixTimestampToDate,
  convexHull,
  copyName,
  copyOfUnderlay,
  countFloorContents,
  countPlanContents,
  countryCodeFor,
  countryName,
  countryOptions,
  countryZones,
  coveragePatch,
  createEmptyFloor,
  createEmptyLayout,
  createFilterPath,
  createFilterPathFromLineage,
  createHourObjects,
  createStackedChartLegend,
  createStackedChartOptions,
  createStackedChartSeries,
  currentEquipmentLoadToken,
  dataModeFor,
  decideColumn,
  decideDateOrder,
  decideNewcomerName,
  decodeHeatGridCells,
  decodeRef,
  defaultFloorName,
  degreesToRadians,
  deleteMachineSummary,
  deleteSpaceSummary,
  deleteSummaryFor,
  deriveSingleEntityBid,
  deriveZone,
  describeEntityExhaustion,
  describeFailedSave,
  describeFixedKind,
  describeSubscriptionError,
  diagnosePlanRegisterDrift,
  directChildSpaces,
  disagreeingPair,
  distance,
  downloadProblemSentences,
  draftEntityChanges,
  drawnUnderlaysInOrder,
  editKey,
  effectiveCodeChanges,
  effectiveCoverage,
  emptyPlacementIndex,
  encodeRef,
  ensurePlanItemIds,
  entitiesWorldBounds,
  entityIdentityFor,
  entityIdentityKey,
  entityKeyFor,
  entityNamesByRefid,
  entityWorldCentre,
  equipmentDropHint,
  equipmentWeighing,
  escapeHtml,
  extractBIDs,
  extractClaims,
  extractFloorplannerBIDs,
  extractFloorplannerNamesByBid,
  extractOrgBidFromDgraphError,
  extractOrgBidFromTwinBid,
  fetchModelFootprint,
  fileableFloorBids,
  filedByDrawing,
  findAncestors,
  findContainingSurface,
  findDuplicateEntityCodes,
  findDuplicateNames,
  findEntityByBusinessId,
  findEntityById,
  findEntityWithBID,
  findIndicatorConfigForMetric,
  findLatestDesign,
  findMinMaxDates,
  findObjectById,
  findObjectByPropertyValue,
  firstGuessParams,
  firstLiveSurfaces,
  fitCameraModel,
  fitInformation,
  fitViewToBounds,
  fittedHeading,
  flatShareFilename,
  floatEquals,
  floorCameraCounts,
  floorCountsLine,
  floorDeleteSentence,
  floorDrawState,
  floorElevationM,
  floorEntityBid,
  floorIdFromTargetRefid,
  floorLaunchBid,
  floorOutline,
  floorOutlineCentre,
  floorPixelsToWorld,
  floorPlaceLabel,
  floorPlanLandingTab,
  floorRemovedRefs,
  floorTargetRefid,
  floorToWorld,
  fmlPageLink,
  foldLegacyBackground,
  footprintCorners,
  footprintOutlineInPicture,
  formatCopilotMessage,
  formatDateRange,
  formatDateTime,
  formatDateToDateTimeString,
  formatDateToDayMonthYear,
  formatDateToISO8601,
  formatHourRange,
  formatPublishedDate,
  formatRoles,
  formatTimeRange,
  formatToOneDecimal,
  formatWeekdayRange,
  generateApiKey,
  generateCalendarMonth,
  generateCalendarWeek,
  generateTimeSlots,
  generationTargets,
  geocodeAddress,
  getCookie,
  getCurrentTime,
  getDataValue,
  getDaysOfTheWeek,
  getDiffBetweenTimes,
  getDifferenceBetweenDates,
  getISOWeekNumber,
  getIndicator,
  getIndicatorColor,
  getInitials,
  getMapStyleUrl,
  getMetricColor,
  getMetricData,
  getNotificationTypeColors,
  getOrdinalSuffix,
  getPerceptualDifference,
  getSelectionProfile,
  getTodaysDateRange,
  getVersionSuffix,
  getWeekRange,
  getWindOrientation,
  getWindSpeed,
  groupHint,
  hasCalendarBeenUsed,
  hasValidPolygon,
  heatBlobOutlines,
  heroMetricModeFor,
  hexToRgb,
  highWaterMarkFor,
  indicatorRampDomain,
  indicatorScopeFor,
  installEquipmentLoadEnvelopes,
  isArealessSceneMode,
  isCalibrationCurrent,
  isCompleteWrongLetter,
  isDatelessCalendarMode,
  isDgraphNamespaceError,
  isEmptyModelDiff,
  isEntityCapacity,
  isExistingTwinRoute,
  isFileableObserver,
  isFiledSelfOf,
  isFloorLaunched,
  isFloorRecordRestored,
  isFloorVisible,
  isFullCircle,
  isFullDay,
  isGenericCatalogueManufacturer,
  isHeroMetricEnabledForMode,
  isIdentityPlacement,
  isKeptOffPlan,
  isLabelTarget,
  isLive,
  isLoadedComposite,
  isLoneFloor,
  isNameTaken,
  isNullOrUndefined,
  isPlaceableIdentity,
  isPointVisibleFromCamera,
  isSameDate,
  isUncatalogued,
  isValidCalibration,
  itemDisplayName,
  joinRegisterOntoPlan,
  jointEndsAt,
  json,
  kindOfLetter,
  kindOfNewSpace,
  kindUnder,
  labelModeFor,
  layoutCurrentDesigns,
  layoutHasGeometry,
  leftOutEntries,
  legalDocumentLinks,
  letterOfCode,
  levelToPicture,
  liftLoneFloor,
  linesText,
  liveEntityBidsOf,
  lowestFreeNumber,
  mapLiveSubscriptionData,
  mapMetric,
  mapTimeSeriesData,
  markEnd,
  markStart,
  markerAgreement,
  maskApiKey,
  matchPlanEntities,
  mergeWeekHoursData,
  mergeWeekdayHoursData,
  metersToPixels,
  mintEntityBid,
  mirrorRegisterOntoPlan,
  mirrorRegisterThroughJoin,
  modeConfigFor,
  modelFootprint,
  modelFootprintPlanCentre,
  modelURL,
  moveInFloor,
  nameFamily,
  nameForNewcomer,
  nameKey,
  naturalSort,
  newClientId,
  newCompositeRefid,
  newUnderlay,
  newWallId,
  nextFloorId,
  nextFreeName,
  nextGroupToIdentify,
  nextSelectionOnToggle,
  nextSort,
  normaliseFloorLevels,
  normaliseFloorPlacement,
  normaliseHeadcountValue,
  normaliseRotation,
  normaliseServerMessage,
  normaliseSpaceLabel,
  normaliseZoneName,
  noteEquipmentDataArrived,
  observerTargetNamesByRefid,
  observerTargetRef,
  observersOnRemovedCamera,
  onMeasure,
  oneIsNullOrUndefined,
  opensIdentifyCard,
  orderRenames,
  originalIdentityOf,
  panForOffset,
  panePointToPlan,
  paneToWorldPx,
  parseAssetCsv,
  parseCalendarDateStringToDate,
  parseCfgArrayToPolygons,
  parseCustomName,
  parseLegalDocumentVersion,
  parseScenarioPayload,
  passwordValidation,
  pendingCodeChanges,
  pictureToPlan,
  pictureToPlanAt,
  pictureUpHeading,
  pixelsToMeters,
  placedBounds,
  placementIndexFor,
  placementOf,
  placementStateFor,
  placementWhereFor,
  placementWhereForRefid,
  planDrawingOutline,
  planOpeningFloorIndex,
  planPointToPane,
  planProjector,
  planSaveFailureReason,
  planScreenBox,
  planScrollAxes,
  planScrollBounds,
  planScrollBoundsForDesigns,
  planSearchEntry,
  planSetRename,
  planToPicture,
  planToPictureIn,
  planViewKey,
  pointInPolygon,
  polygonArea,
  polygonAreaCentroid,
  polygonCentroid,
  polygonInteriorPoint,
  polygonToFlatArray,
  preImportWarningText,
  prepareModelVersion,
  prependZero,
  previewRows,
  problemSentences,
  projectPlanOutline,
  projectedScore,
  projectsInsideView,
  promoteAfterRemoval,
  pruneCompositesForRemovedRefs,
  pruneObserversForRemovedRefs,
  radiansToDegrees,
  rasteriseIndicatorRamp,
  readAdoptedBidCell,
  readCapacityCell,
  readGlbJson,
  readModel3dCell,
  readPlanSaveConflict,
  readPlanView,
  readReferenceIdCell,
  readValueCell,
  reconcileGeneratedComposites,
  recordCodeChange,
  recordMeasure,
  reexpressUnderlay,
  registerCapacityLookup,
  registerRowName,
  registerRowsForMirror,
  registerToSidebarEntries,
  removeCameraFromLayout,
  removeNonNumeric,
  removePlacementsFromLayout,
  removeProtocol,
  renameBase,
  renameInLayout,
  renamesOf,
  renumberBid,
  reservedRegisterIdentities,
  resolveActiveConfigId,
  resolveEndpoints,
  resolveEntityBehaviourColor,
  resolveIndicatorConfigScope,
  resolveItemBaseName,
  resolveItemRegisterState,
  resolveLabelRow,
  resolveLabelRows,
  resolveMachineCapacity,
  resolveOnboardingMode,
  resolveOnboardingRoute,
  resolvePlanCapacity,
  resolvePlanHierarchy,
  resolvePlanParentage,
  resolveWeekCellAppearance,
  restoreFloorIntoLayout,
  retiredAfterLaunch,
  rotatePlacementAbout,
  rotationFromLocalOffset,
  roundToMm,
  roundToOneDecimalPlace,
  rovingIndexForKey,
  samePlacement,
  sameRefList,
  sameZone,
  sanitizeLegalHtml,
  screenDirectionToPlanDelta,
  screenNudge,
  scrollPlanByKey,
  secureFileURL,
  selectedNodesFromBids,
  setCookie,
  shuffleArray,
  slugToDocument,
  snapBuildRotation,
  snapItemRotation,
  snapPlannerDistance,
  snapPlannerPoint,
  snapRotationDrag,
  snapWallPoint,
  sortCapacitySources,
  sortDataValuesByType,
  sortRows,
  spaceTypePath,
  spatialParentOfItem,
  spatialParentOfSurface,
  stackPlace,
  stackedChartColors,
  stampEntityCodes,
  stepRotation,
  stringToTimeUnit,
  stringToTwinEntityType,
  stripTwinBidPrefix,
  suggestColumnMap,
  suggestSpaceType,
  suggestsNotTracked,
  surfaceDisplayName,
  surfaceName,
  temporaryName,
  timeToMinutes,
  toDisplayManufacturer,
  trackedPlanEntities,
  transformTimeSeriesData,
  translateAnswerLines,
  trimStringFromChar,
  truncateString,
  twinBelongsToOrg,
  twinFloorShape,
  twinNameEntryFor,
  twinNameSignature,
  underlayAtPoint,
  underlayContrast,
  underlayImageScale,
  underlayNameFromFile,
  underlayPixelsToWorld,
  underlayWidthMetres,
  underlayWorldBounds,
  underlayWorldCorners,
  unverifiedMintBlock,
  updateProgressFactory,
  usablePoly,
  viewIndexAt,
  visibleFloors,
  wallPerpendicularOffset,
  wallRunCurrent,
  wallRunPresent,
  wallRunPresentIds,
  warnMissingIndicatorConfig,
  weekdayWindowsToCellIds,
  withAlignStateFrom,
  withCapacityReview,
  withFloorOrderFrom,
  withFloorTargetBids,
  withoutRenumbered,
  worldPixelsToFloor,
  worldPxToPane,
  worldToFloor,
  worldToUnderlayPixels,
  writePlanView,
  zeroPad,
  zoomAboutPoint
};
